import { COMPANY, BILL_INFO } from "@/lib/company";
import { LOGO_DATA } from "@/lib/logoData";
import QRCode from "qrcode";

async function staffVcardQr(s, p) {
  const clean = (v) => String(v || "").replace(/[\n\r;]/g, " ").trim();
  const vcard = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:;${clean(s.name)};;;`,
    `FN:${clean(s.name)}`,
    `ORG:${clean((p && p.name) || "Nisha Transport Corporation")}`,
    s.designation ? `TITLE:${clean(s.designation)}` : "",
    s.phone ? `TEL;TYPE=CELL:${clean(s.phone)}` : "",
    s.email || (p && p.email) ? `EMAIL:${clean(s.email || p.email)}` : "",
    p && p.website ? `URL:${clean(p.website)}` : "",
    (s.branch_address || (p && p.address)) ? `ADR;TYPE=WORK:;;${clean(s.branch_address || p.address)};;;;` : "",
    "END:VCARD",
  ].filter(Boolean).join("\n");
  try {
    return await QRCode.toDataURL(vcard, { margin: 1, width: 120, errorCorrectionLevel: "M" });
  } catch {
    return "";
  }
}

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(
    Number(n || 0)
  );

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso)
      .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
      .replace(/ /g, "\u00A0");
  } catch {
    return iso;
  }
};

const DAY3 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const signatureImg = (h = 40, withSeal = false) => {
  const s = localStorage.getItem("ntc_signature_image") || "";
  const seal = withSeal ? localStorage.getItem("ntc_seal_image") || "" : "";
  if (!s && !seal) return "";
  const sealH = Math.min(Math.round(h * 1.35), 76);
  return `<span style="display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:2px;">
    ${s ? `<img src="${s}" style="height:${h}px;max-width:160px;object-fit:contain;" />` : ""}
    ${seal ? `<img src="${seal}" style="height:${sealH}px;width:${sealH}px;object-fit:contain;" />` : ""}
  </span>`;
};
const fmtDateDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return fmtDate(iso);
  return `${fmtDate(iso)}\u00A0(${DAY3[d.getDay()]})`;
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigits(n) {
  if (n < 20) return ONES[n];
  return `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`;
}

function threeDigits(n) {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  let out = "";
  if (h) out += `${ONES[h]} Hundred${rest ? " " : ""}`;
  if (rest) out += twoDigits(rest);
  return out;
}

export function amountInWords(num) {
  const n = Math.floor(Number(num || 0));
  if (n === 0) return "Zero Rupees Only";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;
  let parts = [];
  if (crore) parts.push(`${twoDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (rest) parts.push(threeDigits(rest));
  return `${parts.join(" ").trim()} Rupees Only`;
}

const BASE_STYLE = `
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; margin: 0; padding: 32px; font-size: 13px; }
  .doc { max-width: 800px; margin: 0 auto; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0f172a; padding-bottom: 16px; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 46px; height: 46px; border-radius: 8px; background: #0f172a; color: #fff; display: grid; place-items: center; font-size: 24px; font-weight: 800; }
  .logo-img { width: 64px; height: 64px; object-fit: contain; border-radius: 8px; }
  .cname { font-size: 20px; font-weight: 800; letter-spacing: -0.5px; }
  .csub { font-size: 10px; text-transform: uppercase; letter-spacing: 3px; color: #64748b; }
  .cmeta { font-size: 11px; color: #475569; margin-top: 4px; line-height: 1.5; text-align: right; }
  .title { text-align: center; font-size: 16px; font-weight: 800; text-transform: uppercase; letter-spacing: 2px; margin: 20px 0 4px; text-decoration: underline; text-underline-offset: 6px; text-decoration-thickness: 2px; }
  .meta-grid { display: flex; justify-content: space-between; gap: 24px; margin: 18px 0; }
  .box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; flex: 1; }
  .box h4 { margin: 0 0 6px; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #64748b; }
  .box .big { font-size: 14px; font-weight: 700; }
  .row { display: flex; justify-content: space-between; padding: 2px 0; }
  .muted { color: #64748b; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #f1f5f9; color: #0f172a; text-align: left; padding: 8px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #0f172a; }
  td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
  .tight th { padding: 6px 5px; font-size: 9px; letter-spacing: 0.5px; }
  .tight td { padding: 6px 5px; font-size: 10.5px; }
  .grid th, .grid td { border: 1px solid #0f172a; }
  .right { text-align: right; }
  .totals { margin-top: 16px; margin-left: auto; width: 300px; }
  .totals .row { padding: 4px 0; }
  .grand { border-top: 2px solid #0f172a; margin-top: 6px; padding-top: 8px; font-size: 15px; font-weight: 800; }
  .words { margin-top: 12px; font-size: 12px; font-style: italic; color: #334155; }
  .sign { display: flex; justify-content: space-between; margin-top: 56px; }
  .sign div { text-align: center; font-size: 11px; color: #475569; border-top: 1px solid #94a3b8; padding-top: 6px; width: 200px; }
  .foot { margin-top: 32px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
  .status-tag { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; border: 1px solid #cbd5e1; }
  @media print { body { padding: 0; } .doc { max-width: 100%; } }
`;

function header(branch) {
  if (localStorage.getItem("ntc_letterhead_mode") === "1") {
    return `<div style="height:30mm;"></div>`;
  }
  let branchLine = "";
  try {
    const addr = branch ? (JSON.parse(localStorage.getItem("ntc_branch_addresses") || "{}") || {})[branch] : "";
    const ph = branch ? (JSON.parse(localStorage.getItem("ntc_branch_phones") || "{}") || {})[branch] : "";
    if (addr || ph) branchLine = `<div style="font-weight:700;color:#0f172a;">${esc(branch)} Branch: ${esc(addr || "")}${addr && ph ? " · " : ""}${ph ? `Ph: ${esc(ph)}` : ""}</div>`;
  } catch {}
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  return `
    <div class="head">
      <div class="brand">
        <img src="${logo}" alt="logo" class="logo-img" />
        <div>
          <div class="cname">${esc(COMPANY.name)}</div>
          ${COMPANY.brand ? `<div class="csub">${esc(COMPANY.brand)}</div>` : ""}
        </div>
      </div>
      <div class="cmeta">
        ${branchLine}
        ${esc(COMPANY.address).replace("Ma.Po.Si Nagar, ", "Ma.Po.Si Nagar,<br/>")}<br/>
        ${esc(COMPANY.gstin)} &nbsp; ${esc(COMPANY.pan)}<br/>
        ${esc(COMPANY.phone)} &nbsp; ${esc(COMPANY.email)}
      </div>
    </div>`;
}

function open(title, inner, opts = {}) {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    alert("Please allow pop-ups to print this document.");
    return;
  }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(title)}</title>
    <style>${BASE_STYLE}
    .toolbar { position: sticky; top: 0; z-index: 10; display: flex; gap: 10px; justify-content: flex-end; padding: 10px 12px; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; margin: -32px -32px 20px; }
    .toolbar button { cursor: pointer; border: none; border-radius: 6px; padding: 8px 16px; font-size: 13px; font-weight: 700; }
    .toolbar .print-btn { background: #0f172a; color: #fff; }
    .toolbar .pdf-btn { background: #2563eb; color: #fff; }
    .toolbar .img-btn { background: #16a34a; color: #fff; }
    @media print { .toolbar { display: none !important; } }
    .frame { border: 1.5px solid #0f172a; padding: 16px 18px; display: flex; flex-direction: column; }
    .frame-full { min-height: 1040px; }
    .challan-page { page-break-after: always; page-break-inside: avoid; margin-bottom: 16px; }
    .challan-page:last-child { page-break-after: auto; margin-bottom: 0; }
    @page { size: A4; margin: 0; }
    @media print { body { margin: 10mm 12mm; padding: 0; } .frame-full { min-height: 272mm; } .challan-page { margin-bottom: 0; } .challan-page + .challan-page { padding-top: 16px; } .ref-page { margin-top: 12mm !important; min-height: 250mm !important; padding-top: 16px; } }
    ${opts.accent ? `.head { border-bottom-color: ${opts.accent} !important; } .cname { color: ${opts.accent}; }` : ""}
    </style></head>
    <body><div class="doc">
    <div class="toolbar no-print">
      ${opts.downloadImage ? `
      <button class="img-btn" onclick="dlCard('png')">&#11015; PNG</button>
      <button class="img-btn" onclick="dlCard('jpg')">&#11015; JPG</button>
      <button class="img-btn" style="background:#dc2626;" onclick="dlCard('pdf')">&#11015; PDF</button>
      <button class="img-btn" style="background:#1d4ed8;" onclick="dlCard('word')">&#11015; Word</button>` : ""}
      <button class="pdf-btn" onclick="window.print()">⬇ Save as PDF</button>
      <button class="print-btn" onclick="window.print()">🖨 Print</button>
    </div>
    ${opts.downloadImage ? `<div id="capture-root">` : ""}${opts.hideHeader ? "" : ""}${opts.frame ? `<div class="frame${opts.fullPage ? " frame-full" : ""}">` : ""}${opts.hideHeader ? "" : header(opts.branch)}${inner}${opts.frame ? `</div>` : ""}${opts.downloadImage ? `</div>` : ""}
    </div>
    ${opts.downloadImage ? `<script src="https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"><\/script>
    <script src="https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js"><\/script>
    <script>
    var CARD_NAME = ${JSON.stringify(String(title).replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-") || "card")};
    function saveUrl(href, ext){ var a = document.createElement('a'); a.download = CARD_NAME + '.' + ext; a.href = href; a.click(); }
    function dlCard(fmt){
      var btns = document.querySelectorAll('.toolbar button'); btns.forEach(function(b){ b.disabled = true; });
      html2canvas(document.getElementById('capture-root'), { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(function(canvas){
        if (fmt === 'png') saveUrl(canvas.toDataURL('image/png'), 'png');
        else if (fmt === 'jpg') saveUrl(canvas.toDataURL('image/jpeg', 0.92), 'jpg');
        else if (fmt === 'pdf') {
          var jpg = canvas.toDataURL('image/jpeg', 0.92);
          var pdf = new window.jspdf.jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
          var pw = 190, ph = pw * canvas.height / canvas.width;
          if (ph > 277) { ph = 277; pw = ph * canvas.width / canvas.height; }
          pdf.addImage(jpg, 'JPEG', (210 - pw) / 2, 10, pw, ph);
          pdf.save(CARD_NAME + '.pdf');
        }
        else if (fmt === 'word') {
          var jpg2 = canvas.toDataURL('image/jpeg', 0.92);
          var html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><title>' + CARD_NAME + '</title></head><body><img src="' + jpg2 + '" style="width:620px;" /></body></html>';
          var blob = new Blob(['\\ufeff', html], { type: 'application/msword' });
          saveUrl(URL.createObjectURL(blob), 'doc');
        }
        btns.forEach(function(b){ b.disabled = false; });
      }).catch(function(){ btns.forEach(function(b){ b.disabled = false; }); });
    }
    <\/script>` : ""}
    ${opts.downloadImage ? "" : `<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>`}
    </body></html>`);
  w.document.close();
}

const QR_DATA = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOwAAADsAQAAAABfqHe5AAAC5klEQVR4nMWZUapcOQxEj0P+yzvo/S+rd3C8gpoP3eRr5hF4cY+h4dIGy5ItVZW8yhfj/PhqFr45fdZaZ63DgnPO2mvvc/baZ6313cW/nsZWbYmSpkBLY6t3/eYseK2lr80570OzjgfO+v7iX46fAKGwut97xy7Z9G8s/ifTh8UGkLOO+2X3B2xja01TE2qopvb+eS83+K9TG+7apjOgtczPPP9etm0BTLEVKhVs9LbfSgI2EcQAgdjc97sEO5UlsWkspPf9jg2RBmqaZw/JfdsoVSja0KJtNNfvOVPDUyJqxjQf8TuYOWHaJohtk/t3Lbakk1WKJJT4Cb8BtXPjZSpN5uCv51iogEmL0lKsfCDmlJLZw9SzOJl++56nJVar2jQNnQt3HUuUEIc1mUFVygdi3jY+R06MFhPweo6VWksICWSiYMp926YDYzhpDhbp9fz+wcvzxDtwfC27j2Tx8jZfw/h4blW01Jj7OaZAm8wXaCqC92sLtYaO4xmvY73OFSfIpU3r+FwK9/H7BxjO9mz3IRx26IZgb2uiDdAFS1i4Dfuds/C2LmlI0goxpA9FJv2ENrBWIyadfbTpfRyT0d7CULRJ8IdJXOdrhtFEMZi2gyQfiPmwBKvDy0mYDVyvLcS0CYEKKaIx1/laccQnVGvTAI3x+nnnd5J1VEpaEz5QzwsthCTRKe4PZb2PJaN2yZST6fI4xP0D/HyY6gNd6pDz65zpJ/t99jsAbMN8LXLyPtd5C0mnrnUqLI8iv57fTaAjR5oxOn2u+zq0tDI9VNs6qriF+/ntL15c8wvH4sDL9btGt91hgawctu+Xf2PxP8FvmvDcLx0cF3K5p0nbapo4oluow17v4zeYx9i0eoa16H3u0Cp1Giw15eEScjvmij4PFjJ4Xjttr+v8HJDX7nvvCryz9zqSv7T4f49BjmIaa6DTze39vuK8G0AzXY6U6DRBPtFfcx4smG5qhstwHUvW//gu+A/tVkZrmENuLwAAAABJRU5ErkJggg==";

function invoiceInner(inv, customer, customTerms, duplicate = false, podPortalLink = "") {
  const billStyle = `<style>@media print { .frame-full { height: 267mm; min-height: 0 !important; overflow: hidden; } }</style>`;
  const items = inv.items || [];
  const cust = { ...(customer || {}) };
  if (inv.third_party_name) {
    cust.name = inv.third_party_name;
    cust.address = inv.third_party_address || "";
    cust.gstin = inv.third_party_gst || "";
    cust.state = "";
    cust.pincode = "";
  }
  const rcm = !!inv.rcm;
  const gstBy = (inv.gst_payable_by || "CONSIGNEE").toString().toUpperCase();
  const gstByTitle = gstBy.charAt(0) + gstBy.slice(1).toLowerCase();
  const subtotal = inv.subtotal ?? items.reduce((s, i) => s + (i.quantity || 0) * (i.rate || 0), 0);
  const tax = rcm ? 0 : (inv.tax_amount ?? (subtotal * (inv.tax_percent || 0)) / 100);
  const extras = (Number(inv.unloading_charge) || 0) + (Number(inv.halting_charge) || 0);
  const total = inv.total ?? (rcm ? subtotal + extras : subtotal + extras + tax);

  // GST split: intra-state (same state code as company GSTIN 33) -> CGST+SGST, else IGST
  const compStateCode = (COMPANY.gstin.match(/(\d{2})/) || [])[1] || "33";
  const custStateCode = ((cust.gstin || "").trim().slice(0, 2)) || "";
  const intra = custStateCode && custStateCode === compStateCode;
  const cgst = intra ? tax / 2 : 0;
  const sgst = intra ? tax / 2 : 0;
  const igst = intra ? 0 : tax;

  const dmy = (d) => (d ? `${String(d).slice(8, 10)}-${String(d).slice(5, 7)}-${String(d).slice(0, 4)}` : "");
  const rows = items
    .map((it, i) => {
      const freight = inr((it.quantity || 0) * (it.rate || 0));
      const hasCols = it.lr_no || it.from_place || it.destination || it.invoice_no || it.vehicle_no || it.lr_date;
      if (!hasCols && it.description) {
        return `<tr style="height:20px;">
        <td colspan="10" style="white-space:pre-line;">${esc(it.description)}</td>
        <td class="right">${freight}</td>
      </tr>`;
      }
      return `<tr style="height:20px;">
        <td style="border-right:1px solid #cbd5e1;">${esc(it.lr_no || "")}</td>
        <td style="white-space:nowrap;border-right:1px solid #cbd5e1;">${dmy(it.lr_date)}</td>
        <td style="word-break:break-word;border-right:1px solid #cbd5e1;">${esc(it.from_place || "")}</td>
        <td style="word-break:break-word;border-right:1px solid #cbd5e1;">${esc(it.destination || "")}</td>
        <td style="text-align:center;border-right:1px solid #cbd5e1;">${esc(it.packages || "—")}</td>
        <td style="word-break:break-all;border-right:1px solid #cbd5e1;">${esc(it.invoice_no || "")}${it.dc_no ? `<div style="color:#475569;">DC: ${esc(it.dc_no)}</div>` : ""}${Number(it.invoice_value_total) ? `<div style="color:#475569;font-weight:700;">Val: ${inr(it.invoice_value_total)}</div>` : ""}</td>
        <td style="white-space:nowrap;border-right:1px solid #cbd5e1;">${dmy(it.invoice_date)}</td>
        <td style="border-right:1px solid #cbd5e1;">${esc(it.vehicle_no || "")}</td>
        <td style="border-right:1px solid #cbd5e1;">${esc(it.vehicle_type || "")}</td>
        <td style="border-right:1px solid #cbd5e1;">${esc(it.weight || "")}</td>
        <td class="right" style="white-space:nowrap;">${freight}</td>
      </tr>`;
    })
    .join("");

  const bankHtml = BILL_INFO.bank_lines.map((l) => esc(l)).join("<br/>");
  const chargeKeys = ["loading_charge", "unloading_charge", "loading_point_halting_charge", "halting_charge", "labour_union_charge", "crane_charge", "vehicle_return_charge", "extra_delivery_charge", "packing_charge"];
  const fillerCount = Math.max(3, 10 - items.length - chargeKeys.filter((k) => Number(inv[k]) > 0).length);
  const gstFiller = `<td style="height:18px;border-bottom:none;border-right:1px solid #cbd5e1;">&nbsp;</td>`.repeat(9);
  const termsList = (customTerms && customTerms.length ? customTerms : BILL_INFO.terms);
  const termsHtml = termsList
    .map((t, i) => `<div style="margin:2px 0;">${i + 2}. ${esc(t)}</div>`)
    .join("");

  const inner = `
    ${billStyle}
    ${duplicate ? `<div style="position:fixed;top:42%;left:50%;transform:translate(-50%,-50%) rotate(-28deg);font-size:58px;font-weight:900;letter-spacing:10px;color:rgba(100,116,139,0.18);white-space:nowrap;z-index:999;pointer-events:none;">DUPLICATE COPY</div>` : ""}
    <div style="display:flex;justify-content:space-between;align-items:baseline;margin-top:14px;">
      <div style="font-size:15px;font-weight:800;letter-spacing:1px;">BILL No. : ${esc((inv.invoice_no || "—").replace(/^INV[-\s]?/i, ""))}</div>
      <div style="font-size:12px;font-weight:700;">Branch : ${esc(inv.office || BILL_INFO.branch)}</div>
      <div style="font-size:13px;font-weight:600;">BILL DATE : ${dmy(inv.issue_date)}</div>
    </div>
    <div style="display:flex;justify-content:space-between;font-size:12px;color:#334155;margin-top:6px;border-bottom:1px solid #0f172a;padding-bottom:8px;">
      <div>Bill Branch : ${esc(inv.office || BILL_INFO.branch)}</div>
      <div>SAC CODE : ${esc(BILL_INFO.sac_code)}</div>
      <div>Customer Code : ${esc(cust.customer_code || "—")}</div>
    </div>

    <div style="margin-top:12px;">
      <div style="font-size:11px;letter-spacing:1.5px;color:#64748b;font-weight:600;">BILL To</div>
      <div style="font-size:14px;font-weight:700;margin-top:2px;">M/S. ${esc((cust.name || inv.customer_name || "—").toUpperCase())}</div>
      ${(() => {
        const statePin = [cust.state, cust.pincode].filter(Boolean).join("-");
        const full = [cust.address, statePin].filter(Boolean).join(", ");
        const addrLine = (l) => `<div style="font-size:12px;color:#334155;line-height:1.5;white-space:nowrap;">${esc(l)}</div>`;
        if (!full) return "";
        const rawParts = String(full).split(",").map((x) => x.trim()).filter(Boolean);
        const parts = [];
        rawParts.forEach((p) => {
          const m = p.match(/^(.+?)\s+(\S+\s+(?:DISTRICT|DIST\.?))$/i);
          if (m) { parts.push(m[1].trim()); parts.push(m[2].trim()); }
          else parts.push(p);
        });
        let lines = [full];
        if (parts.length >= 3) {
          lines = [parts.slice(0, parts.length - 2).join(", ") + ",", parts.slice(parts.length - 2).join(", ")];
        } else if (parts.length === 2) {
          lines = [parts[0] + ",", parts[1]];
        }
        return lines.map(addrLine).join("");
      })()}
      ${cust.gstin ? `<div style="font-size:12px;color:#334155;">GSTIN: ${esc(cust.gstin)}</div>` : ""}
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:8px 0 0;"></div>

    <div style="margin-top:10px;font-size:12px;">
      <div style="display:flex;gap:24px;">
        <div><b>CONSIGNOR NAME :</b> M/S. ${esc(String(inv.consignor || "—").replace(/^M\/?S\.?\s*/i, ""))}</div>
        ${inv.payment_basis ? `<div style="margin-left:auto;"><b>BASIS :</b> ${esc(String(inv.payment_basis).toUpperCase())}</div>` : ""}
      </div>
      <div style="margin-top:2px;"><b>CONSIGNEE NAME :</b> M/S. ${esc(String(inv.consignee || "—").replace(/^M\/?S\.?\s*/i, ""))}</div>
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:8px 0 0;"></div>

    ${inv.bill_description ? `<div style="margin-top:10px;font-size:12px;color:#0f172a;"><b>Description:</b> ${esc(inv.bill_description)}</div>` : ""}

    <table class="tight" style="margin-top:0;flex:1;width:100%;table-layout:fixed;">
      <thead><tr>
        <th style="width:5.5%;border-right:1px solid #cbd5e1;">LR No.</th><th style="width:9.5%;border-right:1px solid #cbd5e1;">LR Date</th><th style="width:9.5%;border-right:1px solid #cbd5e1;">From</th><th style="width:10.5%;border-right:1px solid #cbd5e1;">Destination</th><th style="width:4.5%;text-align:center;border-right:1px solid #cbd5e1;">PKG</th><th style="width:14.5%;border-right:1px solid #cbd5e1;">Invoice No.</th><th style="width:9.5%;border-right:1px solid #cbd5e1;">Invoice Date</th><th style="width:9.5%;border-right:1px solid #cbd5e1;">Vehicle No.</th><th style="width:10%;border-right:1px solid #cbd5e1;">Vehicle Type</th><th style="width:7%;border-right:1px solid #cbd5e1;">Weight</th><th class="right" style="width:9.5%;">Freight</th>
      </tr></thead>
      <tbody>
        ${rows || `<tr><td colspan="11" class="muted">No line items</td></tr>`}
        ${Number(inv.loading_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Loading Charge</td><td class="right">${inr(inv.loading_charge)}</td></tr>` : ""}
        ${Number(inv.unloading_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Unloading Charge</td><td class="right">${inr(inv.unloading_charge)}</td></tr>` : ""}
        ${Number(inv.loading_point_halting_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Loading Point Halting Charge</td><td class="right">${inr(inv.loading_point_halting_charge)}</td></tr>` : ""}
        ${Number(inv.halting_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Unloading Point Halting Charge</td><td class="right">${inr(inv.halting_charge)}</td></tr>` : ""}
        ${Number(inv.labour_union_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Labour Union Charge</td><td class="right">${inr(inv.labour_union_charge)}</td></tr>` : ""}
        ${Number(inv.crane_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Crane Charge</td><td class="right">${inr(inv.crane_charge)}</td></tr>` : ""}
        ${Number(inv.vehicle_return_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Vehicle Return Charge</td><td class="right">${inr(inv.vehicle_return_charge)}</td></tr>` : ""}
        ${Number(inv.extra_delivery_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Extra Delivery Charge</td><td class="right">${inr(inv.extra_delivery_charge)}</td></tr>` : ""}
        ${Number(inv.packing_charge) > 0 ? `<tr style="height:20px;"><td colspan="10" class="right">Packing Charge</td><td class="right">${inr(inv.packing_charge)}</td></tr>` : ""}
        ${`<tr>${`<td style="border-bottom:none;border-right:1px solid #cbd5e1;">&nbsp;</td>`.repeat(10)}<td style="border-bottom:none;">&nbsp;</td></tr>`.repeat(fillerCount)}
        <tr>${gstFiller}<td class="right" style="height:18px;border-right:1px solid #cbd5e1;border-top:1px solid #94a3b8;border-bottom:1px solid #94a3b8;">SGST</td><td class="right" style="height:18px;border-top:1px solid #94a3b8;border-bottom:1px solid #94a3b8;">${inr(sgst)}</td></tr>
        <tr>${gstFiller}<td class="right" style="height:18px;border-right:1px solid #cbd5e1;border-bottom:1px solid #94a3b8;">CGST</td><td class="right" style="height:18px;border-bottom:1px solid #94a3b8;">${inr(cgst)}</td></tr>
        <tr>${gstFiller}<td class="right" style="height:18px;border-right:1px solid #cbd5e1;">IGST</td><td class="right" style="height:18px;">${inr(igst)}</td></tr>
      </tbody>
    </table>

    <div>
      <div style="border-bottom:1px solid #0f172a;"></div>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-top:6px;font-size:11px;">
        <div style="text-transform:uppercase;"><b>Amount in Words:</b> <b>${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</b></div>
        <div style="white-space:nowrap;font-weight:800;font-size:12px;">TOTAL DUE AMOUNT &nbsp; ${inr(total)}</div>
      </div>
      <div style="border-bottom:1px solid #0f172a;margin:8px 0 0;"></div>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-top:12px;">
        <div style="font-size:11px;color:#334155;line-height:1.6;max-width:60%;">
          <div style="margin:2px 0;">1. ${bankHtml}</div>
          ${termsHtml}
        </div>
        <div style="width:240px;text-align:center;">
          <div style="font-weight:700;font-size:12px;">FOR ${esc(COMPANY.name.toUpperCase())}</div>
          <div style="height:80px;display:flex;align-items:flex-end;justify-content:center;">${signatureImg(60, true)}</div>
          <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Authorised Signatory</div>
        </div>
      </div>
      ${podPortalLink ? `<div style="display:flex;align-items:center;gap:10px;margin-top:10px;border:1px solid #e2e8f0;border-radius:6px;padding:5px 10px;">
        <img src="${podPortalLink}/qr?base=${encodeURIComponent(process.env.REACT_APP_BACKEND_URL || "")}" style="width:52px;height:52px;" />
        <div style="font-size:10px;color:#334155;line-height:1.5;"><b>CUSTOMER POD PORTAL</b> — scan the QR or visit the link to view all your delivered PODs anytime:<br/><span style="word-break:break-all;color:#0369a1;font-weight:600;">${esc(podPortalLink)}</span></div>
      </div>` : ""}
      <div style="text-align:center;margin-top:24px;border-top:1.5px solid #0f172a;padding-top:12px;font-size:12px;font-weight:600;letter-spacing:1px;color:#0f172a;">THANK YOU FOR YOUR BUSINESS</div>
    </div>
  `;
  return inner;
}

async function fetchBillTerms() {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/company-profile`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("ntc_token") || ""}` },
    });
    const data = await res.json();
    return String(data.bill_terms || "").split("\n").map((t) => t.trim()).filter(Boolean);
  } catch {
    return [];
  }
}

async function enrichInvoiceItems(inv) {
  const items = (inv && inv.items) || [];
  if (!items.some((it) => it.lr_no && (!it.vehicle_type || !it.weight))) return inv;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipments`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("ntc_token") || ""}` },
    });
    const ships = await res.json();
    const norm = (v) => String(v || "").replace(/^CN[-\s]?/i, "").replace(/^0+/, "").trim().toLowerCase();
    const byLr = {};
    (Array.isArray(ships) ? ships : []).forEach((s) => {
      const k = norm(s.lr_no || s.consignment_no);
      if (k && !byLr[k]) byLr[k] = s;
    });
    const wt = (s) => s.weight_kgs || (Number(s.weight_tons) ? `${s.weight_tons} MT` : "");
    return {
      ...inv,
      items: items.map((it) => {
        const s = byLr[norm(it.lr_no)];
        if (!s) return it;
        return { ...it, vehicle_type: it.vehicle_type || s.vehicle_type || "", weight: it.weight || wt(s) };
      }),
    };
  } catch {
    return inv;
  }
}

async function fetchPodPortal(customerName) {
  if (!customerName) return "";
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pod-portal`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ntc_token") || ""}` },
      body: JSON.stringify({ customer_name: customerName }),
    });
    if (!res.ok) return "";
    const data = await res.json();
    return `${process.env.REACT_APP_BACKEND_URL}/api/public/pod-portal/${data.id}`;
  } catch {
    return "";
  }
}

export async function printInvoice(inv, customer, opts = {}) {
  const terms = await fetchBillTerms();
  inv = await enrichInvoiceItems(inv);
  const portal = await fetchPodPortal(customer?.name || inv.customer_name);
  open(`Tax-Invoice-${(inv.invoice_no || "").replace(/^INV[-\s]?/i, "")}`, invoiceInner(inv, customer, terms, !!opts.duplicate, portal), { hideFooter: true, frame: true, fullPage: true });
}

export async function printInvoices(list, resolveCustomer) {
  const arr = (list || []).filter(Boolean);
  if (arr.length === 0) return;
  const terms = await fetchBillTerms();
  const enriched = await Promise.all(arr.map((inv) => enrichInvoiceItems(inv)));
  const cust = (inv) => (resolveCustomer ? resolveCustomer(inv) : {});
  const portals = await Promise.all(enriched.map((inv) => fetchPodPortal(cust(inv)?.name || inv.customer_name)));
  if (enriched.length === 1) return open(`Tax-Invoice-${(enriched[0].invoice_no || "").replace(/^INV[-\s]?/i, "")}`, invoiceInner(enriched[0], cust(enriched[0]), terms, false, portals[0]), { hideFooter: true, frame: true, fullPage: true });
  const inner = enriched
    .map((inv, i) => `<div class="frame frame-full" style="${i > 0 ? "page-break-before:always;margin-top:24px;" : ""}">${i > 0 ? header() : ""}${invoiceInner(inv, cust(inv), terms, false, portals[i])}</div>`)
    .join("");
  open("Bills", inner, { hideFooter: true });
}

function challanInner(c, copyName = "") {
  const challanNo = String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—";
  const freight = Number(c.hire_amount) || 0;
  const hamali = Number(c.hamali_charge) || 0;
  const totalLess = c.total_less ?? (Number(c.advance_paid) || 0) + (Number(c.deduction_amt) || 0) + (Number(c.less_tds) || 0);
  const extras = (Number(c.unloading_charge) || 0) + (Number(c.challan_charge) || 0) + (Number(c.extra_weight_charge) || 0) + (Number(c.extra_delivery_charge) || 0);
  const balance = c.balance ?? freight + hamali + (Number(c.less_odc) || 0) + extras - totalLess;
  const fld = (label, val) =>
    `<div style="display:flex;gap:6px;font-size:11.5px;margin:3px 0;"><span style="min-width:96px;color:#64748b;">${esc(label)}</span><span style="flex:1;border-bottom:${c.blank ? "2px solid #0f172a" : "1px dotted #94a3b8"};font-weight:600;${c.blank ? "min-height:18px;" : ""}">${esc(val || "")}</span></div>`;
  const money = (label, val, bold) =>
    `<div style="display:flex;justify-content:space-between;font-size:12px;margin:2px 0;${bold ? "font-weight:800;border-top:1px solid #cbd5e1;padding-top:4px;" : ""}"><span>${esc(label)}</span><span>${c.blank ? "" : inr(val)}</span></div>`;
  const signName = (n) =>
    n && !c.blank ? `<div style="margin-top:2px;font-weight:700;color:#0f172a;">${esc(n)}</div>` : "";
  const lrRows =
    Array.isArray(c.lr_items) && c.lr_items.length
      ? c.lr_items
      : Array.isArray(c.lr_list) && c.lr_list.length > 1
        ? c.lr_list.map((x) => ({ branch: c.branch || c.engaged_office, lr_no: x.lr_no, lr_date: x.lr_date, packages: c.no_of_packages, contents: c.contents || c.goods, weight: c.weight_kgs }))
        : [{ branch: c.branch || c.engaged_office || "Chennai", lr_no: c.lr_no || c.consignment_no, lr_date: c.lr_date, packages: c.no_of_packages, contents: c.contents || c.goods, weight: c.weight_kgs }];
  const inner = `
    ${copyName ? `<div style="text-align:right;margin:0 0 2px;"><span style="display:inline-block;border:1.5px solid #0f172a;border-radius:4px;padding:2px 12px;font-weight:800;font-size:10.5px;letter-spacing:1.5px;">${esc(copyName)}</span></div>` : ""}
    <div class="title" style="letter-spacing:2px;">VEHICLE HIRE CHALLAN</div>
    <div style="display:flex;justify-content:space-between;font-size:11.5px;margin:2px 0 8px;">
      <div>
        <div><b>Branch Office:</b> ${esc(c.engaged_office || c.branch || "Chennai")}</div>
        <div><b>Vehicle No.:</b> ${esc(c.vehicle_no || "—")}</div>
        <div><b>Loading Date:</b> ${fmtDate(c.loading_date || c.date)}</div>
      </div>
      <div style="text-align:right;">
        <div><b>Vendor/Broker Code:</b> ${esc(c.vendor_code || "")}</div>
        <div><b>Challan No.:</b> ${challanNo}</div>
        <div><b>Challan Date:</b> ${c.date || c.booking_date ? fmtDate(c.date || c.booking_date) : "—"}</div>
      </div>
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:0 0 8px;"></div>
    <div style="display:flex;gap:16px;border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;">
      <div style="flex:1;">
        ${fld("Engaged Office", c.engaged_office)}
        ${fld("Vehicle Owner Name", c.owner_name)}
        ${fld("Vehicle Owner Mobile No.", c.owner_mobile)}
        ${fld("Vehicle Owner PAN No.", c.pan_no)}
        ${fld("Driver Name", c.driver_name)}
        ${fld("Driver Mobile No.", c.driver_phone)}
        ${fld("Vendor/Broker Name", c.vendor_broker_name)}
        ${fld("Vendor/Broker Code", c.vendor_code)}
        ${fld("Vendor/Broker Mobile No.", c.vendor_mobile)}
        ${fld("Vendor/Broker PAN No.", c.vendor_pan)}
        ${fld("Vendor/Broker GSTIN No.", c.vendor_gstin)}
      </div>
      <div style="flex:1;">
        ${fld("Chassis No.", c.chassis_no)}
        ${fld("Engine No.", c.engine_no)}
        ${fld("Vehicle Model", c.model)}
        ${fld("Vehicle Model Type", c.model_type)}
        ${fld("Vehicle Make Year", c.make)}
        ${fld("Permit upto", fmtDate(c.permit_valid_upto))}
        ${fld("FC Valid Upto", fmtDate(c.fc_valid_upto))}
        ${fld("Driving License No.", c.dl_no)}
        ${fld("Driving License Validity", c.dl_validity ? fmtDate(c.dl_validity) : "")}
        ${fld("Insurance Company Name", c.insurance_co)}
        ${fld("Insurance Policy No.", c.policy_no)}
        ${fld("Insurance Valid Upto", fmtDate(c.ins_valid_upto))}
      </div>
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:8px 0 0;"></div>
    <div style="display:flex;gap:16px;margin-top:8px;">
      ${(() => {
        const routeBox = (label, v) => {
          const val = v && !c.blank
            ? `<span style="display:inline-block;border:1.6px solid #0f172a;border-radius:18px;padding:1px 16px;font-weight:800;font-size:13.5px;letter-spacing:0.8px;text-transform:uppercase;">${esc(v)}</span>`
            : `<span style="flex:1;border-bottom:${c.blank ? "2px solid #0f172a" : "1px dotted #94a3b8"};min-height:20px;"></span>`;
          return `<div style="flex:1;display:flex;align-items:center;gap:8px;font-size:11.5px;"><span style="min-width:40px;color:#64748b;">${label}</span>${val}</div>`;
        };
        return routeBox("From:", c.origin) + routeBox("To:", c.destination);
      })()}
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:6px 0 8px;"></div>
    <table style="margin-top:8px;">
      <thead><tr>
        <th>SL No.</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>Packages</th>
        <th>Contents</th><th>Weight</th><th>Type of Vehicle</th>
      </tr></thead>
      <tbody>${lrRows.map((x, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(x.branch || c.branch || c.engaged_office || "—")}</td>
        <td>${esc(x.lr_no || "—")}</td>
        <td>${x.lr_date ? fmtDate(x.lr_date) : "—"}</td>
        <td>${esc(x.packages || "—")}</td>
        <td>${esc(x.contents || "—")}</td>
        <td>${i === 0 ? esc(x.weight || "—") : esc(x.weight || "")}</td>
        <td>${i === 0 ? esc(c.vehicle_type || "—") : ""}</td>
      </tr>`).join("")}
      ${lrRows.length > 1 ? `<tr style="font-weight:800;background:#f8fafc;">
        <td colspan="4" style="text-align:right;">TOTAL (${lrRows.length} LRs)</td>
        <td>${(() => { const t = lrRows.reduce((s, x) => s + (parseFloat(String(x.packages ?? "").replace(/[^0-9.]/g, "")) || 0), 0); return t > 0 ? (Number.isInteger(t) ? t : t.toFixed(2)) : "—"; })()}</td>
        <td colspan="3"></td>
      </tr>` : ""}</tbody>
    </table>
    <div style="border-bottom:1.5px solid #0f172a;margin:6px 0 0;"></div>
    <div style="display:flex;gap:16px;margin-top:8px;">
      <div style="flex:1;display:flex;flex-direction:column;">
        <div style="font-size:10.5px;color:#475569;line-height:1.6;border:1px solid #e2e8f0;border-radius:6px;padding:8px;">
          <b>Terms &amp; Conditions:</b> If shipment is delivered late, a penalty of Rs.1000/- per day applies. If Delivery Acknowledgment is not submitted within 20 days after delivery, Rs.100/- per day will be deducted from freight. Payment by A/c Payee cheque only. All required documents given to the lorry driver / owner, who will be responsible if he violates any check-posts on the way.
          <div style="margin-top:8px;font-weight:700;">NO UNLOADING ON HOLIDAY / SUNDAY / AFTER OFFICE HOURS</div>
        </div>
        <div style="margin-top:auto;border-top:1px solid #0f172a;padding-top:6px;font-size:12px;"><b>Amount in words:</b> <b>${c.blank ? "" : esc(amountInWords(balance))}</b></div>
      </div>
      <div style="width:230px;">
        ${money("Vehicle Hire", freight)}
        ${hamali > 0 || c.blank ? money("Loading Charge", hamali) : ""}
        ${Number(c.unloading_charge) > 0 || c.blank ? money("Unloading Charge", c.unloading_charge) : ""}
        ${Number(c.challan_charge) > 0 || c.blank ? money("Challan Charge", c.challan_charge) : ""}
        ${Number(c.extra_weight_charge) > 0 || c.blank ? money("Extra Weight Charge", c.extra_weight_charge) : ""}
        ${Number(c.extra_delivery_charge) > 0 || c.blank ? money("Extra Delivery Charge", c.extra_delivery_charge) : ""}
        ${money("Advance Amount", c.advance_paid)}
        ${money("ODC Amount", c.less_odc)}
        ${money("Deduction Amount", c.deduction_amt)}
        ${money("Less TDS", c.less_tds)}
        <div style="border-bottom:1.5px solid #0f172a;padding-bottom:2px;">${money("Total Less", totalLess)}</div>
        ${money("Balance Amount", balance, true)}
        ${c.extra_charge_reason ? `<div style="margin-top:4px;font-size:9px;border-top:1px dashed #94a3b8;padding-top:3px;"><b>Extra Charges Reason:</b> ${esc(c.extra_charge_reason)}</div>` : ""}
        ${c.charge_approval_status === "Approved" && c.charge_approved_at ? `<div style="margin-top:3px;font-size:8.5px;font-weight:800;color:#047857;border:1.2px solid #047857;border-radius:3px;padding:2px 5px;text-align:center;letter-spacing:0.3px;">✔ EXTRA CHARGES APPROVED BY ADMIN${c.charge_approved_by ? ` (${esc(c.charge_approved_by)})` : ""} — ${new Date(c.charge_approved_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>` : ""}
        ${copyName === "ACCOUNT COPY" && Array.isArray(c.charge_edits) && c.charge_edits.length ? `<div style="margin-top:4px;font-size:8px;border:1px dashed #94a3b8;border-radius:3px;padding:3px 5px;"><div style="font-weight:800;letter-spacing:0.5px;margin-bottom:1px;">CHARGE EDIT AUDIT (Vehicle Payments)</div>${c.charge_edits.map((e) => `<div>• ${esc(fmtDate(e.date) || e.date || "")} — ${esc(e.label || "")}: ${esc(e.old || "")} → <b>${esc(e.new || "")}</b>${e.reason ? ` (${esc(e.reason)})` : ""} — by ${esc(e.by || "")}</div>`).join("")}</div>` : ""}
        ${(() => {
          if (copyName !== "ACCOUNT COPY") return "";
          const vs = [
            ...(c.advance_voucher_no ? [`${esc(c.advance_voucher_no)} (Vehicle Hire Advance${Number(c.advance_paid) ? ` ₹${Number(c.advance_paid).toLocaleString("en-IN")}` : ""})`] : []),
            ...((c.payments || []).filter((p) => p.voucher_no).map((p) => `${esc(p.voucher_no)} (${esc(p.type || "Payment")}${Number(p.amount) ? ` ₹${Number(p.amount).toLocaleString("en-IN")}` : ""})`)),
          ];
          if (!vs.length) return "";
          return `<div style="margin-top:4px;font-size:8px;border:1px dashed #94a3b8;border-radius:3px;padding:3px 5px;"><div style="font-weight:800;letter-spacing:0.5px;margin-bottom:1px;">LINKED DEBIT VOUCHERS (auto-generated)</div><div>${vs.join(" &nbsp;·&nbsp; ")}</div></div>`;
        })()}
      </div>
    </div>
    <div style="border-bottom:1px solid #0f172a;margin:4px 0 2px;"></div>
    <div style="border:1.5px solid #0f172a;border-radius:4px;margin-top:6px;padding:6px 10px;">
      <div style="font-size:8.5px;font-weight:800;letter-spacing:1px;">POD ACKNOWLEDGEMENT — Signed POD / delivery proof received back from the driver &amp; vendor/broker.</div>
      <table style="border-collapse:collapse;width:100%;margin-top:14px;"><tr>
        <td style="border:none;font-size:9px;width:32%;padding:0;">POD Received By: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:120px;">&nbsp;</span></td>
        <td style="border:none;font-size:9px;width:28%;padding:0;">POD Received Date: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:75px;">&nbsp;</span></td>
        <td style="border:none;font-size:9px;width:40%;text-align:right;padding:0;">Signature &amp; Stamp: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:140px;">&nbsp;</span></td>
      </tr></table>
    </div>
    ${c.remarks ? `<div class="words"><b>Remarks: ${esc(c.remarks)}</b></div>` : ""}
    <div style="display:flex;justify-content:space-between;margin-top:auto;padding-top:32px;font-size:10.5px;color:#475569;text-align:center;">
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Driver Signature${signName(c.driver_name)}</div>
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Loading Staff${signName(c.loading_staff_name)}</div>
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Branch Incharge${signName(c.branch_incharge_name)}</div>
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Traffic In Charge${signName(c.traffic_incharge_name)}</div>
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Cashier${signName(c.cashier_name)}</div>
      <div style="border-top:1px solid #94a3b8;padding-top:4px;width:15%;">Passed By${signName(c.passed_by_name)}</div>
    </div>
  `;
  return inner;
}

const CHALLAN_COPIES = ["LORRY COPY", "ACCOUNT COPY", "HO COPY", "FILE COPY"];

const CHALLAN_STYLE = `<style>.head{border-bottom-width:1px !important;} .challan-page th{border-bottom-width:1px !important;}</style>`;

function challanCopyPages(c, copies = CHALLAN_COPIES) {
  return CHALLAN_STYLE + copies.map((cp) => `<div class="challan-page"><div class="frame frame-full">${header()}${challanInner(c, cp)}</div></div>`).join("");
}

export function printChallan(c, copies = CHALLAN_COPIES) {
  open(`Vehicle-Hire-Challan-${String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "Blank"}`, challanCopyPages(c, copies), { hideHeader: true });
}

export function printChallans(list, copies = CHALLAN_COPIES) {
  const arr = (list || []).filter(Boolean);
  if (arr.length === 0) return;
  if (arr.length === 1) return printChallan(arr[0], copies);
  open("Vehicle Hire Challans", arr.map((c) => challanCopyPages(c, copies)).join(""), { hideHeader: true });
}

export function printChallanRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map((c, i) => {
      const freight = Number(c.hire_amount) || 0;
      const bal = freight - (Number(c.advance_paid) || 0);
      return `<tr>
      <td>${i + 1}</td>
      <td>${esc((c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
      <td>${c.loading_date || c.date ? fmtDate(c.loading_date || c.date) : "—"}</td>
      <td>${esc(c.engaged_office || c.office || "—")}</td>
      <td>${esc(c.vehicle_no || "—")}</td>
      <td>${esc(c.owner_name || "—")}</td>
      <td>${esc(c.owner_mobile || "—")}</td>
      <td>${esc(c.origin || "—")}</td>
      <td>${esc(c.destination || "—")}</td>
      <td class="right">${inr(freight)}</td>
      <td class="right">${inr(Number(c.advance_paid) || 0)}</td>
      <td class="right">${inr(bal)}</td>
    </tr>`;
    })
    .join("");
  const tHire = arr.reduce((s, c) => s + (Number(c.hire_amount) || 0), 0);
  const tAdv = arr.reduce((s, c) => s + (Number(c.advance_paid) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLE HIRE CHALLAN REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Total Challans:</b> ${arr.length}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Challan No.</th><th>Loading Date</th><th>Branch</th><th>Vehicle No.</th><th>Owner Name</th><th>Owner Mobile No.</th><th>From</th><th>Destination</th><th class="right">Hire</th><th class="right">Advance</th><th class="right">Balance</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="12" class="muted">No challans.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="9" class="right">TOTAL</td><td class="right">${inr(tHire)}</td><td class="right">${inr(tAdv)}</td><td class="right">${inr(tHire - tAdv)}</td></tr>
      </tbody>
    </table>
  `;
  open("Vehicle Hire Challan Register", inner);
}

export function printAttendanceRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (a, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(a.branch || "Chennai")}</td>
      <td>${a.date ? fmtDate(a.date) : "—"}</td>
      <td>${esc(a.staff_name || "—")}</td>
      <td>${esc(a.designation || "—")}</td>
      <td>${esc(a.in_time || "—")}</td>
      <td>${esc(a.out_time || "—")}</td>
      <td>${esc(a.status || "Present")}</td>
      <td>${esc(a.remarks || "")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">ATTENDANCE REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Total Records:</b> ${arr.length}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl No.</th><th>Branch</th><th>Date</th><th>Staff Name</th><th>Designation</th><th>In Time</th><th>Out Time</th><th>Status</th><th>Remarks</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="9" class="muted">No attendance records.</td></tr>`}</tbody>
    </table>
  `;
  open("Attendance Register", inner);
}

export function printAttendanceSummary(monthLabel, rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (a, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(a.staff_name || "—")}</td>
      <td>${esc(a.designation || "—")}</td>
      <td>${esc(a.branch || "Chennai")}</td>
      <td class="right">${a.Present || 0}</td>
      <td class="right">${a["Half Day"] || 0}</td>
      <td class="right">${a.Absent || 0}</td>
      <td class="right">${a.Leave || 0}</td>
      <td class="right">${a.Holiday || 0}</td>
      <td class="right"><b>${a.payable ?? 0}</b></td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY ATTENDANCE SUMMARY</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel)} &nbsp;&nbsp; <b>Staff:</b> ${arr.length}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Staff Name</th><th>Designation</th><th>Branch</th><th class="right">Present</th><th class="right">Half Day</th><th class="right">Absent</th><th class="right">Leave</th><th class="right">Holiday</th><th class="right">Payable Days</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="10" class="muted">No records.</td></tr>`}</tbody>
    </table>
    <div style="margin-top:8px;font-size:10px;color:#64748b;">Payable Days = Present + Holiday + (Half Day × 0.5)</div>
  `;
  open("Monthly Attendance Summary", inner);
}

export function printCompanyProfile(p) {
  const row = (label, value) => `<tr><td style="width:180px;font-weight:700;text-transform:uppercase;font-size:10px;color:#475569;">${esc(label)}</td><td>${esc(value || "—")}</td></tr>`;
  const openDate = p.opening_date ? fmtDate(p.opening_date) : "—";
  const inner = `
    <div class="title" style="letter-spacing:3px;">COMPANY PROFILE</div>
    <table style="font-size:12px;">
      <tbody>
        ${row("Company Name", p.name)}
        ${row("Opening Date", openDate)}
        ${row("Managing Director", p.md_name)}
        ${row("GSTIN", p.gstin)}
        ${row("PAN", p.pan)}
        ${row("MSME Number", p.msme_number)}
        ${row("Address", p.address)}
        ${row("Branches", (p.branches || []).join(", "))}
        ${row("Phone", p.phone)}
        ${row("Email", p.email)}
        ${row("Website", p.website)}
        ${row("Bank", p.bank_name)}
        ${row("A/C No.", p.account_no)}
        ${row("IFSC", p.ifsc)}
      </tbody>
    </table>
    ${p.vision ? `<div style="margin-top:16px;padding:12px;border:1px solid #e2e8f0;border-radius:6px;">
      <div style="font-weight:700;text-transform:uppercase;font-size:10px;color:#475569;margin-bottom:6px;">NTC Vision</div>
      <div style="font-size:12px;white-space:pre-wrap;">${esc(p.vision)}</div>
    </div>` : ""}
    ${p.md_message ? `<div style="margin-top:16px;padding:12px;border:1px solid #e2e8f0;border-radius:6px;">
      <div style="font-weight:700;text-transform:uppercase;font-size:10px;color:#475569;margin-bottom:6px;">Managing Director's Message</div>
      <div style="font-size:12px;font-style:italic;white-space:pre-wrap;">"${esc(p.md_message)}"</div>
      <div style="font-size:12px;font-weight:700;margin-top:8px;">— ${esc(p.md_name || "Managing Director")}</div>
    </div>` : ""}
  `;
  open("NTC Company Profile", inner);
}

export function printDeliveryReport(groups, from, to) {
  const list = groups || [];
  const total = list.reduce((a, [, g]) => a + g.length, 0);
  const cn = (r) => String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "");
  const dd = (r) => r.delivery_date || String(r.delivered_at || "").slice(0, 10) || "";
  const PER_PAGE = 20;
  const pages = [];
  let cur = [];
  let used = 0;
  list.forEach(([name, g]) => {
    let idx = 0;
    while (idx < g.length) {
      if (used === PER_PAGE) {
        pages.push(cur);
        cur = [];
        used = 0;
      }
      const take = Math.min(PER_PAGE - used, g.length - idx);
      cur.push({ name, rows: g.slice(idx, idx + take), startIdx: idx, contd: idx > 0, groupTotal: g.length });
      idx += take;
      used += take;
    }
  });
  if (cur.length || !pages.length) pages.push(cur);
  const segHtml = (s) => `
    <div style="margin:14px 0 4px;font-size:12px;font-weight:800;">${esc(s.name)} — ${s.groupTotal} delivery(ies)${s.contd ? " (contd.)" : ""}</div>
    <table style="font-size:11px;">
      <thead><tr><th>Sl</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Consignor Name</th><th>Consignee Name</th><th>Basis</th><th>PKG</th><th>Invoice No.</th><th>Invoice Date</th><th class="right">Invoice Value</th><th>Vehicle No</th><th>Vehicle Type</th><th class="right">Freight Amount</th><th>Delivered On</th></tr></thead>
      <tbody>${s.rows
        .map(
          (r, i) => `<tr>
        <td>${s.startIdx + i + 1}</td>
        <td>${esc(r.office || "Chennai")}</td>
        <td>${esc(cn(r) || "—")}</td>
        <td style="white-space:nowrap;">${r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</td>
        <td>${esc(r.origin || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td>${esc(r.customer_name || "—")}</td>
        <td>${esc(r.consignee || "—")}</td>
        <td>${esc(r.basis || "—")}</td>
        <td>${esc(r.packages || "—")}</td>
        <td>${esc(r.invoice_no || "—")}</td>
        <td style="white-space:nowrap;">${r.invoice_date ? fmtDateDay(r.invoice_date) : "—"}</td>
        <td class="right">${Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</td>
        <td>${esc(r.vehicle_no || "—")}</td>
        <td>${esc(r.vehicle_type || "—")}</td>
        <td class="right">${Number(r.freight_amount) ? inr(r.freight_amount) : "—"}</td>
        <td style="white-space:nowrap;">${dd(r) ? fmtDate(dd(r)) : "—"}</td>
      </tr>`
        )
        .join("")}</tbody>
    </table>`;
  const headerHtml = `
    <div class="title" style="letter-spacing:3px;">DELIVERY REPORT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">
      Period: ${from ? fmtDate(from) : "—"} to ${to ? fmtDate(to) : "—"} — ${total} delivery(ies), ${list.length} staff
    </div>`;
  const inner = pages
    .map((segs, pi) => {
      const isLast = pi === pages.length - 1;
      return `<div style="${isLast ? "" : "page-break-after:always;"}">
    ${pi === 0 ? headerHtml : `<div style="margin:0 0 8px;font-size:11px;font-weight:800;letter-spacing:1px;">DELIVERY REPORT (contd.)</div>`}
    ${segs.map(segHtml).join("") || `<div class="muted" style="text-align:center;">No deliveries.</div>`}
    <div style="text-align:right;font-size:10px;margin-top:4px;color:#475569;">Page ${pi + 1} of ${pages.length}</div>
  </div>`;
    })
    .join("");
  open("Delivery Report", inner);
}

export function printStaffDirectory(rows, token) {
  const list = (rows || []).filter((r) => (r.status || "Active") !== "Inactive");
  const byBranch = {};
  list.forEach((r) => { (byBranch[r.branch || "Chennai"] = byBranch[r.branch || "Chennai"] || []).push(r); });
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
  const photo = (r) =>
    r.photo_path
      ? `<img src="${base}${r.photo_path}?auth=${token || ""}" style="width:38px;height:38px;object-fit:cover;border-radius:4px;border:1px solid #e2e8f0;" />`
      : `<div style="width:38px;height:38px;border:1px dashed #cbd5e1;border-radius:4px;"></div>`;
  const sections = Object.keys(byBranch)
    .sort()
    .map((branch) => {
      const g = byBranch[branch].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      return `
    <div style="margin:14px 0 4px;font-size:13px;font-weight:800;letter-spacing:1px;">${esc(branch.toUpperCase())} BRANCH — ${g.length} STAFF</div>
    <table style="font-size:11px;">
      <thead><tr><th>Sl</th><th>Photo</th><th>Staff Code</th><th>Staff Name</th><th>Designation</th><th>Phone</th><th>Email ID</th><th>Joining Date</th></tr></thead>
      <tbody>${g
        .map(
          (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${photo(r)}</td>
        <td>${esc(r.staff_code || "—")}</td>
        <td style="font-weight:700;">${esc(r.name || "—")}</td>
        <td>${esc(r.designation || "—")}</td>
        <td>${esc(r.phone || "—")}</td>
        <td>${esc(r.email || "—")}</td>
        <td>${r.joining_date ? fmtDate(r.joining_date) : "—"}</td>
      </tr>`
        )
        .join("")}</tbody>
    </table>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">STAFF DIRECTORY</div>
    <div style="text-align:center;font-size:11px;color:#64748b;margin-bottom:8px;">Branch-wise staff list with contact numbers — ${list.length} active staff</div>
    ${sections || `<div class="muted" style="text-align:center;">No staff records.</div>`}
  `;
  open("NTC Staff Directory", inner);
}

function staffIdCardHtml(r, token) {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const cardExpired = r.card_valid_upto && String(r.card_valid_upto).slice(0, 10) < new Date().toISOString().slice(0, 10);
  const photo = r.photo_path
    ? `<img src="${base}${r.photo_path}?auth=${token || ""}" style="width:96px;height:96px;object-fit:cover;border-radius:8px;border:3px solid #ffffff;box-shadow:0 1px 4px rgba(0,0,0,.25);" />`
    : `<div style="width:96px;height:96px;border-radius:8px;border:2px dashed #cbd5e1;background:#f8fafc;display:inline-block;"></div>`;
  const row = (label, value) => `
    <tr>
      <td style="padding:3px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;white-space:nowrap;">${esc(label)}</td>
      <td style="padding:3px 8px;font-size:11px;font-weight:600;color:#0f172a;">${esc(value || "—")}</td>
    </tr>`;
  return `
      <div style="width:330px;border:${cardExpired ? "2px solid #dc2626" : "1px solid #cbd5e1"};border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.12);page-break-inside:avoid;">
        ${cardExpired ? `<div style="background:#dc2626;color:#ffffff;text-align:center;font-size:9px;font-weight:800;letter-spacing:2px;padding:3px 0;">EXPIRED — PLEASE RENEW &amp; REPRINT</div>` : ""}
        <div style="background:#f1f3f5;border-bottom:1px solid #dfe3e8;color:#0f172a;padding:10px 14px;display:flex;align-items:center;gap:10px;">
          <img src="${logo}" style="width:36px;height:36px;border-radius:6px;background:#ffffff;object-fit:contain;border:1px solid #dfe3e8;" />
          <div>
            <div style="font-size:13px;font-weight:800;letter-spacing:1px;color:#0f172a;">NISHA TRANSPORT CORPORATION</div>
            <div style="font-size:8px;letter-spacing:3px;color:#5b6470;margin-top:2px;">STAFF IDENTITY CARD</div>
          </div>
        </div>
        <div style="background:linear-gradient(#f1f3f5 40px, #ffffff 40px);text-align:center;padding-bottom:4px;">${photo}</div>
        <div style="text-align:center;padding:6px 14px 2px;">
          <div style="font-size:16px;font-weight:800;color:#0f172a;">${esc(r.name || "—")}</div>
          <div style="font-size:11px;color:#475569;">${esc(r.designation || "—")}</div>
        </div>
        <table style="width:100%;border:none;margin:8px 0 4px;">
          ${r.staff_code ? `<tr>
      <td style="padding:3px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#047857;white-space:nowrap;">Staff Code</td>
      <td style="padding:3px 8px;"><span style="display:inline-block;background:#ecfdf5;border:1px solid #a7f3d0;color:#047857;font-size:12px;font-weight:800;letter-spacing:1px;padding:1px 8px;border-radius:999px;">${esc(r.staff_code)}</span> <span style="font-size:7.5px;font-weight:700;letter-spacing:1px;color:#94a3b8;">USE AT SIGN-IN</span></td>
    </tr>` : row("Staff Code", "")}
          ${row("Branch", r.branch || "Chennai")}
          ${row("Phone", r.phone)}
          ${row("Blood Group", r.blood_group)}
          ${r.aadhar_no ? row("Aadhaar", `XXXX XXXX ${String(r.aadhar_no).replace(/\D/g, "").slice(-4)}`) : ""}
          ${r.pan_no ? row("PAN", `XXXXX${String(r.pan_no).replace(/\s+/g, "").toUpperCase().slice(5)}`) : ""}
          ${row("Joining Date", r.joining_date ? fmtDate(r.joining_date) : "")}
          ${row("Date of Birth", r.birthday ? fmtDate(r.birthday) : "")}
          ${r.card_valid_upto ? `<tr>
      <td style="padding:3px 8px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:${cardExpired ? "#dc2626" : "#64748b"};white-space:nowrap;">Card Valid Upto</td>
      <td style="padding:3px 8px;font-size:11px;font-weight:${cardExpired ? "800" : "600"};color:${cardExpired ? "#dc2626" : "#0f172a"};">${fmtDate(r.card_valid_upto)}${cardExpired ? " — EXPIRED" : ""}</td>
    </tr>` : ""}
          ${r.license_expiry ? row("License Expiry", fmtDate(r.license_expiry)) : ""}
        </table>
        <div style="display:flex;justify-content:space-between;align-items:flex-end;padding:14px 16px 10px;">
          <div style="font-size:8px;color:#64748b;">www.ntclogistic.in<br/>chennai@ntclogistic.in</div>
          <div style="text-align:center;">
            <div style="width:110px;border-top:1px solid #94a3b8;"></div>
            <div style="font-size:8px;color:#64748b;margin-top:2px;">Authorised Signatory</div>
          </div>
        </div>
      </div>`;
}

function staffIdCardBackHtml(r) {
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  return `
      <div style="width:330px;border:1px solid #cbd5e1;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.12);page-break-inside:avoid;display:flex;flex-direction:column;">
        <div style="background:#f1f3f5;border-bottom:1px solid #dfe3e8;color:#0f172a;padding:8px 14px;display:flex;align-items:center;gap:8px;">
          <img src="${logo}" style="width:26px;height:26px;border-radius:5px;background:#ffffff;object-fit:contain;border:1px solid #dfe3e8;" />
          <div style="font-size:10px;font-weight:800;letter-spacing:2px;color:#0f172a;">NISHA TRANSPORT CORPORATION</div>
        </div>
        <div style="padding:12px 16px;flex:1;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Registered Office</div>
          <div style="font-size:10.5px;color:#0f172a;margin:3px 0 10px;line-height:1.5;">No.12, Subash Chandra Bose 3rd Street, Ma.Po.Si Nagar,<br/>Gummidipoondi, Tamilnadu - 601201</div>
          ${r.branch_address ? `<div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">${esc(r.branch || "Branch")} Branch</div>
          <div style="font-size:10.5px;color:#0f172a;margin:3px 0 10px;line-height:1.5;">${esc(r.branch_address)}</div>` : ""}
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Contact</div>
          <div style="font-size:10.5px;color:#0f172a;margin:3px 0 10px;line-height:1.5;">Phone: +91 93973 63140<br/>Email: chennai@ntclogistic.in &nbsp;·&nbsp; www.ntclogistic.in</div>
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#b91c1c;">Emergency Contact</div>
          <div style="font-size:11px;font-weight:700;color:#0f172a;margin:3px 0 10px;">${esc(r.emergency_contact || "—")}${r.blood_group ? ` &nbsp;·&nbsp; Blood Group: <span style="color:#b91c1c;">${esc(r.blood_group)}</span>` : ""}</div>
          <div style="font-size:8.5px;color:#64748b;line-height:1.5;border-top:1px dashed #e2e8f0;padding-top:8px;">
            This card is the property of Nisha Transport Corporation. It must be produced on demand and
            surrendered on leaving the service. If found, please return to the registered office above.
          </div>
        </div>
      </div>`;
}

const KIT_T = {
  English: {
    subtitle: "NEW JOINER WELCOME KIT",
    issued: "Issued",
    welcome: (name) => `Welcome aboard, <b>${name}</b>! Here is everything you need for your first sign-in to the NTC Logistics system.`,
    staffName: "Staff Name", designation: "Designation", branch: "Branch", joining: "Joining Date",
    yourCode: "Your Staff Code",
    codeNote: "Required at <b>every sign-in</b>. Keep it confidential — it works only while your code is Active in NTC Staff Master.",
    stepsTitle: "First Sign-In Steps",
    s1t: "Open the NTC portal", s1d: (host) => `Go to <b>${host}/login</b> in Chrome on your computer or phone.`,
    s2t: "Enter your User ID &amp; Password", s2d: "Collect these from Head Office / Admin.",
    s3t: "Enter your Staff Code", s3d: (code) => `Type <b>${code}</b> exactly in the Staff Code box.`,
    s4t: "Enter your Branch", s4d: (br) => `Type <b>${br}</b>.`,
    s5t: "Verify OTP", s5d: "Enter the one-time code received on the registered mobile — your dashboard opens.",
    lockNote: "Note: 5 wrong Staff Code or password attempts lock the account for 15 minutes. If locked, contact admin for instant unlock from the Dashboard.",
    empSig: "Employee Signature", authSig: "Authorised Signatory — NTC",
  },
  Tamil: {
    subtitle: "புதிய பணியாளர் வரவேற்பு கிட்",
    issued: "வழங்கப்பட்டது",
    welcome: (name) => `வணக்கம், <b>${name}</b>! NTC லாஜிஸ்டிக்ஸ் அமைப்பில் உங்கள் முதல் உள்நுழைவுக்குத் தேவையான அனைத்தும் இங்கே.`,
    staffName: "பணியாளர் பெயர்", designation: "பதவி", branch: "கிளை", joining: "சேர்ந்த தேதி",
    yourCode: "உங்கள் Staff Code",
    codeNote: "<b>ஒவ்வொரு உள்நுழைவிலும்</b> தேவை. ரகசியமாக வைத்திருங்கள் — NTC Staff Master-இல் Active ஆக இருக்கும் வரை மட்டுமே செயல்படும்.",
    stepsTitle: "முதல் உள்நுழைவு படிகள்",
    s1t: "NTC போர்ட்டலைத் திறக்கவும்", s1d: (host) => `கணினி அல்லது மொபைலில் Chrome-இல் <b>${host}/login</b> செல்லவும்.`,
    s2t: "User ID மற்றும் Password உள்ளிடவும்", s2d: "இவற்றைத் தலைமை அலுவலகம் / நிர்வாகியிடம் பெறவும்.",
    s3t: "உங்கள் Staff Code உள்ளிடவும்", s3d: (code) => `Staff Code கட்டத்தில் <b>${code}</b> என்று சரியாக உள்ளிடவும்.`,
    s4t: "கிளை உள்ளிடவும்", s4d: (br) => `<b>${br}</b> என்று உள்ளிடவும்.`,
    s5t: "OTP சரிபார்க்கவும்", s5d: "பதிவு செய்யப்பட்ட மொபைலில் வரும் OTP-ஐ உள்ளிடவும் — உங்கள் டாஷ்போர்டு திறக்கும்.",
    lockNote: "குறிப்பு: 5 முறை தவறான Staff Code அல்லது Password உள்ளிட்டால் கணக்கு 15 நிமிடங்களுக்குப் பூட்டப்படும். பூட்டப்பட்டால் உடனடி அன்லாக்கிற்கு நிர்வாகியைத் தொடர்பு கொள்ளவும்.",
    empSig: "பணியாளர் கையொப்பம்", authSig: "அங்கீகரிக்கப்பட்ட கையொப்பம் — NTC",
  },
  Hindi: {
    subtitle: "नए कर्मचारी स्वागत किट",
    issued: "जारी",
    welcome: (name) => `स्वागत है, <b>${name}</b>! NTC लॉजिस्टिक्स सिस्टम में आपके पहले साइन-इन के लिए सब कुछ यहाँ है।`,
    staffName: "कर्मचारी नाम", designation: "पद", branch: "शाखा", joining: "नियुक्ति तिथि",
    yourCode: "आपका Staff Code",
    codeNote: "<b>हर साइन-इन</b> पर ज़रूरी। गोपनीय रखें — यह तभी काम करता है जब NTC Staff Master में Active हो।",
    stepsTitle: "पहले साइन-इन के चरण",
    s1t: "NTC पोर्टल खोलें", s1d: (host) => `कंप्यूटर या मोबाइल पर Chrome में <b>${host}/login</b> खोलें।`,
    s2t: "User ID और Password डालें", s2d: "ये हेड ऑफ़िस / एडमिन से प्राप्त करें।",
    s3t: "अपना Staff Code डालें", s3d: (code) => `Staff Code बॉक्स में <b>${code}</b> ठीक वैसे ही टाइप करें।`,
    s4t: "शाखा डालें", s4d: (br) => `<b>${br}</b> टाइप करें।`,
    s5t: "OTP सत्यापित करें", s5d: "रजिस्टर्ड मोबाइल पर आए OTP को डालें — आपका डैशबोर्ड खुल जाएगा।",
    lockNote: "नोट: 5 ग़लत Staff Code या Password प्रयासों पर खाता 15 मिनट के लिए लॉक हो जाता है। लॉक होने पर एडमिन से संपर्क करें — डैशबोर्ड से तुरंत अनलॉक हो सकता है।",
    empSig: "कर्मचारी हस्ताक्षर", authSig: "अधिकृत हस्ताक्षरकर्ता — NTC",
  },
};

export function newJoinerKitHtml(r, lang = "English") {
  const t = KIT_T[lang] || KIT_T.English;
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const step = (n, title, detail) => `
    <tr>
      <td style="width:36px;vertical-align:top;padding:8px 0;"><div style="width:25px;height:25px;border-radius:50%;background:#065f46;color:#ffffff;font-size:12px;font-weight:800;text-align:center;line-height:25px;">${n}</div></td>
      <td style="padding:8px 0;"><div style="font-size:12.5px;font-weight:700;color:#0f172a;">${title}</div><div style="font-size:11px;color:#475569;margin-top:2px;line-height:1.5;">${detail}</div></td>
    </tr>`;
  const info = (label, value) => `
    <td style="padding:6px 10px;border:1px solid #e2e8f0;">
      <div style="font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;">${esc(label)}</div>
      <div style="font-size:12px;font-weight:700;color:#0f172a;margin-top:1px;">${esc(value || "—")}</div>
    </td>`;
  const inner = `
  <div style="max-width:700px;margin:0 auto;border:2px solid #0f172a;border-radius:12px;padding:24px 30px;font-family:Arial,sans-serif;page-break-inside:avoid;">
    <div style="display:flex;align-items:center;gap:12px;border-bottom:2px solid #0f172a;padding-bottom:12px;">
      <img src="${logo}" style="width:46px;height:46px;border-radius:8px;object-fit:contain;border:1px solid #e2e8f0;" />
      <div>
        <div style="font-size:16px;font-weight:800;letter-spacing:1px;color:#0f172a;">NISHA TRANSPORT CORPORATION</div>
        <div style="font-size:9px;letter-spacing:3px;color:#64748b;margin-top:2px;">${esc(t.subtitle)}</div>
      </div>
      <div style="margin-left:auto;font-size:10px;color:#64748b;">${esc(t.issued)}: ${esc(new Date().toLocaleDateString("en-IN"))}</div>
    </div>
    <div style="font-size:13px;color:#0f172a;margin:14px 0 10px;">${t.welcome(esc(r.name || "—"))}</div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
      <tr>${info(t.staffName, r.name)}${info(t.designation, r.designation)}${info(t.branch, r.branch || "Chennai")}${info(t.joining, r.joining_date ? fmtDate(r.joining_date) : "")}</tr>
    </table>
    <div style="display:flex;align-items:center;gap:14px;background:#ecfdf5;border:2px solid #a7f3d0;border-radius:10px;padding:12px 16px;margin-bottom:14px;">
      <div>
        <div style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#047857;">${esc(t.yourCode)}</div>
        <div style="font-size:24px;font-weight:900;letter-spacing:2px;color:#065f46;margin-top:2px;">${esc(r.staff_code || "—")}</div>
      </div>
      <div style="font-size:10.5px;color:#047857;line-height:1.5;margin-left:auto;max-width:300px;">${t.codeNote}</div>
    </div>
    <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#0f172a;margin-bottom:2px;">${esc(t.stepsTitle)}</div>
    <table style="width:100%;border-collapse:collapse;">
      ${step(1, t.s1t, t.s1d(esc(window.location.origin.replace(/^https?:\/\//, ""))))}
      ${step(2, t.s2t, `${t.s2d} &nbsp; User ID: <span style="display:inline-block;border-bottom:1px solid #94a3b8;min-width:140px;">&nbsp;</span> &nbsp; Password: <span style="display:inline-block;border-bottom:1px solid #94a3b8;min-width:140px;">&nbsp;</span>`)}
      ${step(3, t.s3t, t.s3d(esc(r.staff_code || "—")))}
      ${step(4, t.s4t, t.s4d(esc(r.branch || "Chennai")))}
      ${step(5, t.s5t, t.s5d)}
    </table>
    <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:8px 12px;font-size:10px;color:#991b1b;margin:12px 0;">
      ${esc(t.lockNote)}
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:26px;">
      <div style="text-align:center;"><div style="width:160px;border-top:1px solid #94a3b8;"></div><div style="font-size:9px;color:#64748b;margin-top:3px;">${esc(t.empSig)}</div></div>
      <div style="text-align:center;"><div style="width:160px;border-top:1px solid #94a3b8;"></div><div style="font-size:9px;color:#64748b;margin-top:3px;">${esc(t.authSig)}</div></div>
    </div>
  </div>`;
  return inner;
}

export function printNewJoinerKit(r, lang = "English") {
  open(`New Joiner Kit — ${r.name || ""}`, newJoinerKitHtml(r, lang), { hideHeader: true });
}

export function printNewJoinerKitsBulk(rows, branchLabel, lang = "English") {
  const list = (rows || []).filter((r) => (r.status || "Active") !== "Inactive");
  if (!list.length) return;
  const inner = list
    .map((r, i) => `<div style="${i < list.length - 1 ? "page-break-after:always;" : ""}padding-top:8px;">${newJoinerKitHtml(r, lang)}</div>`)
    .join("");
  open(`New Joiner Kits — ${branchLabel && branchLabel !== "All" ? branchLabel : "All Branches"} (${list.length})`, inner, { hideHeader: true });
}

export function branchOpeningPackSections(rows, token, branchLabel, lang = "English") {
  const list = (rows || []).filter((r) => (r.status || "Active") !== "Inactive");
  if (!list.length) return null;
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const bl = branchLabel && branchLabel !== "All" ? branchLabel : "All Branches";
  const addr = list.find((x) => x.branch_address)?.branch_address || "";
  const cover = `
    <div style="text-align:center;padding-top:90px;padding-bottom:60px;font-family:Arial,sans-serif;">
      <img src="${logo}" style="width:84px;height:84px;border-radius:14px;object-fit:contain;border:1px solid #e2e8f0;" />
      <div style="font-size:22px;font-weight:800;letter-spacing:2px;color:#0f172a;margin-top:18px;">NISHA TRANSPORT CORPORATION</div>
      <div style="font-size:12px;letter-spacing:5px;color:#64748b;margin-top:6px;">BRANCH OPENING PACK</div>
      <div style="display:inline-block;background:#ecfdf5;border:2px solid #a7f3d0;border-radius:12px;padding:14px 34px;margin-top:28px;">
        <div style="font-size:26px;font-weight:900;color:#065f46;letter-spacing:1px;">${esc(bl)}</div>
        ${addr ? `<div style="font-size:11px;color:#047857;margin-top:5px;max-width:380px;">${esc(addr)}</div>` : ""}
      </div>
      <div style="font-size:12px;color:#334155;margin-top:26px;">${list.length} staff member(s) &nbsp;·&nbsp; Prepared on ${esc(new Date().toLocaleDateString("en-IN"))}</div>
      <table style="margin:30px auto 0;border-collapse:collapse;font-size:11.5px;color:#334155;text-align:left;">
        <tr><td style="padding:4px 10px;font-weight:800;color:#0f172a;">Contents</td><td></td></tr>
        <tr><td style="padding:3px 10px;">1.</td><td style="padding:3px 10px;">Branch Staff Directory</td></tr>
        <tr><td style="padding:3px 10px;">2.</td><td style="padding:3px 10px;">Staff ID Cards (front &amp; back)</td></tr>
        <tr><td style="padding:3px 10px;">3.</td><td style="padding:3px 10px;">New Joiner Welcome Kits — one per staff (${esc(lang)})</td></tr>
      </table>
    </div>`;
  const th = 'style="border:1px solid #cbd5e1;background:#f1f5f9;padding:5px 8px;font-size:10px;text-align:left;font-family:Arial,sans-serif;"';
  const td = 'style="border:1px solid #e2e8f0;padding:5px 8px;font-size:10.5px;font-family:Arial,sans-serif;"';
  const dirRows = list
    .map((r, i) => `<tr><td ${td}>${i + 1}</td><td ${td}>${esc(r.staff_code || "—")}</td><td ${td}><b>${esc(r.name || "—")}</b></td><td ${td}>${esc(r.designation || "—")}</td><td ${td} nowrap>${esc(r.phone || "—")}</td><td ${td}>${esc(r.email || "—")}</td></tr>`)
    .join("");
  const directory = `
    <div style="font-family:Arial,sans-serif;">
      <div style="font-size:15px;font-weight:800;letter-spacing:3px;margin:8px 0 10px;">1. BRANCH STAFF DIRECTORY — ${esc(bl.toUpperCase())}</div>
      ${addr ? `<div style="margin:0 0 10px;font-size:11px;"><b>Address:</b> ${esc(addr)}</div>` : ""}
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr><th ${th}>Sl</th><th ${th}>Staff Code</th><th ${th}>Staff Name</th><th ${th}>Designation</th><th ${th}>Mobile No.</th><th ${th}>Email ID</th></tr></thead>
        <tbody>${dirRows}</tbody>
      </table>
    </div>`;
  const cards = list.map((r) => `${staffIdCardHtml(r, token)}${staffIdCardBackHtml(r)}`).join("");
  const cardSec = `
    <div style="font-family:Arial,sans-serif;">
      <div style="text-align:center;font-size:13px;font-weight:800;letter-spacing:2px;margin:8px 0 14px;">2. STAFF ID CARDS — ${esc(bl.toUpperCase())}</div>
      <div style="display:flex;flex-wrap:wrap;gap:20px;justify-content:center;">${cards}</div>
    </div>`;
  const kits = list.map((r) => `<div style="padding-top:8px;">${newJoinerKitHtml(r, lang)}</div>`);
  return { bl, list, sections: [cover, directory, cardSec, ...kits] };
}

export function printBranchOpeningPack(rows, token, branchLabel, lang = "English") {
  const pack = branchOpeningPackSections(rows, token, branchLabel, lang);
  if (!pack) return;
  const inner = pack.sections
    .map((s, i) => `<div style="${i < pack.sections.length - 1 ? "page-break-after:always;" : ""}">${s}</div>`)
    .join("");
  open(`Branch Opening Pack — ${pack.bl}`, inner, { hideHeader: true });
}

export function coaCertificateHtml(r) {
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const inv = [r.invoice_no, ...(r.extra_invoices || []).map((x) => x.no)].filter(Boolean).join(", ");
  const field = (label, value) => `
    <td style="padding:6px 10px;border:1px solid #e2e8f0;">
      <div style="font-size:8.5px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#64748b;">${esc(label)}</div>
      <div style="font-size:12px;font-weight:700;color:#0f172a;margin-top:1px;">${esc(value || "—")}</div>
    </td>`;
  const inner = `
  <div style="max-width:720px;margin:0 auto;border:2px solid #0f172a;border-radius:10px;padding:26px 32px;font-family:Arial,sans-serif;">
    <div style="display:flex;align-items:center;gap:12px;border-bottom:2px solid #0f172a;padding-bottom:12px;">
      <img src="${logo}" style="width:48px;height:48px;border-radius:8px;object-fit:contain;border:1px solid #e2e8f0;" />
      <div>
        <div style="font-size:16px;font-weight:800;letter-spacing:1px;color:#0f172a;">NISHA TRANSPORT CORPORATION</div>
        <div style="font-size:9px;letter-spacing:3px;color:#991b1b;margin-top:2px;font-weight:800;">COA CERTIFICATE — MATERIAL DAMAGE IN TRANSIT</div>
      </div>
      <div style="margin-left:auto;text-align:right;">
        <div style="font-size:12px;font-weight:800;color:#991b1b;">${esc(r.coa_no || "")}</div>
        <div style="font-size:10px;color:#64748b;">Date: ${r.coa_date ? fmtDate(r.coa_date) : esc(new Date().toLocaleDateString("en-IN"))}</div>
      </div>
    </div>
    <div style="text-align:center;font-size:12px;font-weight:800;letter-spacing:2px;color:#0f172a;margin:14px 0 4px;">TO WHOMSOEVER IT MAY CONCERN</div>
    <div style="font-size:12px;color:#1e293b;line-height:1.7;margin:10px 0;">
      This is to certify that the consignment covered under the following Lorry Receipt, carried by us, sustained <b>material damage in transit</b>.
      This certificate is issued at the request of the consignor/consignee for the purpose of lodging an <b>insurance claim</b>.
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
      <tr>${field("LR No.", String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""))}${field("LR Date", r.lr_date ? fmtDate(r.lr_date) : "")}${field("From", r.origin)}${field("Destination", r.destination)}</tr>
      <tr>${field("Consignor", r.consignor)}${field("Consignee", r.consignee)}${field("Vehicle No.", r.vehicle_no)}${field("Branch", r.office || "Chennai")}</tr>
      <tr>${field("Invoice No(s).", inv)}${field("Invoice Date", r.invoice_date ? fmtDate(r.invoice_date) : "")}${field("Invoice Value", r.invoice_value ? inr(r.invoice_value) : "")}${field("Packages", r.packages)}${field("Weight", r.weight)}</tr>
    </table>
    <div style="border:1.5px solid #fecaca;background:#fef2f2;border-radius:8px;padding:10px 14px;margin-bottom:10px;">
      <div style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:#991b1b;">Nature & Description of Damage${r.coa_damage_category ? ` — <span style="letter-spacing:0.5px;">${esc(r.coa_damage_category)}</span>` : ""}</div>
      <div style="font-size:12px;color:#0f172a;line-height:1.6;margin-top:4px;min-height:40px;">${esc(r.coa_damage_desc || "")}</div>
      ${Number(r.coa_damage_value) > 0 ? `<div style="font-size:12px;font-weight:800;color:#991b1b;margin-top:6px;">Estimated Damage Value: ${inr(r.coa_damage_value)}</div>` : ""}
      ${Number(r.coa_issue_value) > 0 ? `<div style="font-size:12px;font-weight:800;color:#0f172a;margin-top:2px;">COA Issue Value: ${inr(r.coa_issue_value)}</div>` : ""}
    </div>
    ${(r._photoUrls || []).length ? `
    <div style="margin-bottom:10px;">
      <div style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:#991b1b;margin-bottom:5px;">Damage Photographs</div>
      <img src="${(r._photoUrls || [])[0]}" style="width:100%;max-height:280px;object-fit:contain;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;" />
      <div style="font-size:8.5px;color:#64748b;margin-top:2px;">Primary damage photograph — LR ${esc(String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""))}</div>
      ${(r._photoUrls || []).length > 1 ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:6px;">
        ${(r._photoUrls || []).slice(1, 6).map((u) => `<img src="${u}" style="width:130px;height:95px;object-fit:cover;border:1px solid #e2e8f0;border-radius:6px;" />`).join("")}
      </div>` : ""}
    </div>` : ""}
    <div style="font-size:10.5px;color:#475569;line-height:1.6;">
      The above damage was noticed at the time of transit/delivery. This certificate is issued without prejudice and does not constitute an admission of liability;
      the carrier's responsibility remains governed by the terms and conditions of the consignment note and the Carriage by Road Act, 2007.
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:40px;">
      <div style="font-size:10px;color:#64748b;">Issued by: <b style="color:#0f172a;">${esc(r.coa_issued_by || "")}</b><br/>Place: ${esc(r.office || "Chennai")}</div>
      <div style="text-align:center;">${signatureImg(36)}<div style="width:190px;border-top:1px solid #94a3b8;"></div><div style="font-size:9px;color:#64748b;margin-top:3px;">Authorised Signatory with Seal — NTC</div></div>
    </div>
  </div>`;
  return inner;
}

export function printCoaCertificate(r) {
  open(`COA Certificate — ${r.coa_no || ""}`, coaCertificateHtml(r), { hideHeader: true });
}

export function printCoaRegister(rows) {
  const list = (rows || []).filter((r) => r.coa_no).sort((a, b) => String(b.coa_date || "").localeCompare(String(a.coa_date || "")));
  const byMonth = {};
  list.forEach((r) => {
    const m = String(r.coa_date || "").slice(0, 7) || "Unknown";
    (byMonth[m] = byMonth[m] || []).push(r);
  });
  const months = Object.keys(byMonth).sort().reverse();
  const monthName = (m) => {
    const [y, mo] = m.split("-");
    return mo ? new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : m;
  };
  let grand = 0;
  let grandIssue = 0;
  const section = (m) => {
    const rs = byMonth[m];
    const sub = rs.reduce((s, r) => s + (Number(r.coa_damage_value) || 0), 0);
    const subIssue = rs.reduce((s, r) => s + (Number(r.coa_issue_value) || 0), 0);
    grand += sub;
    grandIssue += subIssue;
    const body = rs
      .map(
        (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;color:#991b1b;">${esc(r.coa_no)}</td>
        <td style="white-space:nowrap;">${r.coa_date ? fmtDateDay(r.coa_date) : "—"}</td>
        <td>${esc(String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""))}</td>
        <td style="white-space:nowrap;">${r.lr_date ? fmtDateDay(r.lr_date) : "—"}</td>
        <td>${esc(r.origin || "—")} → ${esc(r.destination || "—")}</td>
        <td>${esc(r.consignor || "—")}</td>
        <td>${esc(r.coa_damage_desc || "—")}</td>
        <td>${esc(r.coa_damage_category || "—")}</td>
        <td style="text-align:right;">${Number(r.coa_damage_value) > 0 ? inr(r.coa_damage_value) : "—"}</td>
        <td style="text-align:right;">${Number(r.coa_issue_value) > 0 ? inr(r.coa_issue_value) : "—"}</td>
        <td>${esc(r.coa_claim_status || "Not Filed")}${r.coa_claim_status === "Settled" && Number(r.coa_claim_amount) > 0 ? ` (${inr(r.coa_claim_amount)})` : ""}</td>
        <td>${esc(r.coa_issued_by || "—")}</td>
      </tr>`
      )
      .join("");
    return `
      <div style="margin:0 0 6px;padding:6px 12px;background:#f1f5f9;border-radius:6px;font-size:12px;font-weight:800;letter-spacing:0.5px;">${esc(monthName(m))} — ${rs.length} certificate(s)</div>
      <table class="tight" style="font-size:10px;margin-bottom:4px;">
        <thead><tr><th>Sl</th><th>COA No.</th><th>COA Date</th><th>LR No.</th><th>LR Date</th><th>Route</th><th>Consignor</th><th>Damage Description</th><th>Category</th><th>Damage Value</th><th>Issue Value</th><th>Claim Status</th><th>Issued By</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr style="font-weight:800;background:#fef2f2;"><td colspan="9" style="text-align:right;">MONTH TOTAL</td><td style="text-align:right;color:#991b1b;">${inr(sub)}</td><td style="text-align:right;">${subIssue > 0 ? inr(subIssue) : "—"}</td><td></td><td></td></tr></tfoot>
      </table>
      <div style="margin-bottom:14px;"></div>`;
  };
  const sections = months.map(section).join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">COA CERTIFICATE REGISTER — MONTH-WISE</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MATERIAL DAMAGE IN TRANSIT — FOR INSURANCE RENEWAL DISCUSSION &nbsp;•&nbsp; AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${list.length} CERTIFICATE(S)</div>
    ${list.length ? sections : `<div class="muted" style="text-align:center;padding:30px;">No COA certificates issued.</div>`}
    ${list.length ? `<div style="text-align:right;font-size:12px;font-weight:800;color:#991b1b;margin-top:4px;">GRAND TOTAL DAMAGE VALUE: ${inr(grand)}${grandIssue > 0 ? ` &nbsp;•&nbsp; <span style="color:#0f172a;">GRAND TOTAL ISSUE VALUE: ${inr(grandIssue)}</span>` : ""}</div>` : ""}
  `;
  open("COA Register", inner);
}

export function printVendorProfile(r) {
  const fld = (label, val) => `
    <tr>
      <td style="width:220px;font-weight:700;color:#334155;padding:5px 10px;border:1px solid #e2e8f0;background:#f8fafc;font-size:11px;text-transform:uppercase;letter-spacing:0.8px;">${label}</td>
      <td style="padding:5px 10px;border:1px solid #e2e8f0;font-size:12.5px;">${esc(val || "—")}</td>
    </tr>`;
  const section = (title, rows) => `
    <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#991b1b;margin:14px 0 6px;">${title}</div>
    <table style="width:100%;border-collapse:collapse;">${rows}</table>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR / BROKER PROFILE</div>
    <div style="display:flex;justify-content:space-between;align-items:center;margin:0 0 10px;padding:10px 14px;border:1.5px solid #0f172a;border-radius:8px;">
      <div>
        <div style="font-size:16px;font-weight:800;">${esc(r.name || "—")}</div>
        <div style="font-size:11px;color:#475569;">${esc(r.type || "Vendor/Broker")} · Branch: ${esc(r.branch || "—")}</div>
        ${r._podScore && r._podScore.avg != null ? `<div style="margin-top:5px;"><span style="display:inline-block;border:2px solid ${r._podScore.avg > 15 ? "#b91c1c" : "#047857"};color:${r._podScore.avg > 15 ? "#b91c1c" : "#047857"};border-radius:6px;padding:2px 10px;font-size:9px;font-weight:800;letter-spacing:1.2px;transform:rotate(-2deg);">${r._podScore.avg > 15 ? "SLOW POD RETURNER" : "FAST POD RETURNER"} — AVG ${r._podScore.avg} DAYS${r._podScore.out ? ` · ${r._podScore.out} STILL OUT` : ""}</span></div>` : ""}
      </div>
      <div style="text-align:right;">
        <div style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Vendor/Broker Code</div>
        <div style="font-size:18px;font-weight:800;color:#991b1b;">${esc(r.vendor_code || "—")}</div>
      </div>
    </div>
    ${section("Contact", fld("Contact Person", r.contact_person) + fld("Mobile No.", r.phone) + fld("Email", r.email) + fld("Vendor/Broker Address", r.address))}
    ${section("Statutory", fld("GSTIN", r.gstin) + fld("PAN", r.pan) + fld("TAN", r.tan))}
    ${section("Bank Details (for payments)", fld("Account Holder Name", r.account_holder) + fld("Account Number", r.account_no) + fld("Bank Name", r.bank_name) + fld("IFSC Code", r.ifsc) + fld("Bank Address", r.bank_address))}
    ${(r._docUrls || []).length ? `
    <div style="font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#991b1b;margin:14px 0 6px;">KYC Documents on File</div>
    <div style="display:flex;flex-wrap:wrap;gap:10px;">
      ${(r._docUrls || []).filter((d) => !d.isPdf).map((d) => `
        <div style="width:220px;">
          <img src="${d.url}" style="width:220px;height:150px;object-fit:contain;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;" />
          <div style="font-size:9.5px;font-weight:700;color:#334155;text-align:center;margin-top:2px;">${esc(d.label)}</div>
        </div>`).join("")}
    </div>
    ${(r._docUrls || []).some((d) => d.isPdf) ? `<div style="font-size:10.5px;color:#475569;margin-top:6px;"><b>PDF documents on file:</b> ${(r._docUrls || []).filter((d) => d.isPdf).map((d) => `${esc(d.label)} (${esc(d.filename)})`).join(", ")}</div>` : ""}
    ` : ""}
    <div style="display:flex;justify-content:space-between;margin-top:40px;font-size:11px;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;width:200px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;width:200px;text-align:center;">Verified By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;width:200px;text-align:center;">Authorised Signatory</div>
    </div>
    <div style="font-size:9.5px;color:#64748b;margin-top:10px;">Printed on ${fmtDate(new Date().toISOString().slice(0, 10))} — keep this profile with the vendor agreement file.</div>
  `;
  open(`Vendor-Profile-${(r.vendor_code || r.name || "").replace(/\s+/g, "-")}`, inner);
}

export function printCorrectionLog(rows) {
  const list = [...(rows || [])].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const byMonth = {};
  for (const r of list) {
    const m = String(r.created_at || "").slice(0, 7) || "Unknown";
    (byMonth[m] = byMonth[m] || []).push(r);
  }
  const monthLabel = (m) => {
    const [y, mo] = m.split("-");
    const names = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
    return mo ? `${names[Number(mo) - 1]} ${y}` : m;
  };
  const section = (m) => {
    const rs = byMonth[m];
    const body = rs
      .map(
        (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${fmtDateDay(String(r.created_at || "").slice(0, 10))}</td>
        <td>${esc(r.office || "—")}</td>
        <td style="font-weight:700;">${esc(r.lr_no || "—")}</td>
        <td>${esc(r.customer_name || "—")}</td>
        <td style="white-space:nowrap;">${esc(r.field_label || "—")}</td>
        <td>${esc(r.old_value || "—")} → <b>${esc(r.new_value || "—")}</b></td>
        <td>${esc(r.reason || "—")}</td>
        <td>${esc(r.requested_by || "—")}</td>
        <td style="font-weight:700;${r.status === "Approved" ? "color:#047857;" : r.status === "Rejected" ? "color:#991b1b;" : "color:#b45309;"}">${esc(r.status || "—")}</td>
        <td>${esc(r.decided_by || "—")}</td>
        <td style="white-space:nowrap;">${r.decided_at ? fmtDateDay(String(r.decided_at).slice(0, 10)) : "—"}</td>
      </tr>`
      )
      .join("");
    const a = rs.filter((r) => r.status === "Approved").length;
    const rj = rs.filter((r) => r.status === "Rejected").length;
    const p = rs.filter((r) => r.status === "Pending").length;
    return `
      <div style="font-size:11px;font-weight:800;letter-spacing:2px;margin:14px 0 6px;border-bottom:1.5px solid #0f172a;padding-bottom:3px;">${monthLabel(m)} — ${rs.length} REQUEST(S) · APPROVED ${a} · REJECTED ${rj} · PENDING ${p}</div>
      <table class="tight" style="font-size:9.5px;">
        <thead><tr><th>Sl</th><th>Requested On</th><th>Branch</th><th>LR No.</th><th>Customer</th><th>Type of Correction</th><th>Old → New</th><th>Reason</th><th>Requested By</th><th>Status</th><th>Decided By</th><th>Decided On</th></tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  };
  const months = Object.keys(byMonth).sort().reverse();
  const inner = `
    <div class="title" style="letter-spacing:3px;">CORRECTION LOG BOOK — AUDIT REGISTER</div>
    <div style="margin:0 0 10px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">FREIGHT / INVOICE CORRECTION REQUESTS &nbsp;•&nbsp; AS ON ${fmtDateDay(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${list.length} TOTAL</div>
    ${list.length ? months.map(section).join("") : `<div class="muted" style="text-align:center;padding:30px;">No correction requests to print.</div>`}
  `;
  open("Correction Log Book", inner);
}

export function printClaimAgeing(rows) {
  const open_ = (rows || []).filter((r) => r.coa_no && (r.coa_claim_status || "Not Filed") !== "Settled");
  const days = (d) => (d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 0);
  const list = open_
    .map((r) => ({ ...r, _days: days(r.coa_date), _filedDays: r.coa_claim_status === "Filed" || r.coa_claim_status === "Approved" ? days(String(r.coa_claim_updated_at || r.coa_date || "").slice(0, 10)) : null }))
    .sort((a, b) => b._days - a._days);
  const tone = (d) => (d > 60 ? "color:#991b1b;font-weight:800;" : d > 30 ? "color:#b45309;font-weight:700;" : "");
  const total = list.reduce((s, r) => s + (Number(r.coa_damage_value) || 0), 0);
  const body = list
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;color:#991b1b;">${esc(r.coa_no)}</td>
      <td style="white-space:nowrap;">${r.coa_date ? fmtDateDay(r.coa_date) : "—"}</td>
      <td>${esc(String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""))}</td>
      <td>${esc(r.origin || "—")} → ${esc(r.destination || "—")}</td>
      <td>${esc(r.consignor || r.customer_name || "—")}</td>
      <td>${esc(r.office || "Chennai")}</td>
      <td style="text-align:right;">${Number(r.coa_damage_value) > 0 ? inr(r.coa_damage_value) : "—"}</td>
      <td>${esc(r.coa_claim_status || "Not Filed")}</td>
      <td style="text-align:center;${tone(r._days)}">${r._days}</td>
      <td style="text-align:center;">${r._filedDays == null ? "—" : r._filedDays}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CLAIM AGEING — OPEN INSURANCE CLAIMS</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">FOR INSURER REVIEW MEETING &nbsp;•&nbsp; AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${list.length} OPEN CLAIM(S)</div>
    ${list.length ? `
    <table class="tight" style="font-size:10px;">
      <thead><tr><th>Sl</th><th>COA No.</th><th>COA Date</th><th>LR No.</th><th>Route</th><th>Party</th><th>Branch</th><th>Damage Value</th><th>Claim Status</th><th>Days Since COA</th><th>Days Since Filed</th></tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr style="font-weight:800;background:#fef2f2;"><td colspan="7" style="text-align:right;">TOTAL DAMAGE VALUE UNDER OPEN CLAIMS</td><td style="text-align:right;color:#991b1b;">${inr(total)}</td><td colspan="3"></td></tr></tfoot>
    </table>
    <div style="font-size:9.5px;color:#64748b;margin-top:8px;">Ageing over 30 days shown amber, over 60 days red. Settled claims are excluded from this sheet.</div>` : `<div class="muted" style="text-align:center;padding:30px;">No open claims — all COA claims are settled.</div>`}
  `;
  open("Claim Ageing Report", inner);
}

export function printStaffIdCard(r, token) {
  const inner = `<div style="display:flex;justify-content:center;gap:24px;flex-wrap:wrap;margin-top:24px;">${staffIdCardHtml(r, token)}${staffIdCardBackHtml(r)}</div>`;
  open(`Staff ID Card — ${r.name || ""}`, inner, { hideHeader: true, downloadImage: true });
}

export function staffIdCardShareHtml(r, token) {
  return `<div style="display:flex;gap:20px;background:#ffffff;padding:4px;">${staffIdCardHtml(r, token)}${staffIdCardBackHtml(r)}</div>`;
}

export function printStaffIdCardsBulk(rows, token, branchLabel) {
  const cards = (rows || []).map((r) => `${staffIdCardHtml(r, token)}${staffIdCardBackHtml(r)}`).join("");
  const inner = `
    <div style="text-align:center;font-size:13px;font-weight:800;letter-spacing:2px;margin:8px 0 4px;">STAFF ID CARDS${branchLabel && branchLabel !== "All" ? ` — ${esc(branchLabel.toUpperCase())} BRANCH` : ""}</div>
    <div style="text-align:center;font-size:10px;color:#64748b;margin-bottom:14px;">${(rows || []).length} card(s)</div>
    <div style="display:flex;flex-wrap:wrap;gap:20px;justify-content:center;">${cards}</div>
  `;
  open("NTC Staff ID Cards", inner, { hideHeader: true, downloadImage: true });
}

export function printExpenseRegister(rows) {
  const arr = rows || [];
  const total = arr.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td>${esc(r.office || "Chennai")}</td>
      <td>${esc(r.category || "—")}</td>
      <td>${esc(r.description || "—")}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td style="text-align:center;">${r.proof_path ? `<span style="color:#059669;font-weight:700;">✓ Yes</span>` : "—"}</td>
      <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
    </tr>`
    )
    .join("");
  const withProof = arr.filter((r) => r.proof_path).length;
  const inner = `
    <div class="title" style="letter-spacing:3px;">EXPENSE REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Records:</b> ${arr.length} &nbsp;&nbsp; <b>With Bill Proof:</b> ${withProof} of ${arr.length} &nbsp;&nbsp; <b>Total:</b> ${inr(total)}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Date</th><th>Branch</th><th>Category</th><th>Description</th><th>Vehicle</th><th style="text-align:center;">Bill Proof</th><th class="right">Amount</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No expenses.</td></tr>`}
        <tr><td colspan="7" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(total)}</td></tr>
      </tbody>
    </table>
  `;
  open("NTC Expense Register", inner);
}

export function printExpenseSummary(monthLabel, rows) {
  const arr = rows || [];
  const grand = arr.reduce((a, r) => a + (r.total || 0), 0);
  const body = arr
    .map((r) => {
      const cats = (r.categories || [])
        .map(
          (c) =>
            `<tr style="background:#f8fafc;"><td style="padding-left:24px;font-size:10px;color:#475569;">↳ ${esc(c.category)}</td><td class="right" style="font-size:10px;color:#475569;">${c.count}</td><td class="right" style="font-size:10px;color:#475569;">${inr(c.total)}</td></tr>`
        )
        .join("");
      return `<tr><td style="font-weight:700;">${esc(r.branch)}</td><td class="right" style="font-weight:700;">${r.count}</td><td class="right" style="font-weight:700;">${inr(r.total)}</td></tr>${cats}`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY EXPENSE SUMMARY</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel)}</div>
    <table style="width:70%;">
      <thead><tr><th>Branch</th><th class="right">Expenses</th><th class="right">Total</th></tr></thead>
      <tbody>${body || `<tr><td colspan="3" class="muted">No expenses.</td></tr>`}
        <tr><td style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${arr.reduce((a, r) => a + (r.count || 0), 0)}</td><td class="right" style="font-weight:800;">${inr(grand)}</td></tr>
      </tbody>
    </table>
  `;
  open("Monthly Expense Summary", inner);
}

export function printDriverRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(r.name || "—")}</td>
      <td>${esc(r.father_name || "—")}</td>
      <td style="white-space:nowrap;">${esc(r.aadhar_no || "—")}</td>
      <td>${esc(r.phone || "—")}</td>
      <td>${esc(r.license_no || "—")}</td>
      <td class="right" style="white-space:nowrap;">${inr(r.monthly_salary)}</td>
      <td>${esc(r.status || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DRIVER REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Drivers:</b> ${arr.length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Name</th><th>Father Name</th><th>Aadhar No.</th><th>Phone</th><th>License</th><th class="right">Salary</th><th>Status</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No drivers.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Driver Register", inner);
}

export function printDailyCollection(dateStr, receipts) {
  const arr = (receipts || []).filter((r) => String(r.date || "").slice(0, 10) === dateStr);
  const groups = {};
  arr.forEach((r) => {
    const b = r.office || "Chennai";
    (groups[b] = groups[b] || []).push(r);
  });
  const order = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
  const branches = [...order.filter((b) => groups[b]), ...Object.keys(groups).filter((b) => !order.includes(b))];
  const grand = arr.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const sections = branches
    .map((b) => {
      const list = groups[b];
      const sub = list.reduce((a, r) => a + (Number(r.amount) || 0), 0);
      const body = list
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td style="white-space:nowrap;">${esc(r.mr_no || "—")}</td>
            <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
            <td>${esc(String(r.against_invoice_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
            <td>${esc(r.payment_mode || "—")}</td>
            <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
          </tr>`
        )
        .join("");
      return `
        <div style="margin-bottom:18px;page-break-inside:avoid;">
          <div style="background:#0f172a;color:#ffffff;padding:6px 12px;border-radius:6px 6px 0 0;font-size:12px;font-weight:800;letter-spacing:2px;">${esc(b.toUpperCase())} BRANCH</div>
          <table class="tight" style="margin-top:0;">
            <thead><tr><th>Sl</th><th>MR No.</th><th>Customer</th><th>Bill No.</th><th>Mode</th><th class="right">Amount</th></tr></thead>
            <tbody>${body}
              <tr><td colspan="5" class="right" style="font-weight:800;">${esc(b.toUpperCase())} TOTAL</td><td class="right" style="font-weight:800;">${inr(sub)}</td></tr>
            </tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DAILY COLLECTION SHEET</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Date:</b> ${fmtDate(dateStr)} &nbsp;&nbsp; <b>Receipts:</b> ${arr.length} &nbsp;&nbsp; <b>Grand Total:</b> ${inr(grand)}</div>
    ${sections || `<div class="muted">No money receipts on this date.</div>`}
    ${sections ? `<div style="text-align:right;font-size:13px;font-weight:800;margin-top:6px;">GRAND TOTAL: ${inr(grand)}</div>` : ""}
  `;
  open("NTC Daily Collection Sheet", inner);
}

export function printMonthlyCollection(monthStr, receipts) {
  const arr = (receipts || [])
    .filter((r) => String(r.date || "").slice(0, 7) === monthStr)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const groups = {};
  arr.forEach((r) => {
    const b = r.office || "Chennai";
    (groups[b] = groups[b] || []).push(r);
  });
  const order = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
  const branches = [...order.filter((b) => groups[b]), ...Object.keys(groups).filter((b) => !order.includes(b))];
  const grand = arr.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const monthLabel = new Date(`${monthStr}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const sections = branches
    .map((b) => {
      const list = groups[b];
      const sub = list.reduce((a, r) => a + (Number(r.amount) || 0), 0);
      const body = list
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
            <td style="white-space:nowrap;">${esc(r.mr_no || "—")}</td>
            <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
            <td>${esc(String(r.against_invoice_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
            <td>${esc(r.payment_mode || "—")}</td>
            <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
          </tr>`
        )
        .join("");
      return `
        <div style="margin-bottom:18px;page-break-inside:avoid;">
          <div style="background:#f1f3f5;border:1px solid #dfe3e8;color:#0f172a;padding:6px 12px;border-radius:6px 6px 0 0;font-size:12px;font-weight:800;letter-spacing:2px;">${esc(b.toUpperCase())} BRANCH <span style="font-weight:600;font-size:10px;color:#5b6470;">— ${list.length} receipt(s)</span></div>
          <table class="tight" style="margin-top:0;">
            <thead><tr><th>Sl</th><th>Date</th><th>MR No.</th><th>Customer</th><th>Bill No.</th><th>Mode</th><th class="right">Amount</th></tr></thead>
            <tbody>${body}
              <tr><td colspan="6" class="right" style="font-weight:800;">${esc(b.toUpperCase())} TOTAL</td><td class="right" style="font-weight:800;">${inr(sub)}</td></tr>
            </tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY COLLECTION REGISTER</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Month:</b> ${esc(monthLabel)} &nbsp;&nbsp; <b>Receipts:</b> ${arr.length} &nbsp;&nbsp; <b>Grand Total:</b> ${inr(grand)}</div>
    ${sections || `<div class="muted">No money receipts in this month.</div>`}
    ${sections ? `<div style="text-align:right;font-size:13px;font-weight:800;margin-top:6px;">GRAND TOTAL: ${inr(grand)}</div>` : ""}
  `;
  open("NTC Monthly Collection Register", inner);
}

export function printPartPaymentSummary(rows) {
  const arr = [...(rows || [])].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  let tBill = 0, tRecd = 0, tDed = 0, tPend = 0;
  const body = arr
    .map((r, i) => {
      const bill = Number(r._bill_amount) || 0;
      const recd = Number(r.amount) || 0;
      const ded = (Number(r.tds) || 0) + (Number(r.deduction) || 0);
      const pend = bill - recd - ded;
      tBill += bill; tRecd += recd; tDed += ded; tPend += pend;
      return `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${esc(String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—")}</td>
        <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
        <td>${esc(r.office || "Chennai")}</td>
        <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
        <td>${esc(r._bill_no || "—")}</td>
        <td style="white-space:nowrap;">${r._bill_date ? fmtDateDay(r._bill_date) : "—"}</td>
        <td class="right">${inr(bill)}</td>
        <td class="right">${inr(recd)}</td>
        <td class="right">${ded ? inr(ded) : "—"}</td>
        <td class="right" style="font-weight:700;color:#dc2626;">${inr(pend)}</td>
      </tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">PART PAYMENT SUMMARY</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:12px;">Receipts received short of bill amount (after TDS/deductions) — as on ${fmtDate(new Date().toISOString())}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>MR No.</th><th>MR Date</th><th>Branch</th><th>Customer Name</th><th>Bill No.</th><th>Bill Date</th><th class="right">Bill Amount</th><th class="right">Received</th><th class="right">TDS/Deduction</th><th class="right">Pending Balance</th></tr></thead>
      <tbody>${body || `<tr><td colspan="11" class="muted">No part-paid receipts.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="7" class="right">TOTAL (${arr.length} receipt${arr.length === 1 ? "" : "s"})</td>
          <td class="right">${inr(tBill)}</td>
          <td class="right">${inr(tRecd)}</td>
          <td class="right">${tDed ? inr(tDed) : "—"}</td>
          <td class="right" style="color:#dc2626;">${inr(tPend)}</td>
        </tr>
      </tbody>
    </table>`;
  open("Part Payment Summary", inner);
}

export function printReceiptRegister(rows, opts = {}) {
  const arr = [...(rows || [])].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const total = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const withAdvice = arr.filter((r) => r.advice_path).length;
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.office || "Chennai")}</td>
        <td style="white-space:nowrap;">${esc(String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—")}</td>
        <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
        <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
        <td style="font-weight:600;">${esc(r.customer_code || "—")}</td>
        <td>${esc(String(r.against_invoice_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
        <td>${esc(r.payment_mode || "—")}</td>
        <td style="text-align:center;">${r.advice_path ? `<span style="color:#059669;font-weight:700;">✓ Yes</span>` : "—"}</td>
        <td style="white-space:nowrap;">${r.payment_date ? fmtDateDay(r.payment_date) : "—"}</td>
        <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
      </tr>`
    )
    .join("");
  const period = opts.from || opts.to ? `<b>Period:</b> ${opts.from ? fmtDate(opts.from) : "Start"} to ${opts.to ? fmtDate(opts.to) : "Today"} &nbsp;&nbsp;` : "";
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONEY RECEIPT REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;">${period}<b>Receipts:</b> ${arr.length} &nbsp;&nbsp; <b>With Payment Advice:</b> ${withAdvice} of ${arr.length} &nbsp;&nbsp; <b>Total:</b> ${inr(total)}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Branch</th><th>MR No.</th><th>MR Date</th><th>Received From</th><th>Code</th><th>Bill No.</th><th>Mode</th><th style="text-align:center;">Payment Advice</th><th>Payment Date</th><th class="right">Amount</th></tr></thead>
      <tbody>${body || `<tr><td colspan="11" class="muted">No receipts.</td></tr>`}
        ${body ? `<tr><td colspan="10" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(total)}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Money Receipt Register", inner);
}

export function printCustomerLedger({ customer, from, to, opening, entries, totals, custTotals }) {
  const body = (entries || [])
    .map(
      (e) => `<tr>
        <td>${esc(e.branch || "—")}</td>
        <td style="white-space:nowrap;">${fmtDateDay(e.date)}</td>
        <td>${esc(e.particulars)}</td>
        <td>${esc(e.bill_no || "—")}</td>
        <td style="white-space:nowrap;">${e.bill_date ? fmtDateDay(e.bill_date) : "—"}</td>
        <td>${esc(e.from_place || "—")}</td>
        <td>${esc(e.destination || "—")}</td>
        <td class="right">${e.debit ? inr(e.debit) : "—"}</td>
        <td>${e.credit ? esc(e.credit_bill_no || "—") : "—"}</td>
        <td style="white-space:nowrap;">${e.credit && e.credit_bill_date ? fmtDate(e.credit_bill_date) : "—"}</td>
        <td class="right">${e.credit ? inr(e.credit) : "—"}</td>
        <td class="right" style="font-weight:600;white-space:nowrap;">${inr(e.balance)}</td>
      </tr>`
    )
    .join("");
  const isAllCust = customer?.id === "__ALL__";
  const brs = Array.from(new Set((entries || []).map((e) => e.branch).filter(Boolean)));
  const branchStr = customer?.branch || brs.join(", ") || "—";
  const inner = `
    <div class="title" style="letter-spacing:3px;">CUSTOMER LEDGER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Customer Name:</b> ${esc(customer.name)}${isAllCust ? "" : ` &nbsp;&nbsp; <b>Customer Code:</b> ${esc(customer.customer_code || "—")} &nbsp;&nbsp; <b>Branch:</b> ${esc(branchStr)}`} &nbsp;&nbsp; <b>Period:</b> ${from ? fmtDate(from) : "Start"} to ${to ? fmtDate(to) : "Today"} &nbsp;&nbsp; <b>Closing Balance:</b> ${inr(totals.closing)}</div>
    <table class="tight" style="font-size:9.5px;">
      <thead><tr><th>Branch</th><th>Date</th><th>Particulars</th><th>Bill No.</th><th>Bill Date</th><th>From</th><th>Destination</th><th class="right">Debit (Bill)</th><th>Credit Bill</th><th>Credit Bill Date</th><th class="right">Credit Amount</th><th class="right">Balance Amount</th></tr></thead>
      <tbody>
        ${from && opening !== null && opening !== undefined ? `<tr><td></td><td style="white-space:nowrap;">${fmtDateDay(from)}</td><td colspan="9" style="font-weight:700;">Opening Balance</td><td class="right" style="font-weight:700;">${inr(opening)}</td></tr>` : ""}
        ${body}
        <tr><td colspan="7" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(totals.debit)}</td><td colspan="2"></td><td class="right" style="font-weight:800;">${inr(totals.credit)}</td><td class="right" style="font-weight:800;">${inr(totals.closing)}</td></tr>
      </tbody>
    </table>
    ${(custTotals || []).length ? `
    <div style="margin-top:14px;font-size:12px;font-weight:800;letter-spacing:1px;">CUSTOMER-WISE TOTALS</div>
    <table class="tight" style="font-size:9.5px;margin-top:4px;">
      <thead><tr><th>Customer</th><th class="right">Billed (Debit)</th><th class="right">Received (Credit)</th><th class="right">Balance Amount</th></tr></thead>
      <tbody>
        ${custTotals.map((g) => `<tr><td style="font-weight:700;">${esc(g.cname)}</td><td class="right">${inr(g.debit)}</td><td class="right">${inr(g.credit)}</td><td class="right" style="font-weight:700;${g.balance > 0.01 ? "color:#b91c1c;" : "color:#047857;"}">${inr(g.balance)}</td></tr>`).join("")}
        <tr style="font-weight:800;background:#f1f5f9;"><td>TOTAL</td><td class="right">${inr(totals.debit)}</td><td class="right">${inr(totals.credit)}</td><td class="right">${inr(totals.closing)}</td></tr>
      </tbody>
    </table>` : ""}
  `;
  open("NTC Customer Ledger", inner);
}

export function printOverdueSheet(list) {
  const total = (list || []).reduce((s, a) => s + (a.balance || 0), 0);
  const body = (list || [])
    .map(
      (a, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:600;">${esc(a.name)}</td>
        <td style="white-space:nowrap;">${esc(a.phone || "—")}</td>
        <td class="right" style="font-weight:700;white-space:nowrap;">${inr(a.balance)}</td>
        <td class="right">${a.days}</td>
        <td style="white-space:nowrap;">${a.oldest ? fmtDateDay(a.oldest) : "—"}</td>
        <td style="min-width:120px;"></td>
        <td style="min-width:140px;"></td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">OVERDUE FOLLOW-UP SHEET</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Date:</b> ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;&nbsp; <b>Customers:</b> ${(list || []).length} &nbsp;&nbsp; <b>Total Pending:</b> ${inr(total)} &nbsp;&nbsp; (balances pending over 30 days)</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Customer</th><th>Phone</th><th class="right">Balance</th><th class="right">Days</th><th>Oldest Bill</th><th>Assigned To</th><th>Call Remarks</th></tr></thead>
      <tbody>${body}
        <tr><td colspan="3" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(total)}</td><td colspan="4"></td></tr>
      </tbody>
    </table>
  `;
  open("NTC Overdue Follow-up Sheet", inner);
}

export function printAgingReport(rows) {
  const sum = (k) => (rows || []).reduce((s, r) => s + (r[k] || 0), 0);
  const amt = (v) => (v ? inr(v) : "—");
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:600;">${esc(r.name)}</td>
        <td class="right">${amt(r.b0)}</td>
        <td class="right">${amt(r.b31)}</td>
        <td class="right">${amt(r.b61)}</td>
        <td class="right" style="color:#dc2626;">${amt(r.b90)}</td>
        <td class="right" style="font-weight:700;white-space:nowrap;">${inr(r.total)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">OUTSTANDING AGING REPORT</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>As on:</b> ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;&nbsp; <b>Total Outstanding:</b> ${inr(sum("total"))} &nbsp;&nbsp; (unpaid bills by age)</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Customer</th><th class="right">0–30 Days</th><th class="right">31–60 Days</th><th class="right">61–90 Days</th><th class="right">90+ Days</th><th class="right">Total</th></tr></thead>
      <tbody>${body}
        <tr><td colspan="2" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(sum("b0"))}</td><td class="right" style="font-weight:800;">${inr(sum("b31"))}</td><td class="right" style="font-weight:800;">${inr(sum("b61"))}</td><td class="right" style="font-weight:800;color:#dc2626;">${inr(sum("b90"))}</td><td class="right" style="font-weight:800;">${inr(sum("total"))}</td></tr>
      </tbody>
    </table>
  `;
  open("NTC Aging Report", inner);
}

export function printVendorPaymentRegister(rows, opts = {}) {
  const arr = [...(rows || [])].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const total = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
        <td style="font-weight:600;">${esc(r.vendor_name || "—")}</td>
        <td>${esc(r.branch || "—")}</td>
        <td>${esc(r.payment_mode || "—")}</td>
        <td>${esc(r.ref_no || "—")}</td>
        <td>${esc(r.narration || "—")}</td>
        <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
      </tr>`
    )
    .join("");
  const period = opts.from || opts.to ? `<b>Period:</b> ${opts.from ? fmtDate(opts.from) : "Start"} to ${opts.to ? fmtDate(opts.to) : "Today"} &nbsp;&nbsp;` : "";
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR PAYMENT REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;">${period}<b>Payments:</b> ${arr.length} &nbsp;&nbsp; <b>Total Paid:</b> ${inr(total)}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Date</th><th>Vendor / Broker</th><th>Branch</th><th>Mode</th><th>Ref No.</th><th>Narration</th><th class="right">Amount</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No payments in this period.</td></tr>`}
        ${body ? `<tr><td colspan="7" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(total)}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Vendor Payment Register", inner);
}

export function printReminderRegister(rows, monthLabel) {
  const arr = [...(rows || [])].sort((a, b) => String(b.ins_reminder_at || "").localeCompare(String(a.ins_reminder_at || "")));
  const body = arr
    .map(
      (c, i) => `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;font-weight:600;">${fmtDate(String(c.ins_reminder_at || "").slice(0, 10))}</td>
        <td style="font-weight:700;">${esc(c.vehicle_no || "—")}</td>
        <td>${esc(c.owner_name || c.vendor_broker_name || "—")}</td>
        <td style="white-space:nowrap;">${esc(c.owner_mobile || c.vendor_mobile || c.driver_phone || "—")}</td>
        <td style="white-space:nowrap;">${c.ins_valid_upto ? fmtDateDay(c.ins_valid_upto) : "—"}</td>
        <td style="white-space:nowrap;">${c.permit_valid_upto ? fmtDateDay(c.permit_valid_upto) : "—"}</td>
        <td>${esc(c.engaged_office || c.office || "—")}</td>
        <td></td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">RENEWAL REMINDER REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Reminders Sent:</b> ${arr.length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Sent On</th><th>Vehicle No.</th><th>Owner / Vendor</th><th>Mobile</th><th>Insurance Upto</th><th>Permit Upto</th><th>Branch</th><th style="min-width:90px;">Follow-up Remark</th></tr></thead>
      <tbody>${body || `<tr><td colspan="9" class="muted">No reminders sent in this month.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Renewal Reminder Register", inner);
}

export function printOldVehicleRegister(rows) {
  const yearOf = (c) => parseInt(String(c.make || "").match(/(19|20)\d{2}/)?.[0] || "", 10) || 0;
  const nowY = new Date().getFullYear();
  const todayStr = new Date().toISOString().slice(0, 10);
  const arr = [...(rows || [])];
  const body = arr
    .map((c, i) => {
      const y = yearOf(c);
      const fcLapsed = c.fc_valid_upto && String(c.fc_valid_upto).slice(0, 10) < todayStr;
      return `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(c.vehicle_no || "—")}</td>
        <td style="text-align:center;">${y || "—"}</td>
        <td style="text-align:center;font-weight:800;color:#b91c1c;">${y ? nowY - y + " yrs" : "—"}</td>
        <td>${esc(c.vehicle_type || "—")}</td>
        <td>${esc(c.owner_name || c.vendor_broker_name || "—")}${c.owner_mobile ? ` (${esc(c.owner_mobile)})` : ""}</td>
        <td style="font-weight:700;">${esc(String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
        <td>${esc([c.origin, c.destination].filter(Boolean).join(" → ") || "—")}</td>
        <td style="white-space:nowrap;">${c.permit_valid_upto ? fmtDateDay(c.permit_valid_upto) : "—"}</td>
        <td style="white-space:nowrap;${fcLapsed ? "font-weight:800;color:#b91c1c;" : ""}">${c.fc_valid_upto ? fmtDateDay(c.fc_valid_upto) + (fcLapsed ? " (LAPSED)" : "") : "—"}</td>
      </tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">OLD VEHICLE REGISTER — 15+ YEARS</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Vehicles:</b> ${arr.length} &nbsp;&nbsp; <b>Rule:</b> Make year older than 15 years — verify route permits before despatch.</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Vehicle No.</th><th>Make Year</th><th>Age</th><th>Vehicle Type</th><th>Owner / Vendor</th><th>Last Challan</th><th>Route</th><th>Permit Upto</th><th>FC Upto</th></tr></thead>
      <tbody>${body || `<tr><td colspan="10" class="muted">No engaged vehicles older than 15 years.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Traffic In Charge</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Old Vehicle Register", inner);
}

export function printBalanceDueRegister(rows, monthLabel) {
  const paidOf = (c) => (c.payments || []).filter((p) => p.type === "Vehicle Balance Payment").reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const dueOf = (c) => Math.max((Number(c.balance) || 0) - paidOf(c), 0);
  const arr = [...(rows || [])].sort((a, b) => dueOf(b) - dueOf(a));
  const tHire = arr.reduce((a, c) => a + (Number(c.hire_amount) || 0), 0);
  const tAdv = arr.reduce((a, c) => a + (Number(c.advance_paid) || 0), 0);
  const tPaid = arr.reduce((a, c) => a + paidOf(c), 0);
  const tDue = arr.reduce((a, c) => a + dueOf(c), 0);
  const body = arr
    .map(
      (c, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
        <td style="white-space:nowrap;">${c.date || c.loading_date ? fmtDateDay(c.date || c.loading_date) : "—"}</td>
        <td style="font-weight:700;">${esc(c.vehicle_no || "—")}</td>
        <td>${esc(c.vendor_broker_name || c.owner_name || "—")}</td>
        <td style="white-space:nowrap;">${esc(c.owner_mobile || c.vendor_mobile || c.driver_phone || "—")}</td>
        <td>${esc(c.engaged_office || c.office || "—")}</td>
        <td style="text-align:right;">${inr(c.hire_amount)}</td>
        <td style="text-align:right;">${inr(c.advance_paid)}</td>
        <td style="text-align:right;">${inr(paidOf(c))}</td>
        <td style="text-align:right;font-weight:800;">${inr(dueOf(c))}</td>
        <td></td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BALANCE DUE REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Challans Pending:</b> ${arr.length} &nbsp;&nbsp; <b>Total Due:</b> ${inr(tDue)}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Challan Date</th><th>Vehicle No.</th><th>Vendor / Owner</th><th>Mobile</th><th>Branch</th><th>Hire</th><th>Advance</th><th>Paid</th><th>Balance Due</th><th style="min-width:80px;">Settled On</th></tr></thead>
      <tbody>${body || `<tr><td colspan="12" class="muted">No unpaid balances for this month.</td></tr>`}
      ${arr.length ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" style="text-align:right;">TOTAL</td><td style="text-align:right;">${inr(tHire)}</td><td style="text-align:right;">${inr(tAdv)}</td><td style="text-align:right;">${inr(tPaid)}</td><td style="text-align:right;">${inr(tDue)}</td><td></td></tr>` : ""}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Cashier</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Balance Due Register", inner);
}

export function printSettlementRegister(rows, monthLabel) {
  const arr = [...(rows || [])].sort((a, b) => String(a.settled_at || "").localeCompare(String(b.settled_at || "")));
  const total = arr.reduce((a, c) => a + (Number(c.balance) || 0), 0);
  const body = arr
    .map(
      (c, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
        <td style="white-space:nowrap;">${c.date || c.loading_date ? fmtDateDay(c.date || c.loading_date) : "—"}</td>
        <td style="font-weight:700;">${esc(c.vehicle_no || "—")}</td>
        <td>${esc(c.vendor_broker_name || c.owner_name || "—")}</td>
        <td>${esc(c.engaged_office || c.office || "—")}</td>
        <td style="text-align:right;">${inr(c.hire_amount)}</td>
        <td style="text-align:right;">${inr(c.advance_paid)}</td>
        <td style="text-align:right;font-weight:800;">${inr(c.balance)}</td>
        <td style="white-space:nowrap;">${c.settled_at ? fmtDateDay(String(c.settled_at).slice(0, 10)) : "—"}</td>
        <td>${esc(c.settled_by || "—")}</td>
        <td style="font-size:9.5px;">${c.balance_release_reason ? `<b>Admin release${c.balance_release_by ? ` (${esc(c.balance_release_by)})` : ""}:</b> ${esc(c.balance_release_reason)}` : "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">SETTLEMENT REGISTER — CASH FILE</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Challans Settled:</b> ${arr.length} &nbsp;&nbsp; <b>Total Settled:</b> ${inr(total)}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Challan Date</th><th>Vehicle No.</th><th>Vendor / Owner</th><th>Branch</th><th>Hire</th><th>Advance</th><th>Settled Amount</th><th>Settled On</th><th>Settled By</th><th>Release Reason</th></tr></thead>
      <tbody>${body || `<tr><td colspan="12" class="muted">No challans settled in this month.</td></tr>`}
      ${arr.length ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="8" style="text-align:right;">TOTAL SETTLED</td><td style="text-align:right;">${inr(total)}</td><td colspan="3"></td></tr>` : ""}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Cashier</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Settlement Register", inner);
}

export function printPodAckRegister(rows, monthLabel) {
  const arr = [...(rows || [])].sort((a, b) => Number(!!a.pod_ack_received) - Number(!!b.pod_ack_received) || String(a.date || a.loading_date || "").localeCompare(String(b.date || b.loading_date || "")));
  const back = arr.filter((c) => c.pod_ack_received).length;
  const out = arr.length - back;
  const lrOf = (c) => (Array.isArray(c.lr_items) && c.lr_items.length ? c.lr_items.map((x) => x.lr_no).filter(Boolean).join(", ") : c.lr_no || c.consignment_no || "—");
  const body = arr
    .map(
      (c, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
        <td style="white-space:nowrap;">${c.date || c.loading_date ? fmtDateDay(c.date || c.loading_date) : "—"}</td>
        <td style="font-weight:700;">${esc(c.vehicle_no || "—")}</td>
        <td>${esc(c.vendor_broker_name || c.owner_name || "—")}</td>
        <td style="white-space:nowrap;">${esc(c.owner_mobile || c.vendor_mobile || c.driver_phone || "—")}</td>
        <td>${esc(c.engaged_office || c.office || "—")}</td>
        <td>${esc(lrOf(c))}</td>
        <td style="text-align:center;font-weight:800;${c.pod_ack_received ? "color:#047857;" : "color:#b91c1c;"}">${c.pod_ack_received ? "BACK ✓" : "STILL OUT"}</td>
        <td style="white-space:nowrap;">${c.pod_ack_received && c.pod_ack_date ? fmtDateDay(c.pod_ack_date) : ""}</td>
        <td>${esc(c.pod_ack_received ? c.pod_ack_by || "" : "")}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">POD ACKNOWLEDGEMENT REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Challans:</b> ${arr.length} &nbsp;&nbsp; <b>POD Back:</b> ${back} &nbsp;&nbsp; <b style="color:#b91c1c;">Still Out:</b> ${out}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Challan Date</th><th>Vehicle No.</th><th>Vendor / Owner</th><th>Mobile</th><th>Branch</th><th>LR No.</th><th>POD Status</th><th>POD Back On</th><th>Received By</th></tr></thead>
      <tbody>${body || `<tr><td colspan="11" class="muted">No challans for this month.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Cashier</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC POD Ack Register", inner);
}

export function printReleaseLog(rows, monthLabel) {
  const arr = [...(rows || [])].sort((a, b) => String(a.decided_at || a.created_at || "").localeCompare(String(b.decided_at || b.created_at || "")));
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(r.lr_no || "—")}</td>
        <td>${esc(r.customer_name || "—")}</td>
        <td>${esc(r.office || "—")}</td>
        <td style="font-weight:700;">${esc(r.decided_by || "—")}</td>
        <td style="white-space:nowrap;">${r.decided_at ? `${fmtDateDay(String(r.decided_at).slice(0, 10))} ${String(r.decided_at).slice(11, 16)}` : "—"}</td>
        <td>${esc(r.reason || "—")}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BALANCE RELEASE APPROVAL LOG</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Approvals:</b> ${arr.length} &nbsp;&nbsp; <span style="color:#475569;">Slow-POD-vendor balance payments released with admin approval.</span></div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Vendor / Owner</th><th>Branch</th><th>Approved By</th><th>Approved On</th><th>Mode</th></tr></thead>
      <tbody>${body || `<tr><td colspan="7" class="muted">No balance-release approvals for this month.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Admin</div>
    </div>
  `;
  open("NTC Balance Release Log", inner);
}

export function printVendorAckScorecard(rows) {
  const body = (rows || [])
    .map(
      (v, i) => `<tr style="${v.avg != null && v.avg > 15 ? "background:#fef2f2;" : ""}">
        <td style="font-weight:800;">${i + 1}${i === 0 && v.avg != null ? " 🏆" : ""}</td>
        <td style="font-weight:700;">${esc(v.name)}</td>
        <td style="text-align:right;">${v.total}</td>
        <td style="text-align:right;color:#047857;">${v.back}</td>
        <td style="text-align:right;font-weight:800;${v.avg != null && v.avg > 15 ? "color:#b91c1c;" : ""}">${v.avg != null ? `${v.avg} days` : "—"}</td>
        <td style="text-align:right;font-weight:800;${v.out ? "color:#b45309;" : ""}">${v.out || "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR POD RETURN SCORECARD</div>
    <div style="margin:0 0 12px;font-size:11px;">Ranked fastest first by average days from loading to signed POD return. Vendors averaging over 15 days are highlighted.</div>
    <table class="tight">
      <thead><tr><th>Rank</th><th>Vendor / Broker / Owner</th><th>Challans</th><th>POD Back</th><th>Avg Days to Return</th><th>Still Out</th></tr></thead>
      <tbody>${body || `<tr><td colspan="6" class="muted">No vendor data.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Vendor POD Scorecard", inner);
}

export function printPayoutTrend(data, opts = {}) {
  const rows = data || [];
  const max = Math.max(...rows.map((r) => Number(r.total) || 0), 1);
  const cur = Number(rows[rows.length - 1]?.total) || 0;
  const prev = Number(rows[rows.length - 2]?.total) || 0;
  const change = prev ? Math.round(((cur - prev) / prev) * 100) : null;
  const yearTotal = rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const bars = rows
    .map((r) => {
      const h = Math.round(((Number(r.total) || 0) / max) * 220);
      return `<div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;">
        <div style="font-size:9px;font-weight:700;white-space:nowrap;">${Number(r.total) ? inr(r.total) : ""}</div>
        <div style="width:70%;height:${Math.max(h, 2)}px;background:${Number(r.total) ? "#2563eb" : "#e2e8f0"};border-radius:3px 3px 0 0;"></div>
        <div style="font-size:9.5px;color:#475569;white-space:nowrap;">${esc(r.label)}</div>
      </div>`;
    })
    .join("");
  const tableBody = rows
    .map((r, i) => {
      const p = i > 0 ? Number(rows[i - 1].total) || 0 : 0;
      const c = Number(r.total) || 0;
      const pct = p ? Math.round(((c - p) / p) * 100) : null;
      return `<tr><td>${esc(r.label)}</td><td class="right">${inr(c)}</td><td class="right">${pct === null ? "—" : `${pct > 0 ? "▲ +" : pct < 0 ? "▼ " : ""}${pct}%`}</td></tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR PAYOUT TREND — LAST 12 MONTHS</div>
    <div style="margin:0 0 10px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">
      ${esc(opts.vendorLabel || "ALL VENDORS")} &nbsp;•&nbsp; THIS MONTH ${inr(cur)}${change !== null ? ` &nbsp;•&nbsp; ${change > 0 ? "▲ +" : "▼ "}${Math.abs(change)}% VS LAST MONTH` : ""} &nbsp;•&nbsp; 12-MONTH TOTAL ${inr(yearTotal)}
    </div>
    <div style="display:flex;align-items:flex-end;gap:4px;height:280px;padding:10px 6px 0;border:1px solid #e2e8f0;border-radius:6px;">${bars}</div>
    <table class="tight" style="margin-top:14px;">
      <thead><tr><th>Month</th><th class="right">Total Paid</th><th class="right">Change vs Prev.</th></tr></thead>
      <tbody>${tableBody}</tbody>
    </table>
  `;
  open("NTC Vendor Payout Trend", inner);
}

export function printVendorPayoutMonthly(rows, monthLabel) {
  const arr = [...(rows || [])].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const total = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const group = (key) => {
    const m = {};
    arr.forEach((r) => { const k = r[key] || "—"; m[k] = (m[k] || 0) + (Number(r.amount) || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const chips = (label, entries) => `<div style="margin:2px 0;font-size:10.5px;"><b>${label}:</b> ${entries.map(([k, v]) => `${esc(k)} — ${inr(v)}`).join(" &nbsp;•&nbsp; ")}</div>`;
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
        <td style="font-weight:600;">${esc(r.vendor_name || "—")}</td>
        <td>${esc(r.branch || "—")}</td>
        <td>${esc(r.payment_mode || "—")}</td>
        <td>${esc(r.ref_no || "—")}</td>
        <td>${esc(r.narration || "—")}</td>
        <td class="right" style="white-space:nowrap;">${inr(r.amount)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY VENDOR PAYOUT REPORT</div>
    <div style="margin:0 0 8px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${esc(monthLabel)} &nbsp;•&nbsp; ${arr.length} PAYMENT(S) &nbsp;•&nbsp; TOTAL PAID ${inr(total)}</div>
    ${arr.length ? chips("By Mode", group("payment_mode")) : ""}
    ${arr.length ? chips("By Branch", group("branch")) : ""}
    <table class="tight" style="margin-top:8px;">
      <thead><tr><th>Sl</th><th>Date</th><th>Vendor / Broker</th><th>Branch</th><th>Mode</th><th>Ref No.</th><th>Narration</th><th class="right">Amount</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No payments in this month.</td></tr>`}
        ${body ? `<tr><td colspan="7" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(total)}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Monthly Vendor Payout", inner);
}

export function printCompanyVehicles(rows) {
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.branch || "—")}</td>
        <td style="font-weight:600;">${esc(r.registration_no || "—")}</td>
        <td>${esc(r.vehicle_no || "—")}</td>
        <td>${esc(r.type || "—")}</td>
        <td>${esc(r.model || "—")}</td>
        <td>${esc(r.model_type || "—")}</td>
        <td class="right">${r.capacity_tons || "—"}</td>
        <td>${esc(r.status || "—")}</td>
        <td style="white-space:nowrap;">${r.fc_expiry ? fmtDateDay(r.fc_expiry) : "—"}</td>
        <td style="white-space:nowrap;">${r.insurance_expiry ? fmtDateDay(r.insurance_expiry) : "—"}</td>
        <td style="white-space:nowrap;">${r.puc_expiry ? fmtDateDay(r.puc_expiry) : "—"}</td>
        <td style="white-space:nowrap;">${r.permit_expiry ? fmtDateDay(r.permit_expiry) : "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">COMPANY VEHICLES</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>As on:</b> ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;&nbsp; <b>Vehicles:</b> ${(rows || []).length}</div>
    <table class="tight" style="font-size:10px;">
      <thead><tr><th>Sl No.</th><th>Branch</th><th>Registration No.</th><th>Fleet Code</th><th>Type</th><th>Model</th><th>Model Type</th><th class="right">Capacity (t)</th><th>Status</th><th>FC Expiry</th><th>Insurance Expiry</th><th>PUC Expiry</th><th>Permit Expiry</th></tr></thead>
      <tbody>${body || `<tr><td colspan="13" class="muted">No vehicles.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Company Vehicles", inner);
}

export function printVehicleDocRegister(rows) {
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.vehicle_no || "—")}</td>
        <td style="font-weight:600;">${esc(r.registration_no || "—")}</td>
        <td>${esc(r.branch || "—")}</td>
        <td>${esc(r.type || "—")}</td>
        <td style="white-space:nowrap;">${r.fc_expiry ? fmtDateDay(r.fc_expiry) : "—"}</td>
        <td style="white-space:nowrap;">${r.insurance_expiry ? fmtDateDay(r.insurance_expiry) : "—"}</td>
        <td style="white-space:nowrap;">${r.permit_expiry ? fmtDateDay(r.permit_expiry) : "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLE DOCUMENTS REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>As on:</b> ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;&nbsp; <b>Vehicles:</b> ${(rows || []).length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Vehicle No.</th><th>Registration</th><th>Branch</th><th>Type</th><th>FC Expiry</th><th>Insurance Expiry</th><th>Permit Expiry</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No vehicles.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Vehicle Documents Register", inner);
}

export function printGstr1Register(rows, { monthLabel = "", hsn = [] } = {}) {
  const tot = { taxable: 0, tax: 0, total: 0 };
  const body = (rows || [])
    .map((r, i) => {
      tot.taxable += r._taxable;
      tot.tax += Number(r.tax_amount) || 0;
      tot.total += Number(r.total) || 0;
      return `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${esc(r._gstin || "—")}</td>
        <td style="font-weight:600;white-space:nowrap;">${esc(r._bill_no || "—")}</td>
        <td style="white-space:nowrap;">${r.issue_date ? fmtDateDay(r.issue_date) : "—"}</td>
        <td>${esc(r.customer_name || "—")}</td>
        <td>${esc(r.from_place || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td style="text-align:right;">${inr(r._taxable)}</td>
        <td style="text-align:center;">${r.rcm ? "RCM" : `${r.tax_percent || 0}%`}</td>
        <td style="text-align:right;">${inr(r.tax_amount || 0)}</td>
        <td style="text-align:right;font-weight:600;">${inr(r.total || 0)}</td>
        <td style="text-align:center;">${r.gstr1_ack_path ? `<span style="color:#059669;font-weight:700;">&#10003; Yes</span>` : "—"}</td>
      </tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">GSTR-1 — B2B OUTWARD SUPPLIES</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${esc(monthLabel || "—")} &nbsp;&nbsp; <b>Bills:</b> ${(rows || []).length} &nbsp;&nbsp; <b>Filed copy uploaded:</b> ${(rows || []).filter((r) => r.gstr1_ack_path).length} of ${(rows || []).length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Recipient GSTIN</th><th>Bill No.</th><th>Bill Date</th><th>Customer</th><th>From</th><th>Destination</th><th>Taxable Value</th><th>GST %</th><th>Tax Amount</th><th>Invoice Value</th><th>Filed Copy</th></tr></thead>
      <tbody>${body || `<tr><td colspan="12" class="muted">No bills in this period.</td></tr>`}
      <tr style="font-weight:700;background:#f1f5f9;"><td colspan="7" style="text-align:right;">TOTAL</td><td style="text-align:right;">${inr(tot.taxable)}</td><td></td><td style="text-align:right;">${inr(tot.tax)}</td><td style="text-align:right;">${inr(tot.total)}</td><td></td></tr>
      </tbody>
    </table>
    ${
      hsn.length
        ? `<div style="margin:16px 0 6px;font-size:12px;font-weight:700;letter-spacing:1px;">HSN / SAC SUMMARY</div>
    <table class="tight">
      <thead><tr><th>HSN/SAC</th><th>Description</th><th>UQC</th><th>No. of Bills</th><th>GST Rate</th><th>Taxable Value</th><th>Tax Amount</th><th>Total Value</th></tr></thead>
      <tbody>${hsn
        .map(
          (h) => `<tr><td style="font-weight:600;">${esc(h.sac)}</td><td>${esc(h.desc)}</td><td style="text-align:center;">${esc(h.uqc)}</td><td style="text-align:center;">${h.qty}</td><td style="text-align:center;">${esc(h.rate)}</td><td style="text-align:right;">${inr(h.taxable)}</td><td style="text-align:right;">${inr(h.tax)}</td><td style="text-align:right;">${inr(h.taxable + h.tax)}</td></tr>`
        )
        .join("")}
      <tr style="font-weight:700;background:#f1f5f9;"><td colspan="3" style="text-align:right;">TOTAL</td><td style="text-align:center;">${hsn.reduce((a, h) => a + h.qty, 0)}</td><td></td><td style="text-align:right;">${inr(hsn.reduce((a, h) => a + h.taxable, 0))}</td><td style="text-align:right;">${inr(hsn.reduce((a, h) => a + h.tax, 0))}</td><td style="text-align:right;">${inr(hsn.reduce((a, h) => a + h.taxable + h.tax, 0))}</td></tr>
      </tbody>
    </table>`
        : ""
    }
  `;
  open("NTC GSTR-1 Register", inner);
}

export function printGstr3bSummary({ monthLabel = "", fwd, rcm, rcmRows = [] }) {
  const inner = `
    <div class="title" style="letter-spacing:3px;">GSTR-3B — MONTHLY TAX SUMMARY</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${esc(monthLabel || "—")}</div>
    <table class="tight">
      <thead><tr><th style="text-align:left;">Nature of Supply</th><th>No. of Bills</th><th>Taxable Value</th><th>Output Tax (GST)</th><th>Total Value</th></tr></thead>
      <tbody>
        <tr><td style="text-align:left;">3.1(a) Outward taxable supplies — forward charge</td><td style="text-align:center;">${fwd.count}</td><td style="text-align:right;">${inr(fwd.taxable)}</td><td style="text-align:right;">${inr(fwd.tax)}</td><td style="text-align:right;">${inr(fwd.taxable + fwd.tax)}</td></tr>
        <tr><td style="text-align:left;">Outward supplies under RCM — tax payable by recipient</td><td style="text-align:center;">${rcm.count}</td><td style="text-align:right;">${inr(rcm.taxable)}</td><td style="text-align:right;">—</td><td style="text-align:right;">${inr(rcm.taxable)}</td></tr>
        <tr style="font-weight:700;background:#f1f5f9;"><td style="text-align:left;">TOTAL</td><td style="text-align:center;">${fwd.count + rcm.count}</td><td style="text-align:right;">${inr(fwd.taxable + rcm.taxable)}</td><td style="text-align:right;">${inr(fwd.tax)}</td><td style="text-align:right;">${inr(fwd.taxable + fwd.tax + rcm.taxable)}</td></tr>
      </tbody>
    </table>
    ${
      rcmRows.length
        ? `<div style="margin:16px 0 6px;font-size:12px;font-weight:700;letter-spacing:1px;">RCM BILLS (TAX PAYABLE BY RECIPIENT)</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Bill No.</th><th>Bill Date</th><th>Customer</th><th>Recipient GSTIN</th><th>Taxable Value</th></tr></thead>
      <tbody>${rcmRows
        .map(
          (r, i) => `<tr><td>${i + 1}</td><td style="font-weight:600;">${esc(r._bill_no)}</td><td style="white-space:nowrap;">${r.issue_date ? fmtDateDay(r.issue_date) : "—"}</td><td>${esc(r.customer_name || "—")}</td><td style="white-space:nowrap;">${esc(r._gstin || "—")}</td><td style="text-align:right;">${inr(r._taxable)}</td></tr>`
        )
        .join("")}</tbody>
    </table>`
        : ""
    }
    <div style="margin-top:10px;font-size:10px;color:#64748b;">Note: Internal working summary for GSTR-3B preparation — verify figures on the GST portal before filing.</div>
  `;
  open("NTC GSTR-3B Summary", inner);
}

export function printGstr1FyRegister(months, { fyLabel = "", tot }) {
  const body = months
    .map(
      (m) => `<tr${m.count ? "" : ' style="color:#94a3b8;"'}>
        <td style="text-align:left;font-weight:600;white-space:nowrap;">${esc(m.label)}</td>
        <td style="text-align:center;">${m.count || "—"}</td>
        <td style="text-align:right;">${m.count ? inr(m.taxable) : "—"}</td>
        <td style="text-align:right;">${m.count ? inr(m.tax) : "—"}</td>
        <td style="text-align:right;">${m.count ? inr(m.total) : "—"}</td>
        <td style="text-align:center;">${m.count ? `${m.filed} / ${m.count}${m.filed === m.count ? ' <span style="color:#059669;font-weight:700;">&#10003;</span>' : ""}` : "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">GSTR-1 — YEARLY REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${esc(fyLabel)} (April – March) &nbsp;&nbsp; <b>Total Bills:</b> ${tot.count} &nbsp;&nbsp; <b>Filed copies uploaded:</b> ${tot.filed} of ${tot.count}</div>
    <table class="tight">
      <thead><tr><th style="text-align:left;">Month</th><th>Bills</th><th>Taxable Value</th><th>Tax Amount</th><th>Invoice Value</th><th>Filed Copy</th></tr></thead>
      <tbody>${body}
      <tr style="font-weight:700;background:#f1f5f9;"><td style="text-align:left;">TOTAL — ${esc(fyLabel)}</td><td style="text-align:center;">${tot.count}</td><td style="text-align:right;">${inr(tot.taxable)}</td><td style="text-align:right;">${inr(tot.tax)}</td><td style="text-align:right;">${inr(tot.total)}</td><td style="text-align:center;">${tot.filed} / ${tot.count}</td></tr>
      </tbody>
    </table>
  `;
  open("NTC GSTR-1 Yearly Register", inner);
}

export function printAnnualReport(cols) {
  const metric = (label, fn, fmt = (v) => inr(v)) =>
    `<tr><td style="text-align:left;font-weight:600;">${label}</td>${cols.map((c) => `<td style="text-align:right;">${fmt(fn(c))}</td>`).join("")}</tr>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">GST ANNUAL REPORT</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Financial year wise summary</b> (April – March)</div>
    <table class="tight">
      <thead><tr><th style="text-align:left;">Particulars</th>${cols.map((c) => `<th>${esc(c.label)}</th>`).join("")}</tr></thead>
      <tbody>
        ${metric("No. of Bills", (c) => c.count, (v) => v || "—")}
        ${metric("Taxable Value", (c) => c.taxable)}
        ${metric("Tax Amount (GST)", (c) => c.tax)}
        ${metric("Invoice Value", (c) => c.total)}
        ${metric("GSTR-1 Filed Copies", (c) => c, (c) => (c.count ? `${c.filed} / ${c.count}` : "—"))}
        ${metric("Annual Return Copy", (c) => c, (c) => (c.ack ? `<span style="color:#059669;font-weight:700;">&#10003; Uploaded</span>` : "Pending"))}
      </tbody>
    </table>
  `;
  open("NTC GST Annual Report", inner);
}

const FESTIVAL_CARD_THEMES = {
  Diwali: {
    header: "#4c1d95",
    band: "linear-gradient(90deg,#f59e0b 0%,#ef4444 50%,#8b5cf6 100%)",
    panel: "#faf5ff",
    accent: "#6d28d9",
    deep: "#4c1d95",
    dash: "#c4b5fd",
    emoji: "&#129684;",
    title: "HAPPY DIWALI",
    img: "/diwali-diyas.jpg",
    msg: "May the festival of lights brighten your path with prosperity,<br/>success and happiness for you, your family and your business! &#10024;",
  },
  Pongal: {
    header: "#713f12",
    band: "linear-gradient(90deg,#eab308 0%,#16a34a 50%,#ea580c 100%)",
    panel: "#fefce8",
    accent: "#a16207",
    deep: "#713f12",
    dash: "#fde047",
    emoji: "&#127806;",
    title: "HAPPY PONGAL",
    img: "/pongal-pot.jpg",
    msg: "May this harvest festival fill your home and business with abundance,<br/>sweetness and prosperity — like a pot overflowing with joy! &#127881;",
  },
  Holi: {
    header: "#86198f",
    band: "linear-gradient(90deg,#ec4899 0%,#eab308 25%,#22c55e 50%,#3b82f6 75%,#8b5cf6 100%)",
    panel: "#fdf4ff",
    accent: "#a21caf",
    deep: "#86198f",
    dash: "#f0abfc",
    emoji: "&#127752;",
    title: "HAPPY HOLI",
    img: "/holi-colors.jpg",
    msg: "May your life and business be painted with the brightest colours<br/>of joy, success and togetherness! &#127912;",
  },
  "New Year": {
    header: "#172554",
    band: "linear-gradient(90deg,#f59e0b 0%,#eab308 50%,#38bdf8 100%)",
    panel: "#eff6ff",
    accent: "#1d4ed8",
    deep: "#172554",
    dash: "#93c5fd",
    emoji: "&#127878;",
    title: `HAPPY NEW YEAR ${new Date().getFullYear() + (new Date().getMonth() >= 10 ? 1 : 0)}`,
    img: "/newyear-fireworks.jpg",
    msg: "Thank you for your trust and partnership.<br/>Wishing you a year full of growth, good health and new milestones! &#127881;",
  },
  Christmas: {
    header: "#7f1d1d",
    band: "linear-gradient(90deg,#dc2626 0%,#16a34a 50%,#f59e0b 100%)",
    panel: "#fef2f2",
    accent: "#b91c1c",
    deep: "#7f1d1d",
    dash: "#fca5a5",
    emoji: "&#127876;",
    title: "MERRY CHRISTMAS",
    img: "/christmas-tree.jpg",
    msg: "May this season of joy bring peace, happiness and success<br/>to you, your family and your business! &#127873;",
  },
  Eid: {
    header: "#064e3b",
    band: "linear-gradient(90deg,#10b981 0%,#f59e0b 100%)",
    panel: "#ecfdf5",
    accent: "#047857",
    deep: "#064e3b",
    dash: "#6ee7b7",
    emoji: "&#127769;",
    title: "EID MUBARAK",
    img: "/eid-mosque.jpg",
    msg: "May this blessed occasion bring peace, happiness and prosperity<br/>to you, your family and your business! &#10024;",
  },
  Dussehra: {
    header: "#7c2d12",
    band: "linear-gradient(90deg,#ea580c 0%,#dc2626 50%,#f59e0b 100%)",
    panel: "#fff7ed",
    accent: "#c2410c",
    deep: "#7c2d12",
    dash: "#fdba74",
    emoji: "&#127993;",
    title: "HAPPY DUSSEHRA",
    img: "/dussehra-ram-ravan.jpg",
    msg: "May the victory of good over evil bring new strength, courage<br/>and success to you, your family and your business! &#127881;",
  },
  "Ayudha Puja": {
    header: "#713f12",
    band: "linear-gradient(90deg,#f59e0b 0%,#eab308 50%,#dc2626 100%)",
    panel: "#fffbeb",
    accent: "#a16207",
    deep: "#713f12",
    dash: "#fcd34d",
    emoji: "&#128591;",
    title: "HAPPY AYUDHA PUJA",
    img: "/ayudha-puja.jpg",
    msg: "May your tools, vehicles and machines be blessed this Ayudha Puja —<br/>wishing your business safety, strength and prosperity! &#128588;",
  },
};

const CARD_COLOR_THEMES = {
  "Royal Purple": { header: "#4c1d95", band: "linear-gradient(90deg,#8b5cf6 0%,#d946ef 100%)", panel: "#faf5ff", accent: "#6d28d9", deep: "#4c1d95", dash: "#c4b5fd" },
  "Festive Gold": { header: "#78350f", band: "linear-gradient(90deg,#f59e0b 0%,#fbbf24 100%)", panel: "#fffbeb", accent: "#b45309", deep: "#78350f", dash: "#fcd34d" },
  "Emerald Green": { header: "#064e3b", band: "linear-gradient(90deg,#10b981 0%,#84cc16 100%)", panel: "#ecfdf5", accent: "#047857", deep: "#064e3b", dash: "#6ee7b7" },
  "Ocean Blue": { header: "#172554", band: "linear-gradient(90deg,#3b82f6 0%,#06b6d4 100%)", panel: "#eff6ff", accent: "#1d4ed8", deep: "#172554", dash: "#93c5fd" },
  "Rose Pink": { header: "#831843", band: "linear-gradient(90deg,#ec4899 0%,#f43f5e 100%)", panel: "#fdf2f8", accent: "#be185d", deep: "#831843", dash: "#f9a8d4" },
  "Teal": { header: "#134e4a", band: "linear-gradient(90deg,#14b8a6 0%,#0ea5e9 100%)", panel: "#f0fdfa", accent: "#0f766e", deep: "#134e4a", dash: "#5eead4" },
  "Crimson Red": { header: "#7f1d1d", band: "linear-gradient(90deg,#dc2626 0%,#f97316 100%)", panel: "#fef2f2", accent: "#b91c1c", deep: "#7f1d1d", dash: "#fca5a5" },
  "Saffron Orange": { header: "#7c2d12", band: "linear-gradient(90deg,#f97316 0%,#facc15 100%)", panel: "#fff7ed", accent: "#c2410c", deep: "#7c2d12", dash: "#fdba74" },
  "Royal Maroon": { header: "#500724", band: "linear-gradient(90deg,#9f1239 0%,#be123c 50%,#f59e0b 100%)", panel: "#fff1f2", accent: "#9f1239", deep: "#500724", dash: "#fda4af" },
  "Peacock": { header: "#0c4a6e", band: "linear-gradient(90deg,#0891b2 0%,#22c55e 50%,#8b5cf6 100%)", panel: "#ecfeff", accent: "#0e7490", deep: "#0c4a6e", dash: "#67e8f9" },
  "Lavender": { header: "#581c87", band: "linear-gradient(90deg,#a78bfa 0%,#f0abfc 100%)", panel: "#faf5ff", accent: "#7e22ce", deep: "#581c87", dash: "#d8b4fe" },
  "Slate Grey": { header: "#1e293b", band: "linear-gradient(90deg,#64748b 0%,#94a3b8 100%)", panel: "#f8fafc", accent: "#334155", deep: "#1e293b", dash: "#cbd5e1" },
};

export function printFestivalGreetingCard(festival, customerName = "", opts = {}) {
  const base = FESTIVAL_CARD_THEMES[festival];
  const colors = CARD_COLOR_THEMES[opts.color] || null;
  let t = base
    ? { ...base }
    : {
        ...(colors || CARD_COLOR_THEMES["Festive Gold"]),
        emoji: "&#127881;",
        title: `HAPPY ${String(festival || "").toUpperCase()}`,
        msg: opts.message
          ? esc(String(opts.message)).replace(/\n/g, "<br/>")
          : "Wishing you and your family joy, success and prosperity<br/>on this special occasion! &#10024;",
      };
  if (colors) t = { ...t, ...colors };
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const inner = `
    <div style="max-width:640px;margin:24px auto;border:2px solid ${t.deep};border-radius:16px;overflow:hidden;">
      <div style="background:${t.header};color:#ffffff;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;">
        <div style="display:flex;align-items:center;gap:12px;">
          <img src="${logo}" alt="logo" style="width:42px;height:42px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid rgba(255,255,255,0.4);" />
          <div>
            <div style="font-size:18px;font-weight:800;letter-spacing:0.5px;">${esc(COMPANY.name)}</div>
            <div style="font-size:10px;font-weight:700;letter-spacing:5px;opacity:0.9;margin-top:2px;">SURFACE &nbsp;&bull;&nbsp; AIR</div>
          </div>
        </div>
        <div style="font-size:22px;">${t.emoji}</div>
      </div>
      <div style="height:8px;background:${t.band};"></div>
      <div style="padding:36px 24px 28px;text-align:center;background:${t.panel};">
        ${t.img ? `<img src="${window.location.origin}${t.img}" alt="${esc(String(festival))}" style="display:block;width:100%;max-width:460px;margin:0 auto 16px;border-radius:12px;border:1px solid ${t.dash};" />` : `<div style="font-size:26px;">${t.emoji} ${t.emoji} ${t.emoji}</div>`}
        <div style="font-size:30px;font-weight:800;letter-spacing:3px;color:${t.deep};margin:14px 0 8px;">${t.title}</div>
        ${customerName ? `<div style="font-size:16px;font-weight:700;color:${t.accent};margin:6px 0 2px;">Dear ${esc(customerName)},</div>` : ""}
        <div style="max-width:480px;margin:14px auto 0;font-size:14px;line-height:1.9;color:${t.deep};">${t.msg}</div>
        <div style="margin-top:22px;font-size:13px;color:${t.accent};">With warm wishes,<br/><b>${esc(COMPANY.name)}</b><br/><span style="font-size:11px;line-height:1.6;">${esc(COMPANY.address)}</span></div>
      </div>
      <div style="border-top:1px dashed ${t.dash};padding:12px 24px;display:flex;justify-content:space-between;font-size:11px;color:${t.accent};background:${t.panel};">
        <span>${esc(COMPANY.phone || "")}</span>
        <span style="font-weight:700;">${esc(COMPANY.website || "www.ntclogistic.in")}</span>
        <span>${esc(COMPANY.email || "")}</span>
      </div>
    </div>
  `;
  open(`NTC ${festival} Card`, inner, { hideHeader: true, downloadImage: true });
}

export function printStaffBirthdayCard(s) {
  const inner = `
    <div style="max-width:640px;margin:24px auto;border:2px solid #831843;border-radius:16px;overflow:hidden;">
      <div style="background:#831843;color:#ffffff;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;">
        <div style="display:flex;align-items:center;gap:12px;">
          <img src="${window.location.origin}/ntc-logo.jpg" alt="logo" style="width:42px;height:42px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid rgba(255,255,255,0.4);" />
          <div>
            <div style="font-size:18px;font-weight:800;letter-spacing:0.5px;">${esc(COMPANY.name)}</div>
            <div style="font-size:10px;font-weight:700;letter-spacing:5px;opacity:0.9;margin-top:2px;">SURFACE &nbsp;&bull;&nbsp; AIR</div>
          </div>
        </div>
        <div style="font-size:22px;">&#127874;</div>
      </div>
      <div style="height:8px;background:linear-gradient(90deg,#ec4899 0%,#f59e0b 50%,#8b5cf6 100%);"></div>
      <div style="padding:34px 24px 26px;text-align:center;background:#fdf2f8;">
        <div style="font-size:12px;letter-spacing:5px;color:#9d174d;">HAPPY BIRTHDAY</div>
        <div style="font-size:34px;font-weight:800;color:#831843;margin:12px 0 6px;">${esc(s.name || "")}</div>
        <div style="font-size:13px;color:#9d174d;">${esc(s.designation || "")}${s.designation && (s.branch || s.office) ? " · " : ""}${esc(s.branch || s.office || "")}</div>
        <div style="max-width:440px;margin:20px auto 0;font-size:14px;line-height:1.8;color:#500724;">
          Wishing you great health, happiness and success in the year ahead.<br/>
          May this special day bring you joy and wonderful memories! &#127881;
        </div>
        <div style="margin-top:22px;font-size:13px;color:#9d174d;">Warm wishes from all of us,<br/><b>${esc(COMPANY.name)}</b><br/><span style="font-size:11px;line-height:1.6;">${esc(COMPANY.address)}</span></div>
      </div>
      <div style="border-top:1px dashed #f9a8d4;padding:12px 24px;display:flex;justify-content:space-between;font-size:11px;color:#9d174d;background:#fdf2f8;">
        <span>${esc(COMPANY.phone || "")}</span>
        <span style="font-weight:700;">${esc(COMPANY.website || "www.ntclogistic.in")}</span>
        <span>${esc(COMPANY.email || "")}</span>
      </div>
    </div>
  `;
  open("NTC Birthday Card", inner, { hideHeader: true, downloadImage: true });
}

export function printStaffAnniversaryCard(s) {
  const yrs = `${s.years || 1} Year${(s.years || 1) > 1 ? "s" : ""}`;
  const inner = `
    <div style="max-width:640px;margin:24px auto;border:2px solid #134e4a;border-radius:16px;overflow:hidden;">
      <div style="background:#134e4a;color:#ffffff;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;">
        <div style="display:flex;align-items:center;gap:12px;">
          <img src="${window.location.origin}/ntc-logo.jpg" alt="logo" style="width:42px;height:42px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid rgba(255,255,255,0.4);" />
          <div>
            <div style="font-size:18px;font-weight:800;letter-spacing:0.5px;">${esc(COMPANY.name)}</div>
            <div style="font-size:10px;font-weight:700;letter-spacing:5px;opacity:0.9;margin-top:2px;">SURFACE &nbsp;&bull;&nbsp; AIR</div>
          </div>
        </div>
        <div style="font-size:22px;">&#127942;</div>
      </div>
      <div style="height:8px;background:linear-gradient(90deg,#0d9488 0%,#f59e0b 50%,#0ea5e9 100%);"></div>
      <div style="padding:34px 24px 26px;text-align:center;background:#f0fdfa;">
        <div style="font-size:12px;letter-spacing:5px;color:#0f766e;">HAPPY WORK ANNIVERSARY</div>
        <div style="font-size:34px;font-weight:800;color:#134e4a;margin:12px 0 6px;">${esc(s.name || "")}</div>
        <div style="font-size:13px;color:#0f766e;">${esc(s.designation || "")}${s.designation && (s.branch || s.office) ? " · " : ""}${esc(s.branch || s.office || "")}</div>
        <div style="display:inline-block;margin-top:16px;padding:10px 22px;border:2px solid #0d9488;border-radius:999px;background:#ffffff;font-size:18px;font-weight:800;color:#0d9488;">&#127881; ${esc(yrs)} of Excellence</div>
        <div style="max-width:460px;margin:20px auto 0;font-size:14px;line-height:1.8;color:#042f2e;">
          Congratulations on completing ${esc(yrs.toLowerCase())} with us!<br/>
          Thank you for your dedication and hard work — here's to many more milestones together.
        </div>
        <div style="margin-top:22px;font-size:13px;color:#0f766e;">With appreciation from all of us,<br/><b>${esc(COMPANY.name)}</b><br/><span style="font-size:11px;line-height:1.6;">${esc(COMPANY.address)}</span></div>
      </div>
      <div style="border-top:1px dashed #5eead4;padding:12px 24px;display:flex;justify-content:space-between;font-size:11px;color:#0f766e;background:#f0fdfa;">
        <span>${esc(COMPANY.phone || "")}</span>
        <span style="font-weight:700;">${esc(COMPANY.website || "www.ntclogistic.in")}</span>
        <span>${esc(COMPANY.email || "")}</span>
      </div>
    </div>
  `;
  open("NTC Anniversary Card", inner, { hideHeader: true, downloadImage: true });
}

export function printStaffWelcomeCard(u, origin = "") {
  const chip = (label, value) =>
    `<div style="display:inline-block;margin:0 6px;padding:8px 16px;border:1.5px solid #e2e8f0;border-radius:10px;background:#f8fafc;text-align:center;">
       <div style="font-size:9px;letter-spacing:2px;color:#64748b;">${label}</div>
       <div style="font-size:14px;font-weight:700;color:#0f172a;margin-top:2px;">${esc(value || "—")}</div>
     </div>`;
  const inner = `
    <div style="max-width:640px;margin:24px auto;border:2px solid #0f172a;border-radius:16px;overflow:hidden;">
      <div style="background:#0f172a;color:#ffffff;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;">
        <div style="display:flex;align-items:center;gap:12px;">
          <img src="${window.location.origin}/ntc-logo.jpg" alt="logo" style="width:42px;height:42px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid rgba(255,255,255,0.4);" />
          <div>
            <div style="font-size:18px;font-weight:800;letter-spacing:0.5px;">${esc(COMPANY.name)}</div>
            <div style="font-size:10px;font-weight:700;letter-spacing:5px;opacity:0.9;margin-top:2px;">SURFACE &nbsp;&bull;&nbsp; AIR</div>
          </div>
        </div>
        <div style="font-size:10px;letter-spacing:2px;opacity:0.85;">LOGISTICS ACCOUNTING SUITE</div>
      </div>
      <div style="height:8px;background:linear-gradient(90deg,#2563eb 0%,#0ea5e9 50%,#10b981 100%);"></div>
      <div style="padding:32px 24px 24px;text-align:center;">
        <div style="font-size:12px;letter-spacing:5px;color:#64748b;">WELCOME TO THE TEAM</div>
        <div style="font-size:32px;font-weight:800;color:#0f172a;margin:10px 0 18px;">${esc(u.name || "")}</div>
        <div>${chip("USER ID", u.user_id || u.email)}${chip("BRANCH", u.office)}${chip("ROLE", (u.role || "staff").toUpperCase())}</div>
        <div style="margin-top:22px;font-size:13px;color:#334155;">Your login is approved — sign in at <b>${esc(origin)}</b></div>
        <div style="margin-top:20px;font-size:13px;color:#475569;">We're glad to have you on board!<br/><b>${esc(COMPANY.name)}</b><br/><span style="font-size:11px;line-height:1.6;">${esc(COMPANY.address)}</span></div>
      </div>
      <div style="border-top:1px dashed #cbd5e1;padding:12px 24px;display:flex;justify-content:space-between;font-size:11px;color:#64748b;">
        <span>${esc(COMPANY.phone || "")}</span>
        <span style="font-weight:700;">${esc(COMPANY.website || "www.ntclogistic.in")}</span>
        <span>${esc(COMPANY.email || "")}</span>
      </div>
    </div>
  `;
  open("NTC Welcome Card", inner, { hideHeader: true, downloadImage: true });
}

export function printVehicleTrackingRegister(rows) {
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.branch || "—")}</td>
        <td style="font-weight:600;">${esc(r.registration_no || "—")}</td>
        <td>${esc(r.vehicle_no || "—")}</td>
        <td>${esc(r.type || "—")}</td>
        <td>${esc(r.model || "—")}</td>
        <td>${esc(r.model_type || "—")}</td>
        <td>${esc(r.fastag_no || "—")}</td>
        <td>${esc(r.gps_provider || "—")}</td>
        <td>${esc(r.gps_id || "—")}</td>
        <td>${esc(r.eway_bill_no || "—")}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLES TRACKING REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Vehicles:</b> ${(rows || []).length}</div>
    <table class="tight">
      <thead><tr><th>Sl No.</th><th>Branch</th><th>Registration No.</th><th>Vehicle No.</th><th>Vehicle Type</th><th>Model</th><th>Model Type</th><th>FASTag No.</th><th>GPS Provider</th><th>GPS ID</th><th>E-Way Bill No.</th></tr></thead>
      <tbody>${body || `<tr><td colspan="11" class="muted">No vehicles.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Vehicles Tracking", inner);
}

export function printDayBook(entries, opts = {}) {
  const arr = entries || [];
  const t = opts.totals || {};
  const rIn = (t.receipt_cash || 0) + (t.receipt_bank || 0);
  const rOut = (t.payment_cash || 0) + (t.payment_bank || 0);
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.office || "—")}</td>
        <td style="font-weight:700;color:${r.entry_type === "Receipt" ? "#059669" : "#b91c1c"};">${esc(r.entry_type)}</td>
        <td>${esc(r.mode || "Cash")}</td>
        <td>${esc(r.category || "—")}</td>
        <td>${esc(r.particulars || "—")}</td>
        <td>${esc(r.ref_no || "—")}</td>
        <td class="right">${r.entry_type === "Receipt" ? inr(r.amount) : "—"}</td>
        <td class="right">${r.entry_type === "Payment" ? inr(r.amount) : "—"}</td>
      </tr>`
    )
    .join("");
  const bal = (o) => `Cash ${inr((o || {}).Cash || 0)} · Bank ${inr((o || {}).Bank || 0)}`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">BRANCH DAY BOOK</div>
    <div style="margin:0 0 10px;padding:7px 12px;border:1px solid #0f172a;border-radius:6px;font-size:12px;display:flex;justify-content:space-between;gap:12px;">
      <div><b>DATE:</b> ${fmtDate(opts.date)} &nbsp;&nbsp; <b>BRANCH:</b> ${esc(opts.office === "All" ? "All Branches" : opts.office || "—")}</div>
      <div><b>Opening:</b> ${bal(opts.opening)}</div>
    </div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Branch</th><th>Type</th><th>Mode</th><th>Category</th><th>Particulars</th><th>Ref No.</th><th class="right">Receipt</th><th class="right">Payment</th></tr></thead>
      <tbody>
        ${body || `<tr><td colspan="9" class="muted">No entries for this day.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" class="right">DAY TOTAL</td><td class="right">${inr(rIn)}</td><td class="right">${inr(rOut)}</td></tr>
      </tbody>
    </table>
    ${(opts.settlements?.rows || []).length ? `
    <div style="font-size:11px;font-weight:800;letter-spacing:1.5px;margin:12px 0 4px;">VEHICLE SETTLEMENTS — CASH PAID TO OWNERS (${opts.settlements.rows.length})</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Vehicle No.</th><th>Vendor / Owner</th><th>Settled By</th><th>Release Note</th><th class="right">Settled Amount</th></tr></thead>
      <tbody>
        ${opts.settlements.rows.map((s, i) => `<tr><td>${i + 1}</td><td style="font-weight:700;">${esc(String(s.challan_no || "").replace(/^VHC[-\s]?/i, ""))}</td><td>${esc(s.vehicle_no || "—")}</td><td>${esc(s.party || "—")}</td><td>${esc(s.by || "—")}</td><td style="font-size:9.5px;">${s.release_reason ? `<b>Admin release${s.release_by ? ` (${esc(s.release_by)})` : ""}:</b> ${esc(s.release_reason)}` : "—"}</td><td class="right" style="font-weight:700;">${inr(s.amount)}</td></tr>`).join("")}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">TOTAL SETTLED CASH</td><td class="right">${inr(opts.settlements.total)}</td></tr>
      </tbody>
    </table>` : ""}
    <div style="margin-top:10px;padding:8px 14px;background:#f1f5f9;border-radius:6px;font-size:12px;font-weight:800;display:flex;justify-content:space-between;">
      <span>CLOSING BALANCE</span><span>${bal(opts.closing)}</span>
    </div>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Cashier</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC Branch Day Book", inner);
}

export function printDayBookSummary(data) {
  const rows = (data && data.rows) || [];
  const t = (data && data.totals) || {};
  const label = data && data.month ? new Date(data.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "—";
  const body = rows
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td><td style="font-weight:700;">${esc(r.office)}</td>
        <td class="right">${inr(r.opening)}</td><td class="right" style="color:#059669;">${inr(r.receipts)}</td>
        <td class="right" style="color:#b91c1c;">${inr(r.payments)}</td><td class="right" style="font-weight:800;">${inr(r.closing)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DAY BOOK — MONTHLY SUMMARY</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(label)} &nbsp;&nbsp; <b>Branches:</b> ${rows.length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Branch</th><th class="right">Opening</th><th class="right">Receipts</th><th class="right">Payments</th><th class="right">Closing Balance</th></tr></thead>
      <tbody>
        ${body || `<tr><td colspan="6" class="muted">No entries for this month.</td></tr>`}
        ${rows.length ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL</td><td class="right">${inr(t.opening)}</td><td class="right">${inr(t.receipts)}</td><td class="right">${inr(t.payments)}</td><td class="right">${inr(t.closing)}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Day Book Monthly Summary", inner);
}

export function printBankDelayReport(stats, period) {
  const arr = stats || [];
  const body = arr
    .map(
      (g, i) => `<tr${i === 0 && g.avg != null ? ' style="background:#ecfdf5;"' : ""}>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(g.bank)}${i === 0 && g.avg != null ? ' <span style="color:#059669;font-size:9px;font-weight:800;">★ FASTEST</span>' : ""}</td>
        <td class="right">${g.total}</td>
        <td class="right">${g.cleared}</td>
        <td class="right" style="font-weight:800;">${g.avg != null ? g.avg.toFixed(1) + " days" : "—"}</td>
        <td class="right">${g.min != null ? g.min + "d" : "—"}</td>
        <td class="right">${g.max != null ? g.max + "d" : "—"}</td>
        <td class="right">${g.pending || "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BANK DELAY REPORT — CHEQUE CLEARING DAYS</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${esc(period || "All dates")} &nbsp;&nbsp; <b>Banks:</b> ${arr.length} &nbsp;&nbsp; (Deposit Date → Cleared Date, received cheques)</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Bank</th><th class="right">Cheques Deposited</th><th class="right">Cleared</th><th class="right">Avg. Clearing Days</th><th class="right">Fastest</th><th class="right">Slowest</th><th class="right">Still Pending</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No cheques with deposit dates found.</td></tr>`}</tbody>
    </table>
    <div style="margin-top:10px;font-size:10px;color:#475569;">Banks are ranked fastest first. Averages use only cheques marked Cleared with both Deposit and Cleared dates.</div>
  `;
  open("NTC Bank Delay Report", inner);
}

export function printNeftOnlyList(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(r.customer)}</td>
        <td>${esc(r.phone || "—")}</td>
        <td class="right" style="color:#b91c1c;font-weight:800;">${r.bounces}</td>
        <td class="right">${inr(r.amount)}</td>
        <td>${esc(r.banks || "—")}</td>
        <td style="white-space:nowrap;">${r.last ? fmtDateDay(r.last) : "—"}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">NEFT-ONLY CUSTOMER LIST</div>
    <div style="margin:0 0 10px;padding:7px 12px;border:2px solid #b91c1c;border-radius:6px;font-size:11px;font-weight:700;color:#b91c1c;">
      ⚠ DO NOT ACCEPT CHEQUES from the customers below (2 or more bounced cheques on record). Insist on NEFT / RTGS / UPI payment only.
    </div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Customer</th><th>Phone</th><th class="right">Bounced Cheques</th><th class="right">Bounced Amount</th><th>Bank(s) Used</th><th>Last Bounce</th></tr></thead>
      <tbody>${body || `<tr><td colspan="7" class="muted">No repeat bouncers — all customers clear. 🎉</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Accounts In-Charge</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open("NTC NEFT-Only Customer List", inner);
}

export function printBranchExpenseCompare(data) {
  const { month, branches = [], rows = [], totals = {} } = data || {};
  const label = month ? new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "—";
  const body = rows
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td><td style="font-weight:700;">${esc(r.category)}</td>
        ${branches.map((b) => `<td class="right" style="${r.maxBranch === b && r.per[b] ? "background:#fee2e2;font-weight:800;" : ""}">${r.per[b] ? inr(r.per[b]) : "—"}</td>`).join("")}
        <td class="right" style="font-weight:800;">${inr(r.total)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BRANCH EXPENSE COMPARISON</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Month:</b> ${esc(label)} &nbsp;&nbsp; <b>Payments (Day Book) by category</b> — highest-spending branch per category highlighted</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Category</th>${branches.map((b) => `<th class="right">${esc(b)}</th>`).join("")}<th class="right">Total</th></tr></thead>
      <tbody>
        ${body || `<tr><td colspan="${branches.length + 3}" class="muted">No expense entries for this month.</td></tr>`}
        ${rows.length ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL</td>${branches.map((b) => `<td class="right">${inr(totals[b] || 0)}</td>`).join("")}<td class="right">${inr(Object.values(totals).reduce((a, v) => a + v, 0))}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Branch Expense Comparison", inner);
}

export function printPaymentScores(rows) {
  const arr = rows || [];
  const CLR = { A: "#059669", B: "#b45309", C: "#b91c1c" };
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:700;">${esc(r.customer)}</td>
        <td class="right">${r.receipts}</td>
        <td class="right" style="${r.bounces ? "color:#b91c1c;font-weight:800;" : ""}">${r.bounces || "—"}</td>
        <td class="right">${r.avgDelay != null ? r.avgDelay + " days" : "—"}</td>
        <td style="text-align:center;"><span style="display:inline-block;min-width:26px;padding:2px 8px;border-radius:99px;background:${CLR[r.score]};color:#fff;font-weight:800;">${r.score}</span></td>
        <td style="font-size:10px;">${esc(r.reason)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CUSTOMER PAYMENT SCORE</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>A</b> = pays clean &amp; on time &nbsp;·&nbsp; <b>B</b> = 1 bounce or 31–60 day avg delay &nbsp;·&nbsp; <b>C</b> = 2+ bounces or &gt;60 day avg delay (delay = Bill Date → MR Date)</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Customer</th><th class="right">Receipts</th><th class="right">Bounces</th><th class="right">Avg. Payment Delay</th><th style="text-align:center;">Score</th><th>Why</th></tr></thead>
      <tbody>${body || `<tr><td colspan="7" class="muted">No receipts to score yet.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Customer Payment Score", inner);
}

export function printBillEnquiry(rows, fromDate, toDate, showTds) {
  const arr = rows || [];
  const bno = (r) => String(r.invoice_no || "").replace(/^INV[-\s]?/i, "");
  const age = (r) => (r.status === "Paid" || !r.issue_date ? null : Math.max(0, Math.floor((Date.now() - new Date(r.issue_date).getTime()) / 86400000)));
  const ageCell = (r) => {
    const d = age(r);
    if (d == null) return "—";
    const c = d <= 30 ? "#059669" : d <= 60 ? "#b45309" : d <= 90 ? "#c2410c" : "#b91c1c";
    return `<span style="color:${c};font-weight:800;">${d}d</span>`;
  };
  const mrN = (r) => (r.receipts || []).map((m) => m.mr_no).filter(Boolean).join(", ");
  const mrD = (r) => (r.receipts || []).map((m) => (m.date ? fmtDate(m.date) : "")).filter(Boolean).join(", ");
  const mrA = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const mrT = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.tds) || 0), 0);
  const mrX = (r) => (r.receipts || []).reduce((s, m) => s + (Number(m.deduction) || 0), 0);
  const subD = (r) => (r.submission_no ? `${r.submission_date ? fmtDate(r.submission_date) : r.submission_no}${r.submission_status ? ` (${r.submission_status})` : ""}` : "—");
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.office || "—")}</td>
      <td>${esc(r.customer_name || "—")}</td>
      <td>${esc(r.customer_code || "—")}</td>
      <td>${esc(r.lr_no || "—")}</td>
      <td style="white-space:nowrap;">${r.lr_date ? fmtDateDay(r.lr_date) : "—"}</td>
      <td style="font-weight:700;">${esc(bno(r))}</td>
      <td style="white-space:nowrap;">${r.issue_date ? fmtDateDay(r.issue_date) : "—"}</td>
      <td class="right">${inr(r.total)}</td>
      <td class="right">${mrA(r) ? inr(mrA(r)) : "—"}</td>
      ${showTds ? `<td class="right" style="color:#b45309;">${mrT(r) ? inr(mrT(r)) : "—"}</td><td class="right" style="color:#b45309;">${mrX(r) ? inr(mrX(r)) : "—"}</td>` : ""}
      <td>${esc(mrN(r) || "—")}</td>
      <td style="white-space:nowrap;">${esc(mrD(r) || "—")}</td>
      <td class="right" style="color:#059669;">${mrA(r) ? inr(mrA(r)) : "—"}</td>
      <td class="right" style="font-weight:700;${r.balance > 0.01 ? "color:#b91c1c;" : ""}">${inr(r.balance)}</td>
      <td>${esc(r.status || "")}</td>
      <td class="right">${ageCell(r)}</td>
      <td>${esc(subD(r))}</td>
    </tr>`
    )
    .join("");
  const totals = arr.reduce((a, r) => ({ t: a.t + (r.total || 0), tds: a.tds + mrT(r), ded: a.ded + mrX(r), rc: a.rc + mrA(r), p: a.p + mrA(r), b: a.b + (r.balance || 0) }), { t: 0, tds: 0, ded: 0, rc: 0, p: 0, b: 0 });
  const nCols = showTds ? 17 : 15;
  const inner = `
    <div class="title" style="letter-spacing:3px;">BILL ENQUIRY — TRACKING REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${fromDate ? fmtDate(fromDate) : "Start"} to ${toDate ? fmtDate(toDate) : "Today"} &nbsp;&nbsp; <b>Bills:</b> ${arr.length} &nbsp;&nbsp; <b>Age:</b> <span style="color:#059669;">0–30d</span> · <span style="color:#b45309;">31–60d</span> · <span style="color:#c2410c;">61–90d</span> · <span style="color:#b91c1c;">90d+</span></div>
    <table class="tight" style="font-size:8.5px;">
      <thead><tr><th>Sl</th><th>Branch</th><th>Customer</th><th>Code</th><th>LR No.</th><th>LR Date</th><th>Bill No.</th><th>Bill Date</th><th class="right">Bill Amount</th><th class="right">Received Amount</th>${showTds ? '<th class="right">TDS</th><th class="right">Deduction</th>' : ""}<th>MR No.</th><th>MR Date</th><th class="right">MR Amount</th><th class="right">Balance Amount</th><th>Status</th><th class="right">Age</th><th>Bill Submission Date</th></tr></thead>
      <tbody>
        ${body || `<tr><td colspan="${nCols + 2}" class="muted">No bills found.</td></tr>`}
        ${body ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="8" class="right">TOTAL</td><td class="right">${inr(totals.t)}</td><td class="right">${inr(totals.rc)}</td>${showTds ? `<td class="right">${inr(totals.tds)}</td><td class="right">${inr(totals.ded)}</td>` : ""}<td colspan="2"></td><td class="right">${inr(totals.p)}</td><td class="right">${inr(totals.b)}</td><td colspan="3"></td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Bill Enquiry", inner);
}

export function printBankStatement(rows) {
  const arr = rows || [];
  const amtCols = [
    ["loading_charge", "Loading"],
    ["unloading_charge", "Unloading"],
    ["vehicle_hire", "Veh. Hire"],
    ["vehicle_advance", "Veh. Adv."],
    ["vehicle_balance", "Veh. Bal."],
    ["salary_expenses", "Salary"],
    ["office_rent", "Office Rent"],
    ["other_expenses", "Other Exp."],
  ];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "—")}</td>
      <td style="white-space:nowrap;">${r.transaction_date ? fmtDateDay(r.transaction_date) : "—"}</td>
      <td style="white-space:nowrap;">${esc(r.cheque_no || "—")}</td>
      <td>${esc(r.mode || "—")}</td>
      <td style="white-space:nowrap;">${esc(r.mr_no || "—")}</td>
      <td style="white-space:nowrap;">${r.mr_date ? fmtDateDay(r.mr_date) : "—"}</td>
      <td class="right" style="white-space:nowrap;">${Number(r.mr_amount) ? inr(r.mr_amount) : "—"}</td>
      <td style="white-space:nowrap;">${r.deposit_date ? fmtDateDay(r.deposit_date) : "—"}</td>
      <td style="white-space:nowrap;">${r.withdrawal_date ? fmtDateDay(r.withdrawal_date) : "—"}</td>
      <td class="right" style="white-space:nowrap;color:#059669;font-weight:600;">${Number(r.credit_amount) ? inr(r.credit_amount) : "—"}</td>
      ${amtCols.map(([k]) => `<td class="right" style="white-space:nowrap;">${Number(r[k]) ? inr(r[k]) : "—"}</td>`).join("")}
      <td class="right" style="white-space:nowrap;">${inr(r.balance)}</td>
      <td>${esc(r.narration || "—")}</td>
      <td>${esc(r.remarks || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BANK STATEMENT</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Entries:</b> ${arr.length}</div>
    <table class="tight" style="font-size:9px;">
      <thead><tr><th>Sl</th><th>Branch</th><th>Trans. Date</th><th>Cheque No.</th><th>DD/RTGS/NEFT</th><th>MR No.</th><th>MR Date</th><th class="right">MR Amount</th><th>Deposit</th><th>Withdrawal</th><th class="right">Received</th>${amtCols.map(([, l]) => `<th class="right">${l}</th>`).join("")}<th class="right">Balance</th><th>Narration</th><th>Remarks</th></tr></thead>
      <tbody>${body || `<tr><td colspan="22" class="muted">No entries.</td></tr>`}
        ${body ? `<tr><td colspan="7" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${inr(arr.reduce((s, r) => s + (Number(r.mr_amount) || 0), 0))}</td><td colspan="2"></td><td class="right" style="font-weight:800;color:#059669;">${inr(arr.reduce((s, r) => s + (Number(r.credit_amount) || 0), 0))}</td>${amtCols.map(([k]) => `<td class="right" style="font-weight:800;">${inr(arr.reduce((s, r) => s + (Number(r[k]) || 0), 0))}</td>`).join("")}<td colspan="3"></td></tr>` : ""}</tbody>
    </table>
  `;
  open("Bank Statement", inner);
}

export function printBankPassbook(branch, from, to, rows) {
  const arr = (rows || [])
    .filter((r) => (branch === "All" || (r.branch || "Chennai") === branch) && r.transaction_date && (!from || r.transaction_date >= from) && (!to || r.transaction_date <= to))
    .sort((a, b) => String(a.transaction_date).localeCompare(String(b.transaction_date)));
  const amt = (v) => (Number(v) ? inr(v) : "—");
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;">${fmtDateDay(r.transaction_date)}</td>
      <td>${esc(r.cheque_no || "—")}</td>
      <td>${esc(r.mode || "—")}</td>
      <td>${esc(r.narration || "—")}</td>
      <td class="right" style="color:#059669;font-weight:600;">${amt(r.credit_amount)}</td>
      <td class="right">${amt(r.loading_charge)}</td>
      <td class="right">${amt(r.unloading_charge)}</td>
      <td class="right">${amt(r.vehicle_hire)}</td>
      <td class="right">${amt(r.salary_expenses)}</td>
      <td class="right">${amt(r.office_rent)}</td>
      <td class="right">${amt(r.other_expenses)}</td>
      <td class="right" style="font-weight:700;white-space:nowrap;">${inr(r.balance)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BANK PASSBOOK — ${esc(branch.toUpperCase())}</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${from ? fmtDate(from) : "Start"} to ${to ? fmtDate(to) : "Today"} &nbsp;&nbsp; <b>Entries:</b> ${arr.length} &nbsp;&nbsp; <b>Closing Balance:</b> ${arr.length ? inr(arr[arr.length - 1].balance) : "—"}</div>
    <table class="tight" style="font-size:9.5px;">
      <thead><tr><th>Sl</th><th>Date</th><th>Cheque</th><th>Mode</th><th>Narration</th><th class="right">Received</th><th class="right">Loading</th><th class="right">Unloading</th><th class="right">Veh. Hire</th><th class="right">Salary</th><th class="right">Rent</th><th class="right">Other</th><th class="right">Balance</th></tr></thead>
      <tbody>${body || `<tr><td colspan="13" class="muted">No entries in this period.</td></tr>`}
        ${body ? `<tr><td colspan="5" class="right" style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;color:#059669;">${inr(arr.reduce((s, r) => s + (Number(r.credit_amount) || 0), 0))}</td>${["loading_charge", "unloading_charge", "vehicle_hire", "salary_expenses", "office_rent", "other_expenses"].map((k) => `<td class="right" style="font-weight:800;">${inr(arr.reduce((s, r) => s + (Number(r[k]) || 0), 0))}</td>`).join("")}<td></td></tr>` : ""}</tbody>
    </table>
  `;
  open("NTC Bank Passbook", inner);
}

export function printBlankLetterhead(accent = "#334155") {
  const inner = `
    <div style="height:9px;margin:2px 0 14px;background:linear-gradient(90deg,${accent} 0%,${accent}B3 45%,${accent}33 100%);border-radius:2px;"></div>
    <div style="flex:1;min-height:880px;"></div>
  `;
  open("NTC Letter Pad", inner, { frame: true, fullPage: true, accent });
}

export function printLetterPad(l, accent = "#334155") {
  const inner = `
    <div style="height:9px;margin:2px 0 14px;background:linear-gradient(90deg,${accent} 0%,${accent}B3 45%,${accent}33 100%);border-radius:2px;"></div>
    <div style="display:flex;justify-content:space-between;margin:8px 0 16px;font-size:12px;">
      <div><b>Ref No.:</b> ${esc(l.ref_no || "")}</div>
      <div><b>Date:</b> ${l.date ? fmtDate(l.date) : ""}</div>
    </div>
    ${l.to ? `<div style="white-space:pre-line;font-size:12px;line-height:1.6;margin-bottom:14px;"><b>To,</b><br/>${esc(l.to)}</div>` : ""}
    ${l.subject ? `<div style="font-size:12.5px;font-weight:700;margin:16px 0;">Subject: <span style="text-decoration:underline;">${esc(l.subject)}</span></div>` : ""}
    <div style="white-space:pre-line;font-size:12px;line-height:1.8;margin:14px 0;">${esc(l.body || "")}</div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-top:30px;">
      <div style="font-size:12px;">Thanking You,</div>
      <div style="text-align:right;font-size:12px;line-height:1.7;padding-right:24px;">
        <b>For ${esc(COMPANY.name)}</b>
        <div style="height:70px;"></div>
        ${l.signatory ? `<b>${esc(l.signatory)}</b><br/>` : ""}${l.designation ? `${esc(l.designation)}<br/>` : ""}Authorized Signatory
      </div>
    </div>
  `;
  open("NTC Letter", inner, { frame: true, fullPage: true, accent });
}

export function printLeaveApplication(l) {
  const dashLine = (label, val) =>
    `<div style="display:flex;align-items:flex-end;gap:6px;margin:7px 0;">
       <span style="white-space:nowrap;font-weight:600;">${esc(label)}</span>
       <span style="flex:1;border-bottom:1px dotted #64748b;padding:0 6px;min-height:18px;">${esc(val || "")}</span>
     </div>`;
  const statusColor = l.status === "Approved" ? "#059669" : l.status === "Rejected" ? "#dc2626" : "#b45309";
  const inner = `
    <div class="title" style="letter-spacing:4px;">LEAVE APPLICATION</div>
    <div style="display:flex;justify-content:space-between;margin:14px 0;">
      <div style="width:48%;">
        ${dashLine("Employee Name:", l.employee_name)}
        ${dashLine("Staff Code:", l.staff_code)}
        ${dashLine("Designation:", l.designation)}
      </div>
      <div style="width:40%;">
        ${dashLine("Branch:", l.branch)}
        ${dashLine("Applied Date:", l.applied_date ? fmtDate(l.applied_date) : "")}
        ${dashLine("Leave Type:", l.leave_type)}
      </div>
    </div>
    <div style="display:flex;gap:24px;margin:10px 0;">
      <div style="flex:1;">${dashLine("Leave From:", l.from_date ? fmtDate(l.from_date) : "")}</div>
      <div style="flex:1;">${dashLine("Leave To:", l.to_date ? fmtDate(l.to_date) : "")}</div>
      <div style="width:140px;">${dashLine("No. of Days:", String(l.days || ""))}</div>
    </div>
    <table>
      <thead><tr><th>Reason for Leave</th></tr></thead>
      <tbody><tr><td style="height:90px;vertical-align:top;">${esc(l.reason || "")}</td></tr></tbody>
    </table>
    <div style="margin:14px 0;font-size:12px;"><b>Status:</b> <span style="color:${statusColor};font-weight:800;letter-spacing:1px;">${esc((l.status || "Pending").toUpperCase())}</span>${l.remarks ? ` &nbsp;&nbsp; <b>Remarks:</b> ${esc(l.remarks)}` : ""}</div>
    <div style="display:flex;justify-content:space-between;margin-top:56px;">
      <div style="text-align:center;width:220px;border-top:1px solid #0f172a;padding-top:6px;font-size:11px;font-weight:600;">Employee Signature</div>
      <div style="text-align:center;width:220px;border-top:1px solid #0f172a;padding-top:6px;font-size:11px;font-weight:600;">Approved By${l.approved_by ? ` — ${esc(String(l.approved_by).replace(/Mangaer/gi, (m) => (m[0] === "M" ? "Manager" : "manager")))}` : ""}</div>
    </div>
  `;
  open("Leave Application", inner);
}

export function printLeaveRegister(rows) {
  const arr = [...(rows || [])].sort((a, b) => String(a.from_date || "").localeCompare(String(b.from_date || "")));
  const totalDays = arr.reduce((s, r) => s + (Number(r.days) || 0), 0);
  const body = arr
    .map(
      (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:600;">${esc(r.employee_name || "—")}</td>
        <td>${esc(r.branch || "—")}</td>
        <td>${esc(r.leave_type || "—")}</td>
        <td style="white-space:nowrap;">${r.from_date ? fmtDateDay(r.from_date) : "—"}</td>
        <td style="white-space:nowrap;">${r.to_date ? fmtDateDay(r.to_date) : "—"}</td>
        <td class="right">${r.days || "—"}</td>
        <td>${esc(r.status || "Pending")}</td>
        <td>${esc(r.reason || "—")}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">LEAVE REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Applications:</b> ${arr.length} &nbsp;&nbsp; <b>Total Days:</b> ${totalDays}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Employee</th><th>Branch</th><th>Type</th><th>From</th><th>To</th><th class="right">Days</th><th>Status</th><th>Reason</th></tr></thead>
      <tbody>${body || `<tr><td colspan="9" class="muted">No leave applications.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Leave Register", inner);
}

export function printBranchDirectory(rows) {
  const active = (rows || []).filter((r) => (r.status || "Active") !== "Inactive");
  const order = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
  const groups = {};
  active.forEach((r) => {
    const b = r.branch || "Chennai";
    (groups[b] = groups[b] || []).push(r);
  });
  const branches = [...order.filter((b) => groups[b]), ...Object.keys(groups).filter((b) => !order.includes(b))];
  const sections = branches
    .map((b) => {
      const list = groups[b];
      const addr = list.find((r) => r.branch_address)?.branch_address || (b === "Chennai" ? COMPANY.address : "");
      const body = list
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td>${esc(r.staff_code || "—")}</td>
            <td style="font-weight:700;">${esc(r.name || "—")}</td>
            <td>${esc(r.designation || "—")}</td>
            <td style="white-space:nowrap;">${esc(r.phone || "—")}</td>
            <td>${esc(r.email || "—")}</td>
          </tr>`
        )
        .join("");
      return `
        <div style="margin-bottom:22px;page-break-inside:avoid;">
          <div style="background:#e2e8f0;color:#0f172a;padding:7px 12px;border-radius:6px 6px 0 0;">
            <span style="font-size:12px;font-weight:800;letter-spacing:2px;">${esc(b.toUpperCase())} BRANCH</span>
            <span style="font-size:10px;color:#475569;margin-left:10px;">${list.length} staff</span>
          </div>
          ${addr ? `<div style="border:1px solid #e2e8f0;border-top:none;padding:6px 12px;font-size:10.5px;color:#334155;"><b>Address:</b> ${esc(addr)}</div>` : ""}
          <table class="tight" style="margin-top:0;">
            <thead><tr><th>Sl</th><th>Staff Code</th><th>Staff Name</th><th>Designation</th><th>Mobile No.</th><th>Email ID</th></tr></thead>
            <tbody>${body}</tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BRANCH CONTACT SHEET</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Branches:</b> ${branches.length} &nbsp;&nbsp; <b>Total Staff:</b> ${active.length}</div>
    ${sections || `<div class="muted">No staff found.</div>`}
  `;
  open("NTC Branch Contact Sheet", inner);
}

export function printOutstandingReport(groups, asOf) {
  const grand = groups.reduce(
    (a, g) => ({ total: a.total + g.total, received: a.received + g.received, balance: a.balance + g.balance }),
    { total: 0, received: 0, balance: 0 }
  );
  const billCount = groups.reduce((n, g) => n + g.bills.length, 0);
  const sections = groups
    .map((g) => {
      const body = g.bills
        .map(
          (b, i) => `<tr>
            <td>${i + 1}</td>
            <td style="font-weight:700;white-space:nowrap;">${esc(b.invoice_no)}</td>
            <td style="white-space:nowrap;">${esc(b.date || "—")}</td>
            <td>${esc(b.status || "")}</td>
            <td style="text-align:right;">${inr(b.total)}</td>
            <td style="text-align:right;">${inr(b.received)}</td>
            <td style="text-align:right;font-weight:800;">${inr(b.balance)}</td>
          </tr>`
        )
        .join("");
      return `
        <div style="margin-bottom:20px;page-break-inside:avoid;">
          <div style="background:#e2e8f0;color:#0f172a;padding:7px 12px;border-radius:6px 6px 0 0;">
            <span style="font-size:12px;font-weight:800;letter-spacing:1px;">${esc((g.customer || "—").toUpperCase())}</span>
            <span style="font-size:10px;color:#475569;margin-left:10px;">${g.bills.length} bill(s)</span>
          </div>
          <table class="tight" style="margin-top:0;">
            <thead><tr><th>Sl</th><th>Bill No.</th><th>Bill Date</th><th>Status</th><th style="text-align:right;">Bill Amount</th><th style="text-align:right;">Received</th><th style="text-align:right;">Balance Due</th></tr></thead>
            <tbody>${body}
              <tr style="background:#f8fafc;">
                <td colspan="4" style="font-weight:800;text-align:right;">SUB TOTAL</td>
                <td style="text-align:right;font-weight:800;">${inr(g.total)}</td>
                <td style="text-align:right;font-weight:800;">${inr(g.received)}</td>
                <td style="text-align:right;font-weight:800;">${inr(g.balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">OUTSTANDING REPORT</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>As on:</b> ${esc(asOf)} &nbsp;&nbsp; <b>Customers:</b> ${groups.length} &nbsp;&nbsp; <b>Pending Bills:</b> ${billCount} &nbsp;&nbsp; <b style="color:#b91c1c;">Total Outstanding: ${inr(grand.balance)}</b></div>
    ${sections || `<div class="muted">No pending bills — all payments received.</div>`}
    ${sections ? `<div style="margin-top:6px;border-top:2px solid #0f172a;padding-top:8px;font-size:13px;font-weight:800;display:flex;justify-content:flex-end;gap:24px;"><span>GRAND TOTAL DUE: ${inr(grand.balance)}</span></div>` : ""}
  `;
  open("NTC Outstanding Report", inner);
}

export function printTdsRegister(groups, period) {
  const grand = groups.reduce(
    (a, g) => ({ amount: a.amount + g.amount, tds: a.tds + g.tds, received: a.received + g.received }),
    { amount: 0, tds: 0, received: 0 }
  );
  const rowCount = groups.reduce((n, g) => n + g.rows.length, 0);
  const sections = groups
    .map((g) => {
      const body = g.rows
        .map(
          (r, i) => `<tr>
            <td>${i + 1}</td>
            <td style="white-space:nowrap;font-weight:700;">${esc(r.mr_no || "—")}</td>
            <td style="white-space:nowrap;">${esc(r.date || "—")}</td>
            <td style="white-space:nowrap;">${esc(r.bill_no || "—")}</td>
            <td style="text-align:right;">${inr(r.amount)}</td>
            <td style="text-align:right;font-weight:800;">${inr(r.tds)}</td>
            <td style="text-align:right;">${inr(r.received)}</td>
          </tr>`
        )
        .join("");
      return `
        <div style="margin-bottom:20px;page-break-inside:avoid;">
          <div style="background:#e2e8f0;color:#0f172a;padding:7px 12px;border-radius:6px 6px 0 0;">
            <span style="font-size:12px;font-weight:800;letter-spacing:1px;">${esc((g.customer || "—").toUpperCase())}</span>
            <span style="font-size:10px;color:#475569;margin-left:10px;">${g.rows.length} entry(ies)</span>
          </div>
          <table class="tight" style="margin-top:0;">
            <thead><tr><th>Sl</th><th>MR No.</th><th>MR Date</th><th>Bill No.</th><th style="text-align:right;">Bill Amount</th><th style="text-align:right;">TDS Deducted</th><th style="text-align:right;">Received Amount</th></tr></thead>
            <tbody>${body}
              <tr style="background:#f8fafc;">
                <td colspan="4" style="font-weight:800;text-align:right;">SUB TOTAL</td>
                <td style="text-align:right;font-weight:800;">${inr(g.amount)}</td>
                <td style="text-align:right;font-weight:800;">${inr(g.tds)}</td>
                <td style="text-align:right;font-weight:800;">${inr(g.received)}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">TDS REGISTER</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Period:</b> ${esc(period)} &nbsp;&nbsp; <b>Customers:</b> ${groups.length} &nbsp;&nbsp; <b>Entries:</b> ${rowCount} &nbsp;&nbsp; <b style="color:#b91c1c;">Total TDS Deducted: ${inr(grand.tds)}</b></div>
    ${sections || `<div class="muted">No TDS deductions found for this period.</div>`}
    ${sections ? `<div style="margin-top:6px;border-top:2px solid #0f172a;padding-top:8px;font-size:13px;font-weight:800;display:flex;justify-content:flex-end;gap:24px;"><span>TOTAL TDS: ${inr(grand.tds)}</span><span>TOTAL RECEIVED: ${inr(grand.received)}</span></div>` : ""}
  `;
  open("NTC TDS Register", inner);
}

export function printTdsYearlySummary(groups, fyLabel, metricLabel = "TDS", branchTotals = null) {
  const gq = (i) => groups.reduce((s, g) => s + (Number(g.q[i]) || 0), 0);
  const gt = groups.reduce((s, g) => s + (Number(g.tds) || 0), 0);
  const ga = groups.reduce((s, g) => s + (Number(g.amount) || 0), 0);
  const body = groups
    .map(
      (g, i) => `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:600;">${esc(g.customer)}</td>
        ${g.q.map((v) => `<td class="right">${v ? inr(v) : "—"}</td>`).join("")}
        <td class="right" style="font-weight:800;">${inr(g.tds)}</td>
        <td class="right">${inr(g.amount)}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">${esc(metricLabel.toUpperCase())} YEARLY SUMMARY — FY ${esc(fyLabel)}</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Customers:</b> ${groups.length} &nbsp;&nbsp; <b style="color:#b91c1c;">Total ${esc(metricLabel)}${metricLabel === "TDS" || metricLabel === "Deduction" ? " Deducted" : ""}: ${inr(gt)}</b>${metricLabel === "TDS" ? " &nbsp;&nbsp; For matching with Form 26AS / AIS (quarter-wise)." : ""}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Customer</th><th class="right">Q1 (Apr–Jun)</th><th class="right">Q2 (Jul–Sep)</th><th class="right">Q3 (Oct–Dec)</th><th class="right">Q4 (Jan–Mar)</th><th class="right">Total ${esc(metricLabel)}</th><th class="right">Bill Amount</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No ${esc(metricLabel)} entries in this financial year.</td></tr>`}
        ${body ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">GRAND TOTAL</td><td class="right">${inr(gq(0))}</td><td class="right">${inr(gq(1))}</td><td class="right">${inr(gq(2))}</td><td class="right">${inr(gq(3))}</td><td class="right">${inr(gt)}</td><td class="right">${inr(ga)}</td></tr>` : ""}
      </tbody>
    </table>
    ${branchTotals && branchTotals.length ? `<div style="margin-top:12px;padding:8px 14px;border:1px solid #e2e8f0;border-radius:6px;font-size:11px;"><b>Branch-wise ${esc(metricLabel)}:</b> ${branchTotals.map(([br, v]) => `${esc(br)} ${inr(v)}`).join(" &nbsp;•&nbsp; ")}</div>` : ""}
  `;
  open(`NTC ${metricLabel} Yearly Summary`, inner);
}

export function printChequeRegister(rows, period, opts = {}) {
  const { title = "CHEQUE REGISTER", refLabel = "MR No.", refPrefix = /^MR[-\s]?/i, partyLabel = "Customer", showDeposit = true, showBounce = true } = opts;
  const amt = (r) => Number(r.cheque_amount) || Number(r.amount) || 0;
  const total = rows.reduce((a, r) => a + amt(r), 0);
  const totalBounce = rows.reduce((a, r) => a + (Number(r.bounce_charge) || 0), 0);
  const counts = { Cleared: 0, Pending: 0, Bounced: 0 };
  rows.forEach((r) => { const s = r.cheque_status || "Pending"; counts[s] = (counts[s] || 0) + 1; });
  const stColor = { Cleared: "#15803d", Pending: "#b45309", Bounced: "#b91c1c" };
  const nCols = 8 + (showDeposit ? 3 : 0) + 1 + (showBounce ? 1 : 0);
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(String(r.mr_no || "—").replace(refPrefix, ""))}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td>${esc(r.customer_name || "—")}${r._repeat ? ' <span style="color:#b91c1c;font-weight:800;font-size:9px;">⚠ REPEAT BOUNCER — PREFER NEFT</span>' : ""}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(r.cheque_no || "—")}</td>
      <td style="white-space:nowrap;">${r.cheque_date ? fmtDateDay(r.cheque_date) : "—"}</td>
      <td>${esc(r.bank_name || "—")}</td>
      <td style="text-align:right;">${inr(amt(r))}</td>
      ${showDeposit ? `<td style="white-space:nowrap;">${r.deposit_date ? fmtDateDay(r.deposit_date) : "—"}</td><td style="white-space:nowrap;">${r.cleared_date ? fmtDateDay(r.cleared_date) : "—"}</td><td style="white-space:nowrap;">${esc(r._days || "—")}</td>` : ""}
      <td style="white-space:nowrap;font-weight:800;color:${stColor[r.cheque_status || "Pending"] || "#b45309"};">${esc(r.cheque_status || "Pending")}</td>
      ${showBounce ? `<td style="text-align:right;color:#b91c1c;">${Number(r.bounce_charge) > 0 ? inr(Number(r.bounce_charge)) : "—"}</td>` : ""}
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">${esc(title)}</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Period:</b> ${esc(period)} &nbsp;&nbsp; <b>Cheques:</b> ${rows.length} &nbsp;&nbsp;
      <span style="color:#15803d;"><b>Cleared:</b> ${counts.Cleared}</span> &nbsp;
      <span style="color:#b45309;"><b>Pending:</b> ${counts.Pending}</span> &nbsp;
      <span style="color:#b91c1c;"><b>Bounced:</b> ${counts.Bounced}</span> &nbsp;&nbsp;
      <b>Total Amount: ${inr(total)}</b>${showBounce && totalBounce > 0 ? ` &nbsp;&nbsp; <b style="color:#b91c1c;">Bounce Charges Recoverable: ${inr(totalBounce)}</b>` : ""}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>${esc(refLabel)}</th><th>Date</th><th>${esc(partyLabel)}</th><th>Cheque No.</th><th>Cheque Date</th><th>Bank Name</th><th style="text-align:right;">Amount</th>${showDeposit ? "<th>Deposit Date</th><th>Cleared Date</th><th>Days</th>" : ""}<th>Status</th>${showBounce ? '<th style="text-align:right;">Bounce Charge</th>' : ""}</tr></thead>
      <tbody>${body || `<tr><td colspan="${nCols}" class="muted">No cheques found.</td></tr>`}
        ${body ? `<tr style="background:#f8fafc;"><td colspan="7" style="font-weight:800;text-align:right;">TOTAL</td><td style="text-align:right;font-weight:800;">${inr(total)}</td>${showDeposit ? "<td></td><td></td><td></td>" : ""}<td></td>${showBounce ? `<td style="text-align:right;font-weight:800;color:#b91c1c;">${totalBounce > 0 ? inr(totalBounce) : "—"}</td>` : ""}</tr>` : ""}
      </tbody>
    </table>
  `;
  open(`NTC ${title.charAt(0)}${title.slice(1).toLowerCase()}`, inner);
}

export function printRecoveryRegister(list, monthLabel) {
  const arr = list || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(String(r.mr_no || "—").replace(/^MR[-\s]?/i, ""))}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
      <td>${esc(r.cheque_no || "—")}${r.bank_name ? ` · ${esc(r.bank_name)}` : ""}</td>
      <td class="right">${inr(Number(r.cheque_amount) || Number(r.amount) || 0)}</td>
      <td>${esc(r._repl || "—")}</td>
      <td style="font-weight:700;${r._recovery === "Recovered" ? "color:#047857;" : "color:#be123c;"}">${esc(r._recovery)}</td>
    </tr>`
    )
    .join("");
  const total = arr.reduce((s, r) => s + (Number(r.cheque_amount) || Number(r.amount) || 0), 0);
  const rec = arr.filter((r) => r._recovery === "Recovered").reduce((s, r) => s + (Number(r.cheque_amount) || Number(r.amount) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">CHEQUE RECOVERY REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")} &nbsp;·&nbsp; BOUNCED ${inr(total)} &nbsp;·&nbsp; RECOVERED ${inr(rec)} &nbsp;·&nbsp; UNRECOVERED ${inr(total - rec)}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>MR No.</th><th>Date</th><th>Customer</th><th>Bounced Cheque</th><th class="right">Amount</th><th>Replacement</th><th>Recovery Status</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No bounced cheques.</td></tr>`}</tbody>
    </table>
  `;
  open(`Recovery Register — ${monthLabel || ""}`, inner);
}


export function printDeductionRegister(list, monthLabel) {
  const arr = list || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(String(r.mr_no || "—").replace(/^MR[-\s]?/i, ""))}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td>${esc(r.office || "Chennai")}</td>
      <td style="font-weight:600;">${esc(r.customer_name || "—")}</td>
      <td class="right">${inr(r.amount)}</td>
      <td class="right" style="font-weight:700;">${inr(r._ded)}</td>
      <td>${esc(r.deduction_reason || "—")}</td>
      <td>${esc(r.deduction_status || "Approved")}</td>
      <td>${esc(r.deduction_reviewed_by || "—")}</td>
    </tr>`
    )
    .join("");
  const td = arr.reduce((s, r) => s + (Number(r._ded) || 0), 0);
  const ta = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">DEDUCTION REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")} &nbsp;·&nbsp; ${arr.length} MR(S)</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>MR No.</th><th>MR Date</th><th>Branch</th><th>Customer</th><th class="right">Received</th><th class="right">Deduction</th><th>Reason</th><th>Status</th><th>Reviewed By</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="10" class="muted">No deductions.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="5" class="right">TOTAL</td><td class="right">${inr(ta)}</td><td class="right">${inr(td)}</td><td colspan="3"></td></tr>
      </tbody>
    </table>
  `;
  open(`Deduction Register — ${monthLabel || ""}`, inner);
}


export function printCollectionSummary(stats, custRows, monthLabel) {
  const body = (custRows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.customer)}</td>
      <td class="right">${r.subs}</td>
      <td class="right">${inr(r.total)}</td>
      <td class="right" style="color:#047857;font-weight:700;">${inr(r.cleared)}</td>
      <td class="right" style="color:#b45309;font-weight:700;">${inr(r.pending)}</td>
      <td class="right">${r.total > 0 ? Math.round((r.cleared / r.total) * 100) : 0}%</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">COLLECTION SUMMARY</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")}</div>
    <div style="display:flex;gap:12px;margin:0 0 14px;font-size:12px;">
      <div style="flex:1;border:1px solid #cbd5e1;border-radius:6px;padding:10px 12px;"><b>Submitted</b><br/>${stats.count} slip(s) · ${inr(stats.total)}</div>
      <div style="flex:1;border:1px solid #10b981;border-radius:6px;padding:10px 12px;color:#047857;"><b>Cleared</b><br/>${stats.clearedCount} slip(s) · ${inr(stats.cleared)}</div>
      <div style="flex:1;border:1px solid #f59e0b;border-radius:6px;padding:10px 12px;color:#b45309;"><b>Pending</b><br/>${stats.pendingCount} slip(s) · ${inr(stats.pending)}</div>
      <div style="flex:1;border:1px solid #0f172a;border-radius:6px;padding:10px 12px;"><b>Collection %</b><br/>${stats.total > 0 ? Math.round((stats.cleared / stats.total) * 100) : 0}% of submitted value</div>
    </div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Customer</th><th class="right">Submissions</th><th class="right">Submitted Amt</th><th class="right">Cleared</th><th class="right">Pending</th><th class="right">% Cleared</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="7" class="muted">No submissions this month.</td></tr>`}</tbody>
    </table>
  `;
  open(`Collection Summary — ${monthLabel || ""}`, inner);
}


export function printDeductionTrend(rows, months, monLabel) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.customer)}</td>
      ${months.map((m) => `<td class="right">${r.months[m] ? inr(r.months[m]) : "—"}</td>`).join("")}
      <td class="right" style="font-weight:700;">${inr(r.deducted)}</td>
      <td class="right" style="font-weight:700;">${(r.pct || 0).toFixed(1)}%</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DEDUCTION TREND — TOP DEDUCTING CUSTOMERS</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">PERIOD: ${esc(monLabel || "")} (6 MONTHS)</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Customer</th>${months.map((m) => `<th class="right">${esc(new Date(m + "-01").toLocaleString("en-IN", { month: "short", year: "2-digit" }))}</th>`).join("")}<th class="right">Total Deducted</th><th class="right">Deduction %</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="${months.length + 4}" class="muted">No deductions.</td></tr>`}</tbody>
    </table>
    <p style="font-size:10px;color:#64748b;margin-top:8px;">Deduction % = deductions ÷ (received + deductions). Rejected deductions excluded. Use during rate discussions.</p>
  `;
  open("Deduction Trend", inner);
}


export function printDepositSlip(rows, depositDate) {
  const amt = (r) => Number(r.cheque_amount) || Number(r.amount) || 0;
  const total = rows.reduce((a, r) => a + amt(r), 0);
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(r.cheque_no || "—")}</td>
      <td style="white-space:nowrap;">${r.cheque_date ? fmtDateDay(r.cheque_date) : "—"}</td>
      <td>${esc(r.customer_name || "—")}</td>
      <td>${esc(r.bank_name || "—")}</td>
      <td style="white-space:nowrap;">${esc(String(r.mr_no || "—").replace(/^MR[-\s]?/i, ""))}</td>
      <td style="text-align:right;font-weight:700;">${inr(amt(r))}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CHEQUE DEPOSIT SLIP</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Deposit Date:</b> ${esc(fmtDate(depositDate))} &nbsp;&nbsp; <b>No. of Cheques:</b> ${rows.length} &nbsp;&nbsp; <b>Total Amount: ${inr(total)}</b></div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Cheque No.</th><th>Cheque Date</th><th>Drawer (Customer)</th><th>Drawn On (Bank)</th><th>MR No.</th><th style="text-align:right;">Amount</th></tr></thead>
      <tbody>${body}
        <tr style="background:#f8fafc;"><td colspan="6" style="font-weight:800;text-align:right;">TOTAL (${rows.length} cheque${rows.length === 1 ? "" : "s"})</td><td style="text-align:right;font-weight:800;">${inr(total)}</td></tr>
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:56px;font-size:11px;font-weight:700;">
      <span style="border-top:1px solid #000;padding-top:4px;">Deposited By (Branch Runner)</span>
      <span style="border-top:1px solid #000;padding-top:4px;">Branch Incharge</span>
      <span style="border-top:1px solid #000;padding-top:4px;">Bank Seal &amp; Signature</span>
    </div>
  `;
  open("NTC Cheque Deposit Slip", inner);
}

export function printCashBook(days) {
  let running = 0;
  const body = (days || [])
    .map(([d, m]) => {
      const opening = running;
      running = opening + m.in - m.out;
      return `<tr>
        <td style="white-space:nowrap;font-weight:700;">${fmtDate(d)}</td>
        <td style="text-align:right;">${inr(opening)}</td>
        <td style="text-align:right;color:#15803d;font-weight:700;">${inr(m.in)}</td>
        <td style="text-align:right;color:#b91c1c;font-weight:700;">${inr(m.out)}</td>
        <td style="text-align:right;font-weight:800;${running < 0 ? "color:#b91c1c;" : ""}">${running < 0 ? "-" + inr(Math.abs(running)) : inr(running)}</td>
      </tr>`;
    })
    .join("");
  const totIn = (days || []).reduce((a, [, m]) => a + m.in, 0);
  const totOut = (days || []).reduce((a, [, m]) => a + m.out, 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">CASH BOOK</div>
    <div style="margin:0 0 14px;font-size:11px;"><b>Day-wise cash receipts &amp; payments</b> &nbsp;&nbsp; <b>Days:</b> ${(days || []).length} &nbsp;&nbsp;
      <span style="color:#15803d;"><b>Total Receipts:</b> ${inr(totIn)}</span> &nbsp;
      <span style="color:#b91c1c;"><b>Total Payments:</b> ${inr(totOut)}</span> &nbsp;
      <b>Closing Balance: ${totIn - totOut < 0 ? "-" + inr(Math.abs(totIn - totOut)) : inr(totIn - totOut)}</b></div>
    <table class="tight">
      <thead><tr><th>Date</th><th style="text-align:right;">Opening</th><th style="text-align:right;">Cash Receipts</th><th style="text-align:right;">Cash Payments</th><th style="text-align:right;">Closing</th></tr></thead>
      <tbody>${body || `<tr><td colspan="5" class="muted">No cash entries found.</td></tr>`}
        ${body ? `<tr style="background:#f8fafc;"><td style="font-weight:800;">TOTAL</td><td></td><td style="text-align:right;font-weight:800;color:#15803d;">${inr(totIn)}</td><td style="text-align:right;font-weight:800;color:#b91c1c;">${inr(totOut)}</td><td style="text-align:right;font-weight:800;">${totIn - totOut < 0 ? "-" + inr(Math.abs(totIn - totOut)) : inr(totIn - totOut)}</td></tr>` : ""}
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:56px;font-size:11px;font-weight:700;">
      <span style="border-top:1px solid #000;padding-top:4px;">Cashier</span>
      <span style="border-top:1px solid #000;padding-top:4px;">Branch Incharge</span>
      <span style="border-top:1px solid #000;padding-top:4px;">Audited By</span>
    </div>
  `;
  open("NTC Cash Book", inner);
}

export function printVehiclePayment(c, p) {
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLE PAYMENT VOUCHER</div>
    <div style="display:flex;justify-content:space-between;font-size:11px;margin:0 0 10px;">
      <span><b>Challan No.:</b> ${esc(String(c.challan_no || "—").replace(/^VHC[-\s]?/i, ""))}</span>
      <span><b>Payment Date:</b> ${p.date ? fmtDate(p.date) : "—"}</span>
      <span><b>Branch:</b> ${esc(c.engaged_office || c.office || "Chennai")}</span>
    </div>
    <table class="tight">
      <tbody>
        <tr><td style="width:35%;font-weight:700;">Payment Type</td><td style="font-weight:800;">${esc(p.type || "—")}</td></tr>
        <tr><td style="font-weight:700;">Vehicle No.</td><td>${esc(c.vehicle_no || "—")}</td></tr>
        <tr><td style="font-weight:700;">Vehicle Owner Name</td><td>${esc(c.owner_name || "—")}${c.owner_mobile ? ` (Mob: ${esc(c.owner_mobile)})` : ""}</td></tr>
        <tr><td style="font-weight:700;">Vendor / Broker</td><td>${esc(c.vendor_broker_name || "—")}</td></tr>
        <tr><td style="font-weight:700;">Route</td><td>${esc(c.origin || "—")} → ${esc(c.destination || "—")}</td></tr>
        <tr><td style="font-weight:700;">Vehicle Hire</td><td>${inr(c.hire_amount)}</td></tr>
        <tr><td style="font-weight:700;">Payment Mode</td><td>${esc(p.mode || "Cash")}${p.reference ? ` / ${esc(p.reference)}` : ""}</td></tr>
        ${p.cheque_no ? `<tr><td style="font-weight:700;">Cheque Details</td><td>No. <b>${esc(p.cheque_no)}</b>${p.cheque_date ? ` Dt. ${fmtDate(p.cheque_date)}` : ""}${p.bank_name ? ` — ${esc(p.bank_name)}` : ""}${Number(p.cheque_amount) > 0 ? ` — ${inr(p.cheque_amount)}` : ""}</td></tr>` : ""}
        <tr><td style="font-weight:700;">Amount Paid</td><td style="font-weight:800;font-size:13px;">${inr(p.amount)}</td></tr>
        <tr><td style="font-weight:700;">Amount in Words</td><td style="font-weight:800;text-transform:uppercase;">${esc(amountInWords(Number(p.amount) || 0))}</td></tr>
        ${p.remarks ? `<tr><td style="font-weight:700;">Remarks</td><td>${esc(p.remarks)}</td></tr>` : ""}
        ${c.balance_release_reason && String(p.type || "").includes("Balance") ? `<tr><td style="font-weight:700;">Balance Release Reason</td><td style="color:#92400e;"><b>Admin-approved release</b>${c.balance_release_by ? ` (by ${esc(c.balance_release_by)})` : ""}: ${esc(c.balance_release_reason)}</td></tr>` : ""}
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:20px;font-size:11px;font-weight:700;text-align:center;align-items:flex-end;">
      <span style="width:22%;"><span style="display:block;border-top:1px solid #000;padding-top:4px;">Prepared By${p.by ? `<br/><span style="font-weight:400;">${esc(p.by)}</span>` : ""}</span></span>
      <span style="width:22%;"><span style="display:block;border-top:1px solid #000;padding-top:4px;">Cashier</span></span>
      <span style="width:26%;"><span style="display:block;border-top:1px solid #000;padding-top:4px;">Received By (Vehicle Owner / Driver)</span></span>
      <span style="width:24%;"><span style="display:block;height:52px;display:flex;align-items:flex-end;justify-content:center;">${signatureImg(40)}</span><span style="display:block;border-top:1px solid #000;padding-top:4px;">Authorised Signatory</span></span>
    </div>
  `;
  open("NTC Vehicle Payment Voucher", inner);
}

export function printVehiclePaymentRegister(rows, opts = {}) {
  const total = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(String(r.challan_no || "—").replace(/^VHC[-\s]?/i, ""))}</td>
      <td style="white-space:nowrap;">${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.owner || "—")}</td>
      <td style="white-space:nowrap;">${esc(String(r.type || "—").replace(/^Vehicle /, ""))}</td>
      <td>${esc(r.mode || "—")}</td>
      <td style="text-align:right;">${Number(r.unloading) ? inr(r.unloading) : "—"}</td>
      <td style="text-align:right;">${Number(r.challan_chg) ? inr(r.challan_chg) : "—"}</td>
      <td style="text-align:right;">${Number(r.extra_wt) ? inr(r.extra_wt) : "—"}</td>
      <td style="text-align:right;">${Number(r.extra_dly) ? inr(r.extra_dly) : "—"}</td>
      <td style="font-size:9px;">${esc(r.charge_reason || "—")}</td>
      <td style="text-align:right;font-weight:700;">${inr(r.amount)}</td>
      <td>${esc(r.by || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLE PAYMENT REGISTER</div>
    <div style="margin:0 0 14px;font-size:11px;">
      <b>Owner:</b> ${esc(opts.owner || "All Owners")} &nbsp;&nbsp;
      <b>Period:</b> ${opts.from || opts.to ? `${opts.from ? fmtDate(opts.from) : "…"} to ${opts.to ? fmtDate(opts.to) : "…"}` : "All dates"} &nbsp;&nbsp;
      <b>Payments:</b> ${rows.length} &nbsp;&nbsp; <b>Total Paid: ${inr(total)}</b></div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Date</th><th>Challan No.</th><th>Vehicle No.</th><th>Owner / Broker</th><th>Payment</th><th>Mode</th><th style="text-align:right;">Unloading</th><th style="text-align:right;">Challan Chg</th><th style="text-align:right;">Extra Wt</th><th style="text-align:right;">Extra Dly</th><th>Charges Reason</th><th style="text-align:right;">Amount</th><th>By</th></tr></thead>
      <tbody>${body || `<tr><td colspan="14" class="muted">No payments found.</td></tr>`}
        ${body ? `<tr style="background:#f8fafc;"><td colspan="12" style="font-weight:800;text-align:right;">TOTAL</td><td style="text-align:right;font-weight:800;">${inr(total)}</td><td></td></tr>` : ""}
      </tbody>
    </table>
  `;
  open("NTC Vehicle Payment Register", inner);
}

export function printCircular(c) {
  const inner = `
    <div class="title" style="letter-spacing:4px;">CIRCULAR</div>
    <div style="display:flex;justify-content:space-between;font-size:11px;margin:0 0 12px;">
      <span><b>Circular No.:</b> ${esc(String(c.circular_no || "—").replace(/^CIR[-\s]?/i, "CIR-"))}</span>
      <span><b>Date:</b> ${c.date ? fmtDate(c.date) : "—"}</span>
    </div>
    <div style="font-size:11px;margin:0 0 4px;"><b>To:</b> ${esc(c.to_branch || "All Branches")}${c.to_branch === "All Branches" || !c.to_branch ? " — All Branch Incharges & Staff" : " Branch Incharge & Staff"}</div>
    <div style="font-size:11px;margin:0 0 12px;"><b>Category:</b> ${esc(c.category || "General")}</div>
    <div style="font-size:13px;font-weight:800;text-decoration:underline;margin:0 0 12px;">Subject: ${esc(c.subject || "")}</div>
    <div style="font-size:12px;line-height:1.7;white-space:pre-wrap;margin:0 0 24px;">${esc(c.message || "")}</div>
    <div style="font-size:11px;margin:0 0 40px;">All concerned are requested to take note of the above and act accordingly.</div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;font-size:11px;font-weight:700;">
      <span style="border-top:1px solid #000;padding-top:4px;">Issued By${c.issued_by ? `<br/><span style="font-weight:400;">${esc(c.issued_by)}</span>` : ""}</span>
      <span style="text-align:center;">For NISHA TRANSPORT CORPORATION<br/><span style="display:block;height:80px;">${signatureImg(68)}</span><span style="display:inline-block;border-top:1px solid #000;padding-top:4px;font-weight:400;min-width:180px;">Authorised Signatory</span></span>
    </div>
  `;
  open("NTC Circular", inner);
}

export function printCustomerRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;">${esc(r.customer_code || "—")}</td>
      <td style="font-weight:700;">${esc(r.name || "—")}</td>
      <td>${esc(r.branch || "—")}</td>
      <td>${esc(r.contact_person || "—")}</td>
      <td style="white-space:nowrap;">${esc(r.phone || "—")}</td>
      <td>${esc(r.email || "—")}</td>
      <td>${esc(r.state || "—")}</td>
      <td style="white-space:nowrap;">${esc(r.gstin || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CUSTOMER REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Customers:</b> ${arr.length}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Code</th><th>Name</th><th>Branch</th><th>Contact Person</th><th>Phone</th><th>Email</th><th>State</th><th>GSTIN</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No customers.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Customer Register", inner);
}

export function printVisitingCard(p) {
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const card = `
      <div style="width:380px;height:220px;border:1px solid #cbd5e1;border-radius:10px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.12);display:flex;flex-direction:column;page-break-inside:avoid;">
        <div style="background:#f1f3f5;border-bottom:1px solid #dfe3e8;color:#0f172a;padding:12px 16px;display:flex;align-items:center;gap:12px;">
          <img src="${logo}" style="width:44px;height:44px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid #dfe3e8;" />
          <div>
            <div style="font-size:15px;font-weight:800;letter-spacing:1px;color:#0f172a;">${esc((p.name || "NISHA TRANSPORT CORPORATION").toUpperCase())}</div>
            <div style="font-size:8px;letter-spacing:2.5px;color:#5b6470;margin-top:2px;">FLEET · FREIGHT · LOGISTICS · SURFACE · AIR</div>
          </div>
        </div>
        <div style="flex:1;display:flex;justify-content:space-between;padding:12px 16px;">
          <div>
            <div style="font-size:13px;font-weight:800;color:#0f172a;">${esc(p.md_name || "B L Baror")}</div>
            <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;margin-bottom:8px;">Managing Director</div>
            <div style="font-size:10px;color:#0f172a;line-height:1.7;">
              ${esc(p.phone || "")}<br/>
              ${esc(p.email || "")}<br/>
              ${esc(p.website || "")}
            </div>
          </div>
          <div style="max-width:150px;text-align:right;">
            <div style="font-size:9px;color:#475569;line-height:1.6;">${esc(p.address || "")}</div>
            <div style="font-size:8.5px;color:#64748b;margin-top:6px;">GSTIN: ${esc(p.gstin || "")}</div>
          </div>
        </div>
        <div style="height:6px;background:linear-gradient(90deg,#64748b 60%,#10b981);"></div>
      </div>`;
  const inner = `<div style="display:flex;justify-content:center;gap:24px;flex-wrap:wrap;margin-top:32px;">${card}${card}</div>
    <div style="text-align:center;font-size:10px;color:#64748b;margin-top:14px;">NTC Visiting Card — standard size, two copies per sheet</div>`;
  open("NTC Visiting Card", inner, { hideHeader: true, downloadImage: true });
}

export const VCARD_THEMES = {
  "Light Grey": { bg: "#f1f3f5", border: "#dfe3e8", tagline: "#5b6470", bar: "linear-gradient(90deg,#64748b 60%,#10b981)" },
  "Light Blue": { bg: "#e8f1fa", border: "#d3e2f2", tagline: "#3b6ea5", bar: "linear-gradient(90deg,#3b6ea5 60%,#10b981)" },
  "Light Green": { bg: "#e9f7ef", border: "#d2ead9", tagline: "#2f855a", bar: "linear-gradient(90deg,#2f855a 60%,#10b981)" },
  "Light Rose": { bg: "#faeef0", border: "#f0d9dd", tagline: "#9b4d5c", bar: "linear-gradient(90deg,#9b4d5c 60%,#f59e0b)" },
};

function staffVisitingCardHtml(s, p, token, qr, themeName) {
  const th = VCARD_THEMES[themeName] || VCARD_THEMES["Light Grey"];
  const logo = `${window.location.origin}/ntc-logo.jpg`;
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
  const photo = s.photo_path
    ? `<img src="${base}${s.photo_path}?auth=${token || ""}" style="width:56px;height:56px;object-fit:cover;border-radius:8px;border:2px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,.2);" />`
    : "";
  const qrBlock = qr
    ? `<div style="text-align:center;">
         <img src="${qr}" style="width:56px;height:56px;border:1px solid #e2e8f0;border-radius:6px;" />
         <div style="font-size:6px;color:#94a3b8;letter-spacing:.5px;margin-top:1px;">SCAN TO SAVE CONTACT</div>
       </div>`
    : "";
  const addrFull = s.branch_address || (p && p.address) || "";
  const parts = String(addrFull).split(",").map((x) => x.trim()).filter(Boolean);
  let addrLines = addrFull ? [addrFull] : [];
  if (parts.length >= 2) {
    const half = String(addrFull).length / 2;
    let len = 0, i = 0;
    for (; i < parts.length - 1; i++) {
      len += parts[i].length + 2;
      if (len >= half) { i++; break; }
    }
    addrLines = [parts.slice(0, i).join(", ") + ",", parts.slice(i).join(", ")];
  }
  return `
      <div style="width:380px;height:220px;border:1px solid #cbd5e1;border-radius:10px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.12);display:flex;flex-direction:column;page-break-inside:avoid;background:#ffffff;">
        <div style="background:${th.bg};border-bottom:1px solid ${th.border};color:#0f172a;padding:10px 16px;display:flex;align-items:center;gap:12px;">
          <img src="${logo}" style="width:40px;height:40px;border-radius:8px;background:#ffffff;object-fit:contain;border:1px solid ${th.border};" />
          <div>
            <div style="font-size:14.5px;font-weight:800;letter-spacing:1px;color:#0f172a;">${esc(((p && p.name) || "NISHA TRANSPORT CORPORATION").toUpperCase())}</div>
            <div style="font-size:7.5px;letter-spacing:2.2px;color:${th.tagline};margin-top:2px;">FLEET · FREIGHT · LOGISTICS · SURFACE · AIR</div>
          </div>
        </div>
        <div style="flex:1;display:flex;justify-content:space-between;align-items:flex-start;padding:10px 16px 4px;">
          <div style="display:flex;gap:10px;">
            ${photo ? `<div>${photo}</div>` : ""}
            <div>
              <div style="font-size:13px;font-weight:800;color:#0f172a;">${esc(s.name || "—")}</div>
              <div style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;margin-bottom:6px;">${esc(s.designation || "Staff")}${s.branch ? " · " + esc(s.branch) : ""}</div>
              <div style="font-size:10px;color:#0f172a;line-height:1.6;">
                ${esc(s.phone || (p && p.phone) || "")}<br/>
                ${esc(s.email || (p && p.email) || "")}<br/>
                ${esc((p && p.website) || "")}
              </div>
            </div>
          </div>
          ${qrBlock}
        </div>
        <div style="text-align:center;padding:2px 14px 6px;">
          ${addrLines.map((l) => `<div style="font-size:8.5px;color:#475569;line-height:1.5;white-space:nowrap;">${esc(l)}</div>`).join("")}
          <div style="font-size:8px;color:#64748b;margin-top:1px;">GSTIN: ${esc((p && p.gstin) || "")}</div>
        </div>
        <div style="height:6px;background:${th.bar};"></div>
      </div>`;
}

export async function printStaffVisitingCard(s, p, token, theme) {
  const qr = await staffVcardQr(s, p);
  const card = staffVisitingCardHtml(s, p, token, qr, theme);
  const inner = `<div style="display:flex;justify-content:center;gap:24px;flex-wrap:wrap;margin-top:32px;">${card}${card}</div>
    <div style="text-align:center;font-size:10px;color:#64748b;margin-top:14px;">NTC Staff Visiting Card — ${esc(s.name || "")}, two copies per sheet · QR saves contact to phone</div>`;
  open(`Visiting Card — ${s.name || ""}`, inner, { hideHeader: true, downloadImage: true });
}

export async function staffVisitingCardShareHtml(s, p, token, theme) {
  const qr = await staffVcardQr(s, p);
  return `<div style="background:#ffffff;padding:4px;">${staffVisitingCardHtml(s, p, token, qr, theme)}</div>`;
}

export async function printStaffVisitingCardsBulk(rows, p, token, branchLabel, theme) {
  const cards = (
    await Promise.all((rows || []).map(async (r) => staffVisitingCardHtml(r, p, token, await staffVcardQr(r, p), theme)))
  ).join("");
  const inner = `
    <div style="text-align:center;font-size:13px;font-weight:800;letter-spacing:2px;margin:8px 0 4px;">STAFF VISITING CARDS${branchLabel && branchLabel !== "All" ? ` — ${esc(branchLabel.toUpperCase())} BRANCH` : ""}</div>
    <div style="text-align:center;font-size:10px;color:#64748b;margin-bottom:14px;">${(rows || []).length} card(s)</div>
    <div style="display:flex;flex-wrap:wrap;gap:20px;justify-content:center;">${cards}</div>
  `;
  open("NTC Staff Visiting Cards", inner, { hideHeader: true, downloadImage: true });
}

export function printVendorLedger(data) {
  const rows = (data && data.rows) || [];
  const period =
    data && (data.from || data.to)
      ? `PERIOD: ${data.from ? fmtDate(data.from) : "Beginning"} TO ${data.to ? fmtDate(data.to) : "Today"}`
      : "";
  let sl = 0;
  const t = { hire: 0, advance: 0, vbal: 0, paid: 0, out: 0 };
  const body = rows
    .map((r) => {
      t.paid += r.total_paid || 0;
      t.out += r.balance || 0;
      const items = r.items && r.items.length ? r.items : [null];
      return items
        .map((it, idx) => {
          sl += 1;
          if (it) {
            t.hire += it.hire || 0;
            t.advance += it.advance || 0;
            t.vbal += it.balance || 0;
          }
          return `<tr>
      <td>${sl}</td>
      <td style="font-weight:600;">${idx === 0 ? esc(r.vendor || "—") : ""}</td>
      <td style="white-space:nowrap;">${it ? esc(it.vehicle_no || "—") : "—"}</td>
      <td>${it ? esc(it.vehicle_type || "—") : "—"}</td>
      <td>${it ? esc(String(it.challan_no || "").replace(/^VHC-?/i, "") || "—") : "—"}</td>
      <td style="white-space:nowrap;">${it && it.date ? fmtDate(it.date) : "—"}</td>
      <td>${it ? esc(it.from_place || "—") : "—"}</td>
      <td>${it ? esc(it.destination || "—") : "—"}</td>
      <td class="right">${it ? inr(it.hire) : "—"}</td>
      <td class="right">${it ? inr(it.advance) : "—"}</td>
      <td class="right">${it ? inr(it.balance) : "—"}</td>
      <td class="right">${idx === 0 ? inr(r.total_paid) : ""}</td>
      <td class="right" style="font-weight:700;">${idx === 0 ? inr(r.balance) : ""}</td>
    </tr>`;
        })
        .join("");
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR / BROKER LEDGER</div>
    ${period ? `<div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${period}</div>` : ""}
    <div style="margin:0 0 12px;font-size:11px;"><b>Total Vendors:</b> ${rows.length}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Vendor / Broker Name</th><th>Vehicle No.</th><th>Vehicle Type</th><th>Challan No.</th><th>Challan Date</th><th>From</th><th>Destination</th><th class="right">Vehicle Hire</th><th class="right">Vehicle Advance</th><th class="right">Vehicle Balance</th><th class="right">Total Paid</th><th class="right">Outstanding</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="13" class="muted">No records.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="8" class="right">TOTAL</td><td class="right">${inr(t.hire)}</td><td class="right">${inr(t.advance)}</td><td class="right">${inr(t.vbal)}</td><td class="right">${inr(t.paid)}</td><td class="right">${inr(t.out)}</td></tr>
      </tbody>
    </table>
  `;
  open("Vendor / Broker Ledger", inner);
}



export function printVendorStatement(r) {
  const items = (r && r.items) || [];
  const body = items
    .map(
      (it, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(it.challan_no || "—")}</td>
      <td style="white-space:nowrap;">${it.date ? fmtDateDay(it.date) : "—"}</td>
      <td style="white-space:nowrap;">${esc(it.vehicle_no || "—")}</td>
      <td>${esc(it.from_place || "—")}</td>
      <td>${esc(it.destination || "—")}</td>
      <td class="right">${inr(it.hire)}</td>
      <td class="right">${inr(it.advance)}</td>
      <td class="right" style="color:#b45309;">${Number(it.credit_note) ? inr(it.credit_note) : "—"}${it.credit_note_reason ? `<div style="font-size:9px;color:#64748b;">${esc(it.credit_note_reason)}</div>` : ""}</td>
      <td class="right" style="font-weight:700;">${inr(it.balance)}</td>
    </tr>`
    )
    .join("");
  const th = items.reduce((s, it) => s + (Number(it.hire) || 0), 0);
  const ta = items.reduce((s, it) => s + (Number(it.advance) || 0), 0);
  const tcn = items.reduce((s, it) => s + (Number(it.credit_note) || 0), 0);
  const tb = items.reduce((s, it) => s + (Number(it.balance) || 0), 0);
  const line = `<span style="display:inline-block;min-width:180px;border-bottom:2px solid #0f172a;">&nbsp;</span>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR / BROKER STATEMENT</div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin:10px 0 12px;font-size:12px;">
      <div><b>Vendor / Broker:</b> <span style="font-size:14px;font-weight:800;">${esc(r.vendor || "—")}</span></div>
      <div><b>Statement Date:</b> ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    </div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Challan No.</th><th>Challan Date</th><th>Vehicle No.</th><th>From</th><th>Destination</th><th class="right">Vehicle Hire</th><th class="right">Paid</th><th class="right">Credit Note</th><th class="right">Balance</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="10" class="muted">No challans.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">TOTAL</td><td class="right">${inr(th)}</td><td class="right">${inr(ta)}</td><td class="right" style="color:#b45309;">${inr(tcn)}</td><td class="right">${inr(tb)}</td></tr>
      </tbody>
    </table>
    <div style="margin:14px 0;display:flex;justify-content:flex-end;">
      <table style="width:auto;font-size:12px;">
        <tbody>
          <tr><td style="font-weight:600;">Total Hire</td><td class="right">${inr(r.total_hire)}</td></tr>
          <tr><td style="font-weight:600;">Total Paid</td><td class="right">${inr(r.total_paid)}</td></tr>
          ${tcn > 0 ? `<tr><td style="font-weight:600;color:#b45309;">Less: Credit Note (Deduction)</td><td class="right" style="color:#b45309;">- ${inr(tcn)}</td></tr>` : ""}
          <tr style="font-weight:800;background:#f1f5f9;"><td>BALANCE PAYABLE</td><td class="right" style="font-size:14px;">${inr(r.balance)}</td></tr>
        </tbody>
      </table>
    </div>
    <div style="margin:18px 0 8px;border:1px solid #0f172a;border-radius:6px;padding:12px 14px;font-size:12px;">
      <div style="font-weight:800;letter-spacing:1px;margin-bottom:10px;">SETTLEMENT</div>
      <div style="margin-bottom:10px;">Amount Settled: ₹ ${line} &nbsp;&nbsp; Mode (Cash / Cheque / NEFT): ${line}</div>
      <div>Reference / Cheque No.: ${line} &nbsp;&nbsp; Date: ${line}</div>
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:56px;font-size:12px;">
      <div style="text-align:center;">
        <div style="border-top:2px solid #0f172a;padding-top:6px;min-width:220px;font-weight:700;">Vendor / Broker Signature</div>
      </div>
      <div style="text-align:center;">
        <div style="font-weight:800;margin-bottom:64px;">For NISHA TRANSPORT CORPORATION</div>
        <div style="border-top:2px solid #0f172a;padding-top:6px;min-width:220px;font-weight:700;">Authorised Signatory</div>
      </div>
    </div>
  `;
  open(`Vendor Statement — ${r.vendor || ""}`, inner);
}


export function printDebitVoucher(v) {
  open(`Debit Voucher ${v.voucher_no || ""}`, dvInner(v), { frame: true });
}

export function printDebitVouchers(list) {
  const arr = (list || []).filter(Boolean);
  if (!arr.length) return;
  open("Debit Vouchers", arr.map((v) => `<div class="challan-page"><div class="frame">${header()}${dvInner(v)}</div></div>`).join(""), { hideHeader: true });
}

function dvInner(v) {
  const blank = !!v.blank;
  const dashLine = (label, val) =>
    `<div style="display:flex;align-items:flex-end;gap:6px;margin:6px 0;">
       <span style="white-space:nowrap;font-weight:600;">${esc(label)}</span>
       <span style="flex:1;border-bottom:1px dotted #64748b;padding:0 6px;min-height:18px;">${esc(val || "")}</span>
     </div>`;
  const inner = `
    <div class="title" style="letter-spacing:4px;">DEBIT VOUCHER</div>
    <div style="display:flex;justify-content:space-between;margin:14px 0;">
      <div style="width:48%;">${dashLine("Branch:", v.branch)}${dashLine("Category:", v.category)}</div>
      <div style="width:40%;">${dashLine("Voucher No:", (v.voucher_no || "").replace(/^DV[-\s]?/i, ""))}${dashLine("Date:", v.date ? fmtDate(v.date) : "")}</div>
    </div>
    <div style="margin:14px 0;">
      ${dashLine("To:", v.party_name)}
      ${dashLine("A/c:", v.account_no)}
    </div>
    <div style="border-bottom:1px solid #0f172a;margin-bottom:2px;"></div>
    <table>
      <thead><tr><th>Description</th><th class="right" style="width:180px;">Rs.</th></tr></thead>
      <tbody>
        <tr><td style="height:120px;vertical-align:top;">Being the amount paid for ${esc(v.narration || v.category || "")}</td>
        <td class="right" style="vertical-align:top;">${blank ? "" : inr(v.amount)}</td></tr>
        <tr><td style="font-weight:700;">Amount in Words :- ${blank ? "" : esc(amountInWords(v.amount))}<span style="float:right;">Total</span></td><td class="right" style="font-weight:800;">${blank ? "" : inr(v.amount)}</td></tr>
      </tbody>
    </table>
    <div style="margin:14px 0;display:flex;gap:24px;flex-wrap:wrap;">
      <div style="flex:1;min-width:200px;">${dashLine("By " + (v.payment_mode || (blank ? "Cash / Bank" : "Cash")) + " / Cheque No.", v.cheque_no || v.reference)}</div>
      <div style="width:180px;">${dashLine("Date:", v.cheque_date ? fmtDate(v.cheque_date) : "")}</div>
    </div>
    <div style="margin:6px 0 14px;display:flex;gap:24px;flex-wrap:wrap;">
      <div style="flex:1;min-width:200px;">${dashLine("Bank Name:", v.bank_name)}</div>
      <div style="width:220px;">${dashLine("Transaction Date:", v.transaction_date ? fmtDate(v.transaction_date) : "")}</div>
    </div>
    ${v.proof_path ? `<div style="margin:4px 0 10px;display:inline-block;border:1.5px solid #059669;border-radius:4px;padding:3px 10px;font-weight:700;font-size:10.5px;color:#047857;letter-spacing:0.5px;">✓ BILL PROOF ATTACHED${v.proof_filename ? ` — ${esc(v.proof_filename)}` : ""}</div>` : ""}
    <div style="text-align:right;margin-top:28px;font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
    <div style="display:flex;justify-content:space-between;margin-top:12px;text-align:center;font-size:11px;color:#475569;align-items:flex-end;">
      <div style="width:23%;"><div style="border-top:1px solid #94a3b8;padding-top:6px;">Prepared By</div></div>
      <div style="width:23%;"><div style="border-top:1px solid #94a3b8;padding-top:6px;">Cashier</div></div>
      <div style="width:23%;"><div style="border-top:1px solid #94a3b8;padding-top:6px;">Passed By</div></div>
      <div style="width:23%;"><div style="height:52px;display:flex;align-items:flex-end;justify-content:center;">${signatureImg(40)}</div><div style="border-top:1px solid #94a3b8;padding-top:6px;">Authorised Signatory</div></div>
    </div>
  `;
  return inner;
}

export function printVendorVoucherSummary2(vendor, cats, total, count) {
  const pref = ["Vehicle Hire Advance", "Vehicle Balance Payment", "Vehicle Hire Payment"];
  const keys = [...pref, ...Object.keys(cats || {}).filter((k) => !pref.includes(k))].filter((k) => cats?.[k]);
  const body = keys
    .map((k, i) => `<tr><td>${i + 1}</td><td>${esc(k)}</td><td class="right">${cats[k].n}</td><td class="right">${inr(cats[k].total)}</td></tr>`)
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR VOUCHER SUMMARY</div>
    <div style="margin:0 0 12px;font-size:12px;"><b>Vendor/Broker:</b> ${esc(vendor)} &nbsp;·&nbsp; <b>As on:</b> ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th style="width:40px;">Sl</th><th>Payment Category</th><th class="right" style="width:110px;">Vouchers</th><th class="right" style="width:150px;">Total Paid</th></tr></thead>
      <tbody>${body || `<tr><td colspan="4" class="muted">No debit vouchers for this vendor yet.</td></tr>`}
        ${body ? `<tr><td></td><td style="font-weight:800;">TOTAL PAID</td><td class="right" style="font-weight:800;">${count}</td><td class="right" style="font-weight:800;">${inr(total)}</td></tr>` : ""}
      </tbody>
    </table>
    <div style="margin-top:18px;font-size:10px;color:#64748b;">Totals compiled from Debit Vouchers (including vouchers auto-generated from Vehicle Hire Challans). For the vendor file.</div>
  `;
  open(`Vendor Voucher Summary — ${vendor}`, inner);
}

export function printVoucherSummary(monthLabel, list) {
  const body = (list || [])
    .map(
      (c, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(c.category)}</td>
      <td class="right">${c.count}</td>
      <td class="right">${inr(c.total)}</td>
    </tr>`
    )
    .join("");
  const grandCount = (list || []).reduce((a, c) => a + c.count, 0);
  const grand = (list || []).reduce((a, c) => a + c.total, 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">VOUCHER EXPENSE SUMMARY</div>
    <div style="margin:0 0 12px;font-size:12px;"><b>Month:</b> ${esc(monthLabel)}</div>
    <table>
      <thead><tr><th style="width:40px;">Sl</th><th>Category</th><th class="right" style="width:110px;">Vouchers</th><th class="right" style="width:150px;">Total</th></tr></thead>
      <tbody>${body || `<tr><td colspan="4" class="muted">No vouchers.</td></tr>`}
        ${body ? `<tr><td></td><td style="font-weight:800;">TOTAL</td><td class="right" style="font-weight:800;">${grandCount}</td><td class="right" style="font-weight:800;">${inr(grand)}</td></tr>` : ""}
      </tbody>
    </table>
  `;
  open(`Voucher Summary ${monthLabel}`, inner);
}

export function printPadLog(rows) {
  const usage = (r) => (r.voided ? `CANCELLED${r.void_reason ? ` (${r.void_reason})` : ""}` : !r.serial_count ? "—" : r.used_count === r.serial_count ? "All used" : r.used_count > 0 ? `${r.used_count} of ${r.serial_count} used` : "NOT USED YET");
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${r.created_at ? `${fmtDate(r.created_at)} ${new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—"}</td>
      <td>${esc(r.doc_type)}</td>
      <td>${esc(r.start_no)}${r.end_no && r.end_no !== r.start_no ? ` – ${esc(r.end_no)}` : ""}</td>
      <td class="right">${r.count}</td>
      <td>${esc(r.issued_by || "—")}</td>
      <td>${esc(r.office || "—")}</td>
      <td style="${usage(r) === "NOT USED YET" ? "color:#b91c1c;font-weight:700;" : ""}">${usage(r)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BLANK PAD ISSUE LOG</div>
    <table>
      <thead><tr><th style="width:36px;">Sl</th><th>When</th><th>Type</th><th>Serial Range</th><th class="right">Count</th><th>Issued By</th><th>Branch</th><th>Usage</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No blank pads issued.</td></tr>`}</tbody>
    </table>
  `;
  open("Pad Issue Log", inner);
}

export function printStaffLogins(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (u, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(u.office || "—")}</td>
      <td>${esc(u.name || "—")}</td>
      <td>${esc(u.user_id || "—")}</td>
      <td>${esc(u.email || "—")}</td>
      <td>${esc(u.phone || "—")}</td>
      <td>${esc(u.role === "admin" ? "Admin" : "Staff")}</td>
      <td>${esc(u.password_plain || "—")}</td>
      <td>${esc(u.active !== false ? "Active" : "Disabled")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">NTC STAFF LOGINS</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Accounts:</b> ${arr.length} &nbsp;&nbsp; <span style="color:#b91c1c;font-weight:700;">CONFIDENTIAL — contains login passwords</span></div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>SL No.</th><th>Branch</th><th>Name</th><th>User ID</th><th>Email</th><th>Mobile</th><th>Role</th><th>Password</th><th>Status</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="9" class="muted">No accounts.</td></tr>`}</tbody>
    </table>
  `;
  open("NTC Staff Logins", inner);
}

export function printPodRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.office || r.office_name || "—")}</td>
      <td>${esc((r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—")}</td>
      <td>${r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</td>
      <td>${esc(r.origin || "—")}</td>
      <td>${esc(r.destination || "—")}</td>
      <td>${esc(r.consignor || "—")}</td>
      <td>${esc(r.consignee || "—")}</td>
      <td>${esc(r.packages || "—")}</td>
      <td>${esc(r.basis || "—")}</td>
      <td>${esc(r.invoice_no || "—")}</td>
      <td>${r.invoice_date ? fmtDate(r.invoice_date) : "—"}</td>
      <td class="right">${Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.vehicle_type || "—")}</td>
      <td class="right">${Number(r.freight_amount) ? inr(r.freight_amount) : "—"}</td>
      <td>${esc(r.vendor_name || "—")}</td>
      <td>${r.delivery_date ? fmtDate(r.delivery_date) : "—"}</td>
      <td>${r.pod_path ? "Uploaded" : "Pending"}</td>
    </tr>`
    )
    .join("");
  const pending = arr.filter((r) => !r.pod_path).length;
  const inner = `
    <div class="title" style="letter-spacing:3px;">POD STATUS REGISTER</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Total:</b> ${arr.length} &nbsp;&nbsp; <b>Uploaded:</b> ${arr.length - pending} &nbsp;&nbsp; <b>Pending:</b> ${pending}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Consignor Name</th><th>Consignee Name</th><th>PKG</th><th>Basis</th><th>Invoice No.</th><th>Invoice Date</th><th class="right">Invoice Value</th><th>Vehicle No.</th><th>Vehicle Type</th><th class="right">Freight Amount</th><th>Vendor/Broker Name</th><th>Delivery Date</th><th>POD</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="19" class="muted">No consignments.</td></tr>`}</tbody>
    </table>
  `;
  open("POD Status Register", inner);
}

const MR_COPIES = ["ACCOUNT COPY", "HO COPY", "FILE COPY"];
const MR_STYLE = `<style>.challan-page th{border-bottom-width:1px !important;}</style>`;
const mrCopyPages = (inner, copies = MR_COPIES) =>
  MR_STYLE + copies.map(
    (cp) =>
      `<div class="challan-page"><div class="frame">${header()}<div style="text-align:right;margin:2px 0 4px;"><span style="display:inline-block;border:1.5px solid #0f172a;border-radius:4px;padding:2px 12px;font-weight:800;font-size:10.5px;letter-spacing:1.5px;">${cp}</span></div>${inner}</div></div>`
  ).join("");

export function printMoneyReceipt(r, copies = MR_COPIES) {
  const dash0 = (val) => `<span style="border-bottom:1px dotted #64748b;padding:0 6px;">${esc(val || "")}</span>`;
  if (r.bills && r.bills.length) {
    const bills = r.bills;
    const sumAmt = bills.reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const sumTds = bills.reduce((s, b) => s + (Number(b.tds) || 0), 0);
    const sumDed = bills.reduce((s, b) => s + (Number(b.deduction) || 0), 0);
    const net = sumAmt - sumTds - sumDed;
    const body = bills
      .map(
        (b, i) => `<tr>
        <td style="padding:4px 8px;">${i + 1}</td>
        <td style="padding:4px 8px;">${esc(b.branch || "—")}</td>
        <td style="padding:4px 8px;">${esc((b.bill_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
        <td style="padding:4px 8px;">${b.bill_date ? fmtDate(b.bill_date) : "—"}</td>
        <td style="padding:4px 8px;">${esc(b.cn_no || "—")}</td>
        <td style="padding:4px 8px;">${b.cn_date ? fmtDate(b.cn_date) : "—"}</td>
        <td style="padding:4px 8px;">${esc(b.from_place || "—")}</td>
        <td style="padding:4px 8px;">${esc(b.destination || "—")}</td>
        <td class="right" style="padding:4px 8px;">${b.deduction ? inr(b.deduction) : "—"}</td>
        <td class="right" style="padding:4px 8px;">${b.tds ? inr(b.tds) : "—"}</td>
        <td class="right" style="padding:4px 8px;">${inr(Number(b.amount) || 0)}</td>
      </tr>`
      )
      .join("");
    const inner = `
      <div class="title" style="letter-spacing:3px;">MONEY RECEIPT</div>
      <div style="display:flex;justify-content:space-between;margin:12px 0;font-size:13px;">
        <div><b>MR No.</b> ${dash0((r.mr_no || "").replace(/^MR[-\s]?/i, ""))}</div>
        <div><b>Date:</b> ${dash0(fmtDate(r.date))}</div>
      </div>
      <div style="font-size:13px;margin:6px 0;"><b>Branch:</b> ${dash0(r.office || "Chennai")} &nbsp;&nbsp; <b>Customer Code:</b> ${dash0(r.customer_code || "")} &nbsp;&nbsp; <b>By:</b> ${dash0(r.payment_mode || "Cash")}</div>
      <div style="font-size:13px;line-height:2.1;margin:8px 0;">
        Received with Thanks from ${dash0(r.customer_name)} A sum of Rupees ${dash0(amountInWords(net).replace(/ Rupees Only$/i, " Only"))} Against the following bills:
      </div>
      <table style="margin-top:6px;font-size:11px;">
        <thead><tr>
          <th style="padding:5px 8px;">Sl</th><th style="padding:5px 8px;">Branch</th><th style="padding:5px 8px;">Bill No.</th><th style="padding:5px 8px;">Bill Date</th><th style="padding:5px 8px;">LR No.</th><th style="padding:5px 8px;">LR Date</th><th style="padding:5px 8px;">From</th><th style="padding:5px 8px;">Destination</th><th class="right" style="padding:5px 8px;">Deduction</th><th class="right" style="padding:5px 8px;">TDS</th><th class="right" style="padding:5px 8px;">Amount</th>
        </tr></thead>
        <tbody>
          ${body}
          <tr style="font-weight:800;background:#f1f5f9;">
            <td colspan="8" class="right" style="padding:6px 8px;">TOTAL</td>
            <td class="right" style="padding:6px 8px;">${inr(sumDed)}</td>
            <td class="right" style="padding:6px 8px;">${inr(sumTds)}</td>
            <td class="right" style="padding:6px 8px;">${inr(sumAmt)}</td>
          </tr>
          <tr style="font-weight:800;background:#e2e8f0;">
            <td colspan="8" class="right" style="padding:6px 8px;">NET AMOUNT RECEIVED</td>
            <td colspan="3" class="right" style="padding:6px 8px;">${inr(net)}</td>
          </tr>
        </tbody>
      </table>
      ${r.narration ? `<div class="words">${esc(r.narration)}</div>` : ""}
      <div style="display:flex;justify-content:space-between;margin-top:44px;text-align:center;">
        <div style="width:200px;">
          <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
          <div style="margin-top:40px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Branch Manager</div>
        </div>
        <div style="width:200px;">
          <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
          <div style="margin-top:40px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Cashier</div>
        </div>
        <div style="width:220px;">
          <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
          <div style="height:52px;display:flex;align-items:flex-end;justify-content:center;">${signatureImg(40)}</div>
          <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Authorised Signatory</div>
        </div>
      </div>
    `;
    open(`Money-Receipt-${String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "Blank"}`, mrCopyPages(inner, copies), { hideFooter: true, hideHeader: true });
    return;
  }
  open(`Money-Receipt-${String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "Blank"}`, mrCopyPages(mrSingleInner(r), copies), { hideFooter: true, hideHeader: true });
}

export function printMoneyReceipts(list, copies = MR_COPIES) {
  const arr = (list || []).filter(Boolean);
  if (!arr.length) return;
  open("Money Receipts", arr.map((r) => mrCopyPages(mrSingleInner(r), copies)).join(""), { hideFooter: true, hideHeader: true });
}

function mrSingleInner(r) {
  const blank = !!r.blank;
  const charges = [
    ["Freight Charge", r.freight_charge],
    ["Hamali Charge", r.hamali_charge],
    ["ODC Charge", r.odc_charge],
    ["M.R. Charges", r.mr_charges],
    ["On Account", r.on_account],
    ["Part Payment", r.part_payment],
  ];
  const sum = charges.reduce((s, [, v]) => s + (Number(v) || 0), 0) + (Number(r.charge) || 0) + (Number(r.other_charges) || 0);
  const tds = Number(r.tds) || 0;
  const deduction = Number(r.deduction) || 0;
  // Fallback: if no breakdown entered, show the received amount as Freight Charge (TDS/deduction are subtracted from it, never added)
  let gross = sum;
  if (sum <= 0) {
    gross = Number(r.amount) || 0;
    charges[0][1] = gross;
  }
  const total = gross - tds - deduction;
  const charge = (label, val) =>
    `<tr><td style="padding:5px 10px;">${esc(label)}</td>
       <td class="right" style="padding:5px 10px;border-left:1px solid #e2e8f0;width:150px;">${val ? inr(val) : ""}</td></tr>`;
  const lessLine = (label, val) =>
    `<tr><td style="padding:5px 10px;">${esc(label)}</td>
       <td class="right" style="padding:5px 10px;border-left:1px solid #e2e8f0;width:150px;">(${inr(val)})</td></tr>`;
  const dash = (val) =>
    `<span style="border-bottom:${blank ? "2px solid #0f172a" : "1px dotted #64748b"};padding:0 6px;${blank ? "display:inline-block;min-width:130px;min-height:16px;" : ""}">${esc(val || "")}</span>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONEY RECEIPT</div>
    <div style="display:flex;justify-content:space-between;margin:12px 0;font-size:13px;">
      <div><b>MR No.</b> ${dash((r.mr_no || "").replace(/^MR[-\s]?/i, ""))}</div>
      <div><b>MR Date:</b> ${dash(r.date ? fmtDate(r.date) : "")}</div>
    </div>
    <div style="display:flex;justify-content:space-between;font-size:13px;margin:6px 0;">
      <div><b>Branch:</b> ${dash(r.office || "Chennai")}</div>
      <div><b>Customer Code:</b> ${dash(r.customer_code || "")}</div>
    </div>
    <div style="border-bottom:1px solid #0f172a;margin:8px 0;"></div>
    <div style="font-size:13px;line-height:2.1;margin:10px 0;">
      Received with Thanks from ${dash(r.customer_name)}
      <br/>A sum of Rupees ${dash(blank ? "" : amountInWords(total).replace(/ Rupees Only$/i, " Only"))}
      <br/>Against our Bill No. ${dash(String(r.against_invoice_no || "").replace(/^INV[-\s]?/i, ""))} ${r.bill_date ? "Bill Date: " + dash(fmtDate(r.bill_date)) : ""} &nbsp; Pertaining our LR No. ${dash(r.against_cn_no)} ${r.cn_date ? "LR Date: " + dash(fmtDate(r.cn_date)) : ""}
      <br/>Payment Received Through ${dash(r.payment_mode || "Cash")} ${r.payment_date ? " On " + dash(fmtDate(r.payment_date)) : ""} ${r.reference || r.cheque_no ? "&nbsp;&nbsp; Cheque No. " + dash(r.reference || r.cheque_no) : ""} ${r.cheque_date ? " Cheque Date: " + dash(fmtDate(r.cheque_date)) : ""} ${r.bank_name ? " Bank Name: " + dash(r.bank_name) : ""} &nbsp;&nbsp; Received Amount ${dash(blank ? "" : inr(total))}
    </div>
    <div style="display:flex;gap:24px;font-size:12px;margin:6px 0;">
      <div style="flex:1;">From ${dash(r.from_place)}</div>
      <div style="flex:1;">To ${dash(r.to_place)}</div>
    </div>
    <div style="border-bottom:1px solid #0f172a;margin:8px 0 4px;"></div>
    <table style="margin-top:8px;">
      <thead><tr><th style="padding:6px 10px;">Particulars</th><th class="right" style="padding:6px 10px;width:150px;">Rs.&nbsp;&nbsp;P.</th></tr></thead>
      <tbody>
        ${charges.map(([l, v]) => charge(l, v)).join("")}
        ${tds > 0 ? lessLine(r.tds_pct ? `Less: TDS @ ${r.tds_pct}%` : "Less: TDS", tds) : ""}
        ${deduction > 0 ? lessLine("Less: Deduction", deduction) : ""}
        <tr style="font-weight:800;background:#f1f5f9;"><td style="padding:6px 10px;">Amount in Words :- ${blank ? "" : esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</td>
          <td class="right" style="padding:6px 10px;border-left:1px solid #e2e8f0;">${blank ? "" : inr(total)}</td></tr>
      </tbody>
    </table>
    <div style="border-bottom:1px solid #0f172a;margin:6px 0 0;"></div>
    ${r.narration ? `<div class="words">${esc(r.narration)}</div>` : ""}
    <div style="border-bottom:1px solid #0f172a;margin:16px 0 0;"></div>
    <div style="display:flex;justify-content:space-between;margin-top:24px;text-align:center;gap:16px;">
      <div style="width:30%;">
        <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
        <div style="margin-top:40px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Cashier</div>
      </div>
      <div style="width:30%;">
        <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
        <div style="margin-top:40px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Branch Manager</div>
      </div>
      <div style="width:30%;">
        <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
        <div style="height:52px;display:flex;align-items:flex-end;justify-content:center;">${signatureImg(40)}</div>
        <div style="border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Authorised Signatory</div>
      </div>
    </div>
  `;
  return inner;
}

export function printBillSubmission(s) {
  const dash = (val) => `<span style="border-bottom:1px dotted #64748b;padding:0 6px;">${esc(val || "")}</span>`;
  if (s.bills && s.bills.length) {
    const bills = s.bills;
    const totalAmt = bills.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const body = bills
      .map(
        (b, i) => `<tr>
        <td style="padding:4px 8px;">${i + 1}</td>
        <td style="padding:4px 8px;">${esc(b.branch || "—")}</td>
        <td style="padding:4px 8px;">${esc(b.cn_no || "—")}</td>
        <td style="padding:4px 8px;">${b.cn_date ? fmtDate(b.cn_date) : "—"}</td>
        <td style="padding:4px 8px;">${esc(b.from_place || "—")}</td>
        <td style="padding:4px 8px;">${esc(b.destination || "—")}</td>
        <td style="padding:4px 8px;">${esc((b.bill_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
        <td style="padding:4px 8px;">${b.bill_date ? fmtDate(b.bill_date) : "—"}</td>
        <td class="right" style="padding:4px 8px;">${inr(Number(b.amount) || 0)}</td>
      </tr>`
      )
      .join("");
    const inner2 = `
      <div class="title" style="letter-spacing:3px;">BILL SUBMISSION SLIP</div>
      <div style="display:flex;justify-content:space-between;margin:12px 0;font-size:13px;">
        <div>
          <div><b>Submission No.:</b> ${dash((s.submission_no || "").replace(/^BS[-\s]?/i, ""))}</div>
          <div style="margin-top:6px;"><b>Customer Code:</b> ${dash(s.customer_code)}</div>
        </div>
        <div style="text-align:right;">
          <div><b>Date:</b> ${dash(fmtDate(s.date))}</div>
          <div style="margin-top:6px;"><b>Branch:</b> ${dash(s.office || (s.bills && s.bills[0] && s.bills[0].branch) || "")}</div>
        </div>
      </div>
      <div style="border-bottom:1.5px solid #0f172a;margin:0 0 10px;"></div>
      <div style="font-size:13px;margin:6px 0;"><b>Submitted To (Customer Name):</b> ${dash(s.customer_name)}</div>
      <div style="font-size:12px;margin:4px 0;"><b>Customer Address:</b> ${dash(s.customer_address)}</div>
      <div style="border-bottom:1.5px solid #0f172a;margin:4px 0 8px;"></div>
      <table style="margin-top:8px;font-size:11px;">
        <thead><tr>
          <th style="padding:5px 8px;">Sl</th><th style="padding:5px 8px;">Branch</th><th style="padding:5px 8px;">LR No.</th><th style="padding:5px 8px;">LR Date</th><th style="padding:5px 8px;">From</th><th style="padding:5px 8px;">Destination</th><th style="padding:5px 8px;">Bill No.</th><th style="padding:5px 8px;">Bill Date</th><th class="right" style="padding:5px 8px;">Bill Amount</th>
        </tr></thead>
        <tbody>
          ${body}
          <tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" style="padding:6px 8px;text-transform:uppercase;font-size:10px;border-top:2px solid #0f172a;border-bottom:2px solid #0f172a;">Amount in Words: ${esc(amountInWords(totalAmt).replace(/ Rupees Only$/i, " Only"))}</td><td class="right" style="padding:6px 8px;white-space:nowrap;border-top:2px solid #0f172a;border-bottom:2px solid #0f172a;">TOTAL DUE AMOUNT</td><td class="right" style="padding:6px 8px;white-space:nowrap;border-top:2px solid #0f172a;border-bottom:2px solid #0f172a;">${inr(totalAmt)}</td></tr>
        </tbody>
      </table>
      ${s.remarks ? `<div class="words">Remarks: ${esc(s.remarks)}</div>` : ""}
      <div style="display:flex;justify-content:space-between;margin-top:56px;text-align:center;font-size:11px;color:#475569;">
        <div style="border-top:1px solid #94a3b8;padding-top:6px;width:40%;">Received By (Customer) with Seal &amp; Signature</div>
        <div style="border-top:1px solid #94a3b8;padding-top:6px;width:40%;">For ${esc(COMPANY.name)}</div>
      </div>
    `;
    open(`Bill Submission ${String(s.submission_no || "").replace(/^BS[-\s]?/i, "")}`, inner2, { frame: true });
    return;
  }
  const inner = `
    <div class="title" style="letter-spacing:3px;">BILL SUBMISSION SLIP</div>
    <div style="display:flex;justify-content:space-between;margin:12px 0;font-size:13px;">
      <div>
        <div><b>Submission No.:</b> ${dash(String(s.submission_no || "").replace(/^BS[-\s]?/i, ""))}</div>
        <div style="margin-top:6px;"><b>Customer Code:</b> ${dash(s.customer_code)}</div>
      </div>
      <div style="text-align:right;">
        <div><b>Date:</b> ${dash(fmtDate(s.date))}</div>
        <div style="margin-top:6px;"><b>Branch:</b> ${dash(s.office || "")}</div>
      </div>
    </div>
    <div style="border-bottom:1.5px solid #0f172a;margin:0 0 10px;"></div>
    <table style="margin-top:8px;">
      <tbody>
        <tr><td style="width:38%;font-weight:600;background:#f8fafc;">Submitted To (Customer Name)</td><td>${esc(s.customer_name || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Customer Code</td><td>${esc(s.customer_code || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Customer Address</td><td>${esc(s.customer_address || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Bill No.</td><td>${esc(s.invoice_no || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Bill Date</td><td>${s.bill_date ? esc(fmtDate(s.bill_date)) : "—"}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">LR No.</td><td>${esc(s.cn_no || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">LR Date</td><td>${s.cn_date ? esc(fmtDate(s.cn_date)) : "—"}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Route</td><td>${esc(s.from_place || "—")} To ${esc(s.destination || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Bill Amount</td><td><b>${inr(s.amount)}</b></td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Received By</td><td>${esc(s.received_by || "—")}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Expected Payment Date</td><td>${s.expected_date ? esc(fmtDate(s.expected_date)) : "—"}</td></tr>
        <tr><td style="font-weight:600;background:#f8fafc;">Status</td><td>${esc(s.status || "Submitted")}</td></tr>
      </tbody>
    </table>
    <div class="words"><b>Amount in Words:</b> ${esc(amountInWords(s.amount).replace(/ Rupees Only$/i, " Only"))}</div>
    ${s.remarks ? `<div class="words">Remarks: ${esc(s.remarks)}</div>` : ""}
    <div style="display:flex;justify-content:space-between;margin-top:56px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:40%;">Received By (Customer) with Seal &amp; Signature</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:40%;">For ${esc(COMPANY.name)}</div>
    </div>
  `;
  open(`Bill Submission ${String(s.submission_no || "").replace(/^BS[-\s]?/i, "")}`, inner, { frame: true });
}

export function printCallOutcomeReport(rows, monthLabel, broken) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.customer || "—")}</td>
      <td class="right">${r.calls}</td>
      <td>${esc(Object.entries(r.outcomes || {}).map(([o, n]) => (n > 1 ? `${o} ×${n}` : o)).join(", ") || "—")}</td>
      <td style="white-space:nowrap;">${r.last_date ? fmtDateDay(r.last_date) : "—"}</td>
      <td class="right">${inr(r.pending)}</td>
    </tr>`
    )
    .join("");
  const totalCalls = arr.reduce((s, r) => s + (r.calls || 0), 0);
  const brokenBody = (broken || [])
    .map(
      (b, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(b.customer)}</td>
      <td>${esc(b.sub_no)}</td>
      <td style="white-space:nowrap;">${b.promised_on ? fmtDateDay(b.promised_on) : "—"}</td>
      <td style="white-space:nowrap;">${b.expected_by ? fmtDateDay(b.expected_by) : "—"}</td>
      <td class="right">${inr(b.amount)}</td>
      <td class="right" style="font-weight:700;color:#be123c;">${b.days}d</td>
    </tr>`
    )
    .join("");
  const brokenSection = (broken || []).length
    ? `
    <div style="font-size:11px;font-weight:800;letter-spacing:1px;margin:16px 0 4px;color:#be123c;">BROKEN PROMISES — FOR MANAGEMENT REVIEW</div>
    <table style="font-size:11px;">
      <thead><tr><th>Sl</th><th>Customer</th><th>Sub. No.</th><th>Promised On</th><th>Expected By</th><th class="right">Amount Pending</th><th class="right">Overdue</th></tr></thead>
      <tbody>${brokenBody}</tbody>
    </table>`
    : "";
  const inner = `
    <div class="title" style="letter-spacing:3px;">CALL OUTCOME REPORT</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Customer</th><th class="right">Calls</th><th>Outcomes</th><th>Last Call</th><th class="right">Pending Amount</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="6" class="muted">No calls logged.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL</td><td class="right">${totalCalls}</td><td colspan="3"></td></tr>
      </tbody>
    </table>
    ${brokenSection}
  `;
  open(`Call Outcome Report — ${monthLabel || ""}`, inner);
}


export function printBillSubmissionRegister(rows, opts = {}) {
  const arr = rows || [];
  const title = opts.title || "BILL SUBMISSION REGISTER";
  const body = arr
    .map(
      (s, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc((s.submission_no || "").replace(/^BS[-\s]?/i, "") || "—")}</td>
      <td>${s.date ? fmtDate(s.date) : "—"}</td>
      <td>${esc((s.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
      <td>${s.bill_date ? fmtDate(s.bill_date) : "—"}</td>
      <td>${esc(s.customer_name || "—")}</td>
      <td class="right">${s.amount ? inr(s.amount) : "—"}</td>
      <td>${esc(s.submitted_to || "—")}</td>
      <td>${s.expected_date ? fmtDate(s.expected_date) : "—"}</td>
      <td>${esc(s.status || "—")}</td>
    </tr>`
    )
    .join("");
  const totalAmount = arr.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">${esc(title)}</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Total Submissions:</b> ${arr.length} &nbsp;&nbsp; <b>Total Amount:</b> ${inr(totalAmount)}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>SL No.</th><th>Sub. No.</th><th>Submission Date</th><th>Bill No.</th><th>Bill Date</th><th>Customer Name</th><th class="right">Amount</th><th>Submitted To</th><th>Expected</th><th>Status</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="10" class="muted">No submissions.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">TOTAL</td><td class="right">${inr(totalAmount)}</td><td></td><td></td><td></td></tr>
      </tbody>
    </table>
  `;
  open(title === "BILL SUBMISSION REGISTER" ? "Bill Submission Register" : title, inner);
}


export function printStatement(data) {
  const cust = data.customer || {};
  const rows = (data.entries || [])
    .map(
      (e) => `<tr>
        <td>${fmtDate(e.date)}</td>
        <td>${esc(e.ref || "—")}</td>
        <td>${esc(e.particulars || "—")}</td>
        <td class="right">${e.debit ? inr(e.debit) : ""}</td>
        <td class="right">${e.credit ? inr(e.credit) : ""}</td>
        <td class="right">${inr(e.balance)}</td>
      </tr>`
    )
    .join("");
  const stmtDate = fmtDate(new Date().toISOString());
  const inner = `
    <div class="title" style="letter-spacing:3px;">LETTER OF ACCOUNT</div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin:12px 0;">
      <div style="font-size:12px;">
        <div style="text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Account Of</div>
        <div style="font-size:14px;font-weight:700;">M/S. ${esc((cust.name || "—").toUpperCase())}</div>
        ${cust.address ? `<div class="muted">${esc(cust.address)}</div>` : ""}
        ${cust.gstin ? `<div class="muted">GSTIN: ${esc(cust.gstin)}</div>` : ""}
        <div class="muted">${esc(cust.phone || "")} ${esc(cust.email || "")}</div>
      </div>
      <div style="font-size:13px;text-align:right;">
        <div><b>Date:</b> ${stmtDate}</div>
        <div style="margin-top:4px;"><b>Office / Branch:</b> ${esc(data.office || BILL_INFO.branch)}</div>
      </div>
    </div>

    <table>
      <thead><tr>
        <th>Date</th><th>Bill / LR No.</th><th>On Account</th>
        <th class="right">Debit</th><th class="right">Credit</th><th class="right">Balance</th>
      </tr></thead>
      <tbody>
        <tr style="background:#f8fafc;font-weight:600;">
          <td colspan="5">Opening Balance</td>
          <td class="right">${inr(0)}</td>
        </tr>
        ${rows || `<tr><td colspan="6" class="muted">No transactions</td></tr>`}
        <tr style="font-weight:700;background:#f1f5f9;">
          <td colspan="3">Total</td>
          <td class="right">${inr(data.total_debit)}</td>
          <td class="right">${inr(data.total_credit)}</td>
          <td class="right"></td>
        </tr>
      </tbody>
    </table>

    <div class="totals" style="width:320px;">
      <div class="row"><span class="muted">Total Debit (Billed)</span><span>${inr(data.total_debit)}</span></div>
      <div class="row"><span class="muted">Total Credit (Received)</span><span>${inr(data.total_credit)}</span></div>
      <div class="row grand"><span>Closing Balance</span><span>${inr(data.closing_balance)}</span></div>
    </div>
    <div class="words" style="text-transform:uppercase;"><b>Amount in Words:</b> ${esc(amountInWords(data.closing_balance).replace(/ Rupees Only$/i, " Only"))}</div>
    <p style="font-size:12px;color:#334155;margin-top:12px;">Kindly arrange the payment of the outstanding balance at the earliest. For any discrepancy, please contact our accounts department.</p>

    <div style="display:flex;justify-content:space-between;margin-top:56px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:22%;">Approved By</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:22%;">Passed By</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:22%;">Cashier</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:22%;">Prepared By</div>
    </div>
    <div style="text-align:right;margin-top:16px;font-size:12px;font-weight:700;">For ${esc(COMPANY.name)}</div>
  `;
  open(`Letter of Account - ${cust.name || ""}`, inner);
}

export const LR_COPIES = ["CONSIGNEE COPY", "CONSIGNOR COPY", "ACCOUNT COPY", "LORRY COPY", "FILE COPY"];

function manualLrInner(c, copyLabel, duplicate = false, podPortalLink = "") {
  const chn = String(c.lr_no || c.consignment_no || "").replace(/^CN[-\s]?/i, "");
  const num = (v) => (v && Number(v) ? inr(v) : "");
  const invLines = [{ no: c.invoice_no, date: c.invoice_date, value: c.invoice_value }, ...(c.extra_invoices || [])].filter((x) => x && (x.no || x.date || Number(x.value)));
  const dcLines = [{ no: c.dc_no, date: c.dc_date, value: c.dc_value }, ...(c.extra_dcs || [])].filter((x) => x && (x.no || x.date || Number(x.value)));
  const invTotal = invLines.reduce((s, x) => s + (Number(x.value) || 0), 0);
  const hamali = (Number(c.loading_charge) || 0) + (Number(c.unloading_charge) || 0);
  const surch = Number(c.halting_charge) || 0;
  const stch = (Number(c.odc_charge) || 0) + (Number(c.demurrage_charge) || 0);
  const fov = Number(c.fov_charge) || 0;
  const grand = (Number(c.freight_amount) || 0) + hamali + surch + stch + fov;
  const basis = String(c.basis || "").toLowerCase();
  const gstBy = String(c.gst_payable_by || "").toLowerCase();
  const modeOp = String(c.mode_of_operation || "road").toLowerCase();
  const tick = (on) => `<span style="display:inline-block;width:12px;height:12px;border:1.2px solid #0f172a;text-align:center;line-height:11px;font-size:10px;font-weight:800;margin-right:3px;vertical-align:-2px;">${on ? "✓" : "&nbsp;"}</span>`;
  const dot = (v, w) => `<span style="display:inline-block;min-width:${w || 60}px;border-bottom:1px dotted #64748b;font-weight:700;padding:0 2px;">${v ? esc(String(v)) : "&nbsp;"}</span>`;
  const boxHead = (t) => `<div style="font-weight:800;font-size:9.5px;letter-spacing:1px;text-align:center;border-bottom:1px solid #0f172a;padding:2px;background:#f1f5f9;">${t}</div>`;
  const cell = "border:1px solid #0f172a;padding:0;";
  return `<div class="challan-page" style="position:relative;">${duplicate ? `<div style="position:absolute;top:45%;left:50%;transform:translate(-50%,-50%) rotate(-28deg);font-size:46px;font-weight:900;letter-spacing:8px;color:rgba(100,116,139,0.18);white-space:nowrap;z-index:999;pointer-events:none;">DUPLICATE COPY</div>` : ""}<div class="frame" style="font-size:10px;padding:10px 12px;line-height:1.35;">
    <table style="border-collapse:collapse;width:100%;margin:0;"><tr>
      <td style="border:none;width:24%;vertical-align:top;font-size:9px;padding:0;">
        <div><b>${esc(COMPANY.gstin)}</b></div>
        <div><b>${esc(COMPANY.pan)}</b></div>
        <div><b>${esc(COMPANY.msme || "MSME No.: ")}</b>${COMPANY.msme && COMPANY.msme.trim().endsWith(":") ? dot("", 90) : ""}</div>
        <div style="margin-top:2px;">E-Way Bill No.: ${dot(c.eway_bill, 90)}</div>
        <div style="margin-top:2px;">E-Way Bill Valid: ${dot(c.eway_valid ? fmtDate(c.eway_valid) : "", 80)}</div>
        <div style="margin-top:2px;">Vehicle No.: <b>${esc(c.vehicle_no || "—")}</b></div>
      </td>
      <td style="border:none;width:52%;text-align:center;vertical-align:top;padding:0;">
        <div style="display:flex;align-items:center;justify-content:center;gap:8px;">
          <img src="${LOGO_DATA}" alt="NTC" style="height:46px;width:46px;object-fit:contain;flex-shrink:0;" />
          <div>
            <div style="font-size:19px;font-weight:900;letter-spacing:1px;">${esc(COMPANY.name.toUpperCase())}</div>
            <div style="font-size:9px;">${esc(COMPANY.address)}</div>
            ${(() => { try { const b = c.office || c.office_name; const a = (JSON.parse(localStorage.getItem("ntc_branch_addresses") || "{}") || {})[b]; const p = (JSON.parse(localStorage.getItem("ntc_branch_phones") || "{}") || {})[b]; return a || p ? `<div style="font-size:9px;font-weight:700;">${esc(b)} Branch: ${esc(a || "")}${a && p ? " · " : ""}${p ? `Ph: ${esc(p)}` : ""}</div>` : ""; } catch { return ""; } })()}
            <div style="font-size:9px;">${esc(COMPANY.email)} &nbsp;·&nbsp; Web: ${esc(COMPANY.website)} &nbsp;·&nbsp; ${esc(COMPANY.phone)}</div>
          </div>
          <img src="${QR_DATA}" alt="QR" data-testid="lr-print-qr" style="height:58px;width:58px;object-fit:contain;flex-shrink:0;" />
        </div>
      </td>
      <td style="border:none;width:24%;vertical-align:top;text-align:right;padding:0;">
        <div style="display:inline-block;border:1.5px solid #0f172a;border-radius:4px;padding:2px 10px;font-weight:800;font-size:9.5px;letter-spacing:1px;">${esc(copyLabel || "CONSIGNMENT NOTE")}</div>
        <div style="margin-top:5px;font-size:11px;font-weight:800;">CONSIGNMENT NOTE</div>
        <div style="margin-top:2px;">CHN No. <span style="color:#dc2626;font-size:14px;font-weight:900;">${esc(chn || "—")}</span></div>
        <div>Date: <b>${c.lr_date ? fmtDate(c.lr_date) : "—"}</b></div>
        <div>Branch: <b>${esc(c.office || c.office_name || "—")}</b></div>
      </td>
    </tr></table>

    <table style="border-collapse:collapse;width:100%;margin:6px 0 0;table-layout:fixed;">
      <tr>
        <td style="${cell}width:33%;vertical-align:top;">
          ${boxHead("NOTICE")}
          <div style="padding:4px 5px;font-size:8px;text-align:justify;">The Consignment covered by this set of special Lorry Receipt form shall be stored at the destination under the control of the Transport Operator and shall be delivered to or to the order of the Consignee Bank whose name is mentioned in the Lorry Receipt. It will under no circumstances be delivered to anyone without the written authority from the Consignee Bank or its order, endorsed on the Consignee Copy or on a separate Letter of Authority.</div>
        </td>
        <td style="${cell}width:34%;vertical-align:top;">
          ${boxHead("AT OWNER'S RISK & CARRIER RISK")}
          <div style="padding:3px 5px;font-size:9px;">
            <div style="font-weight:800;text-align:center;letter-spacing:1px;">INSURANCE</div>
            <div style="font-size:8.5px;">The customer has stated that he ${c.insurance_policy_no || c.insurance_company ? "has" : "has / has not"} insured the consignment.</div>
            <div style="margin-top:2px;">Company: ${dot(c.insurance_company || "", 120)}</div>
            <div style="margin-top:2px;">Policy No.: ${dot(c.insurance_policy_no || "", 60)} Date: ${dot(c.insurance_date ? fmtDate(c.insurance_date) : "", 50)}</div>
            <div style="margin-top:2px;">Amount: ${dot(c.insurance_amount && Number(c.insurance_amount) ? inr(c.insurance_amount) : "", 70)} Risk: ${dot(c.insurance_risk || "", 55)}</div>
          </div>
        </td>
        <td style="${cell}width:33%;vertical-align:top;">
          ${boxHead("CAUTION")}
          <div style="padding:4px 5px;font-size:8.5px;text-align:justify;">This Consignment Note is not negotiable. The consignment will be delivered against acknowledgement of the Consignee only. Any complaint regarding shortage or damage must be reported at the time of delivery.</div>
        </td>
      </tr>
    </table>

    <table style="border-collapse:collapse;width:100%;margin:0;table-layout:fixed;">
      <tr>
        <td style="${cell}width:50%;vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">Consignor Name &amp; Address</div>
            <div style="font-size:12px;font-weight:800;">${esc(c.consignor || "")}</div>
            <div style="font-size:9.5px;min-height:13px;">${esc(c.consignor_address || "")}</div>
            <div style="font-size:9px;">GST No.: ${dot(c.consignor_gst || "", 140)}</div>
          </div>
        </td>
        <td style="${cell}width:25%;vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">FROM</div>
            <div style="font-size:12px;font-weight:800;min-height:20px;">${esc(c.origin || "")}</div>
          </div>
        </td>
        <td style="${cell}width:25%;vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">MODE OF OPERATION</div>
            <div style="font-size:9px;margin-top:3px;">${["AIR", "ROAD", "TRAIN", "EXPRESS"].map((m) => `${tick(modeOp.includes(m.toLowerCase()))}${m}`).join(" &nbsp;")}</div>
          </div>
        </td>
      </tr>
      <tr>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">Consignee Name &amp; Address</div>
            <div style="font-size:12px;font-weight:800;">${esc(c.consignee || "")}</div>
            <div style="font-size:9.5px;min-height:13px;">${esc(c.consignee_address || "")}</div>
            <div style="font-size:9px;">GST No.: ${dot(c.consignee_gst || "", 140)}</div>
          </div>
        </td>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">TO</div>
            <div style="font-size:12px;font-weight:800;min-height:20px;">${esc(c.destination || "")}</div>
            ${c.delivery_at ? `<div style="font-size:9px;"><b>Delivery At:</b> ${esc(c.delivery_at)}</div>` : ""}
          </div>
        </td>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;">
            <div style="font-size:8.5px;font-weight:700;color:#475569;">GST PAYABLE BY</div>
            <div style="font-size:9px;margin-top:3px;">${["CONSIGNOR", "CONSIGNEE", "TRANSPORT"].map((g) => `${tick(gstBy.includes(g.toLowerCase().slice(0, 8)))}${g}`).join("<br/>")}</div>
          </div>
        </td>
      </tr>
      <tr>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;font-size:9px;">
            ${(invLines.length ? invLines : [{}])
              .map((x) => `Invoice No.: <b>${esc(x.no || "—")}</b> &nbsp; Dt: <b>${x.date ? fmtDate(x.date) : "—"}</b> &nbsp; Rs.: <b>${num(x.value) || "—"}</b>`)
              .join("<br/>")}
            ${dcLines.length ? "<br/>" + dcLines.map((x) => `DC No.: <b>${esc(x.no || "—")}</b> &nbsp; Dt: <b>${x.date ? fmtDate(x.date) : "—"}</b> &nbsp; Rs.: <b>${num(x.value) || "—"}</b>`).join("<br/>") : ""}
            ${invTotal ? `<div style="margin-top:2px;font-weight:800;">Total Invoice Value Rs.: ${inr(invTotal)}</div>` : ""}
          </div>
        </td>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;font-size:9px;">Address of delivery office:<br/>${dot(c.office_name || "", 130)}<div style="font-weight:900;font-size:11px;letter-spacing:1px;margin-top:2px;">DOOR DELIVERY</div></div>
        </td>
        <td style="${cell}vertical-align:top;">
          <div style="padding:3px 5px;font-size:9px;">${tick(basis.includes("to pay"))}TO PAY &nbsp; ${tick(basis === "paid" || basis.includes("paid"))}PAID<br/>${tick(basis.includes("bill") || basis.includes("tbb"))}TO BE BILLED</div>
        </td>
      </tr>
    </table>

    <table style="border-collapse:collapse;width:100%;margin:0;table-layout:fixed;font-size:9.5px;">
      <thead>
        <tr style="background:#f1f5f9;">
          <th style="${cell}width:9%;padding:3px;" rowspan="2">Packages</th>
          <th style="${cell}width:25%;padding:3px;" rowspan="2">Description (Said to Contain)</th>
          <th style="${cell}width:10%;padding:3px;" rowspan="2">Weight in Kgs</th>
          <th style="${cell}width:9%;padding:3px;" rowspan="2">Vehicle Type</th>
          <th style="${cell}padding:3px;" colspan="8">FREIGHT (Rs.)</th>
        </tr>
        <tr style="background:#f1f5f9;">
          <th style="${cell}padding:3px;">Freight</th>
          <th style="${cell}padding:3px;">Hamali</th>
          <th style="${cell}padding:3px;">Sur.Ch.</th>
          <th style="${cell}padding:3px;">St.Ch.</th>
          <th style="${cell}padding:3px;">FOV</th>
          <th style="${cell}padding:3px;">CGST</th>
          <th style="${cell}padding:3px;">SGST</th>
          <th style="${cell}padding:3px;">Grand Total</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="${cell}padding:4px;text-align:center;height:48px;vertical-align:top;">${c.packages ? esc(String(c.packages)) : ""}</td>
          <td style="${cell}padding:4px;vertical-align:top;">${esc(c.said_to_contain || c.goods || "")}</td>
          <td style="${cell}padding:4px;text-align:center;font-weight:700;vertical-align:top;">${esc(String(c.weight_kgs || c.weight_tons || "")) !== "0" ? esc(String(c.weight_kgs || c.weight_tons || "")) : ""}</td>
          <td style="${cell}padding:4px;text-align:center;font-weight:800;vertical-align:top;">${esc(c.vehicle_type || c.container_type || "")}</td>
          <td style="${cell}padding:4px;text-align:right;vertical-align:top;">${num(c.freight_amount)}</td>
          <td style="${cell}padding:4px;text-align:right;vertical-align:top;">${num(hamali)}</td>
          <td style="${cell}padding:4px;text-align:right;vertical-align:top;">${num(surch)}</td>
          <td style="${cell}padding:4px;text-align:right;vertical-align:top;">${num(stch)}</td>
          <td style="${cell}padding:4px;text-align:right;vertical-align:top;" data-testid="lr-print-fov">${num(fov)}</td>
          <td style="${cell}padding:4px;vertical-align:top;"></td>
          <td style="${cell}padding:4px;vertical-align:top;"></td>
          <td style="${cell}padding:4px;text-align:right;font-weight:800;vertical-align:top;">${num(grand)}</td>
        </tr>
      </tbody>
    </table>
    ${grand ? `<div style="font-size:9px;margin-top:3px;"><b>Grand Total (in words):</b> ${esc(amountInWords(grand))}</div>` : ""}

    <table style="border-collapse:collapse;width:100%;margin-top:14px;"><tr>
      <td style="border:none;width:33%;font-size:9.5px;vertical-align:bottom;padding:0;">
        <div style="border-top:1px solid #0f172a;display:inline-block;padding-top:3px;min-width:150px;">Signature of Consignor</div>
      </td>
      <td style="border:none;width:33%;font-size:9.5px;text-align:center;vertical-align:bottom;padding:0;">
        <div style="border-top:1px solid #0f172a;display:inline-block;padding-top:3px;min-width:150px;">Signature of Transport Operator</div>
        ${c.created_by_code ? `<div style="font-size:8px;color:#475569;margin-top:2px;">Staff Code: <b>${esc(c.created_by_code)}</b></div>` : ""}
      </td>
      <td style="border:none;width:33%;font-size:9.5px;text-align:right;vertical-align:bottom;padding:0;">
        <div style="font-weight:800;">For ${esc(COMPANY.name.toUpperCase())}</div>
        <div style="margin-top:18px;">Authorised Signatory</div>
        <div style="margin-top:4px;text-align:right;"><span style="display:inline-block;border:1.5px solid #0f172a;border-radius:4px;padding:1px 8px;font-weight:800;font-size:8.5px;letter-spacing:1px;">${esc(copyLabel || "")}</span></div>
      </td>
    </tr></table>
    ${String(copyLabel || "").toUpperCase() === "CONSIGNEE COPY" ? `
    <div style="border:1.5px solid #0f172a;border-radius:4px;margin-top:10px;padding:6px 10px;">
      <div style="font-size:8.5px;font-weight:800;letter-spacing:1px;">DELIVERY ACKNOWLEDGEMENT — Received the above consignment in good condition.</div>
      <table style="border-collapse:collapse;width:100%;margin-top:16px;"><tr>
        <td style="border:none;font-size:9px;width:28%;padding:0;">Name: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:130px;">&nbsp;</span></td>
        <td style="border:none;font-size:9px;width:42%;text-align:center;padding:0;">Consignee Signature &amp; Stamp: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:140px;">&nbsp;</span></td>
        <td style="border:none;font-size:9px;width:30%;text-align:right;padding:0;">Date: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:80px;">&nbsp;</span> Time: <span style="display:inline-block;border-bottom:1px dotted #64748b;min-width:55px;">&nbsp;</span></td>
      </tr></table>
    </div>
    ${c.delivery_otp_confirmed ? `<div style="margin-top:6px;"><span style="display:inline-block;border:2px solid #047857;color:#047857;border-radius:6px;padding:3px 12px;font-size:9px;font-weight:800;letter-spacing:1.5px;transform:rotate(-2deg);">&#10004; DELIVERY CONFIRMED BY CONSIGNEE OTP${c.delivery_otp_confirmed_at ? " — " + fmtDate(String(c.delivery_otp_confirmed_at).slice(0, 10)) : ""}</span></div>` : ""}
    ${podPortalLink ? `<div style="display:flex;align-items:center;gap:8px;margin-top:6px;border:1px solid #cbd5e1;border-radius:4px;padding:4px 8px;">
      <img src="${podPortalLink}/qr?base=${encodeURIComponent(process.env.REACT_APP_BACKEND_URL || "")}" style="width:46px;height:46px;" />
      <div style="font-size:8px;color:#334155;line-height:1.45;"><b>POD PORTAL</b> — Scan the QR to view the signed delivery proof (POD) online once uploaded, along with all delivered consignments.<br/><span style="word-break:break-all;color:#0369a1;font-weight:700;">${esc(podPortalLink)}</span></div>
    </div>` : ""}` : ""}
    ${c.created_by ? `<div style="font-size:8.5px;color:#64748b;margin-top:6px;">Entered by: <b>${esc(c.created_by)}</b>${c.created_by_code ? ` (Staff Code: ${esc(c.created_by_code)})` : ""}${c.office || c.office_name ? ` — ${esc(c.office || c.office_name)}` : ""}</div>` : ""}
  </div></div>`;
}

export async function printConsignment(c, copies = null, opts = {}) {
  if (!c) return;
  const dup = !!opts.duplicate;
  const chn = String(c.lr_no || c.consignment_no || "").replace(/^CN[-\s]?/i, "");
  const needPortal = !!(copies && copies.length && copies.some((cp) => String(cp).toUpperCase() === "CONSIGNEE COPY"));
  const portal = needPortal ? await fetchPodPortal(c.customer_name || c.consignor) : "";
  if (copies && copies.length) {
    open(`Consignment-Note-${chn}`, copies.map((cp) => manualLrInner(c, cp, dup, portal)).join(""), { hideHeader: true });
  } else {
    open(`Consignment-Note-${chn}`, manualLrInner(c, "", dup, portal), { hideHeader: true });
  }
}

export function printConsignmentDetail(c, billing = null) {
  if (!c) return;
  const lr = String(c.lr_no || c.consignment_no || "").replace(/^CN[-\s]?/i, "");
  const b = billing || {};
  const row = (label, value) => `<tr><td style="width:42%;color:#64748b;padding:4px 10px;border-bottom:1px solid #e2e8f0;">${esc(label)}</td><td style="font-weight:600;text-align:right;padding:4px 10px;border-bottom:1px solid #e2e8f0;">${value || "—"}</td></tr>`;
  const money = (v) => (v || v === 0 ? inr(v) : "");
  const left = [
    row("Branch", esc(c.office || c.office_name)),
    row("From", esc(c.origin)),
    row("Destination", esc(c.destination)),
    row("Consignor Name", esc(c.customer_name)),
    row("Consignee Name", esc(c.consignee)),
    row("Goods(Material)", esc(c.goods || c.said_to_contain)),
    row("E-way bill No.", esc(c.eway_bill)),
    row("E-way bill valid", c.eway_valid ? fmtDate(c.eway_valid) : ""),
    row("Packages", esc(c.packages)),
    row("Weight In KGS", esc(c.weight_kgs || c.weight_tons)),
    row("Vehicle Hire", money(b.lorry_hire || c.lorry_hire)),
    row("Vehicle Advance", money(b.vehicle_advance)),
    row("Vehicle Balance", b.vehicle_balance || b.vehicle_balance === 0 ? inr(b.vehicle_balance) : ""),
    row("Challan No.", esc(String(b.challan_no || "").replace(/^VHC[-\s]?/i, ""))),
    row("Challan Date", b.challan_date ? fmtDate(b.challan_date) : ""),
    row("Vendor/Broker Name", esc(b.vendor_broker_name || c.vendor_name)),
  ].join("");
  const right = [
    row("Invoice No.", esc(c.invoice_no)),
    row("Invoice Date", c.invoice_date ? fmtDate(c.invoice_date) : ""),
    row("Invoice value", money(c.invoice_value)),
    ...(c.extra_invoices || []).map((x, i) => row(`Invoice No. ${i + 2}`, `${esc(x.no || "—")} · ${x.date ? fmtDate(x.date) : "—"} · ${money(x.value) || "—"}`)),
    row("DC No.", esc(c.dc_no)),
    row("DC Date", c.dc_date ? fmtDate(c.dc_date) : ""),
    row("DC Value", money(c.dc_value)),
    ...(c.extra_dcs || []).map((x, i) => row(`DC No. ${i + 2}`, `${esc(x.no || "—")} · ${x.date ? fmtDate(x.date) : "—"} · ${money(x.value) || "—"}`)),
    row("Total Invoice Value", money((Number(c.invoice_value) || 0) + (c.extra_invoices || []).reduce((s, x) => s + (Number(x.value) || 0), 0))),
    row("Freight Charge", money(c.freight_amount)),
    row("Vehicle No.", esc(c.vehicle_no)),
    row("Vehicle Type", esc(c.vehicle_type)),
    row("Driver Mobile No.", esc(c.driver_mobile)),
    row("Dispatch Date", c.dispatch_date ? fmtDate(c.dispatch_date) : ""),
    row("Delivery date", c.delivery_date ? fmtDate(c.delivery_date) : ""),
    row("Delivered by", esc(c.delivered_by)),
    row("Vehicle Status", esc(c.status)),
    row("Remarks", esc(c.remarks)),
  ].join("");
  const billRows = [
    row("Bill No.", esc(String(b.bill_no || "").replace(/^INV[-\s]?/i, ""))),
    row("Bill Date", b.bill_date ? fmtDate(b.bill_date) : ""),
    row("Bill Amount", money(b.bill_amount)),
  ].join("");
  const mrRows = [
    row("MR No.", esc(String(b.mr_no || "").replace(/^MR[-\s]?/i, ""))),
    row("MR Date", b.mr_date ? fmtDate(b.mr_date) : ""),
    row("MR Amount", money(b.mr_amount)),
  ].join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CONSIGNMENT DETAIL SHEET</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:13px;font-weight:800;text-align:center;letter-spacing:0.5px;">Consignment No. ${esc(lr)}${c.lr_date || c.dispatch_date ? ` &nbsp;·&nbsp; LR Date: ${fmtDate(c.lr_date || c.dispatch_date)}` : ""}</div>
    <div style="display:flex;gap:16px;font-size:11px;">
      <table style="flex:1;border-collapse:collapse;"><tbody>${left}</tbody></table>
      <table style="flex:1;border-collapse:collapse;"><tbody>${right}</tbody></table>
    </div>
    <div style="display:flex;gap:16px;margin-top:14px;font-size:11px;font-weight:800;letter-spacing:1px;">
      <div style="flex:1;">BILLING</div>
      <div style="flex:1;">MONEY RECEIPTS (MR)</div>
    </div>
    <div style="display:flex;gap:16px;font-size:11px;margin-top:4px;">
      <table style="flex:1;border-collapse:collapse;"><tbody>${billRows}</tbody></table>
      <table style="flex:1;border-collapse:collapse;"><tbody>${mrRows}</tbody></table>
    </div>
    <div style="margin-top:16px;text-align:right;font-size:10px;color:#475569;">Printed on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
  `;
  open(`Consignment-Detail-${lr}`, inner + (Array.isArray(c.edit_log) && c.edit_log.length
    ? `<div style="margin-top:14px;font-size:10px;color:#64748b;"><b style="letter-spacing:1px;">UPDATE HISTORY:</b> ${c.edit_log.slice(-5).map((e) => `${esc(e.by || "—")} on ${fmtDate(String(e.at || "").slice(0, 10))}${e.note ? ` (${esc(e.note)})` : ""}`).join(" · ")}</div>`
    : ""));
}

export function printConsignmentRegister(rows, fromDate, toDate, title = "CONSIGNMENT REGISTER", meta = null) {
  let arr = rows || [];
  const dayTot = !!(meta && meta.dayTotals);
  const dkOf = (r) => String(r.lr_date || r.dispatch_date || "").slice(0, 10);
  if (dayTot) arr = [...arr].sort((a, b) => dkOf(a).localeCompare(dkOf(b)));
  const period =
    fromDate || toDate
      ? `PERIOD: ${fromDate ? fmtDate(fromDate) : "Beginning"} TO ${toDate ? fmtDate(toDate) : "Today"}`
      : "ALL RECORDS";
  const statusStyle = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "delivered") return "background:#d1fae5;color:#065f46;font-weight:700;";
    if (v === "cancelled") return "background:#fee2e2;color:#991b1b;font-weight:700;";
    if (v.includes("transit")) return "background:#dbeafe;color:#1e40af;font-weight:700;";
    if (v) return "background:#fef3c7;color:#92400e;font-weight:700;";
    return "";
  };
  const bk = !!(meta && meta.bookingCols);
  const rowHtml = (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.office_name || r.office || "—")}</td>
      <td>${esc(r.lr_no || r.consignment_no || "—")}</td>
      <td>${r.lr_date || r.dispatch_date ? fmtDateDay(r.lr_date || r.dispatch_date) : "—"}</td>
      <td>${esc(r.origin || "—")}</td>
      <td>${esc(r.destination || "—")}</td>
      <td>${esc(r.consignor || "—")}</td>
      <td>${esc(r.consignee || "—")}</td>
      <td>${esc(r.customer_name ? `${r.customer_name}${r.customer_code ? ` (${r.customer_code})` : ""}` : "—")}</td>
      ${bk ? `<td>${esc(r.basis || "—")}</td><td>${esc(r.basis === "PAID" ? r.payment_mode || "—" : "—")}</td><td>${esc(r.payment_mode === "DD" || r.payment_mode === "Cheque" ? [r.cheque_no ? `No. ${r.cheque_no}` : "", r.cheque_date ? fmtDate(r.cheque_date) : "", Number(r.cheque_amount) ? inr(r.cheque_amount) : "", r.cheque_bank || ""].filter(Boolean).join(" · ") || "—" : "—")}</td>` : ""}
      <td>${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.vehicle_type || "—")}</td>
      ${bk ? `<td>${esc(r.packages || "—")}</td><td>${esc(r.invoice_no || "—")}</td><td>${r.invoice_date ? fmtDate(r.invoice_date) : "—"}</td><td class="right">${Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</td>` : ""}
      <td class="right">${Number(r.dc_value) ? inr(r.dc_value) : "—"}</td>
      <td class="right">${r.freight_amount ? inr(r.freight_amount) : "—"}</td>
      ${bk ? `<td>${esc(r._bill_no || "—")}</td><td>${r._bill_date ? fmtDate(r._bill_date) : "—"}</td><td class="right">${r._bill_amount || r._bill_amount === 0 ? inr(r._bill_amount) : "—"}</td>` : ""}
      <td class="right">${r.vehicle_hire ? inr(r.vehicle_hire) : "—"}</td>
      <td class="right">${r.vehicle_advance ? inr(r.vehicle_advance) : "—"}</td>
      <td class="right">${r.vehicle_balance || r.vehicle_balance === 0 ? inr(r.vehicle_balance) : "—"}</td>
      <td class="right">${r.balance_amount ? inr(r.balance_amount) : "—"}</td>
      ${bk ? `<td>${r.delivery_date ? fmtDateDay(r.delivery_date) : "—"}</td>` : ""}
      <td style="${statusStyle(r.status)}">${esc(r.status || "—")}</td>
    </tr>`;
  const totalFreight = arr.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
  const totalDcValue = arr.reduce((s, r) => s + (Number(r.dc_value) || 0), 0);
  const totalHire = arr.reduce((s, r) => s + (Number(r.vehicle_hire) || 0), 0);
  const totalAdv = arr.reduce((s, r) => s + (Number(r.vehicle_advance) || 0), 0);
  const totalVBal = arr.reduce((s, r) => s + (Number(r.vehicle_balance) || 0), 0);
  const totalBillAmt = (() => {
    const seen = new Set();
    return arr.reduce((s, r) => {
      if (!r._bill_key) return s;
      if (seen.has(r._bill_key)) return s;
      seen.add(r._bill_key);
      return s + (Number(r._bill_amount) || 0);
    }, 0);
  })();
  const statusCounts = (() => {
    const c = { delivered: 0, pending: 0, transit: 0, cancelled: 0, other: 0 };
    arr.forEach((r) => {
      const v = String(r.status || "").toLowerCase();
      if (v === "delivered") c.delivered++;
      else if (v === "cancelled") c.cancelled++;
      else if (v.includes("transit")) c.transit++;
      else if (v === "pending" || !v) c.pending++;
      else c.other++;
    });
    return c;
  })();
  const summaryLine = `
    <div style="margin:0 0 12px;display:flex;flex-wrap:wrap;gap:8px;font-size:11px;font-weight:700;">
      <span style="padding:4px 12px;border-radius:999px;background:#f1f5f9;border:1px solid #cbd5e1;">Total: ${arr.length}</span>
      <span style="padding:4px 12px;border-radius:999px;background:#d1fae5;color:#065f46;">Delivered: ${statusCounts.delivered}</span>
      <span style="padding:4px 12px;border-radius:999px;background:#fef3c7;color:#92400e;">Pending: ${statusCounts.pending + statusCounts.other}</span>
      ${statusCounts.transit ? `<span style="padding:4px 12px;border-radius:999px;background:#dbeafe;color:#1e40af;">In Transit: ${statusCounts.transit}</span>` : ""}
      ${statusCounts.cancelled ? `<span style="padding:4px 12px;border-radius:999px;background:#fee2e2;color:#991b1b;">Cancelled: ${statusCounts.cancelled}</span>` : ""}
      ${arr.length ? (() => { const eligible = arr.length - statusCounts.cancelled; const pct = eligible > 0 ? Math.round((statusCounts.delivered / eligible) * 100) : 0; return `<span style="padding:4px 12px;border-radius:999px;background:${pct >= 90 ? "#d1fae5;color:#065f46" : pct >= 60 ? "#fef3c7;color:#92400e" : "#fee2e2;color:#991b1b"};border:1px solid currentColor;">${pct}% Delivered</span>`; })() : ""}
    </div>
    ${(() => {
      const byBranch = {};
      arr.forEach((r) => {
        const b = r.office_name || r.office || "—";
        if (!byBranch[b]) byBranch[b] = { total: 0, delivered: 0, cancelled: 0 };
        byBranch[b].total++;
        const v = String(r.status || "").toLowerCase();
        if (v === "delivered") byBranch[b].delivered++;
        else if (v === "cancelled") byBranch[b].cancelled++;
      });
      const names = Object.keys(byBranch);
      if (names.length < 2) return "";
      const pills = names
        .sort()
        .map((b) => {
          const s = byBranch[b];
          const el = s.total - s.cancelled;
          const pct = el > 0 ? Math.round((s.delivered / el) * 100) : 0;
          const col = pct >= 90 ? "#d1fae5;color:#065f46" : pct >= 60 ? "#fef3c7;color:#92400e" : "#fee2e2;color:#991b1b";
          return `<span style="padding:3px 10px;border-radius:999px;background:${col};">${esc(b)}: ${pct}% (${s.delivered}/${el})</span>`;
        })
        .join("");
      return `<div style="margin:-6px 0 12px;display:flex;flex-wrap:wrap;gap:6px;font-size:10px;font-weight:700;"><span style="padding:3px 10px;border-radius:999px;background:#f1f5f9;border:1px solid #cbd5e1;">Branch delivery:</span>${pills}</div>`;
    })()}
  `;
  const headerHtml = `
    <div class="title" style="letter-spacing:3px;">${esc(title)}</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${period}</div>
    ${summaryLine}
    ${meta && meta.bookingCols ? `<div style="margin:0 0 12px;display:flex;flex-wrap:wrap;gap:8px 20px;font-size:11px;">
      <div><b>Branch:</b> ${esc(meta.branch || "All Branches")}</div>
      <div><b>Customer:</b> ${esc(meta.customer || "All Customers")}</div>
      <div><b>Total Bookings:</b> ${esc(String(meta.total ?? rows.length))}</div>
      <div><b>Delivered:</b> ${esc(String(meta.delivered ?? 0))}</div>
      <div><b>Pending:</b> ${esc(String(meta.pending ?? 0))}</div>
      ${meta.topRoutes && meta.topRoutes.length ? `<div><b>Top Routes:</b> ${esc(meta.topRoutes.map((t) => `${t[0]} (${t[1]})`).join(", "))}</div>` : ""}
    </div>` : ""}`;
  const thead = `<thead><tr>
        <th>Sl</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Consignor</th><th>Consignee</th><th>Customer (Code)</th>${bk ? "<th>Basis</th><th>Payment Mode</th><th>Cheque/DD Details</th>" : ""}<th>Vehicle No.</th><th>Vehicle Type</th>${bk ? '<th>PKG</th><th>Invoice No.</th><th>Invoice Date</th><th class="right">Invoice Value</th>' : ""}<th class="right">DC Value</th><th class="right">Freight</th>${bk ? '<th>Bill No.</th><th>Bill Date</th><th class="right">Bill Amount</th>' : ""}<th class="right">Vehicle Hire</th><th class="right">Vehicle Advance</th><th class="right">Vehicle Balance</th><th class="right">Balance</th>${bk ? "<th>Delivery Date</th>" : ""}<th>Status</th>
      </tr></thead>`;
  const totalRow = `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="${bk ? 18 : 11}" class="right">TOTAL</td><td class="right">${inr(totalDcValue)}</td><td class="right">${inr(totalFreight)}</td>${bk ? `<td></td><td></td><td class="right">${inr(totalBillAmt)}</td>` : ""}<td class="right">${inr(totalHire)}</td><td class="right">${inr(totalAdv)}</td><td class="right">${inr(totalVBal)}</td><td></td>${bk ? "<td></td>" : ""}<td></td></tr>`;
  const PER_PAGE = 20;
  let items;
  if (dayTot) {
    const dayAgg = {};
    arr.forEach((r) => {
      const k = dkOf(r);
      if (!dayAgg[k]) dayAgg[k] = { n: 0, freight: 0 };
      dayAgg[k].n++;
      dayAgg[k].freight += Number(r.freight_amount) || 0;
    });
    const runMap = {};
    let acc = 0;
    Object.keys(dayAgg).sort().forEach((k) => {
      acc += dayAgg[k].freight;
      runMap[k] = acc;
    });
    items = [];
    arr.forEach((r, i) => {
      items.push({ t: "r", r, i });
      if (i === arr.length - 1 || dkOf(arr[i + 1]) !== dkOf(r)) items.push({ t: "d", k: dkOf(r), a: dayAgg[dkOf(r)], run: runMap[dkOf(r)] });
    });
  } else {
    items = arr.map((r, i) => ({ t: "r", r, i }));
  }
  const dayRowHtml = (it) =>
    `<tr style="background:#e0f2fe;font-weight:800;"><td colspan="${bk ? 29 : 18}" style="text-align:left;padding:5px 8px;letter-spacing:1px;font-size:10px;">${it.k ? fmtDate(it.k).toUpperCase() : "NO LR DATE"} — ${it.a.n} CONSIGNMENT${it.a.n === 1 ? "" : "S"} · DAY FREIGHT ${inr(it.a.freight)} · RUNNING ${inr(it.run)}</td></tr>`;
  const chunks = [];
  for (let i = 0; i < items.length; i += PER_PAGE) chunks.push(items.slice(i, i + PER_PAGE));
  if (!chunks.length) chunks.push([]);
  const inner = chunks
    .map((chunk, pi) => {
      const isLast = pi === chunks.length - 1;
      const body = chunk.map((it) => (it.t === "r" ? rowHtml(it.r, it.i) : dayRowHtml(it))).join("");
      const rIdx = chunk.filter((it) => it.t === "r").map((it) => it.i);
      return `<div style="${isLast ? "" : "page-break-after:always;"}">
    ${pi === 0 ? headerHtml : `<div style="margin:0 0 8px;font-size:11px;font-weight:800;letter-spacing:1px;">${esc(title)} — ${period} (contd.)</div>`}
    <table style="font-size:11px;">
      ${thead}
      <tbody>
        ${body || `<tr><td colspan="${bk ? 29 : 18}" class="muted">No consignments in this period.</td></tr>`}
        ${isLast ? totalRow : ""}
      </tbody>
    </table>
    <div style="text-align:right;font-size:10px;margin-top:4px;color:#475569;">Page ${pi + 1} of ${chunks.length} · Entries ${rIdx.length ? rIdx[0] + 1 : 0}–${rIdx.length ? rIdx[rIdx.length - 1] + 1 : 0} of ${arr.length}</div>
  </div>`;
    })
    .join("");
  open(title === "CONSIGNMENT REGISTER" ? "Consignment Register" : title, inner);
}

export function printUnbilled(rows) {
  const arr = rows || [];
  const daysNum = (r) => {
    const d = r.lr_date || r.dispatch_date;
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt)) return null;
    const diff = Math.floor((Date.now() - dt.getTime()) / 86400000);
    return diff >= 0 ? diff : null;
  };
  const body = arr
    .map((r, i) => {
      const dn = daysNum(r);
      const dayStyle = dn != null && dn > 30 ? "background:#fee2e2;font-weight:700;" : dn != null && dn > 15 ? "background:#fef3c7;font-weight:700;" : "";
      return `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.office || "—")}</td>
      <td>${esc(r.customer_name || r.consignor || "—")}</td>
      <td>${esc((r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—")}</td>
      <td>${r.lr_date ? fmtDate(r.lr_date) : "—"}</td>
      <td>${esc(r.origin || "—")}</td>
      <td>${esc(r.destination || "—")}</td>
      <td>${esc(r.basis || "—")}</td>
      <td>${esc(r.packages || "—")}</td>
      <td>${esc(r.invoice_no || "—")}</td>
      <td>${r.invoice_date ? fmtDate(r.invoice_date) : "—"}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.vendor_name || "—")}</td>
      <td class="right">${r.freight_amount ? inr(r.freight_amount) : "—"}</td>
      <td class="right" style="${dayStyle}">${dn != null ? dn : "—"}</td>
      <td>${esc(r.status || "—")}</td>
    </tr>`;
    })
    .join("");
  const totalFreight = arr.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">UNBILLED DETAILS</div>
    <div style="margin:0 0 12px;display:flex;flex-wrap:wrap;gap:8px 20px;font-size:11px;">
      <div><b>Total Unbilled Shipments:</b> ${arr.length}</div>
      <div><b>Total Unbilled Amount:</b> ${inr(totalFreight)}</div>
    </div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th>Customer</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Basis</th><th>PKG</th><th>Invoice No.</th><th>Invoice Date</th><th>Vehicle No.</th><th>Vendor/Broker</th><th class="right">Freight</th><th class="right">Days</th><th>Status</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="16" class="muted">No unbilled shipments.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="13" class="right">TOTAL FREIGHT</td><td class="right">${inr(totalFreight)}</td><td></td><td></td></tr>
      </tbody>
    </table>
  `;
  open("Unbilled Details", inner);
}


export async function printConsignments(list, copies = null) {
  const arr = (list || []).filter(Boolean);
  if (arr.length === 0) return;
  const needPortal = copies && copies.some((cp) => String(cp).toUpperCase() === "CONSIGNEE COPY");
  const portals = needPortal ? await Promise.all(arr.map((c) => fetchPodPortal(c.customer_name || c.consignor))) : arr.map(() => "");
  const inner = arr
    .map((c, i) => (copies && copies.length ? copies.map((cp) => manualLrInner(c, cp, false, portals[i])).join("") : manualLrInner(c, "")))
    .join("");
  open(arr.length === 1 ? "Consignment Note" : "Consignment Notes", inner, { hideHeader: true });
}


export function printSupplementaryBill(d) {
  if (!d) return;
  const row = (label, val, bold) =>
    `<div class="row"${bold ? ' style="font-weight:800;font-size:15px;"' : ""}><span class="muted">${esc(label)}</span><span>${esc(val)}</span></div>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;color:#b91c1c;">SUPPLEMENTARY BILL</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:14px;">${d.supp_no ? "No. " + esc(String(d.supp_no).replace(/^SUPP[-\s]?/i, "")) + " • " : ""}Balance Due against Original Bill</div>
    <div class="meta-grid">
      <div class="box">
        <h4>Customer</h4>
        <div class="big">${esc(d.customer_name || "—")}</div>
        ${d.billing_customer_address ? `<div style="font-size:12px;color:#475569;margin-top:2px;">${esc(d.billing_customer_address)}</div>` : ""}
      </div>
      <div class="box">
        <h4>Original Bill</h4>
        ${row("Bill No.", d.original_no)}
        ${row("Bill Date", d.original_date ? fmtDate(d.original_date) : "—")}
      </div>
    </div>
    <div style="margin-top:18px;max-width:420px;margin-left:auto;">
      ${row("Original Bill Amount", inr(d.original_amount))}
      ${row("Amount Received (Part Payment)", inr(d.received))}
      <div style="border-top:2px solid #0f172a;margin:8px 0;"></div>
      ${row("BALANCE DUE", inr(d.balance), true)}
    </div>
    <div class="words">Balance Due: ${amountInWords(d.balance)}</div>
    ${d.remarks ? `<div style="margin-top:12px;font-size:12px;"><span class="muted">Remarks: </span>${esc(d.remarks)}</div>` : ""}
    <div class="sign">
      <div>Customer Signature</div>
      <div>For Nisha Transport Corporation</div>
    </div>
  `;
  open("Supplementary Bill", inner);
}

export function printOutstanding(data, view = "bill", opts = {}) {
  if (!data) return;
  const explicitParty = opts.party || null;
  const distinctParties = Array.from(new Set((data.rows || []).map((r) => r.customer_name).filter(Boolean)));
  const party = explicitParty || (distinctParties.length === 1 ? distinctParties[0] : null);
  const billNo = (v) => String(v || "").replace(/^INV[-\s]?/i, "") || "—";
  const nd = (r) => {
    if (r.days != null && !isNaN(Number(r.days))) return Number(r.days);
    const d = r.bill_date || r.lr_date;
    if (!d) return "—";
    const dt = new Date(d);
    if (isNaN(dt)) return "—";
    const n = Math.floor((Date.now() - dt.getTime()) / 86400000);
    return n >= 0 ? n : "—";
  };
  let body;
  if (view === "state") {
    const groups = Object.values(
      (data.rows || []).reduce((acc, r) => {
        const k = r.customer_state || "Not specified";
        const g = (acc[k] = acc[k] || { state: k, count: 0, amount: 0, balance: 0 });
        g.count += 1;
        g.amount += Number(r.total) || 0;
        g.balance += Number(r.net_balance) || 0;
        return acc;
      }, {})
    ).sort((a, b) => b.balance - a.balance);
    const gCount = groups.reduce((s, g) => s + g.count, 0);
    const gBal = groups.reduce((s, g) => s + g.balance, 0);
    const rws = groups
      .map(
        (g) => `<tr><td>${esc(g.state)}</td><td class="right">${g.count}</td><td class="right">${inr(g.amount)}</td><td class="right">${inr(g.balance)}</td></tr>`
      )
      .join("");
    body = `<table>
      <thead><tr><th>State</th><th class="right">No. of Bills</th><th class="right">Bill Amount</th><th class="right">Outstanding</th></tr></thead>
      <tbody>${rws || `<tr><td colspan="4" class="muted">No outstanding.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td class="right">TOTAL</td><td class="right">${gCount}</td><td></td><td class="right">${inr(gBal)}</td></tr>
      </tbody></table>`;
  } else if (view === "customer" || view === "bill") {
    const showTds = !!opts.showTds;
    const groups = Object.values(
      (data.rows || []).reduce((acc, r) => {
        const k = r.customer_name || "—";
        (acc[k] = acc[k] || { customer: k, rows: [] }).rows.push(r);
        return acc;
      }, {})
    ).sort((a, b) => a.customer.localeCompare(b.customer));
    const grandAmt = (data.rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
    const grandBal = (data.rows || []).reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
    const creditMap = data.on_account_by_customer || {};
    const grandCredit = groups.reduce((s, g) => s + Number(creditMap[g.customer] || 0), 0);
    const groupHtml = groups
      .map((g) => {
        const rws = g.rows
          .map(
            (r) => {
              const dd = Number(nd(r));
              const bal = Number(r.net_balance) || 0;
              const b = isNaN(dd) ? 0 : dd > 60 ? 2 : dd > 30 ? 1 : 0;
              const bc = ["", "", ""];
              bc[b] = inr(bal);
              return `<tr>
        <td>${esc(r.lr_no || "—")}</td>
        <td style="white-space:nowrap;">${r.lr_date ? fmtDateDay(r.lr_date) : "—"}</td>
        <td>${esc(r.from_place || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td>${billNo(r.invoice_no)}</td>
        <td style="white-space:nowrap;">${r.bill_date ? fmtDateDay(r.bill_date) : "—"}</td>
        <td class="right">${inr(r.total)}</td>
        ${showTds ? `<td class="right">${Number(r.tds) ? inr(r.tds) : "—"}</td><td class="right">${Number(r.deduction) ? inr(r.deduction) : "—"}</td>` : ""}
        <td>${esc(r.bill_submitted || "")}${r.bill_submitted_date ? " (" + fmtDate(r.bill_submitted_date) + ")" : ""}</td>
        <td class="right">${inr(r.on_account)}</td>
        <td class="right">${inr(r.net_balance)}${Number(r.debit_note) ? `<div style="font-size:9px;color:#dc2626;">Less DN ${inr(r.debit_note)}${r.debit_note_reason ? ` — ${esc(r.debit_note_reason)}` : ""}</div>` : ""}</td>
        <td class="right">${nd(r)}</td>
        <td class="right">${bc[0]}</td>
        <td class="right">${bc[1]}</td>
        <td class="right" style="color:#dc2626;font-weight:700;">${bc[2]}</td>
      </tr>`;
            }
          )
          .join("");
        const sa = g.rows.reduce((s, r) => s + (Number(r.total) || 0), 0);
        const st = g.rows.reduce((s, r) => s + (Number(r.tds) || 0), 0);
        const sd = g.rows.reduce((s, r) => s + (Number(r.deduction) || 0), 0);
        const soa = g.rows.reduce((s, r) => s + (Number(r.on_account) || 0), 0);
        const sb = g.rows.reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
        const bsum = [0, 0, 0];
        g.rows.forEach((r) => {
          const dd = Number(nd(r));
          bsum[isNaN(dd) ? 0 : dd > 60 ? 2 : dd > 30 ? 1 : 0] += Number(r.net_balance) || 0;
        });
        const credit = Number(creditMap[g.customer] || 0);
        const code = g.rows.find((r) => r.customer_code)?.customer_code;
        const offs = Array.from(new Set(g.rows.map((r) => r.office).filter(Boolean)));
        const office = offs.join(", ") || opts.office || "Chennai";
        const heading = `<div style="margin:18px 0 8px;padding:10px 16px;border:1px solid #0f172a;border-radius:6px;font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:0.5px;line-height:1.7;display:flex;justify-content:space-between;align-items:center;gap:12px;">
          <div>
            <div>Customer Name: ${esc(g.customer)}</div>
            ${(() => { const addr = g.rows.find((r) => r.customer_address)?.customer_address || opts.custShort?.[String(g.customer || "").trim().toLowerCase()]; return addr ? `<div>Customer Address: ${esc(addr)}</div>` : ""; })()}
            ${code ? `<div>Customer Code: ${esc(code)}</div>` : ""}
          </div>
          <div style="text-align:right;">
            <div>Branch Office: ${esc(office)}</div>
            <div style="font-size:10px;letter-spacing:1px;color:#475569;">CBS UP TO ${fmtDate(new Date().toISOString())}</div>
            <div style="font-size:15px;">${inr(sb - credit)}</div>
          </div>
        </div>`;
        const creditRow = credit > 0
          ? `<tr style="background:#ecfdf5;font-weight:700;"><td colspan="${10 + (showTds ? 2 : 0)}" class="right">Less: On Account (Advance / Part Payment)</td><td class="right">- ${inr(credit)}</td><td colspan="4"></td></tr>`
          : "";
        return `${heading}
      <table class="tight">
        <thead><tr>
          <th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Bill No.</th>
          <th>Bill Date</th><th class="right">Bill Amount</th>${showTds ? `<th class="right">TDS</th><th class="right">Deduction</th>` : ""}<th>Bill Submitted</th><th class="right">On Account</th><th class="right">Net Balance</th><th class="right">No of Days</th><th class="right">0-30 Days</th><th class="right">31-60 Days</th><th class="right">60+ Days</th>
        </tr></thead>
        <tbody>
          ${rws}
          ${creditRow}
          <tr style="background:#f8fafc;font-weight:700;"><td colspan="6" class="right">Subtotal</td><td class="right">${inr(sa)}</td>${showTds ? `<td class="right">${inr(st)}</td><td class="right">${inr(sd)}</td>` : ""}<td></td><td class="right">${inr(soa)}</td><td class="right">${inr(sb - credit)}</td><td></td><td class="right">${inr(bsum[0])}</td><td class="right">${inr(bsum[1])}</td><td class="right" style="color:#dc2626;">${inr(bsum[2])}</td></tr>
        </tbody>
      </table>`;
      })
      .join("");
    body = `${groupHtml || `<div class="muted" style="padding:24px;text-align:center;">No outstanding by any customer.</div>`}
      <div style="margin-top:16px;padding:12px 16px;background:#f1f5f9;border-radius:6px;display:flex;justify-content:space-between;font-weight:800;font-size:14px;">
        <span>TOTAL OUTSTANDING</span>
        <span>Bill Amount ${inr(grandAmt)}${showTds ? ` &nbsp;•&nbsp; TDS ${inr((data.rows || []).reduce((s, r) => s + (Number(r.tds) || 0), 0))} &nbsp;•&nbsp; Deduction ${inr((data.rows || []).reduce((s, r) => s + (Number(r.deduction) || 0), 0))}` : ""} &nbsp;•&nbsp; Balance Amount ${inr(grandBal - grandCredit)}</span>
      </div>
      ${(() => {
        const gb = [0, 0, 0];
        (data.rows || []).forEach((r) => {
          const dd = Number(nd(r));
          gb[isNaN(dd) ? 0 : dd > 60 ? 2 : dd > 30 ? 1 : 0] += Number(r.net_balance) || 0;
        });
        return `<div style="margin-top:8px;padding:10px 16px;border:1.5px solid #0f172a;border-radius:6px;display:flex;justify-content:space-between;font-weight:700;font-size:12px;">
          <span style="text-transform:uppercase;letter-spacing:1px;">Aging Summary</span>
          <span>0-30 Days: ${inr(gb[0])} &nbsp;•&nbsp; 31-60 Days: ${inr(gb[1])} &nbsp;•&nbsp; <span style="color:#dc2626;">60+ Days: ${inr(gb[2])}</span></span>
        </div>`;
      })()}`;
  } else {
    const single = !!party;
    const rows = (data.rows || [])
      .map(
        (r) => `<tr>
        <td>${esc(r.office || "Chennai")}</td>
        ${single ? "" : `<td>${esc(r.customer_name || "—")}</td><td>${esc(r.customer_code || "—")}</td>`}
        <td>${esc(r.lr_no || "—")}</td>
        <td style="white-space:nowrap;">${r.lr_date ? fmtDateDay(r.lr_date) : "—"}</td>
        <td>${esc(r.from_place || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td>${billNo(r.invoice_no)}</td>
        <td style="white-space:nowrap;">${r.bill_date ? fmtDateDay(r.bill_date) : "—"}</td>
        <td class="right">${inr(r.total)}</td>
        <td>${esc(r.bill_submitted || "")}${r.bill_submitted_date ? " (" + fmtDate(r.bill_submitted_date) + ")" : ""}</td>
        <td class="right">${inr(r.on_account)}</td>
        <td class="right">${inr(r.net_balance)}</td>
        <td class="right">${nd(r)}</td>
      </tr>`
      )
      .join("");
    const sumAmount = (data.rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
    const sumOnAccount = (data.rows || []).reduce((s, r) => s + (Number(r.on_account) || 0), 0);
    const sumBalance = (data.rows || []).reduce((s, r) => s + (Number(r.net_balance) || 0), 0);
    const colCount = single ? 12 : 14;
    const totalColspan = single ? 7 : 9;
    body = `<table class="tight">
      <thead><tr>
        <th>Branch</th>${single ? "" : "<th>Customer Name</th><th>Customer Code</th>"}<th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Bill No.</th>
        <th>Bill Date</th><th class="right">Bill Amount</th><th>Submitted</th><th class="right">On Account</th><th class="right">Balance</th><th class="right">No of Days</th>
      </tr></thead>
      <tbody>${rows || `<tr><td colspan="${colCount}" class="muted">No outstanding invoices — all receivables collected.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="${totalColspan}" class="right">TOTAL OUTSTANDING</td>
          <td class="right">${inr(sumAmount)}</td>
          <td></td>
          <td class="right">${inr(sumOnAccount)}</td>
          <td class="right">${inr(sumBalance)}</td>
          <td></td>
        </tr>
      </tbody>
    </table>`;
  }
  const partyHeading = "";
  const inner = `
    <div class="title" style="letter-spacing:3px;">OUTSTANDING REPORT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:12px;">As on ${fmtDate(new Date().toISOString())}</div>
    ${partyHeading}
    ${body}
  `;
  open("Outstanding Report", inner);
}

export function printTdsDeductionSummary(rows) {
  const groups = Object.values(
    (rows || []).reduce((acc, r) => {
      const k = r.customer_name || "—";
      const g = (acc[k] = acc[k] || { customer: k, code: "", bills: 0, tds: 0, deduction: 0 });
      if (!g.code && r.customer_code) g.code = r.customer_code;
      g.bills += 1;
      g.tds += Number(r.tds) || 0;
      g.deduction += Number(r.deduction) || 0;
      return acc;
    }, {})
  )
    .filter((g) => g.tds || g.deduction)
    .sort((a, b) => b.tds + b.deduction - (a.tds + a.deduction));
  const tT = groups.reduce((s, g) => s + g.tds, 0);
  const tD = groups.reduce((s, g) => s + g.deduction, 0);
  const body = groups
    .map(
      (g, i) => `<tr><td>${i + 1}</td><td>${esc(g.code || "—")}</td><td>${esc(g.customer)}</td><td class="right">${g.bills}</td><td class="right">${g.tds ? inr(g.tds) : "—"}</td><td class="right">${g.deduction ? inr(g.deduction) : "—"}</td><td class="right" style="font-weight:700;">${inr(g.tds + g.deduction)}</td></tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">TDS &amp; DEDUCTION SUMMARY</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:12px;">As on ${fmtDate(new Date().toISOString())} — customer-wise, from pending outstanding bills</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Code</th><th>Party Name</th><th class="right">No. of Bills</th><th class="right">TDS</th><th class="right">Deduction</th><th class="right">Total Less</th></tr></thead>
      <tbody>
        ${body || `<tr><td colspan="7" class="muted">No TDS or Deduction entries in the current outstanding.</td></tr>`}
        ${body ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="4" class="right">TOTAL</td><td class="right">${inr(tT)}</td><td class="right">${inr(tD)}</td><td class="right">${inr(tT + tD)}</td></tr>` : ""}
      </tbody>
    </table>`;
  open("TDS & Deduction Summary", inner);
}

const REMINDER_STAMP_DAYS = 60;

export function printPaymentReminder(row, customer, profile, stampDays) {
  if (!row) return;
  const amount = row.net_balance ?? row.outstanding ?? row.total;
  const days = row.days ?? 0;
  const p = profile || {};
  const stampAt = Number(stampDays) || REMINDER_STAMP_DAYS;
  const hasBank = p.bank_name || p.account_no || p.ifsc;
  const branch = row.office || "";
  const brAddr = ((p.branch_addresses || {})[branch] || "").trim();
  const billNo = String(row.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—";
  const addr = customer ? [customer.address, customer.state, customer.pincode].filter(Boolean).join(", ") : row.customer_address || "";
  const gst = customer?.gstin || "";
  const inner = (copyLabel) => `
    <div style="position:relative;">
    <div style="position:absolute;top:6px;left:0;font-size:10px;font-weight:800;letter-spacing:2px;color:#475569;border:1px solid #94a3b8;border-radius:4px;padding:2px 8px;">${copyLabel}</div>
    ${days >= stampAt ? `<div style="position:absolute;top:34px;right:10px;transform:rotate(-12deg);border:3px solid #dc2626;color:#dc2626;font-weight:900;font-size:19px;letter-spacing:2px;padding:6px 14px;border-radius:6px;opacity:0.85;">OVERDUE ${days} DAYS</div>` : ""}
    <div style="text-align:right;font-size:12px;color:#334155;margin-top:8px;">Date: ${fmtDate(new Date().toISOString())}</div>
    ${brAddr ? `<div style="margin-top:2px;font-size:12px;color:#334155;border-left:3px solid #0f172a;padding:2px 0 2px 8px;max-width:420px;"><b>${esc(branch.toUpperCase())} BRANCH</b><br/>${esc(brAddr)}</div>` : ""}
    <div class="title" style="letter-spacing:3px;">PAYMENT REMINDER</div>
    <div style="margin-top:8px;font-size:13px;">
      <div style="text-transform:uppercase;letter-spacing:1.5px;color:#64748b;font-size:11px;">To,</div>
      <div style="font-weight:700;font-size:14px;">M/S. ${esc((row.customer_name || "—").toUpperCase())}</div>
      ${addr ? `<div style="font-size:12px;color:#334155;max-width:440px;">${esc(addr)}</div>` : ""}
      ${gst ? `<div style="font-size:12px;color:#334155;"><b>GSTIN:</b> ${esc(gst)}</div>` : ""}
    </div>
    <div style="margin-top:16px;font-size:13px;line-height:1.9;">
      <p>Dear Sir / Madam,</p>
      <p>We wish to bring to your kind attention that the following bill remains outstanding in our records. We kindly request you to arrange the payment at the earliest.</p>
    </div>
    <table style="margin-top:8px;">
      <thead><tr>
        <th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Bill No.</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Bill Date</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">From</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">To</th>
        <th class="right" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Outstanding Amount</th><th class="right" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Days Overdue</th>
      </tr></thead>
      <tbody><tr>
        <td>${esc(billNo)}</td>
        <td>${row.bill_date ? fmtDate(row.bill_date) : row.issue_date ? fmtDate(row.issue_date) : "—"}</td>
        <td>${esc(row.from_place || "—")}</td>
        <td>${esc(row.destination || "—")}</td>
        <td class="right"><b>${inr(amount)}</b></td>
        <td class="right">${days} days</td>
      </tr></tbody>
    </table>
    <div class="words" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;padding-top:8px;padding-bottom:8px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;"><span><b>Amount in Words:</b> ${esc(amountInWords(amount).replace(/ Rupees Only$/i, " Only"))}</span><span style="font-weight:800;">TOTAL DUE AMOUNT: ${inr(amount)}</span></div>
    <div style="margin-top:10px;font-size:13px;font-weight:700;">Please arrange payment on or before <u>${fmtDate(new Date(Date.now() + 7 * 86400000).toISOString())}</u>.</div>
    ${hasBank ? `<div style="margin-top:10px;border:1.5px solid #0f172a;border-radius:4px;padding:8px 12px;font-size:12px;">
      <div style="font-weight:800;text-transform:uppercase;letter-spacing:1px;font-size:10px;color:#475569;margin-bottom:4px;">Our Bank Details for Payment : <span style="font-size:11px;color:#0f172a;">${esc(COMPANY.name)}</span></div>
      <div style="display:flex;gap:24px;flex-wrap:wrap;">
        ${p.bank_name ? `<span><b>Bank:</b> ${esc(p.bank_name)}</span>` : ""}
        ${p.account_no ? `<span><b>A/C No.:</b> ${esc(p.account_no)}</span>` : ""}
        ${p.ifsc ? `<span><b>IFSC:</b> ${esc(p.ifsc)}</span>` : ""}
      </div>
    </div>` : ""}
    <div style="margin-top:16px;font-size:13px;line-height:1.9;">
      <p>Kindly make the payment by A/c Payee Cheque / NEFT / RTGS${hasBank ? " to the bank account given above" : " to the bank account mentioned on our invoice"}. In case the payment has already been made, please ignore this reminder and kindly share the payment details with us.</p>
      <p>Thanking you and assuring you of our best services at all times.</p>
    </div>
    <div style="display:flex;justify-content:flex-end;margin-top:32px;text-align:center;">
      <div style="width:260px;">
        <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
        <div style="margin-top:48px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Authorised Signatory</div>
      </div>
    </div>
    </div>
  `;
  const page = (label) => `<div class="challan-page rem-page" style="border:2px solid #0f172a;padding:14px 16px;">${header()}${inner(label)}</div>`;
  const remStyle = `<style>.challan-page.rem-page{page-break-after:always;} .challan-page.rem-page:last-child{page-break-after:auto;} @media print { .rem-page { height: 250mm; box-sizing: border-box; overflow: hidden; margin: 0 !important; } .rem-page + .rem-page { margin-top: 8mm !important; padding-top: 14px !important; } }</style>`;
  open(`Payment Reminder - ${String(row.invoice_no || "").replace(/^INV[-\s]?/i, "")}`, remStyle + page("CUSTOMER COPY") + page("OFFICE COPY"), { hideHeader: true });
}


export function printBulkReminders(rows, customers, profile, stampDays) {
  const list = rows || [];
  if (!list.length) return;
  const p = profile || {};
  const stampAt = Number(stampDays) || REMINDER_STAMP_DAYS;
  const hasBank = p.bank_name || p.account_no || p.ifsc;
  const custOf = (r) =>
    (customers || []).find((x) => (r.customer_id && x.id === r.customer_id) || (x.name || "").trim().toLowerCase() === String(r.customer_name || "").trim().toLowerCase());
  const groups = Object.values(
    list.reduce((acc, r) => {
      const k = r.customer_name || "—";
      (acc[k] = acc[k] || { customer: k, rows: [] }).rows.push(r);
      return acc;
    }, {})
  ).sort((a, b) => a.customer.localeCompare(b.customer));
  const today = fmtDate(new Date().toISOString());
  const sections = groups
    .map((g, idx) => {
      const rws = g.rows
        .map(
          (r) => `<tr>
        <td>${esc(String(r.invoice_no || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
        <td>${r.bill_date ? fmtDate(r.bill_date) : r.issue_date ? fmtDate(r.issue_date) : "—"}</td>
        <td>${esc(r.from_place || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td class="right">${inr(r.net_balance ?? r.total)}</td>
        <td class="right">${r.days ?? 0} days</td>
      </tr>`
        )
        .join("");
      const total = g.rows.reduce((s, r) => s + (Number(r.net_balance ?? r.total) || 0), 0);
      const c = custOf(g.rows[0] || {});
      const addr = c ? [c.address, c.state, c.pincode].filter(Boolean).join(", ") : g.rows[0]?.customer_address || "";
      const gst = c?.gstin || "";
      const maxDays = Math.max(...g.rows.map((r) => Number(r.days) || 0));
      const branch = g.rows[0]?.office || "";
      const brAddr = ((p.branch_addresses || {})[branch] || "").trim();
      return `<div class="challan-page" style="border:2px solid #0f172a;padding:14px 16px;">${header()}<div style="position:relative;">
      ${maxDays >= stampAt ? `<div style="position:absolute;top:34px;right:10px;transform:rotate(-12deg);border:3px solid #dc2626;color:#dc2626;font-weight:900;font-size:19px;letter-spacing:2px;padding:6px 14px;border-radius:6px;opacity:0.85;">OVERDUE ${maxDays} DAYS</div>` : ""}
      <div style="text-align:right;font-size:12px;color:#334155;margin-top:8px;">Date: ${today}</div>
      ${brAddr ? `<div style="margin-top:2px;font-size:12px;color:#334155;border-left:3px solid #0f172a;padding:2px 0 2px 8px;max-width:420px;"><b>${esc(branch.toUpperCase())} BRANCH</b><br/>${esc(brAddr)}</div>` : ""}
      <div class="title" style="letter-spacing:3px;">PAYMENT REMINDER</div>
      <div style="margin-top:8px;font-size:13px;">
        <div style="text-transform:uppercase;letter-spacing:1.5px;color:#64748b;font-size:11px;">To,</div>
        <div style="font-weight:700;font-size:14px;">M/S. ${esc((g.customer || "—").toUpperCase())}</div>
        ${addr ? `<div style="font-size:12px;color:#334155;max-width:440px;">${esc(addr)}</div>` : ""}
        ${gst ? `<div style="font-size:12px;color:#334155;"><b>GSTIN:</b> ${esc(gst)}</div>` : ""}
      </div>
      <div style="margin-top:16px;font-size:13px;line-height:1.9;">
        <p>Dear Sir / Madam,</p>
        <p>The following bill(s) remain outstanding in our records. We kindly request you to arrange the payment at the earliest.</p>
      </div>
      <table style="margin-top:8px;">
        <thead><tr>
          <th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Bill No.</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Bill Date</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">From</th><th style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">To</th>
          <th class="right" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Outstanding Amount</th><th class="right" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;">Days Overdue</th>
        </tr></thead>
        <tbody>${rws}
          <tr style="font-weight:800;background:#f1f5f9;"><td colspan="4" class="right">TOTAL OUTSTANDING</td><td class="right">${inr(total)}</td><td></td></tr>
        </tbody>
      </table>
      <div class="words" style="border-top:1.5px solid #0f172a;border-bottom:1.5px solid #0f172a;padding-top:8px;padding-bottom:8px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;"><span><b>Amount in Words:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</span><span style="font-weight:800;">TOTAL DUE AMOUNT: ${inr(total)}</span></div>
      <div style="margin-top:10px;font-size:13px;font-weight:700;">Please arrange payment on or before <u>${fmtDate(new Date(Date.now() + 7 * 86400000).toISOString())}</u>.</div>
      ${hasBank ? `<div style="margin-top:10px;border:1.5px solid #0f172a;border-radius:4px;padding:8px 12px;font-size:12px;">
        <div style="font-weight:800;text-transform:uppercase;letter-spacing:1px;font-size:10px;color:#475569;margin-bottom:4px;">Our Bank Details for Payment : <span style="font-size:11px;color:#0f172a;">${esc(COMPANY.name)}</span></div>
        <div style="display:flex;gap:24px;flex-wrap:wrap;">
          ${p.bank_name ? `<span><b>Bank:</b> ${esc(p.bank_name)}</span>` : ""}
          ${p.account_no ? `<span><b>A/C No.:</b> ${esc(p.account_no)}</span>` : ""}
          ${p.ifsc ? `<span><b>IFSC:</b> ${esc(p.ifsc)}</span>` : ""}
        </div>
      </div>` : ""}
      <div style="margin-top:16px;font-size:13px;line-height:1.9;">
        <p>Kindly make the payment by A/c Payee Cheque / NEFT / RTGS${hasBank ? " to the bank account given above" : " to the bank account mentioned on our invoice"}. In case the payment has already been made, please ignore this reminder and kindly share the payment details with us.</p>
        <p>Thanking you and assuring you of our best services at all times.</p>
      </div>
      <div style="display:flex;justify-content:flex-end;margin-top:28px;text-align:center;">
        <div style="width:260px;">
          <div style="font-weight:700;font-size:12px;">For ${esc(COMPANY.name)}</div>
          <div style="margin-top:40px;border-top:1px solid #94a3b8;padding-top:6px;font-size:11px;color:#475569;">Authorised Signatory</div>
        </div>
      </div>
    </div></div>`;
    })
    .join("");
  open("Payment Reminders — All Overdue Customers", sections, { hideHeader: true });
}

export function printReminderHistory(logs, customer) {
  const list = (logs || []).filter((l) => !customer || String(l.customer_name || "").trim().toLowerCase() === customer.trim().toLowerCase());
  const fmtTs = (s) => (s ? `${fmtDate(s)} ${new Date(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—");
  const body = list
    .map(
      (l, i) => `<tr>
      <td>${i + 1}</td>
      <td>${fmtTs(l.sent_at)}</td>
      <td>${esc(l.customer_name || "—")}</td>
      <td>${esc((l.bills || []).join(", ") || "—")}</td>
      <td style="text-transform:capitalize;">${esc(l.channel || "whatsapp")}</td>
      <td class="right">${inr(l.total_balance)}</td>
      <td>${esc(l.sent_by || "—")}</td>
      <td>${esc(l.response ? `${l.response}${l.promised_date ? ` (by ${fmtDate(l.promised_date)})` : ""}` : "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">REMINDER HISTORY${customer ? ` — ${esc(customer.toUpperCase())}` : ""}</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:12px;">Payment reminders sent — record for legal follow-up · As on ${fmtDate(new Date().toISOString())}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Sent At</th><th>Customer</th><th>Bill No(s)</th><th>Channel</th><th class="right">Outstanding</th><th>Sent By</th><th>Customer Response</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No reminders sent yet.</td></tr>`}</tbody>
    </table>
    <div style="margin-top:10px;font-size:11px;color:#64748b;">Total reminders: ${list.length}</div>`;
  open("Reminder History", inner);
}

export function printNoteRegister(data) {
  open("Note Register", noteRegisterInner(data));
}

function noteRegisterInner(data) {
  const monthKey = (d) => String(d || "").slice(0, 7) || "—";
  const monthLabel = (m) => (m === "—" ? "No Date" : new Date(`${m}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" }));
  const section = (title, list, color) => {
    const months = {};
    (list || []).forEach((n) => (months[monthKey(n.date)] = months[monthKey(n.date)] || []).push(n));
    const total = (list || []).reduce((s, n) => s + (Number(n.amount) || 0), 0);
    const blocks = Object.keys(months).sort().reverse().map((m) => {
      const rows = months[m]
        .map(
          (n, i) => `<tr>
          <td>${i + 1}</td>
          <td style="white-space:nowrap;">${n.date ? fmtDateDay(n.date) : "—"}</td>
          <td>${esc(n.party)}</td>
          <td>${esc(String(n.ref || "").replace(/^INV[-\s]?/i, "")) || "—"}</td>
          <td>${esc(n.branch || "—")}</td>
          <td>${esc(n.reason || "—")}</td>
          <td class="right" style="font-weight:700;">${inr(n.amount)}</td>
        </tr>`
        )
        .join("");
      const mt = months[m].reduce((s, n) => s + (Number(n.amount) || 0), 0);
      return `<div style="margin-top:10px;font-weight:800;font-size:12px;letter-spacing:1px;">${monthLabel(m).toUpperCase()}</div>
      <table class="tight">
        <thead><tr><th>Sl</th><th>Date</th><th>Party</th><th>Ref No.</th><th>Branch</th><th>Reason</th><th class="right">Amount</th></tr></thead>
        <tbody>${rows}<tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">MONTH TOTAL</td><td class="right">${inr(mt)}</td></tr></tbody>
      </table>`;
    }).join("");
    return `<div style="margin-top:16px;">
      <div style="font-weight:800;font-size:13px;letter-spacing:2px;color:${color};border-bottom:2px solid ${color};padding-bottom:4px;">${title} — TOTAL ${inr(total)}</div>
      ${blocks || `<div class="muted" style="margin-top:8px;font-size:12px;">No notes issued.</div>`}
    </div>`;
  };
  const inner = `
    <div class="title" style="letter-spacing:3px;">CREDIT / DEBIT NOTE REGISTER</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">Month-wise register for the CA's file · As on ${fmtDate(new Date().toISOString())}</div>
    ${section("CREDIT NOTES — VENDOR / BROKER DEDUCTIONS", data.credit_notes, "#b45309")}
    ${section("DEBIT NOTES — OUR BILL DEDUCTIONS", data.debit_notes, "#dc2626")}`;
  return inner;
}

export function printNoteSummary(data) {
  open("Yearly Note Summary", noteSummaryInner(data));
}

function noteSummaryInner(data) {
  const fyOf = (d) => {
    const dt = new Date(d || "");
    if (isNaN(dt)) return "—";
    const y = dt.getMonth() + 1 >= 4 ? dt.getFullYear() : dt.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  };
  const fys = {};
  const add = (n, kind) => {
    const f = (fys[fyOf(n.date)] = fys[fyOf(n.date)] || {});
    const p = (f[n.party] = f[n.party] || { party: n.party, cn: 0, cnCount: 0, dn: 0, dnCount: 0 });
    p[kind] += Number(n.amount) || 0;
    p[kind + "Count"] += 1;
  };
  (data.credit_notes || []).forEach((n) => add(n, "cn"));
  (data.debit_notes || []).forEach((n) => add(n, "dn"));
  const blocks = Object.keys(fys)
    .sort()
    .reverse()
    .map((fy) => {
      const rows = Object.values(fys[fy]).sort((a, b) => b.cn + b.dn - (a.cn + a.dn));
      const tc = rows.reduce((s, r) => s + r.cn, 0);
      const td = rows.reduce((s, r) => s + r.dn, 0);
      const body = rows
        .map(
          (r, i) => `<tr>
          <td>${i + 1}</td>
          <td>${esc(r.party)}</td>
          <td class="right">${r.cnCount || "—"}</td>
          <td class="right">${r.cn ? inr(r.cn) : "—"}</td>
          <td class="right">${r.dnCount || "—"}</td>
          <td class="right">${r.dn ? inr(r.dn) : "—"}</td>
          <td class="right" style="font-weight:700;">${inr(r.cn + r.dn)}</td>
        </tr>`
        )
        .join("");
      return `<div style="margin-top:16px;font-weight:800;font-size:13px;letter-spacing:2px;border-bottom:2px solid #0f172a;padding-bottom:4px;">FINANCIAL YEAR ${fy === "—" ? "— NO DATE" : fy} (APR–MAR)</div>
      <table class="tight">
        <thead><tr><th>Sl</th><th>Party</th><th class="right">CN Count</th><th class="right">Credit Notes (Vendor)</th><th class="right">DN Count</th><th class="right">Debit Notes (Bills)</th><th class="right">Total Notes</th></tr></thead>
        <tbody>${body}<tr style="font-weight:800;background:#f1f5f9;"><td colspan="3" class="right">FY TOTAL</td><td class="right">${inr(tc)}</td><td></td><td class="right">${inr(td)}</td><td class="right">${inr(tc + td)}</td></tr></tbody>
      </table>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">YEARLY NOTE SUMMARY</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">Financial-year totals of credit & debit notes issued per party · For assessments · As on ${fmtDate(new Date().toISOString())}</div>
    ${blocks || `<div class="muted" style="margin-top:8px;font-size:12px;">No notes issued.</div>`}`;
  return inner;
}

export function printNoteApprovalLog(rows) {
  open("Note Approval Log", noteApprovalLogInner(rows));
}

function noteApprovalLogInner(rows) {
  const body = (rows || [])
    .map(
      (d, i) => `<tr>
    <td>${i + 1}</td>
    <td style="white-space:nowrap;">${d.at ? new Date(d.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
    <td style="font-weight:700;color:${d.kind === "credit" ? "#b45309" : "#dc2626"};">${d.kind === "credit" ? "CN" : "DN"}</td>
    <td>${esc(d.party)}</td>
    <td>${esc(String(d.ref || "").replace(/^(INV|VHC)[-\s]?/i, "")) || "—"}</td>
    <td class="right" style="font-weight:700;">${inr(d.amount)}</td>
    <td>${esc(d.reason || "—")}</td>
    <td style="font-weight:800;color:${d.action === "Approved" ? "#047857" : d.action === "Rejected" ? "#dc2626" : "#b45309"};">${esc(String(d.action || "").toUpperCase())}</td>
    <td>${esc(d.by || "—")}</td>
    <td>${esc(d.reject_reason || "—")}</td>
  </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">NOTE APPROVAL LOG</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">Audit trail of credit/debit note approvals, rejections & resubmissions · As on ${fmtDate(new Date().toISOString())}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>When</th><th>Type</th><th>Party</th><th>Ref</th><th class="right">Amount</th><th>Note Reason</th><th>Action</th><th>By</th><th>Reject Reason</th></tr></thead>
      <tbody>${body || `<tr><td colspan="10" class="muted">No approval actions recorded yet.</td></tr>`}</tbody>
    </table>
    <div style="margin-top:10px;font-size:11px;color:#64748b;">Total actions: ${(rows || []).length}</div>`;
  return inner;
}

export function printNoteAuditPack(data, decisions) {
  const pb = `<div style="page-break-after:always;height:1px;"></div>`;
  const packHead = `
    <div style="text-align:center;margin-top:4px;font-size:11px;font-weight:800;letter-spacing:3px;color:#475569;border:1px solid #94a3b8;border-radius:4px;display:inline-block;padding:3px 14px;">MONTHLY NOTE AUDIT PACK — FOR THE CA'S FILE</div>`;
  const inner = `
    <div style="text-align:center;">${packHead}</div>
    ${noteRegisterInner(data)}
    ${pb}${header()}
    ${noteApprovalLogInner(decisions)}
    ${pb}${header()}
    ${noteSummaryInner(data)}`;
  open("Note Audit Pack", inner);
}

export function printUnderInsured(list) {
  const months = {};
  list.forEach((r) => {
    const m = String(r.date || "").slice(0, 7) || "—";
    (months[m] = months[m] || []).push(r);
  });
  const blocks = Object.keys(months)
    .sort()
    .reverse()
    .map((m) => {
      const rows = months[m];
      const ts = rows.reduce((s, r) => ({ inv: s.inv + r.invTotal, ins: s.ins + r.insured, short: s.short + r.short }), { inv: 0, ins: 0, short: 0 });
      const label = m === "—" ? "— NO DATE" : new Date(m + "-01").toLocaleString("en-IN", { month: "long", year: "numeric" }).toUpperCase();
      const body = rows
        .map(
          (r, i) => `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
        <td style="font-weight:700;">${esc(String(r.lr || "").replace(/^CN[-\s]?/i, "")) || "—"}</td>
        <td>${esc(r.party || "—")}</td>
        <td>${esc(r.route || "—")}</td>
        <td class="right">${inr(r.invTotal)}</td>
        <td class="right">${r.insured ? inr(r.insured) : "<span style='color:#dc2626;font-weight:700;'>NOT INSURED</span>"}</td>
        <td class="right" style="font-weight:800;color:#dc2626;">${inr(r.short)}</td>
      </tr>`
        )
        .join("");
      return `<div style="margin-top:14px;font-weight:800;font-size:12px;letter-spacing:2px;border-bottom:2px solid #0f172a;padding-bottom:3px;">${label}</div>
      <table class="tight">
        <thead><tr><th>Sl</th><th>Date</th><th>LR No.</th><th>Party</th><th>Route</th><th class="right">Invoice Value</th><th class="right">Insured</th><th class="right">Shortfall</th></tr></thead>
        <tbody>${body}<tr style="font-weight:800;background:#fef2f2;"><td colspan="5" class="right">MONTH TOTAL</td><td class="right">${inr(ts.inv)}</td><td class="right">${inr(ts.ins)}</td><td class="right" style="color:#dc2626;">${inr(ts.short)}</td></tr></tbody>
      </table>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;color:#dc2626;">UNDER-INSURED CONSIGNMENTS</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">Month-wise insured value report — goods value exceeding cover · For insurance renewal discussion · As on ${fmtDate(new Date().toISOString())}</div>
    ${blocks}
    <div style="margin-top:10px;font-size:11px;color:#64748b;">Total under-insured consignments: ${list.length}</div>`;
  open("Insured Value Report", inner);
}

export function printEwayExtensionLog(rows) {
  const body = (rows || [])
    .map(
      (d, i) => `<tr>
    <td>${i + 1}</td>
    <td style="white-space:nowrap;">${d.at ? new Date(d.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
    <td style="font-weight:700;">${esc(d.eway_no || "—")}</td>
    <td>${esc(String(d.lr_no || "").replace(/^CN[-\s]?/i, "")) || "—"}</td>
    <td>${esc(d.route || "—")}</td>
    <td>${d.old_valid ? fmtDate(d.old_valid) : "—"}</td>
    <td style="font-weight:700;color:#047857;">${d.new_valid ? fmtDate(d.new_valid) : "—"}</td>
    <td>${esc(d.by || "—")}</td>
  </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">E-WAY EXTENSION LOG</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:8px;">Every e-way bill valid-date extension with who confirmed it and when · For GST audits · As on ${fmtDate(new Date().toISOString())}</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>When</th><th>E-Way Bill No.</th><th>LR</th><th>Route</th><th>Old Valid</th><th>New Valid</th><th>Extended By</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No extensions recorded yet.</td></tr>`}</tbody>
    </table>
    <div style="margin-top:10px;font-size:11px;color:#64748b;">Total extensions: ${(rows || []).length}</div>`;
  open("E-Way Extension Log", inner);
}

export function printNoteVoucher(n, type) {
  const isCN = type === "credit";
  const color = isCN ? "#b45309" : "#dc2626";
  const title = isCN ? "CREDIT NOTE" : "DEBIT NOTE";
  const refNum = String(n.ref || "").replace(/^(INV|VHC)[-\s]?/i, "");
  const vno = `${isCN ? "CN" : "DN"}-${refNum || "—"}`;
  const inner = `
    <style>@page { size: A5 portrait; margin: 8mm; } @media print { body { margin: 0 !important; } }</style>
    <div class="title" style="letter-spacing:3px;color:${color};">${title}</div>
    <div style="text-align:center;font-size:11px;color:#64748b;margin-bottom:10px;">${isCN ? "Issued against Vendor/Broker deduction" : "Issued against our bill deduction"} · For the party's file</div>
    <table class="grid" style="margin-top:4px;">
      <tbody>
        <tr><td style="width:38%;font-weight:700;">Voucher No.</td><td style="font-weight:800;">${esc(vno)}</td></tr>
        <tr><td style="font-weight:700;">Date</td><td>${n.date ? fmtDate(n.date) : "—"}</td></tr>
        <tr><td style="font-weight:700;">${isCN ? "Vendor / Broker" : "Customer"}</td><td style="font-weight:700;">${esc(n.party || "—")}</td></tr>
        <tr><td style="font-weight:700;">Against ${isCN ? "Challan" : "Bill"} No.</td><td>${esc(refNum || "—")}</td></tr>
        <tr><td style="font-weight:700;">Branch</td><td>${esc(n.branch || "—")}</td></tr>
        <tr><td style="font-weight:700;">Reason</td><td>${esc(n.reason || "—")}</td></tr>
        <tr><td style="font-weight:700;">Amount</td><td style="font-weight:800;font-size:16px;color:${color};">${inr(n.amount)}</td></tr>
      </tbody>
    </table>
    <div class="words" style="margin-top:10px;"><b>Amount in Words:</b> ${esc(amountInWords(n.amount))}</div>
    <div style="margin-top:8px;font-size:11px;color:#475569;">${isCN ? "The above amount stands deducted from your hire payable against the referenced challan." : "The above amount stands adjusted against the referenced bill."}</div>
    <div style="display:flex;justify-content:space-between;gap:10px;margin-top:70px;">
      ${["Prepared By", "Party Signature", `Authorised Signatory<br/>For ${esc(COMPANY.name)}`]
        .map((t) => `<div style="text-align:center;font-size:10px;color:#475569;border-top:1px solid #94a3b8;padding-top:6px;width:31%;">${t}</div>`)
        .join("")}
    </div>`;
  open(`${title} ${vno}`, inner);
}

export function printSalarySlip(s) {
  if (!s) return;
  const basic = Number(s.basic) || 0;
  const allow = Number(s.allowances) || 0;
  const ded = Number(s.deductions) || 0;
  const pf = Number(s.pf) || 0;
  const esi = Number(s.esi) || 0;
  const loan = Number(s.loan) || 0;
  const totalDed = ded + pf + esi + loan;
  const net = s.net_pay ?? basic + allow - totalDed;
  const inner = `
    <div class="title" style="letter-spacing:3px;">SALARY SLIP</div>
    <div style="display:flex;justify-content:space-between;font-size:12px;color:#334155;margin-bottom:12px;">
      <div><b>Office:</b> ${esc(s.office || s.branch || "Chennai")}</div>
      <div><b>Date:</b> ${fmtDate(s.paid_date || s.slip_date || new Date().toISOString())}</div>
    </div>
    <div class="meta-grid">
      <div class="box"><h4>Employee</h4>
        <div class="row"><span class="muted">Name</span><span>${esc(s.employee_name || "—")}</span></div>
        <div class="row"><span class="muted">Employee Code</span><span>${esc(s.employee_code || "—")}</span></div>
        <div class="row"><span class="muted">Designation</span><span>${esc(s.designation || "—")}</span></div>
        <div class="row"><span class="muted">Branch</span><span>${esc(s.office || "—")}</span></div>
        <div class="row"><span class="muted">Ref No.</span><span>${esc(String(s.salary_no || "").replace(/^SAL[-\s]?/i, "") || "—")}</span></div>
      </div>
      <div class="box"><h4>Payment</h4>
        <div class="row"><span class="muted">Month</span><span>${esc(s.month || "—")}</span></div>
        <div class="row"><span class="muted">Payable Days</span><span>${s.payable_days ? esc(String(s.payable_days)) : "—"}</span></div>
        <div class="row"><span class="muted">Mode</span><span>${esc(s.payment_mode || "—")}</span></div>
        ${s.payment_mode === "Cheque" ? `
        <div class="row"><span class="muted">Cheque No.</span><span>${esc(s.cheque_no || "—")}</span></div>
        <div class="row"><span class="muted">Cheque Date</span><span>${s.cheque_date ? fmtDate(s.cheque_date) : "—"}</span></div>
        ${Number(s.cheque_amount) ? `<div class="row"><span class="muted">Cheque Amount</span><span>${inr(Number(s.cheque_amount))}</span></div>` : ""}` : ""}
        <div class="row"><span class="muted">Bank Name</span><span>${esc(s.bank_name || "—")}</span></div>
        <div class="row"><span class="muted">Account No.</span><span>${esc(s.account_no || "—")}</span></div>
        <div class="row"><span class="muted">Status</span><span>${esc(s.status || "—")}</span></div>
        <div class="row"><span class="muted">Paid Date</span><span>${s.paid_date ? fmtDate(s.paid_date) : "—"}</span></div>
      </div>
    </div>
    <table>
      <thead><tr><th>Earnings</th><th class="right">Amount</th><th>Deductions</th><th class="right">Amount</th></tr></thead>
      <tbody>
        <tr><td>Basic Pay</td><td class="right">${inr(basic)}</td><td>Deductions</td><td class="right">${inr(ded)}</td></tr>
        <tr><td>Allowances</td><td class="right">${inr(allow)}</td><td>PF</td><td class="right">${inr(pf)}</td></tr>
        <tr><td></td><td class="right"></td><td>ESI</td><td class="right">${inr(esi)}</td></tr>
        <tr><td></td><td class="right"></td><td>Loan</td><td class="right">${inr(loan)}</td></tr>
        <tr style="font-weight:800;background:#f1f5f9;"><td>Gross Earnings</td><td class="right">${inr(basic + allow)}</td><td>Total Deductions</td><td class="right">${inr(totalDed)}</td></tr>
      </tbody>
    </table>
    <div class="totals" style="width:320px;margin-left:auto;">
      <div class="row grand"><span>Net Pay</span><span>${inr(net)}</span></div>
    </div>
    <div class="words"><b>Amount in Words:</b> ${esc(amountInWords(net).replace(/ Rupees Only$/i, " Only"))}</div>
    ${s.remarks ? `<p style="font-size:12px;color:#334155;margin-top:8px;"><b>Remarks:</b> ${esc(s.remarks)}</p>` : ""}
    <div style="display:flex;justify-content:space-between;margin-top:56px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:30%;">Employee Signature</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:30%;">For ${esc(COMPANY.name)}</div>
    </div>
  `;
  open(`Salary Slip - ${s.employee_name || ""} (${s.month || ""})`, inner);
}

export function printSalaryRegister(salaries, month) {
  const list = (salaries || []).filter((s) => !month || month === "All" || s.month === month);
  if (!list.length) return;
  const rows = list
    .map(
      (s, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(String(s.salary_no || "").replace(/^SAL[-\s]?/i, "") || "—")}</td>
      <td>${esc(s.employee_name || "—")}</td>
      <td>${esc(s.designation || "—")}</td>
      <td>${esc(s.office || "—")}</td>
      <td class="right">${s.payable_days ? esc(String(s.payable_days)) : "—"}</td>
      <td class="right">${inr(Number(s.basic) || 0)}</td>
      <td class="right">${inr(Number(s.allowances) || 0)}</td>
      <td class="right">${inr(Number(s.deductions) || 0)}</td>
      <td class="right">${inr(Number(s.pf) || 0)}</td>
      <td class="right">${inr(Number(s.esi) || 0)}</td>
      <td class="right">${inr(s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0))}</td>
      <td>${esc(s.bank_name || "—")}</td>
      <td>${esc(s.account_no || "—")}</td>
      <td>${esc(s.status || "—")}</td>
    </tr>`
    )
    .join("");
  const tBasic = list.reduce((a, s) => a + (Number(s.basic) || 0), 0);
  const tAllow = list.reduce((a, s) => a + (Number(s.allowances) || 0), 0);
  const tDed = list.reduce((a, s) => a + (Number(s.deductions) || 0), 0);
  const tPf = list.reduce((a, s) => a + (Number(s.pf) || 0), 0);
  const tEsi = list.reduce((a, s) => a + (Number(s.esi) || 0), 0);
  const tNet = list.reduce((a, s) => a + (s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0)), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">SALARY REGISTER</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:12px;">${month && month !== "All" ? "For the month of " + esc(month) : "All months"} — ${list.length} employee(s)</div>
    <table>
      <thead><tr>
        <th>#</th><th>Ref No.</th><th>Employee</th><th>Designation</th><th>Branch</th>
        <th class="right">Days</th><th class="right">Basic</th><th class="right">Allowances</th><th class="right">Deductions</th><th class="right">PF</th><th class="right">ESI</th><th class="right">Net Pay</th><th>Bank Name</th><th>Account No.</th><th>Status</th>
      </tr></thead>
      <tbody>${rows}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="6" class="right">TOTAL</td>
          <td class="right">${inr(tBasic)}</td>
          <td class="right">${inr(tAllow)}</td>
          <td class="right">${inr(tDed)}</td>
          <td class="right">${inr(tPf)}</td>
          <td class="right">${inr(tEsi)}</td>
          <td class="right">${inr(tNet)}</td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <div class="words"><b>Total Net Payable:</b> ${esc(amountInWords(tNet).replace(/ Rupees Only$/i, " Only"))}</div>
    <div style="display:flex;justify-content:flex-end;margin-top:48px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:260px;">For ${esc(COMPANY.name)} — Authorised Signatory</div>
    </div>
  `;
  open(`Salary Register${month && month !== "All" ? " - " + month : ""}`, inner);
}

export function printMaintenanceReport(rows) {
  const assets = (rows || [])
    .map((r) => {
      const reps = r.repairs || [];
      return {
        ...r,
        rep_count: reps.length,
        rep_total: reps.reduce((a, x) => a + (Number(x.cost) || 0), 0),
        last_date: reps.map((x) => x.date || "").sort().pop() || "",
      };
    })
    .filter((r) => r.rep_count > 0);
  if (!assets.length) return false;
  const branches = {};
  assets.forEach((a) => {
    (branches[a.branch || "Chennai"] = branches[a.branch || "Chennai"] || []).push(a);
  });
  const grand = assets.reduce((a, x) => a + x.rep_total, 0);
  let sl = 0;
  const sections = Object.keys(branches)
    .sort()
    .map((br) => {
      const list = branches[br].sort((a, b) => b.rep_total - a.rep_total);
      const sub = list.reduce((a, x) => a + x.rep_total, 0);
      const body = list
        .map((a) => {
          const heavy = a.rep_total >= (Number(a.purchase_value) || 0) * 0.5 && (Number(a.purchase_value) || 0) > 0;
          return `<tr>
          <td>${++sl}</td>
          <td style="font-weight:700;">${esc(a.name || "—")}${heavy ? ' <span style="color:#b91c1c;font-size:9px;font-weight:800;">⚠ HEAVY REPAIR</span>' : ""}</td>
          <td>${esc(a.asset_code || "—")}</td>
          <td>${esc(a.category || "—")}</td>
          <td class="right">${inr(Number(a.purchase_value) || 0)}</td>
          <td class="right">${a.rep_count}</td>
          <td style="white-space:nowrap;">${a.last_date ? fmtDateDay(a.last_date) : "—"}</td>
          <td class="right" style="font-weight:700;color:${heavy ? "#b91c1c" : "#0f172a"};">${inr(a.rep_total)}</td>
        </tr>`;
        })
        .join("");
      return `
        <tr style="background:#e2e8f0;font-weight:800;"><td colspan="8">BRANCH: ${esc(br)} — ${list.length} asset(s)</td></tr>
        ${body}
        <tr style="font-weight:800;background:#f8fafc;"><td colspan="7" class="right">${esc(br)} SUBTOTAL</td><td class="right">${inr(sub)}</td></tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">ASSET MAINTENANCE REPORT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">Branch-wise repair costs · ${assets.length} asset(s) with repairs · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Asset Name</th><th>Asset Code</th><th>Category</th><th class="right">Purchase Value</th><th class="right">Repairs</th><th>Last Repair</th><th class="right">Maintenance Cost</th></tr></thead>
      <tbody>${sections}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" class="right">GRAND TOTAL MAINTENANCE COST</td><td class="right">${inr(grand)}</td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Maintenance Cost:</b> ${esc(amountInWords(grand).replace(/ Rupees Only$/i, " Only"))}</div>
    <div style="margin-top:8px;font-size:10px;color:#475569;">⚠ HEAVY REPAIR = maintenance cost has crossed 50% of the asset's purchase value — consider replacement.</div>
  `;
  open("Asset-Maintenance-Report", inner);
  return true;
}

export function printAssetRegister(rows) {
  const list = (rows || []).slice().sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.name || "").localeCompare(String(b.name || "")));
  const total = list.reduce((a, r) => a + (Number(r.purchase_value) || 0), 0);
  const bookVal = (r) => {
    const v = Number(r.purchase_value) || 0;
    const rate = Number(r.depreciation_rate) || 0;
    if (!rate || !r.purchase_date) return v;
    const yrs = Math.max(0, (Date.now() - new Date(String(r.purchase_date).slice(0, 10)).getTime()) / (365.25 * 86400000));
    return Math.max(0, v * Math.pow(1 - rate / 100, yrs));
  };
  const totalBook = list.reduce((a, r) => a + bookVal(r), 0);
  const body = list
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "Chennai")}</td>
      <td style="font-weight:700;">${esc(r.name || "—")}</td>
      <td>${esc(r.asset_code || "—")}</td>
      <td>${esc(r.category || "—")}</td>
      <td style="white-space:nowrap;">${r.purchase_date ? fmtDateDay(r.purchase_date) : "—"}</td>
      <td class="right">${inr(Number(r.purchase_value) || 0)}</td>
      <td class="right">${Number(r.depreciation_rate) ? `${Number(r.depreciation_rate)}%` : "—"}</td>
      <td class="right" style="font-weight:700;">${inr(Math.round(bookVal(r)))}</td>
      <td>${esc(r.condition || "Good")}</td>
      <td>${esc(r.status || "In Use")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">NTC ASSET REGISTER</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">${list.length} asset(s) · All branches · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Branch</th><th>Asset Name</th><th>Asset Code</th><th>Category</th><th>Purchase Date</th><th class="right">Value</th><th class="right">Dep %/yr</th><th class="right">Book Value</th><th>Condition</th><th>Status</th></tr></thead>
      <tbody>${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">TOTAL</td><td class="right">${inr(total)}</td><td></td><td class="right">${inr(Math.round(totalBook))}</td><td colspan="2"></td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Asset Value:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))} · <b>Current Book Value:</b> ${esc(amountInWords(Math.round(totalBook)).replace(/ Rupees Only$/i, " Only"))}</div>
  `;
  open("Asset-Register", inner);
}

export function printLogBook(rows, branchLabel) {
  const list = (rows || []).slice().sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.date || "").localeCompare(String(b.date || "")));
  const audit = list.filter((r) => (r.record_type || "Audit Record") === "Audit Record").length;
  const singleBranch = branchLabel && branchLabel !== "All Branches" ? branchLabel : null;
  const body = list
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "Chennai")}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td style="white-space:nowrap;font-weight:600;">${esc(r.record_type || "Audit Record")}</td>
      <td style="font-weight:700;">${esc(r.title || "—")}</td>
      <td style="white-space:pre-line;">${esc(r.details || "—")}</td>
      <td>${esc(r.remarks || "—")}</td>
      <td>${esc(r.recorded_by || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">NTC LOG BOOK${singleBranch ? ` — ${esc(singleBranch.toUpperCase())} BRANCH` : ""}</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">${list.length} record(s) · ${audit} Audit · ${list.length - audit} Business · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Branch</th><th>Date</th><th>Record Type</th><th>Subject / Title</th><th>Details</th><th>Remarks</th><th>Recorded By</th></tr></thead>
      <tbody>${body}</tbody>
    </table>
    <div class="sign">
      <div>Checked By</div>
      <div>For Nisha Transport Corporation</div>
    </div>
  `;
  open("Log-Book", inner);
}

export function printDepreciationSchedule(rows) {
  const list = (rows || []).filter((r) => Number(r.depreciation_rate) > 0 && r.purchase_date).sort((a, b) => String(a.branch || "").localeCompare(String(b.branch || "")) || String(a.name || "").localeCompare(String(b.name || "")));
  const sections = list
    .map((r) => {
      const rate = Number(r.depreciation_rate) || 0;
      const start = new Date(String(r.purchase_date).slice(0, 10));
      const now = new Date();
      let opening = Number(r.purchase_value) || 0;
      const yrs = [];
      let y = 0;
      while (true) {
        const from = new Date(start); from.setFullYear(start.getFullYear() + y);
        const to = new Date(start); to.setFullYear(start.getFullYear() + y + 1);
        if (from >= now) break;
        const partial = to > now;
        const frac = partial ? Math.max(0, (now - from) / (to - from)) : 1;
        const closing = opening * Math.pow(1 - rate / 100, frac);
        yrs.push(`<tr>
          <td>Year ${y + 1}${partial ? " (to date)" : ""}</td>
          <td style="white-space:nowrap;">${fmtDateDay(from.toISOString().slice(0, 10))} — ${fmtDate((partial ? now : to).toISOString().slice(0, 10))}</td>
          <td class="right">${inr(Math.round(opening))}</td>
          <td class="right">${inr(Math.round(opening - closing))}</td>
          <td class="right" style="font-weight:700;">${inr(Math.round(closing))}</td>
        </tr>`);
        opening = closing;
        y += 1;
        if (y > 40) break;
      }
      return `
        <div style="margin-top:14px;page-break-inside:avoid;">
          <div style="font-size:13px;font-weight:800;">${esc(r.name || "—")} <span style="font-weight:600;color:#64748b;">· ${esc(r.branch || "Chennai")} · ${esc(r.category || "—")} · Purchased ${fmtDate(r.purchase_date)} · WDV @ ${rate}% p.a.</span></div>
          <table class="tight" style="margin-top:4px;">
            <thead><tr><th>Year</th><th>Period</th><th class="right">Opening Value</th><th class="right">Depreciation</th><th class="right">Closing (Book) Value</th></tr></thead>
            <tbody>${yrs.join("")}</tbody>
          </table>
        </div>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DEPRECIATION SCHEDULE</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:6px;">${list.length} asset(s) with depreciation rate · WDV method · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    ${sections || '<p style="text-align:center;color:#64748b;">No assets have a depreciation rate set.</p>'}
    <div class="sign">
      <div>Prepared By</div>
      <div>For Nisha Transport Corporation</div>
    </div>
  `;
  open("Depreciation-Schedule", inner);
}

export function printScrapValueReport(rows) {
  const bookVal = (r) => {
    const v = Number(r.purchase_value) || 0;
    const rate = Number(r.depreciation_rate) || 0;
    if (!rate || !r.purchase_date) return v;
    const yrs = Math.max(0, (Date.now() - new Date(String(r.purchase_date).slice(0, 10)).getTime()) / (365.25 * 86400000));
    return Math.max(0, v * Math.pow(1 - rate / 100, yrs));
  };
  const list = (rows || [])
    .filter((r) => Number(r.purchase_value) > 0 && bookVal(r) < 0.1 * Number(r.purchase_value))
    .sort((a, b) => bookVal(a) / (Number(a.purchase_value) || 1) - bookVal(b) / (Number(b.purchase_value) || 1));
  const body = list
    .map((r, i) => {
      const bv = bookVal(r);
      const pct = (bv / (Number(r.purchase_value) || 1)) * 100;
      return `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "Chennai")}</td>
      <td style="font-weight:700;">${esc(r.name || "—")}</td>
      <td>${esc(r.category || "—")}</td>
      <td style="white-space:nowrap;">${r.purchase_date ? fmtDateDay(r.purchase_date) : "—"}</td>
      <td class="right">${inr(Number(r.purchase_value) || 0)}</td>
      <td class="right" style="font-weight:700;color:#dc2626;">${inr(Math.round(bv))}</td>
      <td class="right" style="color:#dc2626;">${pct.toFixed(1)}%</td>
      <td>${esc(r.condition || "Good")}</td>
      <td style="font-weight:700;">${(r.condition || "") === "Needs Repair" || pct < 5 ? "Dispose" : "Review"}</td>
    </tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">SCRAP VALUE REPORT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">Assets with book value below 10% of purchase value · ${list.length} asset(s) · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Branch</th><th>Asset Name</th><th>Category</th><th>Purchase Date</th><th class="right">Purchase Value</th><th class="right">Book Value</th><th class="right">% Left</th><th>Condition</th><th>Suggestion</th></tr></thead>
      <tbody>${body || '<tr><td colspan="10" style="text-align:center;color:#64748b;">No assets below the 10% scrap threshold.</td></tr>'}</tbody>
    </table>
    <div class="sign">
      <div>Reviewed By</div>
      <div>For Nisha Transport Corporation</div>
    </div>
  `;
  open("Scrap-Value-Report", inner);
}

export function printVendorConsignments(vendor, rows) {
  const list = rows || [];
  const total = list.reduce((a, s) => a + (Number(s.lorry_hire) || 0), 0);
  const body = list
    .map(
      (s, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(s.lr_no || s.consignment_no || "—")}</td>
      <td style="white-space:nowrap;">${(s.lr_date || s.dispatch_date) ? fmtDate(s.lr_date || s.dispatch_date) : "—"}</td>
      <td>${esc(s.origin || "—")}</td>
      <td>${esc(s.destination || "—")}</td>
      <td>${esc(s.customer_name || "—")}</td>
      <td>${esc(s.vehicle_no || "—")}</td>
      <td class="right" style="font-weight:700;">${inr(Number(s.lorry_hire) || 0)}</td>
      <td>${esc(s.status || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR CONSIGNMENT STATEMENT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;"><b>${esc(vendor?.name || "—")}</b>${vendor?.vendor_code ? " (" + esc(vendor.vendor_code) + ")" : ""} — ${list.length} consignment(s) · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Customer</th><th>Vehicle No.</th><th class="right">Lorry Hire</th><th>Status</th></tr></thead>
      <tbody>${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" class="right">TOTAL LORRY HIRE</td><td class="right">${inr(total)}</td><td></td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Lorry Hire:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</div>
  `;
  open(`Vendor-Consignments-${vendor?.name || ""}`, inner);
}

export function printVendorVouchers(vendor, rows) {
  const list = rows || [];
  const total = list.reduce((a, v) => a + (Number(v.amount) || 0), 0);
  const body = list
    .map(
      (v, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(String(v.voucher_no || "—").replace(/^DV[-\s]?/i, ""))}</td>
      <td style="white-space:nowrap;">${v.date ? fmtDateDay(v.date) : "—"}</td>
      <td>${esc(v.branch || "Chennai")}</td>
      <td>${esc(v.category || "—")}</td>
      <td>${esc(v.payment_mode || "Cash")}${v.cheque_no ? " (Chq " + esc(v.cheque_no) + ")" : ""}</td>
      <td class="right" style="font-weight:700;">${inr(Number(v.amount) || 0)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR PAYMENT STATEMENT</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;"><b>${esc(vendor?.name || "—")}</b>${vendor?.vendor_code ? " (" + esc(vendor.vendor_code) + ")" : ""} — ${list.length} voucher(s) · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Voucher No.</th><th>Date</th><th>Branch</th><th>Category</th><th>Payment Mode</th><th class="right">Amount</th></tr></thead>
      <tbody>${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="6" class="right">TOTAL PAID</td><td class="right">${inr(total)}</td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Paid:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</div>
  `;
  open(`Vendor-Payments-${vendor?.name || ""}`, inner);
}

export function printVendorPaymentSummary(monthLabel, list) {
  const total = (list || []).reduce((a, v) => a + (v.total || 0), 0);
  const body = (list || [])
    .map(
      (v, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(v.vendor || "—")}</td>
      <td>${esc(v.code || "—")}</td>
      <td class="right">${v.count}</td>
      <td>${esc((v.modes || []).join(", ") || "—")}</td>
      <td class="right" style="font-weight:700;">${inr(v.total || 0)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR / BROKER PAYMENTS</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">${esc(monthLabel)} — ${(list || []).length} vendor(s) · Paid via Debit Vouchers</div>
    <table>
      <thead><tr><th>Sl</th><th>Vendor / Broker</th><th>Code</th><th class="right">Vouchers</th><th>Modes</th><th class="right">Total Paid</th></tr></thead>
      <tbody>${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="5" class="right">TOTAL</td><td class="right">${inr(total)}</td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Paid:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</div>
  `;
  open(`Vendor-Payments-${monthLabel}`, inner);
}

export function printBirthdaysThisMonth(rows) {
  const now = new Date();
  const month = now.getMonth() + 1;
  const monthLabel = now.toLocaleString("en-GB", { month: "long", year: "numeric" });
  const list = (rows || [])
    .filter((r) => (r.status || "Active") !== "Inactive" && r.birthday && Number(String(r.birthday).slice(5, 7)) === month)
    .sort((a, b) => Number(String(a.birthday).slice(8, 10)) - Number(String(b.birthday).slice(8, 10)));
  if (!list.length) return false;
  const body = list
    .map(
      (s, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:800;color:#be185d;white-space:nowrap;">${Number(String(s.birthday).slice(8, 10))} ${monthLabel.split(" ")[0]}</td>
      <td style="font-weight:700;">${esc(s.name || "—")}</td>
      <td>${esc(s.designation || "—")}</td>
      <td>${esc(s.branch || "Chennai")}</td>
      <td>${esc(s.phone || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;color:#be185d;">🎂 BIRTHDAYS THIS MONTH</div>
    <div style="text-align:center;font-size:13px;color:#64748b;margin-bottom:12px;">${esc(monthLabel)} — ${list.length} staff birthday${list.length > 1 ? "s" : ""} · Let's make their day special!</div>
    <table>
      <thead><tr><th>Sl</th><th>Date</th><th>Staff Name</th><th>Designation</th><th>Branch</th><th>Mobile No.</th></tr></thead>
      <tbody>${body}</tbody>
    </table>
    <div style="margin-top:16px;text-align:center;font-size:12px;color:#9d174d;font-weight:600;">Warm wishes from all of us at ${esc(COMPANY.name)} 🎉</div>
  `;
  open("Birthdays-This-Month", inner);
  return true;
}

export function printOutstanding45(rows) {
  const list = rows || [];
  const body = list
    .map(
      (b, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(b.office || "—")}</td>
      <td style="font-weight:700;">${esc(String(b.invoice_no || "—").replace(/^INV[-\s]?/i, ""))}</td>
      <td style="white-space:nowrap;">${b.issue_date ? fmtDateDay(b.issue_date) : "—"}</td>
      <td>${esc(b.customer_name || "—")}</td>
      <td class="right">${inr(b.total || 0)}</td>
      <td class="right">${inr(b.paid || 0)}</td>
      <td class="right" style="font-weight:700;color:#b91c1c;">${inr(b.outstanding || 0)}</td>
      <td class="right">${b.days}d</td>
    </tr>`
    )
    .join("");
  const total = list.reduce((a, b) => a + (b.outstanding || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">OUTSTANDING ABOVE 45 DAYS</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">${list.length} bill(s) · Total due ${inr(total)} · As on ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table>
      <thead><tr><th>Sl</th><th>Branch</th><th>Bill No.</th><th>Bill Date</th><th>Customer</th><th class="right">Bill Amount</th><th class="right">Received</th><th class="right">Balance</th><th class="right">Age</th></tr></thead>
      <tbody>${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="7" class="right">TOTAL</td><td class="right">${inr(total)}</td><td></td></tr>
      </tbody>
    </table>
    <div class="words"><b>Total Outstanding:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</div>
  `;
  open("Outstanding-45-Days", inner);
}

export function printSalaryChequeRegister(salaries, month) {
  const list = (salaries || [])
    .filter((s) => s.payment_mode === "Cheque" && (!month || month === "All" || s.month === month))
    .sort((a, b) => String(a.cheque_date || "").localeCompare(String(b.cheque_date || "")) || String(a.cheque_no || "").localeCompare(String(b.cheque_no || "")));
  if (!list.length) return;
  const amt = (s) => Number(s.cheque_amount) || (s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0));
  const rows = list
    .map(
      (s, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(s.cheque_no || "—")}</td>
      <td style="white-space:nowrap;">${s.cheque_date ? fmtDateDay(s.cheque_date) : "—"}</td>
      <td>${esc(s.bank_name || "—")}</td>
      <td>${esc(s.employee_name || "—")}</td>
      <td>${esc(s.office || "—")}</td>
      <td>${esc(s.month || "—")}</td>
      <td class="right" style="font-weight:700;">${inr(amt(s))}</td>
      <td style="white-space:nowrap;font-weight:800;color:${{ Cleared: "#047857", Bounced: "#b91c1c" }[s.cheque_status] || "#b45309"};">${esc(s.cheque_status || "Pending")}</td>
      <td style="border-bottom:1px dotted #94a3b8;min-width:90px;"></td>
    </tr>`
    )
    .join("");
  const total = list.reduce((a, s) => a + amt(s), 0);
  const stCounts = { Pending: 0, Cleared: 0, Bounced: 0 };
  list.forEach((s) => { stCounts[s.cheque_status || "Pending"] = (stCounts[s.cheque_status || "Pending"] || 0) + 1; });
  const banks = {};
  list.forEach((s) => { const b = s.bank_name || "—"; banks[b] = (banks[b] || 0) + amt(s); });
  const bankChips = Object.entries(banks)
    .sort((a, b) => b[1] - a[1])
    .map(([b, v]) => `<span style="display:inline-block;border:1px solid #cbd5e1;border-radius:4px;padding:2px 8px;margin:2px 4px 2px 0;font-size:10px;"><b>${esc(b)}</b>: ${inr(v)}</span>`)
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">SALARY CHEQUE REGISTER</div>
    <div style="text-align:center;font-size:12px;color:#64748b;margin-bottom:10px;">${month && month !== "All" ? "For the month of " + esc(month) : "All months"} — ${list.length} cheque(s) · For bank reconciliation</div>
    <div style="margin-bottom:10px;">${bankChips}<span style="display:inline-block;border:1px solid #cbd5e1;border-radius:4px;padding:2px 8px;margin:2px 0 2px 8px;font-size:10px;"><b style="color:#047857;">Cleared ${stCounts.Cleared}</b> · <b style="color:#b45309;">Pending ${stCounts.Pending}</b> · <b style="color:#b91c1c;">Bounced ${stCounts.Bounced}</b></span></div>
    <table>
      <thead><tr>
        <th>#</th><th>Cheque No.</th><th>Cheque Date</th><th>Bank Name</th><th>Employee Name</th><th>Branch</th><th>Month</th><th class="right">Amount</th><th>Clearing Status</th><th>Bank Cleared On</th>
      </tr></thead>
      <tbody>${rows}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="7" class="right">TOTAL (${list.length} cheque${list.length === 1 ? "" : "s"})</td>
          <td class="right">${inr(total)}</td>
          <td colspan="2"></td>
        </tr>
      </tbody>
    </table>
    <div class="words"><b>Total Cheque Amount:</b> ${esc(amountInWords(total).replace(/ Rupees Only$/i, " Only"))}</div>
    <div style="margin-top:8px;font-size:10px;color:#475569;">Tick the "Bank Cleared On" column against the bank statement while reconciling. Only salaries paid by Cheque appear here.</div>
    <div style="display:flex;justify-content:space-between;margin-top:48px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:220px;">Reconciled By</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:260px;">For ${esc(COMPANY.name)} — Authorised Signatory</div>
    </div>
  `;
  open(`Salary Cheque Register${month && month !== "All" ? " - " + month : ""}`, inner);
}

export function printOfficeStatement(data, hist, loa) {
  void hist;
  if (!data) return;
  const rows = (data.rows || [])
    .map(
      (r) => `<tr>
      <td>${esc(data.office || "")}</td>
      <td>${esc(r.particulars || "")}</td>
      <td>${esc(String(r.ref || "").replace(/^INV[-\s]?/i, "") || "—")}</td>
      <td>${fmtDate(r.date)}</td>
      <td class="right">${r.debit ? inr(r.debit) : "—"}</td>
      <td class="right">${r.vehicle_hire ? inr(r.vehicle_hire) : "—"}</td>
      <td class="right">${r.vehicle_advance ? inr(r.vehicle_advance) : "—"}</td>
      <td class="right">${r.vehicle_balance ? inr(r.vehicle_balance) : "—"}</td>
      <td class="right">${r.expenses ? inr(r.expenses) : "—"}</td>
      <td class="right">${r.credit ? inr(r.credit) : "—"}</td>
      <td class="right">${inr(r.balance)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <div style="font-size:13px;font-weight:800;white-space:nowrap;">LOA No. : ${esc(loa?.loa_no || "")}</div>
      <div class="title" style="letter-spacing:2px;margin:0;">LETTER OF ACCOUNT — ${esc((data.office || "").toUpperCase())} BRANCH</div>
      <div style="font-size:13px;font-weight:800;white-space:nowrap;">LOA Date : ${loa?.date ? `${loa.date.slice(8, 10)}-${loa.date.slice(5, 7)}-${loa.date.slice(0, 4)}` : `${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")}`}</div>
    </div>
    <div style="text-align:center;margin:6px 0 12px;">
      <span style="display:inline-block;border:2px solid #0f172a;border-radius:6px;padding:6px 18px;font-size:14px;font-weight:800;letter-spacing:0.5px;">
        PERIOD: ${data.from_date ? fmtDate(data.from_date) : "BEGINNING"} &nbsp;TO&nbsp; ${data.to_date ? fmtDate(data.to_date) : fmtDate(new Date().toISOString())}
      </span>
    </div>
    <table style="border-collapse:collapse;margin:0 0 12px;font-size:11px;">
      <thead><tr>
        <th style="border:1.5px solid #0f172a;background:#e0f2fe;padding:4px 16px;text-align:left;letter-spacing:1px;">DATE FROM</th>
        <th style="border:1.5px solid #0f172a;background:#e0f2fe;padding:4px 16px;text-align:left;letter-spacing:1px;">DATE TO</th>
        <th style="border:1.5px solid #0f172a;background:#e0f2fe;padding:4px 16px;text-align:right;letter-spacing:1px;">CBS</th>
      </tr></thead>
      <tbody><tr>
        <td style="border:1.5px solid #0f172a;padding:4px 16px;font-weight:700;">${data.from_date ? fmtDate(data.from_date) : "BEGINNING"}</td>
        <td style="border:1.5px solid #0f172a;padding:4px 16px;font-weight:700;">${data.to_date ? fmtDate(data.to_date) : fmtDate(new Date().toISOString())}</td>
        <td style="border:1.5px solid #0f172a;padding:4px 16px;text-align:right;font-weight:800;">${inr(data.closing)}</td>
      </tr></tbody>
    </table>
    ${(data.manual_opening || 0) ? `<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:700;border:1px dashed #94a3b8;border-radius:6px;padding:6px 12px;margin-bottom:8px;"><span>Opening Balance (Brought Forward)</span><span>${inr(data.manual_opening)}</span></div>` : ""}
    <table class="tight" style="font-size:10px;border-top:2px solid #0f172a;">
      <thead><tr style="border-top:2px solid #0f172a;">
        <th style="border-top:2px solid #0f172a;">Branch</th><th style="border-top:2px solid #0f172a;">Particulars</th><th style="border-top:2px solid #0f172a;">Ref No.</th><th style="border-top:2px solid #0f172a;">Date</th>
        <th class="right" style="border-top:2px solid #0f172a;">Debit (Bill)</th><th class="right" style="border-top:2px solid #0f172a;">Vehicle Hire</th><th class="right" style="border-top:2px solid #0f172a;">Vehicle Advance</th><th class="right" style="border-top:2px solid #0f172a;">Vehicle Balance</th><th class="right" style="border-top:2px solid #0f172a;">Debit Expenses</th><th class="right" style="border-top:2px solid #0f172a;">Credit (Received)</th><th class="right" style="border-top:2px solid #0f172a;">Balance Amount</th>
      </tr></thead>
      <tbody>
        <tr style="background:#f8fafc;font-weight:700;"><td colspan="10" class="right">Opening Balance</td><td class="right">${inr(data.opening)}</td></tr>
        ${rows || `<tr><td colspan="11" class="muted">No transactions in this period.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="4" class="right">TOTAL</td>
          <td class="right">${inr(data.total_debit)}</td>
          <td class="right">${inr(data.total_hire || 0)}</td>
          <td class="right">${inr(data.total_advance || 0)}</td>
          <td class="right">${inr(data.total_vbalance || 0)}</td>
          <td class="right">${inr(data.total_expenses || 0)}</td>
          <td class="right">${inr(data.total_credit)}</td>
          <td class="right">${inr(data.closing)}</td>
        </tr>
        <tr style="font-weight:800;background:#e0f2fe;">
          <td colspan="10" class="right">CBS — CLOSING BALANCE (${data.from_date ? fmtDate(data.from_date) : "BEGINNING"} TO ${data.to_date ? fmtDate(data.to_date) : fmtDate(new Date().toISOString())})</td>
          <td class="right">${inr(data.closing)}</td>
        </tr>
      </tbody>
    </table>
    <div class="words"><b>Closing Balance:</b> ${esc(amountInWords(Math.abs(data.closing)))}${data.closing < 0 ? " (Credit)" : ""}</div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-top:48px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:180px;">Cashier</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:180px;">Branch Manager</div>
      <div style="width:240px;">
        <div style="font-weight:700;color:#0f172a;margin-bottom:38px;">For ${esc(COMPANY.name)}</div>
        <div style="border-top:1px solid #94a3b8;padding-top:6px;">Authorised Signatory</div>
      </div>
    </div>
  `;
  open(`Letter of Account - ${data.office || ""}`, inner, { frame: true });
}

const CBS_PRINT_COLORS = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#14b8a6", "#f43f5e", "#64748b"];

function cbsHistorySvg(hist) {
  const branches = hist?.branches || [];
  if (!branches.length || !branches[0].points?.length) return "";
  const n = branches[0].points.length;
  const vals = branches.flatMap((b) => b.points.map((p) => Number(p.cbs) || 0));
  let min = Math.min(...vals, 0);
  let max = Math.max(...vals, 0);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const W = 680, H = 180, PL = 76, PR = 12, PT = 12, PB = 26;
  const x = (i) => PL + (i * (W - PL - PR)) / Math.max(n - 1, 1);
  const y = (v) => PT + ((max - v) * (H - PT - PB)) / (max - min);
  const fmtK = (v) => (Math.abs(v) >= 1000 ? `₹${Math.round(v / 1000)}k` : `₹${Math.round(v)}`);
  const yTicks = [max, (max + min) / 2, min]
    .map((v) => `<line x1="${PL}" y1="${y(v)}" x2="${W - PR}" y2="${y(v)}" stroke="#e2e8f0" stroke-dasharray="3 3"/><text x="${PL - 6}" y="${y(v) + 3}" text-anchor="end" font-size="9" fill="#64748b">${fmtK(v)}</text>`)
    .join("");
  const xIdx = [0, Math.floor((n - 1) / 2), n - 1];
  const xTicks = xIdx
    .map((i) => `<text x="${x(i)}" y="${H - 8}" text-anchor="middle" font-size="9" fill="#64748b">${fmtDate(branches[0].points[i].date)}</text>`)
    .join("");
  const lines = branches
    .map((b, bi) => `<polyline fill="none" stroke="${CBS_PRINT_COLORS[bi % CBS_PRINT_COLORS.length]}" stroke-width="2" points="${b.points.map((p, i) => `${x(i).toFixed(1)},${y(Number(p.cbs) || 0).toFixed(1)}`).join(" ")}"/>`)
    .join("");
  const legend = branches
    .map((b, bi) => `<span style="margin-right:16px;white-space:nowrap;"><span style="display:inline-block;width:10px;height:10px;background:${CBS_PRINT_COLORS[bi % CBS_PRINT_COLORS.length]};border-radius:2px;margin-right:4px;vertical-align:middle;"></span>${esc(b.branch)}</span>`)
    .join("");
  return `<div style="margin-top:16px;page-break-inside:avoid;">
    <div style="font-weight:800;font-size:12px;letter-spacing:1px;margin-bottom:4px;">CBS HISTORY — LAST ${hist.days || 30} DAYS</div>
    <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="border:1px solid #cbd5e1;border-radius:4px;background:#fff;">
      ${yTicks}${xTicks}${lines}
    </svg>
    <div style="font-size:10px;margin-top:4px;">${legend}</div>
  </div>`;
}


export function printOfficeLedgerT(data) {
  if (!data) return;
  const debits = (data.rows || []).filter((r) => (r.effect || 0) > 0);
  const credits = (data.rows || []).filter((r) => (r.credit || 0) > 0);
  const totalDr = debits.reduce((s, r) => s + (r.effect || 0), 0) + (data.opening > 0 ? data.opening : 0);
  const totalCr = credits.reduce((s, r) => s + (r.credit || 0), 0) + (data.opening < 0 ? -data.opening : 0);
  const side = (rows, amtKey) =>
    rows
      .map(
        (r) => `<tr>
        <td style="white-space:nowrap;">${fmtDateDay(r.date)}</td>
        <td>${esc(r.particulars || "")}${r.ref ? ` <span style="color:#64748b;">(${esc(r.ref)})</span>` : ""}</td>
        <td class="right" style="white-space:nowrap;">${inr(amtKey === "effect" ? r.effect : r.credit)}</td>
      </tr>`
      )
      .join("");
  const inner = `
    <div class="title" style="letter-spacing:2px;">LETTER OF ACCOUNT — ${esc((data.office || "").toUpperCase())} OFFICE</div>
    <div style="text-align:center;margin:6px 0 12px;font-size:12px;font-weight:800;">
      PERIOD: ${data.from_date ? fmtDate(data.from_date) : "BEGINNING"} TO ${data.to_date ? fmtDate(data.to_date) : fmtDate(new Date().toISOString())}
    </div>
    <div style="display:flex;gap:0;border:2px solid #0f172a;border-radius:6px;overflow:hidden;">
      <div style="flex:1;border-right:2px solid #0f172a;">
        <div style="background:#0f172a;color:#fff;text-align:center;font-weight:800;padding:6px;font-size:12px;letter-spacing:2px;">DEBIT (DR.)</div>
        <table class="tight" style="font-size:10px;margin:0;">
          <thead><tr><th>Date</th><th>Particulars</th><th class="right">Amount</th></tr></thead>
          <tbody>
            ${data.opening > 0 ? `<tr style="background:#f8fafc;font-weight:700;"><td>—</td><td>Opening Balance b/f</td><td class="right">${inr(data.opening)}</td></tr>` : ""}
            ${side(debits, "effect") || `<tr><td colspan="3" class="muted">No debit entries.</td></tr>`}
            <tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL DR.</td><td class="right">${inr(totalDr)}</td></tr>
          </tbody>
        </table>
      </div>
      <div style="flex:1;">
        <div style="background:#065f46;color:#fff;text-align:center;font-weight:800;padding:6px;font-size:12px;letter-spacing:2px;">CREDIT (CR.)</div>
        <table class="tight" style="font-size:10px;margin:0;">
          <thead><tr><th>Date</th><th>Particulars</th><th class="right">Amount</th></tr></thead>
          <tbody>
            ${data.opening < 0 ? `<tr style="background:#f8fafc;font-weight:700;"><td>—</td><td>Opening Balance b/f</td><td class="right">${inr(-data.opening)}</td></tr>` : ""}
            ${side(credits, "credit") || `<tr><td colspan="3" class="muted">No credit entries.</td></tr>`}
            <tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL CR.</td><td class="right">${inr(totalCr)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <div style="margin-top:10px;padding:8px 14px;background:#f1f5f9;border-radius:6px;font-size:12px;font-weight:800;display:flex;justify-content:space-between;">
      <span>CLOSING BALANCE (Dr. − Cr.)</span><span>${inr(data.closing)}</span>
    </div>
    <div class="words"><b>Closing Balance:</b> ${esc(amountInWords(Math.abs(data.closing)))}${data.closing < 0 ? " (Credit)" : ""}</div>
    <div style="display:flex;justify-content:flex-end;margin-top:44px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:260px;">For ${esc(COMPANY.name)} — Authorised Signatory</div>
    </div>
  `;
  open(`Letter of Account - ${data.office || ""}`, inner);
}


export function printVoucherRegister(rows, opts = {}) {
  const arr = rows || [];
  const total = arr.reduce((s, v) => s + (Number(v.amount) || 0), 0);
  const byMode = {};
  arr.forEach((v) => {
    const m = v.payment_mode || "Cash";
    byMode[m] = (byMode[m] || 0) + (Number(v.amount) || 0);
  });
  const period = `${opts.from ? fmtDate(opts.from) : "START"} — ${opts.to ? fmtDate(opts.to) : "TODAY"}`;
  const body = arr
    .map(
      (v, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(String(v.voucher_no || "").replace(/^DV[-\s]?/i, "") || "—")}</td>
      <td>${v.date ? fmtDate(v.date) : "—"}</td>
      <td>${esc(v.branch || "Chennai")}</td>
      <td>${esc(v.party_name || "—")}</td>
      <td>${esc(v.account_no || "—")}</td>
      <td>${esc(v.category || "—")}</td>
      <td>${esc(v.payment_mode || "Cash")}${v.cheque_no || v.reference ? " / " + esc(v.cheque_no || v.reference) : ""}</td>
      <td>${esc(v.narration || "—")}</td>
      <td class="right">${inr(Number(v.amount) || 0)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DEBIT VOUCHER REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${esc(period)}</div>
    <div style="margin:0 0 12px;display:flex;flex-wrap:wrap;gap:8px 20px;font-size:11px;">
      <div><b>Vouchers:</b> ${arr.length}</div>
      ${Object.entries(byMode).map(([m, a]) => `<div><b>${esc(m)}:</b> ${inr(a)}</div>`).join("")}
      <div><b>Grand Total:</b> ${inr(total)}</div>
    </div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>Voucher No.</th><th>Date</th><th>Branch</th><th>Paid To</th><th>A/c</th><th>Category</th><th>Mode / Ref</th><th>Narration</th><th class="right">Amount</th>
      </tr></thead>
      <tbody>
        ${body}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="9" class="right">TOTAL</td><td class="right">${inr(total)}</td></tr>
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:44px;text-align:center;font-size:11px;color:#475569;">
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:23%;">Prepared By</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:23%;">Passed By</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:23%;">Cashier</div>
      <div style="border-top:1px solid #94a3b8;padding-top:6px;width:23%;">Authorised Signatory</div>
    </div>
  `;
  open("Voucher Register", inner);
}

export function printStationaryUsage(entries, opts = {}) {
  const arr = entries || [];
  const period = opts.from || opts.to ? `${opts.from ? fmtDate(opts.from) : "START"} — ${opts.to ? fmtDate(opts.to) : "TODAY"}` : "ALL DATES";
  const block = (label, u) => {
    if (!u) return "";
    const nums = u.pending
      .map((n) => `<span style="display:inline-block;border:1px solid #cbd5e1;border-radius:4px;padding:2px 8px;margin:2px;font-weight:600;">${n}</span>`)
      .join("");
    return `
      <div style="margin:8px 0 12px;">
        <div style="font-size:11px;font-weight:800;margin:4px 0;">${esc(label)} — Range ${u.from} → ${u.to} &nbsp;|&nbsp; Used: ${u.used} &nbsp;|&nbsp; Pending: ${u.pending.length}</div>
        <div style="font-size:11px;line-height:1.9;">${nums || '<span style="color:#059669;font-weight:700;">None — all numbers in this range are used.</span>'}</div>
      </div>`;
  };
  const section = (r, i) => {
    const u = r._usage;
    return `
    <div style="${i < arr.length - 1 ? "page-break-after:always;" : ""}margin-bottom:16px;">
      <div style="margin:0 0 8px;padding:7px 12px;border:1px solid #0f172a;border-radius:6px;font-size:12px;display:flex;justify-content:space-between;gap:12px;">
        <div><b>BRANCH:</b> ${esc(r.office || "Chennai")} &nbsp;&nbsp; <b>Issued:</b> ${r.date ? fmtDate(r.date) : "—"}</div>
        <div>${["cn", "challan", "mr"].filter((k) => u[k]).map((k) => ({ cn: "LR", challan: "Challan", mr: "MR" }[k])).join(" · ")} usage</div>
      </div>
      ${block("PENDING LR / CN NUMBERS", u.cn)}
      ${block("PENDING CHALLAN NUMBERS", u.challan)}
      ${block("PENDING MR NUMBERS", u.mr)}
    </div>`;
  };
  const inner = `
    <div class="title" style="letter-spacing:3px;">STATIONARY — PENDING NUMBERS REPORT</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${esc(period)}</div>
    ${arr.map(section).join("")}
  `;
  open("Stationary Pending Numbers", inner);
}

export function printBranchActivity(data, monthLabel) {
  const rows = (data && data.rows) || [];
  const t = (data && data.totals) || {};
  const body = rows
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.branch)}</td>
      <td class="right">${r.bookings}</td>
      <td class="right">${r.billing_count}</td>
      <td class="right">${inr(r.billing_total)}</td>
      <td class="right">${r.receipts_count}</td>
      <td class="right">${inr(r.receipts_total)}</td>
      <td class="right">${r.market_trips || 0}</td>
      <td class="right">${inr(r.market_hire)}</td>
      <td class="right">${r.challan_trips || 0}</td>
      <td class="right" style="font-weight:700;">${inr(r.challan_hire)}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">BRANCH ACTIVITY SNAPSHOT</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th class="right">Bookings</th><th class="right">Bills</th><th class="right">Billing (Rs.)</th><th class="right">MRs</th><th class="right">Receipts (Rs.)</th><th class="right">Mkt Trips</th><th class="right">Mkt Hire (Rs.)</th><th class="right">Challans</th><th class="right">Vehicle Hire (Rs.)</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="11" class="muted">No records.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL</td><td class="right">${t.bookings || 0}</td><td class="right">${t.billing_count || 0}</td><td class="right">${inr(t.billing_total)}</td><td class="right">${t.receipts_count || 0}</td><td class="right">${inr(t.receipts_total)}</td><td class="right">${t.market_trips || 0}</td><td class="right">${inr(t.market_hire)}</td><td class="right">${t.challan_trips || 0}</td><td class="right">${inr(t.challan_hire)}</td></tr>
      </tbody>
    </table>
  `;
  open("Branch Activity Snapshot", inner);
}

export function printMarketVehicles(rows, monthLabel) {
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.date ? fmtDate(r.date) : "")}</td>
      <td style="font-weight:600;">${esc(r.branch || "")}</td>
      <td>${esc(r.from_place || "")}</td>
      <td>${esc(r.destination || "")}</td>
      <td>${esc(r.customer_code || "")}</td>
      <td>${esc(r.customer_name || "")}</td>
      <td>${esc(r.vendor_name || "")}</td>
      <td>${esc(r.vehicle_type || "")}</td>
      <td style="white-space:nowrap;">${esc(r.vehicle_no || "")}</td>
      <td>${esc(r.weight || "")}</td>
      <td class="right">${inr(r.vehicle_hire)}</td>
      <td class="right">${inr(r.freight)}</td>
      <td class="right">${inr((Number(r.freight) || 0) - (Number(r.vehicle_hire) || 0))}</td>
      <td>${esc(r.status || "Pending")}</td>
    </tr>`
    )
    .join("");
  const th = (rows || []).reduce((s, r) => s + (Number(r.vehicle_hire) || 0), 0);
  const tf = (rows || []).reduce((s, r) => s + (Number(r.freight) || 0), 0);
  const inner = `
    <div class="title" style="letter-spacing:3px;">MARKET VEHICLE ENGAGED</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")}</div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>Date</th><th>Branch</th><th>From</th><th>Destination</th><th>Code</th><th>Customer</th><th>Vendor/Broker</th><th>Vehicle Type</th><th>Vehicle No.</th><th>Weight</th><th class="right">Vehicle Hire</th><th class="right">Freight</th><th class="right">Margin</th><th>Status</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="15" class="muted">No records.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="11" class="right">TOTAL</td><td class="right">${inr(th)}</td><td class="right">${inr(tf)}</td><td class="right">${inr(tf - th)}</td><td></td></tr>
      </tbody>
    </table>
  `;
  open("Market Vehicle Engaged", inner);
}

export function printMarketMargin(rows, monthLabel) {
  const byBranch = {};
  (rows || []).forEach((r) => {
    const b = r.branch || "—";
    const a = (byBranch[b] = byBranch[b] || { trips: 0, freight: 0, hire: 0 });
    a.trips += 1;
    a.freight += Number(r.freight) || 0;
    a.hire += Number(r.vehicle_hire) || 0;
  });
  const entries = Object.entries(byBranch).sort((a, b) => a[0].localeCompare(b[0]));
  const t = { trips: 0, freight: 0, hire: 0 };
  const body = entries
    .map(([b, a], i) => {
      t.trips += a.trips;
      t.freight += a.freight;
      t.hire += a.hire;
      const m = a.freight - a.hire;
      const pct = a.freight > 0 ? ((m / a.freight) * 100).toFixed(1) : "—";
      return `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(b)}</td>
      <td class="right">${a.trips}</td>
      <td class="right">${inr(a.freight)}</td>
      <td class="right">${inr(a.hire)}</td>
      <td class="right" style="font-weight:700;color:${m >= 0 ? "#047857" : "#dc2626"};">${inr(m)}</td>
      <td class="right">${pct}${pct === "—" ? "" : "%"}</td>
    </tr>`;
    })
    .join("");
  const tm = t.freight - t.hire;
  const tpct = t.freight > 0 ? ((tm / t.freight) * 100).toFixed(1) + "%" : "—";
  const inner = `
    <div class="title" style="letter-spacing:3px;">MARKET VEHICLE MARGIN REPORT</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">MONTH: ${esc(monthLabel || "")} &nbsp;•&nbsp; APPROVED / CHALLAN ISSUED TRIPS ONLY</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th class="right">Trips</th><th class="right">Freight (Rs.)</th><th class="right">Vehicle Hire (Rs.)</th><th class="right">Margin (Rs.)</th><th class="right">Margin %</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="7" class="muted">No approved trips for this month.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="2" class="right">TOTAL</td><td class="right">${t.trips}</td><td class="right">${inr(t.freight)}</td><td class="right">${inr(t.hire)}</td><td class="right">${inr(tm)}</td><td class="right">${tpct}</td></tr>
      </tbody>
    </table>
  `;
  open("Market Vehicle Margin Report", inner);
}

export function printEwayBills(rows) {
  const arr = rows || [];
  const PER_PAGE = 20;
  const chunks = [];
  for (let i = 0; i < arr.length; i += PER_PAGE) chunks.push(arr.slice(i, i + PER_PAGE));
  if (!chunks.length) chunks.push([]);
  const rowHtml = (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.eway_bill || "")}</td>
      <td>${esc(r.lr_no || r.consignment_no || "—")}</td>
      <td style="white-space:nowrap;">${r.lr_date ? fmtDateDay(r.lr_date) : "—"}</td>
      <td>${esc(r._customer || "—")}</td>
      <td>${esc(r.origin || "—")}</td>
      <td>${esc(r.destination || "—")}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.vehicle_type || "—")}</td>
      <td>${r._validUpto ? fmtDate(r._validUpto) : "—"}</td>
      <td style="${r._expired ? "color:#dc2626;font-weight:700;" : ""}">${r._expired ? fmtDate(r._autoExtended) : "—"}</td>
      <td>${r._delivered ? "Yes" : "No"}</td>
      <td>${r._delivered ? "Delivered · closed" : r._expired ? "Needs extension" : "Active"}</td>
    </tr>`;
  const thead = `<thead><tr>
        <th>Sl</th><th>E-Way Bill No.</th><th>LR No.</th><th>LR Date</th><th>Customer Name</th><th>From</th><th>Destination</th><th>Vehicle No.</th><th>Vehicle Type</th><th>Valid Upto</th><th>Auto-extended</th><th>Delivered</th><th>Status</th>
      </tr></thead>`;
  const inner = chunks
    .map((chunk, pi) => {
      const body = chunk.map((r, j) => rowHtml(r, pi * PER_PAGE + j)).join("");
      return `<div style="${pi < chunks.length - 1 ? "page-break-after:always;" : ""}">
    ${pi === 0
      ? `<div class="title" style="letter-spacing:3px;">E-WAY BILL REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${arr.length} E-WAY BILL(S)</div>`
      : `<div style="margin:0 0 8px;font-size:11px;font-weight:800;letter-spacing:1px;">E-WAY BILL REGISTER (contd.)</div>`}
    <table style="font-size:10px;">
      ${thead}
      <tbody>${body || `<tr><td colspan="13" class="muted">No records.</td></tr>`}</tbody>
    </table>
    <div style="text-align:right;font-size:10px;margin-top:4px;color:#475569;">Page ${pi + 1} of ${chunks.length} · Entries ${arr.length ? pi * PER_PAGE + 1 : 0}–${Math.min((pi + 1) * PER_PAGE, arr.length)} of ${arr.length}</div>
  </div>`;
    })
    .join("");
  open("E-Way Bill Register", inner);
}

export function printDriverCertificate(d) {
  const monthLabel = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const inner = `
    <div style="border:3px double #0f172a;border-radius:10px;padding:48px 40px;text-align:center;margin-top:12px;">
      <div style="font-size:12px;letter-spacing:6px;font-weight:800;">NISHA TRANSPORT CORPORATION</div>
      <div style="font-size:30px;font-weight:900;letter-spacing:4px;margin:18px 0 6px;">CERTIFICATE OF APPRECIATION</div>
      <div style="font-size:13px;letter-spacing:2px;font-weight:700;color:#b45309;">★ DRIVER OF THE MONTH — ${esc(monthLabel.toUpperCase())} ★</div>
      <div style="margin:30px auto 10px;font-size:14px;">This certificate is proudly presented to</div>
      <div style="font-size:34px;font-weight:900;letter-spacing:2px;border-bottom:2px solid #0f172a;display:inline-block;padding:0 28px 6px;">${esc(d.name || d.driver_name || "")}</div>
      <div style="margin:22px auto 0;font-size:13px;max-width:520px;line-height:1.7;">
        in recognition of completing <b>${d.trips || 0} trip${(d.trips || 0) === 1 ? "" : "s"}</b> this month${(d.routes || []).length ? ` on <b>${esc((d.routes || []).slice(0, 3).join(", "))}</b>` : ""},
        with reliability and dedication that keeps NTC consignments moving on time.
      </div>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:56px;font-size:11px;font-weight:700;">
        <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:170px;text-align:center;">Date: ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
        <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:200px;text-align:center;">For Nisha Transport Corporation<br/>Authorised Signatory</div>
      </div>
    </div>
  `;
  open(`Driver of the Month — ${d.name || ""}`, inner);
}

export function printExtraChargesReport(rows, monthLabel) {
  const ex = (r, k) => Number(r[k]) || 0;
  const tot = (r) => ex(r, "unloading_charge") + ex(r, "challan_charge") + ex(r, "extra_weight_charge") + ex(r, "extra_delivery_charge");
  const byBranch = {};
  (rows || []).forEach((r) => {
    const b = r.engaged_office || r.office || "Chennai";
    (byBranch[b] = byBranch[b] || []).push(r);
  });
  const cell = (v) => `<td style="text-align:right;">${v ? inr(v) : "—"}</td>`;
  let grand = { u: 0, c: 0, w: 0, d: 0, t: 0 };
  const sections = Object.keys(byBranch).sort().map((b) => {
    const list = byBranch[b].slice().sort((a, x) => String(a.loading_date || "").localeCompare(String(x.loading_date || "")));
    const sub = { u: 0, c: 0, w: 0, d: 0, t: 0 };
    const body = list.map((r, i) => {
      sub.u += ex(r, "unloading_charge"); sub.c += ex(r, "challan_charge"); sub.w += ex(r, "extra_weight_charge"); sub.d += ex(r, "extra_delivery_charge"); sub.t += tot(r);
      return `<tr>
        <td>${i + 1}</td>
        <td style="white-space:nowrap;">${r.loading_date ? fmtDate(r.loading_date) : "—"}</td>
        <td style="font-weight:700;">${esc(String(r.challan_no || "—").replace(/^VHC[-\s]?/i, ""))}</td>
        <td style="white-space:nowrap;">${esc(r.vehicle_no || "—")}</td>
        <td>${esc(r.vendor_broker_name || r.owner_name || "—")}</td>
        ${cell(ex(r, "unloading_charge"))}${cell(ex(r, "challan_charge"))}${cell(ex(r, "extra_weight_charge"))}${cell(ex(r, "extra_delivery_charge"))}
        <td style="text-align:right;font-weight:700;">${inr(tot(r))}</td>
        <td style="font-size:9px;">${esc(r.extra_charge_reason || "—")}</td>
      </tr>`;
    }).join("");
    grand = { u: grand.u + sub.u, c: grand.c + sub.c, w: grand.w + sub.w, d: grand.d + sub.d, t: grand.t + sub.t };
    return `
      <div style="margin:10px 0 4px;font-size:11px;font-weight:800;">${esc(b)} — ${list.length} challan(s)</div>
      <table class="tight">
        <thead><tr><th>Sl</th><th>Loading</th><th>Challan No.</th><th>Vehicle No.</th><th>Vendor / Owner</th><th style="text-align:right;">Unloading</th><th style="text-align:right;">Challan</th><th style="text-align:right;">Extra Wt</th><th style="text-align:right;">Extra Dly</th><th style="text-align:right;">Total</th><th>Reason</th></tr></thead>
        <tbody>${body}
          <tr style="background:#f8fafc;font-weight:800;"><td colspan="5" style="text-align:right;">${esc(b)} TOTAL</td><td style="text-align:right;">${inr(sub.u)}</td><td style="text-align:right;">${inr(sub.c)}</td><td style="text-align:right;">${inr(sub.w)}</td><td style="text-align:right;">${inr(sub.d)}</td><td style="text-align:right;">${inr(sub.t)}</td><td></td></tr>
        </tbody>
      </table>`;
  }).join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">EXTRA CHARGES REPORT</div>
    <div style="margin:0 0 10px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "All")} &nbsp;&nbsp; <b>Challans with extra charges:</b> ${(rows || []).length} &nbsp;&nbsp; <b>Grand Total: ${inr(grand.t)}</b></div>
    ${sections || '<div class="muted">No extra charges found for this month.</div>'}
    ${sections ? `<div style="margin-top:10px;font-size:11px;font-weight:800;">GRAND TOTAL — Unloading ${inr(grand.u)} · Challan ${inr(grand.c)} · Extra Wt ${inr(grand.w)} · Extra Dly ${inr(grand.d)} · <u>Total ${inr(grand.t)}</u></div>` : ""}
  `;
  open("NTC Extra Charges Report", inner);
}

export function printYearlyCharges(data, prev) {
  const rows = (data?.rows || []).filter((r) => r.charges_total > 0 || r.edits_count > 0);
  const t = data?.totals || {};
  const pt = prev?.totals || {};
  const money = (v) => (Number(v) ? inr(v) : "—");
  const body = rows.map((r) => `<tr${r.over_cap ? ' style="background:#fef2f2;"' : ""}>
    <td style="font-weight:700;">${esc(r.branch)}${r.over_cap ? ' <b style="color:#dc2626;font-size:8.5px;">⚠ OVER CAP</b>' : ""}</td>
    <td style="text-align:right;">${money(r.unloading_charge)}</td>
    <td style="text-align:right;">${money(r.challan_charge)}</td>
    <td style="text-align:right;">${money(r.extra_weight_charge)}</td>
    <td style="text-align:right;">${money(r.extra_delivery_charge)}</td>
    <td style="text-align:right;font-weight:800;${r.over_cap ? "color:#dc2626;" : ""}">${money(r.charges_total)}</td>
    <td style="text-align:right;">${money(r.cap)}</td>
    <td style="text-align:right;">${r.challans_with_extras || "—"}</td>
    <td style="text-align:right;">${r.edits_count || "—"}</td>
    <td style="text-align:right;">${money(r.edits_total)}</td>
  </tr>`).join("");
  const pct = Number(pt.charges_total) > 0 && Number(t.charges_total) > 0 ? Math.round(((t.charges_total - pt.charges_total) / pt.charges_total) * 100) : null;
  const prevRow = prev && (Number(pt.charges_total) > 0 || Number(pt.edits_count) > 0)
    ? `<tr style="color:#64748b;"><td style="font-weight:700;">LAST FY (${esc(prev.fy || "")})</td><td style="text-align:right;">${money(pt.unloading_charge)}</td><td style="text-align:right;">${money(pt.challan_charge)}</td><td style="text-align:right;">${money(pt.extra_weight_charge)}</td><td style="text-align:right;">${money(pt.extra_delivery_charge)}</td><td style="text-align:right;font-weight:700;">${money(pt.charges_total)}</td><td></td><td style="text-align:right;">${pt.challans_with_extras || "—"}</td><td style="text-align:right;">${pt.edits_count || "—"}</td><td style="text-align:right;">${money(pt.edits_total)}</td></tr>`
    : "";
  const inner = `
    <div class="title" style="letter-spacing:3px;">YEARLY EXTRA CHARGES SUMMARY</div>
    <div style="margin:0 0 10px;font-size:11px;"><b>Financial Year:</b> ${esc(data?.fy || "")} (Apr–Mar) &nbsp;&nbsp; <b>Total extra charges:</b> ${money(t.charges_total)}${pct !== null ? ` <b style="color:${pct > 0 ? "#dc2626" : "#047857"};">(${pct > 0 ? "▲ +" : "▼ "}${Math.abs(pct)}% vs FY ${esc(prev?.fy || "")})</b>` : ""} &nbsp;&nbsp; <b>Admin edits:</b> ${t.edits_count || 0} (${money(t.edits_total)} added)</div>
    ${body ? `<table class="tight">
      <thead><tr><th>Branch</th><th style="text-align:right;">Unloading</th><th style="text-align:right;">Challan</th><th style="text-align:right;">Extra Weight</th><th style="text-align:right;">Extra Delivery</th><th style="text-align:right;">Total Charges</th><th style="text-align:right;">Yearly Cap</th><th style="text-align:right;">Challans w/ Extras</th><th style="text-align:right;">Admin Edits</th><th style="text-align:right;">Edits Added</th></tr></thead>
      <tbody>${body}
      <tr style="background:#f1f5f9;font-weight:800;"><td>TOTAL</td><td style="text-align:right;">${money(t.unloading_charge)}</td><td style="text-align:right;">${money(t.challan_charge)}</td><td style="text-align:right;">${money(t.extra_weight_charge)}</td><td style="text-align:right;">${money(t.extra_delivery_charge)}</td><td style="text-align:right;">${money(t.charges_total)}</td><td></td><td style="text-align:right;">${t.challans_with_extras || "—"}</td><td style="text-align:right;">${t.edits_count || "—"}</td><td style="text-align:right;">${money(t.edits_total)}</td></tr>
      ${prevRow}
      </tbody>
    </table>` : '<div class="muted">No extra charges or admin edits recorded in this financial year.</div>'}
    <div style="margin-top:14px;font-size:10px;color:#64748b;">For audit — extra charges from Vehicle Hire Challans plus admin extra-amount edits (Vehicle Payments), April to March. Branches over their yearly cap are flagged.</div>
  `;
  open("NTC Yearly Extra Charges Summary", inner);
}

export function printChargeDecisions(rows, monthLabel, edits) {
  let ap = 0, rj = 0;
  const body = (rows || []).map((h, i) => {
    const rejected = /Rejected/i.test(h.field_label || "") || /^Rejected/i.test(h.new_value || "");
    rejected ? rj++ : ap++;
    const note = String(h.new_value || "").replace(/^(Approved|Rejected)\s*—\s*/i, "").replace(/^Extra charges [^—]*—\s*/i, "");
    const amt = (String(h.old_value || "").match(/₹[\d,]+/) || [""])[0] || (String(h.new_value || "").match(/₹[\d,]+/) || [""])[0];
    return `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;">${fmtDate(String(h.decided_at || h.created_at || "").slice(0, 10))}</td>
      <td style="font-weight:700;">${esc(h.lr_no || "—")}</td>
      <td style="white-space:nowrap;">${esc(h.customer_name || "—")}</td>
      <td>${esc(h.office || "—")}</td>
      <td style="text-align:right;">${esc(amt || "—")}</td>
      <td style="font-weight:800;color:${rejected ? "#b91c1c" : "#047857"};">${rejected ? "REJECTED" : "APPROVED"}${h.field === "charge_override" ? ' <span style="font-weight:400;font-size:8.5px;color:#64748b;">(over-limit save)</span>' : ""}</td>
      <td style="font-size:9px;">${esc(note || "—")}</td>
      <td style="white-space:nowrap;">${esc(h.decided_by || h.requested_by || "—")}</td>
    </tr>`;
  }).join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">EXTRA CHARGE APPROVAL DECISIONS</div>
    <div style="margin:0 0 10px;font-size:11px;"><b>Month:</b> ${esc(monthLabel || "All")} &nbsp;&nbsp; <b>Decisions:</b> ${(rows || []).length} &nbsp;&nbsp; <b style="color:#047857;">Approved: ${ap}</b> &nbsp;&nbsp; <b style="color:#b91c1c;">Rejected: ${rj}</b></div>
    ${body ? `<table class="tight">
      <thead><tr><th>Sl</th><th>Decided On</th><th>Challan No.</th><th>Vehicle No.</th><th>Branch</th><th style="text-align:right;">Extra Charges</th><th>Decision</th><th>Note / Reason</th><th>By</th></tr></thead>
      <tbody>${body}</tbody>
    </table>` : '<div class="muted">No approve/reject decisions found for this month.</div>'}
    ${(() => {
      const ed = edits || [];
      if (!ed.length) return "";
      const num = (s) => Number(String(s || "").split("—")[0].replace(/[^\d.]/g, "")) || 0;
      let added = 0;
      const erows = ed.map((h, i) => {
        const delta = Math.max(num(h.new_value) - num(h.old_value), 0);
        added += delta;
        const note = String(h.new_value || "").split("—").slice(1).join("—").trim();
        return `<tr>
          <td>${i + 1}</td>
          <td style="white-space:nowrap;">${fmtDate(String(h.created_at || "").slice(0, 10))}</td>
          <td style="font-weight:700;">${esc(h.lr_no || "—")}</td>
          <td style="white-space:nowrap;">${esc(h.customer_name || "—")}</td>
          <td>${esc(h.office || "—")}</td>
          <td style="white-space:nowrap;">${esc(String(h.field_label || "").replace(" (Vehicle Payments)", ""))}</td>
          <td style="text-align:right;">${esc(h.old_value || "₹0")} → <b>${esc(String(h.new_value || "").split("—")[0].trim())}</b></td>
          <td style="font-size:9px;">${esc(note || "—")}</td>
          <td style="white-space:nowrap;">${esc(h.decided_by || h.requested_by || "—")}</td>
        </tr>`;
      }).join("");
      return `
        <div style="margin:14px 0 4px;font-size:11px;font-weight:800;letter-spacing:1px;">ADMIN EXTRA-AMOUNT EDITS (Vehicle Payments) — ${ed.length} edit(s), ${inr(added)} added</div>
        <table class="tight">
          <thead><tr><th>Sl</th><th>Date</th><th>Challan No.</th><th>Vehicle No.</th><th>Branch</th><th>Charge</th><th style="text-align:right;">Old → New</th><th>Reason</th><th>By</th></tr></thead>
          <tbody>${erows}</tbody>
        </table>`;
    })()}
    <div style="margin-top:14px;font-size:10px;color:#64748b;">For the accounts file — every decision above is also recorded in the Correction Log Book.</div>
  `;
  open("NTC Charge Approval Decisions", inner);
}

export function printRouteRateRegister(stats) {
  const body = (stats || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(r.origin)} → ${esc(r.destination)}</td>
      <td style="text-align:center;">${r.trips}</td>
      <td style="text-align:right;font-weight:800;">${inr(r.avg)}</td>
      <td style="text-align:right;">${inr(r.min)}</td>
      <td style="text-align:right;">${inr(r.max)}</td>
      <td style="text-align:right;">${inr(r.last?.hire_amount)}</td>
      <td style="white-space:nowrap;">${esc(r.last?.vehicle_no || "—")} · ${fmtDate(String(r.last?.loading_date || r.last?.date || "").slice(0, 10))}</td>
      <td style="text-align:right;">${r.saved ? inr(r.saved) : "—"}</td>
      <td style="text-align:center;white-space:nowrap;${r.trend === null ? "" : r.trend > 0 ? "font-weight:800;color:#b91c1c;" : r.trend < 0 ? "font-weight:800;color:#047857;" : ""}">${r.trend === null ? "—" : r.trend === 0 ? "No change" : `${r.trend > 0 ? "▲ +" : "▼ −"}${inr(Math.abs(r.trend))}`}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">ROUTE RATE REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:11px;font-weight:800;text-align:center;letter-spacing:0.5px;">AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${(stats || []).length} ROUTE(S) — HIRE BENCHMARKS FOR RATE NEGOTIATIONS</div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Route</th><th>Trips</th><th>Avg Hire</th><th>Min</th><th>Max</th><th>Last Paid</th><th>Last Vehicle · Date</th><th>Freight Master Rate</th><th>Avg vs Last Month</th></tr></thead>
      <tbody>${body || `<tr><td colspan="10" class="muted">No routes with paid hires.</td></tr>`}</tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Traffic In Charge</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Admin</div>
    </div>
  `;
  open("NTC Route Rate Register", inner);
}

export function printDriverTrips(d, rows) {
  const trips = rows || [];
  const totHire = trips.reduce((a, c) => a + (Number(c.hire_amount) || 0), 0);
  const totBal = trips.reduce((a, c) => a + (Number(c.balance) || 0), 0);
  const body = trips
    .map(
      (c, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:700;">${esc(String(c.challan_no || "").replace(/^VHC[-\s]?/i, "") || "—")}</td>
      <td style="white-space:nowrap;">${fmtDate(String(c.loading_date || c.date || "").slice(0, 10))}</td>
      <td>${esc(c.vehicle_no || "—")}</td>
      <td>${esc([c.origin, c.destination].filter(Boolean).join(" → ") || "—")}</td>
      <td style="text-align:right;">${inr(c.hire_amount)}</td>
      <td style="text-align:right;">${inr(c.balance)}</td>
      <td style="text-align:center;${c.pod_ack_received ? "" : "font-weight:800;color:#b45309;"}">${c.pod_ack_received ? "Back" : "OUT"}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DRIVER TRIP HISTORY</div>
    <div style="margin:0 0 12px;padding:10px 14px;border:1px solid #0f172a;border-radius:6px;font-size:11px;display:flex;flex-wrap:wrap;gap:4px 28px;">
      <span><b>Driver:</b> ${esc(d.driver_name || "—")}</span>
      <span><b>Mobile:</b> ${esc(d.mobile || "—")}</span>
      <span><b>License No.:</b> ${esc(d.license_no || "—")}</span>
      <span><b>DL Expiry:</b> ${d.dl_expiry ? fmtDate(String(d.dl_expiry).slice(0, 10)) : "—"}</span>
      <span><b>Vehicle:</b> ${esc(d.vehicle_no || "—")}</span>
      <span><b>Owner:</b> ${esc(d.owner_name || "—")}</span>
      <span><b>Trips:</b> ${trips.length}</span>
    </div>
    <table class="tight">
      <thead><tr><th>Sl</th><th>Challan No.</th><th>Loading Date</th><th>Vehicle</th><th>Route</th><th>Vehicle Hire</th><th>Balance</th><th>POD</th></tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No trips.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;"><td colspan="5" style="text-align:right;">TOTAL</td><td style="text-align:right;">${inr(totHire)}</td><td style="text-align:right;">${inr(totBal)}</td><td></td></tr>
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:48px;font-size:11px;font-weight:700;">
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Driver Signature</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Prepared By</div>
      <div style="border-top:1px solid #0f172a;padding-top:4px;min-width:160px;text-align:center;">Branch Manager</div>
    </div>
  `;
  open(`Driver Trips — ${d.driver_name || ""}`, inner);
}

export function printDriverMaster(rows) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const expired = (d) => d && String(d).slice(0, 10) < todayStr;
  const tick = (r, k) => ((r.documents || {})[k] ? "✓" : "✗");
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.dm_no || "—")}</td>
      <td style="font-weight:700;">${esc(r.driver_name || "")}</td>
      <td>${esc(r.branch || "—")}</td>
      <td>${esc(r.mobile || "—")}</td>
      <td>${esc(r.license_no || "—")}</td>
      <td style="white-space:nowrap;${expired(r.dl_expiry) ? "font-weight:800;color:#b91c1c;" : ""}">${r.dl_expiry ? fmtDate(r.dl_expiry) + (expired(r.dl_expiry) ? " (EXPIRED)" : "") : "—"}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td>${esc(r.owner_name || "—")}</td>
      <td>${esc(r.owner_mobile || "—")}</td>
      <td style="text-align:center;">${tick(r, "driver_photo")}</td>
      <td style="text-align:center;">${tick(r, "driving_license")}</td>
      <td style="text-align:center;">${tick(r, "aadhar_card")}</td>
      <td style="text-align:center;">${Object.keys(r.documents || {}).length}/8</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">DRIVER MASTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${(rows || []).length} DRIVER(S)</div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>DM No.</th><th>Driver Name</th><th>Branch</th><th>Driver Mobile No.</th><th>License No.</th><th>DL Expiry Date</th><th>Vehicle No.</th><th>Vehicle Owner Name</th><th>Owner Mobile</th><th>Photo</th><th>DL</th><th>Aadhar</th><th>Docs</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="14" class="muted">No drivers.</td></tr>`}</tbody>
    </table>
  `;
  open("Driver Master", inner);
}

export function printVehicleMaster(rows) {
  const isBl = (r) => String(r?.blacklisted || "").toLowerCase() === "yes";
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "—")}</td>
      <td style="font-weight:700;">${esc(r.vehicle_no || "")}${isBl(r) ? ' <span style="color:#b91c1c;font-weight:800;">(BLACKLISTED)</span>' : ""}</td>
      <td>${esc(r.vehicle_type || "—")}</td>
      <td style="text-align:center;">${esc(r.make || "—")}</td>
      <td>${esc(r.model || "—")}</td>
      <td>${esc(r.chassis_no || "—")}</td>
      <td>${esc(r.engine_no || "—")}</td>
      <td>${esc(r.owner_name || "—")}</td>
      <td>${esc(r.owner_mobile || "—")}</td>
      <td>${esc(r.insurance_co || "—")}</td>
      <td style="white-space:nowrap;">${r.ins_valid_upto ? fmtDate(r.ins_valid_upto) : "—"}</td>
      <td style="white-space:nowrap;">${r.permit_valid_upto ? fmtDate(r.permit_valid_upto) : "—"}</td>
      <td style="white-space:nowrap;">${r.fc_valid_upto ? fmtDate(r.fc_valid_upto) : "—"}</td>
      <td>${esc(r.driver_name || "—")}</td>
      <td style="white-space:nowrap;">${r.dl_validity ? fmtDate(r.dl_validity) : "—"}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VEHICLE MASTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${(rows || []).length} VEHICLE(S)</div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th>Vehicle No.</th><th>Vehicle Type</th><th>Make Year</th><th>Model</th><th>Chassis No.</th><th>Engine No.</th><th>Owner Name</th><th>Owner Mobile</th><th>Insurance Co.</th><th>Insurance Upto</th><th>Permit Upto</th><th>FC Upto</th><th>Driver</th><th>DL Validity</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="16" class="muted">No vehicles.</td></tr>`}</tbody>
    </table>
  `;
  open("Vehicle Master", inner);
}

export function printVendorMaster(rows) {
  const body = (rows || [])
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${esc(r.branch || "—")}</td>
      <td>${esc(r.vendor_code || "—")}</td>
      <td style="font-weight:600;">${esc(r.name || "")}</td>
      <td>${esc(r.type || "")}</td>
      <td>${esc(r.contact_person || "—")}</td>
      <td>${esc(r.phone || "—")}</td>
      <td>${esc(r.gstin || "—")}</td>
      <td>${esc(r.bank_name || "—")}</td>
      <td>${esc(r.account_holder || "—")}</td>
      <td>${esc(r.account_no || "—")}</td>
      <td>${esc(r.ifsc || "—")}</td>
      <td>${esc(r.bank_address || "—")}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">VENDOR MASTER / BROKER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">AS ON ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;•&nbsp; ${(rows || []).length} VENDOR(S)</div>
    <table style="font-size:10px;">
      <thead><tr>
        <th>Sl</th><th>Branch</th><th>Vendor/Broker Code</th><th>Vendor/Broker Name</th><th>Vendor/Broker Type</th><th>Contact Person</th><th>Mobile No.</th><th>GSTIN</th><th>Bank Name</th><th>Account Holder Name</th><th>A/c No.</th><th>IFSC</th><th>Bank Address</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="13" class="muted">No vendors.</td></tr>`}</tbody>
    </table>
  `;
  open("Vendor Master", inner);
}

export function printBranchSummary(rows, opts = {}) {
  const offices = opts.offices || [];
  const money = !!opts.money;
  const key = opts.metricKey || "bookings";
  const val = (v) => (money ? inr(v || 0) : String(v || 0));
  const rowTotal = (row) => offices.reduce((s, o) => s + (row[`${o}_${key}`] || 0), 0);
  const colTotal = (o) => (rows || []).reduce((s, row) => s + (row[`${o}_${key}`] || 0), 0);
  const grand = (rows || []).reduce((s, row) => s + rowTotal(row), 0);
  const body = (rows || [])
    .map(
      (row) => `<tr>
        <td style="font-weight:600;">${esc(row.label)} ${esc(String(row.key || "").slice(0, 4))}</td>
        ${offices.map((o) => `<td class="right">${val(row[`${o}_${key}`])}</td>`).join("")}
        <td class="right" style="font-weight:700;">${val(rowTotal(row))}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY BRANCH SUMMARY</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${esc(opts.metricLabel || "")} — LAST 6 MONTHS</div>
    <table style="font-size:12px;">
      <thead><tr>
        <th>Month</th>${offices.map((o) => `<th class="right">${esc(o)}</th>`).join("")}<th class="right">Total</th>
      </tr></thead>
      <tbody>
        ${body}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td>TOTAL</td>${offices.map((o) => `<td class="right">${val(colTotal(o))}</td>`).join("")}<td class="right">${val(grand)}</td>
        </tr>
      </tbody>
    </table>
  `;
  open("Branch Summary", inner);
}

export function printMISReport(groups, opts = {}) {
  const arr = groups || [];
  const period = opts.periodLabel || "ALL RECORDS";
  const totRows = arr.reduce((s, g) => s + g.rows.length, 0);
  const totPending = arr.reduce((s, g) => s + g.rows.filter((r) => !r._delivered).length, 0);
  const totFreight = arr.reduce((s, g) => s + g.rows.reduce((a, r) => a + (Number(r.freight_amount) || 0), 0), 0);
  const totInvValue = arr.reduce((s, g) => s + g.rows.reduce((a, r) => a + (Number(r.invoice_value) || 0), 0), 0);
  const totPkg = arr.reduce((s, g) => s + g.rows.reduce((a, r) => a + (Number(r.packages) || 0), 0), 0);
  const PER_PAGE = 20;
  const chunks = [];
  arr.forEach((g) => {
    const n = Math.max(1, Math.ceil(g.rows.length / PER_PAGE));
    for (let c = 0; c < n; c++) chunks.push({ g, rows: g.rows.slice(c * PER_PAGE, (c + 1) * PER_PAGE), startIdx: c * PER_PAGE, contd: c > 0, lastOfGroup: c === n - 1 });
  });
  const pgList = [];
  let curPg = { chunks: [], count: 0 };
  chunks.forEach((ch) => {
    if (curPg.count > 0 && curPg.count + ch.rows.length > PER_PAGE) {
      pgList.push(curPg);
      curPg = { chunks: [], count: 0 };
    }
    curPg.chunks.push(ch);
    curPg.count += ch.rows.length;
  });
  if (curPg.chunks.length) pgList.push(curPg);
  const misInvTotal = (rs) => rs.reduce((s, r) => s + (Number(r.invoice_value) || 0), 0);
  const chunkHtml = (ch) => {
    const g = ch.g;
    const sub = g.rows.reduce((a, r) => a + (Number(r.freight_amount) || 0), 0);
    const subInv = misInvTotal(g.rows);
    const subPkg = g.rows.reduce((a, r) => a + (Number(r.packages) || 0), 0);
    const pend = g.rows.filter((r) => !r._delivered).length;
    const body = ch.rows
      .map(
        (r, i) => `<tr>
        <td>${ch.startIdx + i + 1}</td>
        <td>${esc(r.office || r.office_name || "—")}</td>
        <td>${esc(String(r.lr_no || r.consignment_no || "—").replace(/^CN[-\s]?/i, ""))}</td>
        <td>${r.lr_date || r.dispatch_date ? fmtDate(r.lr_date || r.dispatch_date) : "—"}</td>
        <td>${esc(r.origin || "—")}</td>
        <td>${esc(r.destination || "—")}</td>
        <td>${esc(r.consignee || "—")}</td>
        <td>${esc(r.basis || "—")}</td>
        <td>${esc(r.vehicle_no || "—")}</td>
        <td>${esc(r.vehicle_type || "—")}</td>
        <td>${esc(r.packages || "—")}</td>
        <td>${esc(r.invoice_no || "—")}</td>
        <td>${r.invoice_date ? fmtDate(r.invoice_date) : "—"}</td>
        <td class="right">${Number(r.invoice_value) ? inr(r.invoice_value) : "—"}</td>
        <td class="right">${r.freight_amount ? inr(r.freight_amount) : "—"}</td>
        <td>${r.delivery_date ? fmtDate(r.delivery_date) : "—"}</td>
        <td>${r.pod_path ? "Uploaded" : "Pending"}</td>
        <td style="font-weight:700;${r._delivered ? "color:#059669;" : "color:#d97706;"}">${r._delivered ? "DELIVERED" : "PENDING"}</td>
      </tr>`
      )
      .join("");
    return `
      <div style="margin-bottom:18px;">
        <div style="margin:0 0 8px;padding:7px 12px;border:1px solid #0f172a;border-radius:6px;font-size:12px;display:flex;justify-content:space-between;gap:12px;">
          <div><b>PARTY NAME:</b> ${esc(g.customer)}${g.code ? ` &nbsp;·&nbsp; <b>CODE:</b> ${esc(g.code)}` : ""}${ch.contd ? " (contd.)" : ""}</div>
          <div><b>Pending:</b> ${pend} &nbsp;&nbsp; <b>Delivered:</b> ${g.rows.length - pend} &nbsp;&nbsp; <b>Total:</b> ${g.rows.length}</div>
        </div>
        <table style="font-size:10px;">
          <thead><tr>
            <th>Sl</th><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Consignee Name</th><th>Basis</th><th>Vehicle No.</th><th>Vehicle Type</th><th>PKG</th><th>Invoice No.</th><th>Invoice Date</th><th class="right">Invoice Value</th><th class="right">Freight Amount</th><th>Delivery Date</th><th>POD</th><th>Status</th>
          </tr></thead>
          <tbody>
            ${body || `<tr><td colspan="18" class="muted">No consignments.</td></tr>`}
            ${ch.lastOfGroup ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="10" class="right">TOTAL</td><td>${subPkg}</td><td colspan="2"></td><td class="right">${inr(subInv)}</td><td class="right">${inr(sub)}</td><td colspan="3"></td></tr>` : ""}
          </tbody>
        </table>
      </div>`;
  };
  const inner = `
    <div class="title" style="letter-spacing:3px;">DAILY MIS REPORT${opts.branchLabel ? ` — ${esc(String(opts.branchLabel).toUpperCase())} BRANCH` : ""}</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${esc(period)}</div>
    <div style="margin:0 0 12px;display:flex;flex-wrap:wrap;gap:8px 20px;font-size:11px;">
      <div><b>Consignments:</b> ${totRows}</div>
      <div><b>Pending:</b> ${totPending}</div>
      <div><b>Delivered:</b> ${totRows - totPending}</div>
      <div><b>Total PKG:</b> ${totPkg}</div>
      <div><b>Total Invoice Value:</b> ${inr(totInvValue)}</div>
      <div><b>Total Freight:</b> ${inr(totFreight)}</div>
      ${opts.modeLabel ? `<div><b>Basis:</b> ${esc(opts.modeLabel)}</div>` : ""}
    </div>
    ${pgList
      .map(
        (pg, pi) => `<div style="${pi < pgList.length - 1 ? "page-break-after:always;" : ""}">
      ${pi > 0 ? `<div style="margin:0 0 8px;font-size:11px;font-weight:800;letter-spacing:1px;">DAILY MIS REPORT — ${esc(period)} (contd.)</div>` : ""}
      ${pg.chunks.map(chunkHtml).join("")}
      <div style="text-align:right;font-size:10px;margin-top:4px;color:#475569;">Page ${pi + 1} of ${pgList.length} · ${pg.count} consignment(s) on this page</div>
    </div>`
      )
      .join("")}
  `;
  open("MIS Report", inner);
}

export function printRateTalkPack(cust, rates, stats) {
  const list = rates || [];
  const rateBody = list
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="font-weight:600;">${esc(r.origin || "—")} → ${esc(r.destination || "—")}</td>
      <td>${esc(r.unit || "—")}</td>
      <td class="right" style="font-weight:700;">${inr(r.freight_amount)}</td>
      <td class="right">${Number(r.loading_charge) ? inr(r.loading_charge) : "—"}</td>
      <td class="right">${Number(r.unloading_charge) ? inr(r.unloading_charge) : "—"}</td>
      <td>${esc(r.payment_terms || "—")}</td>
      <td style="white-space:nowrap;">${r.valid_till ? `${r.valid_from ? fmtDate(r.valid_from) + " → " : ""}${fmtDate(r.valid_till)}` : "Always"}</td>
    </tr>`
    )
    .join("");
  const months = Object.keys(stats.monthsDed || {}).sort();
  const dedLine = months.length
    ? months.map((m) => `${new Date(m + "-01").toLocaleString("en-IN", { month: "short", year: "2-digit" })}: ${inr(stats.monthsDed[m])}`).join(" &nbsp;·&nbsp; ")
    : "No deductions in the last 6 months";
  const stat = (label, value, color) => `
    <div style="flex:1;border:1px solid ${color || "#cbd5e1"};border-radius:6px;padding:10px 12px;">
      <div style="font-size:10px;font-weight:700;letter-spacing:1px;color:#64748b;">${label}</div>
      <div style="font-size:14px;font-weight:800;margin-top:2px;">${value}</div>
    </div>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">RATE TALK PACK — NEGOTIATION SHEET</div>
    <div style="display:flex;justify-content:space-between;align-items:center;margin:8px 0 12px;font-size:12px;">
      <div><b>Customer:</b> <span style="font-size:14px;font-weight:800;">${esc(cust.name || "—")}</span>${cust.customer_code ? ` (${esc(cust.customer_code)})` : ""}</div>
      <div><b>Prepared:</b> ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    </div>
    <div style="font-size:11px;font-weight:800;letter-spacing:1px;margin:10px 0 4px;">1. AGREED RATE CARD</div>
    <table style="font-size:11px;">
      <thead><tr><th>Sl</th><th>Route</th><th>Vehicle Type</th><th class="right">Freight</th><th class="right">Loading</th><th class="right">Unloading</th><th>Payment Terms</th><th>Validity</th></tr></thead>
      <tbody>${rateBody || `<tr><td colspan="8" class="muted">No rates on file.</td></tr>`}</tbody>
    </table>
    <div style="font-size:11px;font-weight:800;letter-spacing:1px;margin:14px 0 4px;">2. PAYMENT HABITS (LAST 6 MONTHS)</div>
    <div style="display:flex;gap:10px;font-size:12px;">
      ${stat("RECEIVED", inr(stats.received))}
      ${stat("OUTSTANDING NOW", inr(stats.outstanding), "#f59e0b")}
      ${stat("DEDUCTED", `${inr(stats.deducted)} (${(stats.dedPct || 0).toFixed(1)}%)`, stats.dedPct >= 3 ? "#e11d48" : "#cbd5e1")}
      ${stat("BOUNCED CHEQUES", String(stats.bounces || 0), stats.bounces ? "#e11d48" : "#cbd5e1")}
      ${stat("BROKEN PROMISES", String(stats.brokenPromises || 0), stats.brokenPromises ? "#e11d48" : "#cbd5e1")}
    </div>
    <div style="font-size:11px;font-weight:800;letter-spacing:1px;margin:14px 0 4px;">3. DEDUCTION HISTORY (MONTH-WISE)</div>
    <div style="border:1px solid #cbd5e1;border-radius:6px;padding:10px 12px;font-size:11px;">${dedLine}</div>
    <div style="margin-top:16px;border:1px solid #0f172a;border-radius:6px;padding:12px 14px;font-size:12px;">
      <div style="font-weight:800;letter-spacing:1px;margin-bottom:10px;">NEGOTIATION NOTES / AGREED CHANGES</div>
      ${Array.from({ length: 3 }).map(() => `<div style="border-bottom:1.5px solid #0f172a;height:26px;"></div>`).join("")}
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-top:48px;font-size:12px;">
      <div style="text-align:center;"><div style="border-top:2px solid #0f172a;padding-top:6px;min-width:220px;font-weight:700;">Customer Signature</div></div>
      <div style="text-align:center;">
        <div style="font-weight:800;margin-bottom:56px;">For NISHA TRANSPORT CORPORATION</div>
        <div style="border-top:2px solid #0f172a;padding-top:6px;min-width:220px;font-weight:700;">Authorised Signatory</div>
      </div>
    </div>
  `;
  open(`Rate Talk Pack — ${cust.name || ""}`, inner);
}


export function printFreightMaster(rows) {
  const arr = rows || [];
  const branches = [...new Set(arr.map((r) => String(r.branch || "").trim()).filter(Boolean))];
  const branchLabel = branches.length === 1 ? branches[0] : null;
  const groups = {};
  arr.forEach((r) => {
    const k = r.customer_name || "—";
    (groups[k] = groups[k] || []).push(r);
  });
  const names = Object.keys(groups).sort();
  const sheet = (name, list, idx) => {
    const first = list[0] || {};
    const body = list
      .map(
        (r, i) => `<tr>
        <td>${i + 1}</td>
        <td>${esc(r.origin || "—")}</td>
        <td style="font-weight:700;">${esc(r.destination || "—")}</td>
        <td class="right" style="font-weight:700;">${r.freight_amount ? inr(r.freight_amount) : "—"}</td>
        <td class="right">${r.loading_charge ? inr(r.loading_charge) : "—"}</td>
        <td class="right">${r.unloading_charge ? inr(r.unloading_charge) : "—"}</td>
        <td>${esc(r.unit || "—")}</td>
        <td>${r.valid_from || r.valid_till ? `${r.valid_from ? fmtDate(r.valid_from) : "—"} → ${r.valid_till ? fmtDate(r.valid_till) : "open"}` : "Always"}</td>
        <td>${esc(r.remarks || "—")}</td>
      </tr>`
      )
      .join("");
    return `
    <div style="${idx < names.length - 1 ? "page-break-after:always;" : ""}">
      <div class="title" style="letter-spacing:3px;">FREIGHT MASTER — CUSTOMER RATE CARD${branchLabel ? ` · ${esc(branchLabel.toUpperCase())} BRANCH` : ""}</div>
      <div style="display:flex;justify-content:space-between;gap:10px;margin:0 0 10px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;">
        <span>CUSTOMER: ${esc(name)}${first.customer_code ? ` (${esc(first.customer_code)})` : ""}</span>
        <span>BRANCH: ${esc(first.branch || "—")}</span>
        ${first.payment_terms ? `<span>PAYMENT TERMS: ${esc(first.payment_terms)}</span>` : ""}
        <span>${list.length} ROUTE(S) · PRINTED ${fmtDate(new Date().toISOString().slice(0, 10))}</span>
      </div>
      <table style="font-size:11px;">
        <thead><tr>
          <th>Sl</th><th>From</th><th>Destination</th><th class="right">Freight Charge</th><th class="right">Loading</th><th class="right">Unloading</th><th>Vehicle Type</th><th>Validity</th><th>Remarks</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>
      <div style="display:flex;justify-content:flex-end;margin-top:28px;font-size:11px;font-weight:700;">
        <span style="text-align:center;">For NISHA TRANSPORT CORPORATION<br/><span style="display:block;height:80px;">${signatureImg(68)}</span><span style="display:inline-block;border-top:1px solid #000;padding-top:4px;font-weight:400;min-width:180px;">Authorised Signatory</span></span>
      </div>
    </div>`;
  };
  const inner = names.length
    ? names.map((n, i) => sheet(n, groups[n], i)).join("")
    : '<p class="muted">No freight rates.</p>';
  open("Freight Master", inner);
}

export function printCircularRegister(rows) {
  const arr = rows || [];
  const body = arr
    .map(
      (r, i) => `<tr>
      <td>${i + 1}</td>
      <td style="white-space:nowrap;font-weight:700;">${esc(String(r.circular_no || "—").replace(/^CIR[-\s]?/i, "CIR-"))}</td>
      <td style="white-space:nowrap;">${r.date ? fmtDateDay(r.date) : "—"}</td>
      <td style="font-weight:600;">${esc(r.subject || "—")}</td>
      <td>${esc(r.category || "General")}</td>
      <td>${esc(r.to_branch || "All Branches")}</td>
      <td>${esc(r.issued_by || "—")}</td>
      <td>${(r.acks || []).length ? `${(r.acks || []).length} branch(es)` : "—"}</td>
    </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">CIRCULAR REGISTER</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${arr.length} CIRCULAR(S) &nbsp;·&nbsp; PRINTED ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table style="font-size:11px;">
      <thead><tr>
        <th>Sl</th><th>Circular No.</th><th>Date</th><th>Subject</th><th>Category</th><th>To</th><th>Issued By</th><th>Read By</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="8" class="muted">No circulars.</td></tr>`}</tbody>
    </table>
  `;
  open("Circular Register", inner);
}

export function printRateCompare(sections) {
  const arr = sections || [];
  const block = (s) => `
    <div style="margin-bottom:14px;">
      <div style="padding:6px 10px;border:1px solid #0f172a;border-bottom:none;border-radius:6px 6px 0 0;background:#f1f5f9;font-size:12px;font-weight:800;">${esc(s.route)} &nbsp;·&nbsp; ${s.rows.length} CUSTOMER RATE(S)</div>
      <table style="font-size:11px;margin:0;">
        <thead><tr><th>Sl</th><th>Customer</th><th>Vehicle Type</th><th class="right">Freight</th><th class="right">vs Lowest</th><th class="right">Loading</th><th class="right">Unloading</th><th>Validity</th><th></th></tr></thead>
        <tbody>
          ${s.rows
            .map((r, i) => {
              const amt = Number(r.freight_amount) || 0;
              const diff = s.min ? Math.round(((amt - s.min) / s.min) * 100) : 0;
              return `<tr${amt === s.min && s.rows.length > 1 ? ' style="background:#fef3c7;"' : ""}>
              <td>${i + 1}</td>
              <td style="font-weight:700;">${esc(r.customer_name || "—")}</td>
              <td>${esc(r.unit || "—")}</td>
              <td class="right" style="font-weight:700;">${inr(amt)}</td>
              <td class="right">${amt === s.min ? "—" : `+${diff}%`}</td>
              <td class="right">${r.loading_charge ? inr(r.loading_charge) : "—"}</td>
              <td class="right">${r.unloading_charge ? inr(r.unloading_charge) : "—"}</td>
              <td>${r.valid_till ? `till ${fmtDate(r.valid_till)}` : "Always"}</td>
              <td>${amt === s.min && s.rows.length > 1 ? "LOWEST" : ""}</td>
            </tr>`;
            })
            .join("")}
        </tbody>
      </table>
    </div>`;
  const inner = `
    <div class="title" style="letter-spacing:3px;">RATE COMPARE — ROUTE-WISE</div>
    <div style="margin:0 0 12px;padding:8px 14px;border:1px solid #0f172a;border-radius:6px;font-size:12px;font-weight:800;text-align:center;letter-spacing:0.5px;">${arr.length} ROUTE(S) &nbsp;·&nbsp; PRINTED ${fmtDate(new Date().toISOString().slice(0, 10))} &nbsp;·&nbsp; FOR RATE NEGOTIATION USE</div>
    ${arr.map(block).join("") || '<p class="muted">No rates.</p>'}
  `;
  open("Rate Compare", inner);
}

export function printRouteProfit({ rows, totals, from, to }) {
  const arr = rows || [];
  const body = arr
    .map(
      (r) => `<tr style="${r.profit < 0 ? "background:#fef2f2;" : ""}">
      <td>${esc(r.branch || "—")}</td>
      <td>${esc(r.lr_no || "—")}</td>
      <td style="white-space:nowrap;">${r.lr_date ? r.lr_date.split(", ").map((d) => fmtDate(d)).join(", ") : "—"}</td>
      <td style="font-weight:700;">${esc(r.from_place || "—")}</td>
      <td style="font-weight:700;">${esc(r.destination || "—")}${r.profit < 0 ? ' <span style="color:#b91c1c;font-weight:800;">(LOSS)</span>' : ""}</td>
      <td>${esc(r.customer || "—")}</td>
      <td class="right">${r.bills}</td>
      <td class="right">${inr(r.billing)}</td>
      <td class="right">${r.trips}</td>
      <td class="right" style="color:#b45309;">${inr(r.hire)}</td>
      <td>${esc(r.vehicle_no || "—")}</td>
      <td class="right" style="font-weight:800;color:${r.profit < 0 ? "#b91c1c" : "#047857"};">${r.profit < 0 ? "-" + inr(Math.abs(r.profit)) : inr(r.profit)}</td>
      <td class="right" style="color:${r.profit < 0 ? "#b91c1c" : "#334155"};">${r.margin === null || r.margin === undefined ? "—" : r.margin + "%"}</td>
    </tr>`
    )
    .join("");
  const losses = arr.filter((r) => r.profit < 0).length;
  const inner = `
    <div class="title" style="letter-spacing:3px;">ROUTE PROFIT — MONTHLY REVIEW</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Period:</b> ${from ? fmtDate(from) : "Start"} to ${to ? fmtDate(to) : "Today"} &nbsp;&nbsp; <b>Lanes:</b> ${arr.length} &nbsp;&nbsp; ${losses ? `<span style="color:#b91c1c;font-weight:800;">${losses} loss-making lane(s)</span>` : '<span style="color:#047857;font-weight:700;">All lanes profitable</span>'}</div>
    <table class="tight" style="font-size:9.5px;">
      <thead><tr><th>Branch</th><th>LR No.</th><th>LR Date</th><th>From</th><th>Destination</th><th>Customer Name</th><th class="right">Bills</th><th class="right">Billing (Rs.)</th><th class="right">Hire Trips</th><th class="right">Vehicle Hire (Rs.)</th><th>Vehicle No.</th><th class="right">Profit (Rs.)</th><th class="right">Margin %</th></tr></thead>
      <tbody>
        ${body || '<tr><td colspan="13" class="muted">No routes found.</td></tr>'}
        ${body ? `<tr style="font-weight:800;background:#f1f5f9;"><td colspan="6">TOTAL</td><td class="right">${arr.reduce((s, r) => s + r.bills, 0)}</td><td class="right">${inr(totals.billing)}</td><td class="right">${arr.reduce((s, r) => s + r.trips, 0)}</td><td class="right">${inr(totals.hire)}</td><td></td><td class="right" style="color:${totals.profit < 0 ? "#b91c1c" : "#047857"};">${totals.profit < 0 ? "-" + inr(Math.abs(totals.profit)) : inr(totals.profit)}</td><td></td></tr>` : ""}
      </tbody>
    </table>
    <div style="margin-top:28px;display:flex;justify-content:space-between;font-size:11px;"><span>Prepared by: ______________</span><span>Reviewed by: ______________</span><span>For Nisha Transport Corporation</span></div>
  `;
  open("NTC Route Profit", inner);
}

function attendanceRefPage() {
  const codes = [
    ["P", "Present", "Full working day"],
    ["HD", "Half Day", "Counted as 0.5 payable day"],
    ["A", "Absent", "Not payable unless regularised"],
    ["L", "Leave", "Approved leave application required"],
    ["H", "Holiday", "Company / government holiday — payable"],
  ];
  const rules = [
    "Mark In Time on arrival and Out Time while leaving, every working day. The branch in-charge must countersign weekly.",
    "Leave must be applied in advance through a Leave Application and approved by the branch in-charge / head office.",
    "Yearly leave quota is set per staff in Staff Master; leaves beyond quota are treated as Loss of Pay unless approved by management.",
    "Sundays and notified festival holidays (marked on this card) are paid holidays.",
    "Half day applies when working hours are less than half the scheduled hours.",
    "Payable days = Present + Holidays + (Half Days × 0.5). Salary is computed pro-rata on payable days.",
    "Submit this card to the branch in-charge at month end for salary processing.",
  ];
  return `
    <div class="frame frame-full challan-page ref-page">
      ${header()}
      <div class="title" style="letter-spacing:3px;">STAFF REFERENCE — STATUS CODES & LEAVE RULES</div>
      <table class="tight grid" style="font-size:11px;margin-top:10px;">
        <thead><tr><th style="width:60px;">Code</th><th style="width:120px;">Status</th><th>Meaning</th></tr></thead>
        <tbody>${codes.map((c) => `<tr><td style="text-align:center;font-weight:800;">${c[0]}</td><td style="font-weight:600;">${c[1]}</td><td>${c[2]}</td></tr>`).join("")}</tbody>
      </table>
      <div style="font-size:12px;font-weight:800;letter-spacing:1px;margin:18px 0 6px;">LEAVE & ATTENDANCE RULES</div>
      <ol style="font-size:11px;line-height:1.9;margin:0;padding-left:18px;">
        ${rules.map((r) => `<li>${r}</li>`).join("")}
      </ol>
      <div style="margin-top:auto;padding-top:30px;font-size:10px;color:#64748b;">This reference page is part of the Monthly Attendance Card — Nisha Transport Corporation.</div>
    </div>`;
}

export function printBlankAttendanceCard(month, branch, holidayList) {
  const line = (label, w) => `<div style="display:flex;align-items:flex-end;gap:6px;font-size:11px;"><b>${label}:</b><span style="display:inline-block;width:${w}px;border-bottom:1px solid #0f172a;height:14px;"></span></div>`;
  const hmap = {};
  (holidayList || []).forEach((h) => {
    if (!h.branch || h.branch === "All" || !branch || h.branch === branch) hmap[(h.date || "").slice(0, 10)] = h.title || "Holiday";
  });
  let monthLabel = "";
  let dim = 31;
  let y = 0, m = 0;
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    y = Number(month.slice(0, 4)); m = Number(month.slice(5, 7));
    dim = new Date(y, m, 0).getDate();
    monthLabel = new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }
  const dayCell = (d) => {
    if (!y) return { day: "", sunday: false };
    const dt = new Date(y, m - 1, d);
    const sunday = dt.getDay() === 0;
    return { day: sunday ? "SUNDAY" : dt.toLocaleDateString("en-GB", { weekday: "short" }), sunday };
  };
  const half = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => {
    const d = from + i;
    const { day, sunday } = dayCell(d);
    const iso = y ? `${month}-${String(d).padStart(2, "0")}` : "";
    const hol = !sunday && iso && hmap[iso];
    return `<tr style="${sunday ? "background:#fee2e2;" : hol ? "background:#fef3c7;" : ""}">
      <td style="text-align:center;font-weight:600;">${d}</td>
      <td style="text-align:center;${sunday ? "color:#b91c1c;font-weight:700;font-size:8px;" : hol ? "color:#b45309;font-weight:700;font-size:7.5px;" : ""}">${hol ? esc(String(hmap[iso]).toUpperCase()) : day}</td>
      <td></td><td></td><td></td>
    </tr>`;
  }).join("");
  const headRow = `<tr><th style="width:30px;">Date</th><th style="width:52px;">Day</th><th>In Time</th><th>Out Time</th><th>Staff Signature</th></tr>`;
  const inner = `
      <div class="frame frame-full challan-page">
      ${header()}
      <div class="title" style="letter-spacing:3px;">MONTHLY ATTENDANCE CARD</div>
      <div style="display:flex;flex-wrap:wrap;gap:10px 28px;margin:10px 0 12px;">
        ${line("Staff Name", 200)}
        ${line("Staff Code", 100)}
        ${line("Designation", 150)}
        ${branch ? `<div style="font-size:11px;"><b>Branch:</b> ${esc(branch)}</div>` : line("Branch", 110)}
        ${monthLabel ? `<div style="font-size:11px;"><b>Month:</b> ${esc(monthLabel)}</div>` : line("Month", 120)}
      </div>
      <div style="display:flex;gap:14px;align-items:flex-start;">
        <table class="tight grid" style="font-size:10px;flex:1;">
          <thead>${headRow}</thead>
          <tbody>${half(1, 15)}</tbody>
        </table>
        <table class="tight grid" style="font-size:10px;flex:1;">
          <thead>${headRow}</thead>
          <tbody>${half(16, dim)}</tbody>
        </table>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:auto;padding-top:40px;font-size:11px;">
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Staff Signature</span>
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Branch In-charge</span>
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Authorized Signatory</span>
      </div>
      </div>
      ${attendanceRefPage()}
  `;
  open("NTC Blank Attendance Card", inner, { hideHeader: true });
}

export function printLeaveBalance(list, year) {
  const TYPES = ["Casual Leave", "Sick Leave", "Earned Leave", "Loss of Pay", "Other"];
  const tot = { count: 0, total: 0, quota: 0, types: {} };
  const body = (list || [])
    .map((a, i) => {
      tot.count += a.count; tot.total += a.total; tot.quota += a.quota || 0;
      TYPES.forEach((t) => { tot.types[t] = (tot.types[t] || 0) + (a.types[t] || 0); });
      const rem = a.remaining;
      return `<tr>
        <td>${i + 1}</td>
        <td style="font-weight:600;">${esc(a.name || "—")}</td>
        <td>${esc(a.branch || "—")}</td>
        ${TYPES.map((t) => `<td class="right">${a.types[t] || "—"}</td>`).join("")}
        <td class="right">${a.count}</td>
        <td class="right">${a.quota || "—"}</td>
        <td class="right" style="font-weight:700;">${a.total}</td>
        <td class="right" style="font-weight:700;${rem != null && rem < 0 ? "color:#b91c1c;" : ""}">${rem == null ? "—" : rem}</td>
      </tr>`;
    })
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:3px;">YEARLY LEAVE BALANCE SUMMARY — ${esc(String(year))}</div>
    <div style="margin:0 0 12px;font-size:11px;"><b>Approved leaves only</b> &nbsp;·&nbsp; <b>Staff:</b> ${(list || []).length} &nbsp;·&nbsp; <b>Printed:</b> ${fmtDate(new Date().toISOString().slice(0, 10))}</div>
    <table class="tight" style="font-size:10.5px;">
      <thead><tr>
        <th>Sl</th><th>Employee Name</th><th>Branch</th>${TYPES.map((t) => `<th class="right">${t}</th>`).join("")}<th class="right">Applications</th><th class="right">Quota</th><th class="right">Taken</th><th class="right">Remaining</th>
      </tr></thead>
      <tbody>
        ${body || `<tr><td colspan="12" class="muted">No approved leaves in ${esc(String(year))}.</td></tr>`}
        <tr style="font-weight:800;background:#f1f5f9;">
          <td colspan="3" class="right">TOTAL</td>
          ${TYPES.map((t) => `<td class="right">${tot.types[t] || "—"}</td>`).join("")}
          <td class="right">${tot.count}</td><td class="right">${tot.quota || "—"}</td><td class="right">${tot.total}</td><td class="right">${tot.quota ? tot.quota - tot.total : "—"}</td>
        </tr>
      </tbody>
    </table>
    <div style="display:flex;justify-content:space-between;margin-top:40px;font-size:11px;">
      <span style="border-top:1px solid #0f172a;padding-top:4px;">Prepared By (HR)</span>
      <span style="border-top:1px solid #0f172a;padding-top:4px;">Authorized Signatory</span>
    </div>
  `;
  open(`NTC Leave Balance ${year}`, inner);
}

export function printBulkAttendanceCards(staffList, month, holidayList) {
  const y = Number(month.slice(0, 4)), m = Number(month.slice(5, 7));
  const dim = new Date(y, m, 0).getDate();
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const headRow = `<tr><th style="width:30px;">Date</th><th style="width:52px;">Day</th><th>In Time</th><th>Out Time</th><th>Staff Signature</th></tr>`;
  const mapFor = (branch) => {
    const hm = {};
    (holidayList || []).forEach((h) => {
      if (!h.branch || h.branch === "All" || h.branch === branch) hm[(h.date || "").slice(0, 10)] = h.title || "Holiday";
    });
    return hm;
  };
  const half = (from, to, hm) => Array.from({ length: to - from + 1 }, (_, i) => {
    const d = from + i;
    const dt = new Date(y, m - 1, d);
    const sunday = dt.getDay() === 0;
    const iso = `${month}-${String(d).padStart(2, "0")}`;
    const hol = !sunday && hm[iso];
    const day = sunday ? "SUNDAY" : hol ? String(hm[iso]).toUpperCase() : dt.toLocaleDateString("en-GB", { weekday: "short" });
    return `<tr style="${sunday ? "background:#fee2e2;" : hol ? "background:#fef3c7;" : ""}">
      <td style="text-align:center;font-weight:600;">${d}</td>
      <td style="text-align:center;${sunday ? "color:#b91c1c;font-weight:700;font-size:8px;" : hol ? "color:#b45309;font-weight:700;font-size:7.5px;" : ""}">${esc(day)}</td>
      <td></td><td></td><td></td>
    </tr>`;
  }).join("");
  const cards = (staffList || []).map((s, idx) => {
    const hm = mapFor(s.branch || "Chennai");
    return `
    <div class="frame frame-full challan-page">
      ${header()}
      <div class="title" style="letter-spacing:3px;">MONTHLY ATTENDANCE CARD</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px 26px;margin:10px 0 12px;font-size:11.5px;">
        <div><b>Staff Name:</b> ${esc(s.name || "—")}</div>
        <div><b>Staff Code:</b> ${esc(s.staff_code || "—")}</div>
        <div><b>Designation:</b> ${esc(s.designation || "—")}</div>
        <div><b>Branch:</b> ${esc(s.branch || "Chennai")}</div>
        <div><b>Month:</b> ${esc(monthLabel)}</div>
      </div>
      <div style="display:flex;gap:14px;align-items:flex-start;">
        <table class="tight grid" style="font-size:10px;flex:1;"><thead>${headRow}</thead><tbody>${half(1, 15, hm)}</tbody></table>
        <table class="tight grid" style="font-size:10px;flex:1;"><thead>${headRow}</thead><tbody>${half(16, dim, hm)}</tbody></table>
      </div>
      <div style="display:flex;justify-content:space-between;margin-top:auto;padding-top:40px;font-size:11px;">
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Staff Signature</span>
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Branch In-charge</span>
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Authorized Signatory</span>
      </div>
    </div>
  `;}).join("");
  open(`NTC Attendance Cards — ${monthLabel}`, cards + attendanceRefPage(), { hideHeader: true });
}

export function printStaffMonthCard(info, monthLabel, month, staffRows) {
  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const byDate = {};
  (staffRows || []).forEach((r) => { byDate[(r.date || "").slice(0, 10)] = r; });
  const counts = { Present: 0, Absent: 0, "Half Day": 0, Leave: 0, Holiday: 0 };
  const rows = Array.from({ length: daysInMonth }, (_, i) => {
    const iso = `${month}-${String(i + 1).padStart(2, "0")}`;
    const d = new Date(y, m - 1, i + 1);
    const day = d.toLocaleDateString("en-GB", { weekday: "short" });
    const r = byDate[iso];
    if (r) counts[counts[r.status] != null ? r.status : "Present"] += 1;
    const sun = d.getDay() === 0 ? "background:#fef2f2;" : "";
    const stClr = r ? (r.status === "Absent" ? "color:#b91c1c;font-weight:700;" : r.status === "Present" ? "color:#047857;font-weight:600;" : "color:#b45309;font-weight:600;") : "";
    return `<tr style="${sun}">
      <td style="text-align:center;font-weight:600;">${i + 1}</td>
      <td>${day}</td>
      <td>${r?.in_time ? esc(r.in_time) : ""}</td>
      <td>${r?.out_time ? esc(r.out_time) : ""}</td>
      <td style="${stClr}">${r ? esc(r.status || "Present") : ""}</td>
      <td style="font-size:9px;">${r?.remarks ? esc(r.remarks) : ""}</td>
    </tr>`;
  }).join("");
  const payable = counts.Present + counts.Holiday + counts["Half Day"] * 0.5;
  const inner = `
    <div class="title" style="letter-spacing:3px;">MONTHLY ATTENDANCE CARD</div>
    <div style="display:flex;flex-wrap:wrap;gap:8px 26px;margin:10px 0 12px;font-size:11.5px;">
      <div><b>Staff Name:</b> ${esc(info.staff_name || "—")}</div>
      <div><b>Designation:</b> ${esc(info.designation || "—")}</div>
      <div><b>Branch:</b> ${esc(info.branch || "Chennai")}</div>
      <div><b>Month:</b> ${esc(monthLabel)}</div>
    </div>
    <table class="tight" style="font-size:10px;">
      <thead><tr>
        <th style="width:40px;">Date</th><th style="width:60px;">Day</th><th style="width:80px;">In Time</th><th style="width:80px;">Out Time</th><th style="width:100px;">Status</th><th>Remarks</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="display:flex;flex-wrap:wrap;gap:8px 22px;margin-top:10px;font-size:11.5px;font-weight:700;">
      <div>Present: ${counts.Present}</div>
      <div>Half Day: ${counts["Half Day"]}</div>
      <div>Absent: ${counts.Absent}</div>
      <div>Leave: ${counts.Leave}</div>
      <div>Holiday: ${counts.Holiday}</div>
      <div style="color:#047857;">Payable Days: ${payable}</div>
    </div>
    <div style="display:flex;justify-content:space-between;margin-top:34px;font-size:11px;">
      <span style="border-top:1px solid #0f172a;padding-top:4px;">Staff Signature</span>
      <span style="border-top:1px solid #0f172a;padding-top:4px;">Branch In-charge</span>
      <span style="border-top:1px solid #0f172a;padding-top:4px;">Authorized Signatory</span>
    </div>
  `;
  open(`Attendance Card — ${info.staff_name || ""}`, inner);
}

export function printStaffJoiningLetter(r, token) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const refNo = `NTC/HR/${String(r.staff_code || "STAFF").replace(/\s+/g, "")}/${new Date().getFullYear()}`;
  const jd = r.joining_date ? fmtDate(r.joining_date) : "____________";
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
  const photo = r.photo_path
    ? `<img src="${base}${r.photo_path}?auth=${token || ""}" style="width:92px;height:110px;object-fit:cover;border:1px solid #cbd5e1;border-radius:4px;" />`
    : `<div style="width:92px;height:110px;border:1px dashed #cbd5e1;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:9px;color:#94a3b8;">PHOTO</div>`;
  const inner = `
    <div style="display:flex;justify-content:space-between;margin:8px 0 16px;font-size:12px;">
      <div><b>Ref No.:</b> ${esc(refNo)}</div>
      <div><b>Date:</b> ${fmtDate(todayIso)}</div>
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:14px;">
      <div style="font-size:12px;line-height:1.6;">
        <b>To,</b><br/>
        <b>${esc(r.name || "")}</b><br/>
        ${r.phone ? `Mobile: ${esc(r.phone)}<br/>` : ""}${r.email ? `Email: ${esc(r.email)}<br/>` : ""}
      </div>
      ${photo}
    </div>
    <div style="text-align:center;font-size:14px;font-weight:800;letter-spacing:3px;text-decoration:underline;margin:14px 0;">APPOINTMENT LETTER</div>
    <div style="font-size:12.5px;font-weight:700;margin:14px 0;">Subject: <span style="text-decoration:underline;">Appointment as ${esc(r.designation || "Staff")} — ${esc(r.branch || "Chennai")} Branch</span></div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin:10px 0 14px;">
      <tr>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;background:#f8fafc;font-weight:700;width:18%;">Mobile Number</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;width:32%;">${esc(r.phone || "____________")}</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;background:#f8fafc;font-weight:700;width:18%;">Staff Code</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;width:32%;">${esc(r.staff_code || "—")}</td>
      </tr>
      <tr>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;background:#f8fafc;font-weight:700;">Aadhaar Card No.</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;">${esc(r.aadhar_no || "____________")}</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;background:#f8fafc;font-weight:700;">PAN Card No.</td>
        <td style="border:1px solid #cbd5e1;padding:5px 8px;">${esc(r.pan_no || "____________")}</td>
      </tr>
    </table>
    <div style="font-size:12px;line-height:1.9;margin:14px 0;">
      Dear ${esc(r.name || "")},<br/><br/>
      With reference to your application and the subsequent interview you had with us, we are pleased to appoint you as
      <b>${esc(r.designation || "Staff")}</b> at our <b>${esc(r.branch || "Chennai")}</b> branch of <b>${esc(COMPANY.name)}</b>,
      with effect from <b>${jd}</b>, on the following terms:<br/><br/>
      1. Your remuneration will be as mutually agreed at the time of joining and reviewed as per company policy.<br/>
      2. You will report to the Branch In-charge / Management and perform duties assigned to you diligently and honestly.<br/>
      3. Your working hours, holidays and leave shall be governed by the rules of the company in force from time to time.<br/>
      4. You shall maintain strict confidentiality of all business, customer and financial information of the company.<br/>
      5. Your appointment is subject to satisfactory verification of documents and references; the first three months shall be treated as a probation period.<br/>
      6. Either party may terminate this appointment by giving notice as per company policy.<br/><br/>
      Please sign and return the duplicate copy of this letter as a token of your acceptance of the above terms.<br/><br/>
      We welcome you to the ${esc(COMPANY.name)} family and wish you a long and successful career with us.
    </div>
    <div style="display:flex;justify-content:space-between;align-items:flex-start;padding-top:26px;">
      <div style="font-size:12px;line-height:1.8;">
        <b>ACCEPTANCE</b><br/>
        I accept the above terms of appointment.<br/><br/><br/>
        <span style="border-top:1px solid #0f172a;padding-top:4px;">Signature of Employee&nbsp;&nbsp;&nbsp;&nbsp;Date: ____________</span>
      </div>
      <div style="text-align:right;font-size:12px;line-height:1.7;padding-right:24px;">
        <b>For ${esc(COMPANY.name)}</b>
        <div style="height:64px;"></div>
        Authorized Signatory
      </div>
    </div>
  `;
  open("NTC Appointment Letter", inner, { frame: true, fullPage: true });
}

export function printStaffExperienceLetter(r, leavingDate) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const refNo = `NTC/HR/${String(r.staff_code || "STAFF").replace(/\s+/g, "")}/${new Date().getFullYear()}`;
  const jd = r.joining_date ? fmtDate(r.joining_date) : "____________";
  const ld = leavingDate ? fmtDate(leavingDate) : fmtDate(todayIso);
  const inner = `
    <div style="display:flex;justify-content:space-between;margin:8px 0 16px;font-size:12px;">
      <div><b>Ref No.:</b> ${esc(refNo)}</div>
      <div><b>Date:</b> ${fmtDate(todayIso)}</div>
    </div>
    <div style="text-align:center;font-size:14px;font-weight:800;letter-spacing:3px;text-decoration:underline;margin:18px 0;">EXPERIENCE &amp; RELIEVING LETTER</div>
    <div style="text-align:center;font-size:12.5px;font-weight:700;margin:12px 0 18px;">TO WHOMSOEVER IT MAY CONCERN</div>
    <div style="font-size:12px;line-height:1.9;margin:14px 0;">
      This is to certify that <b>${esc(r.name || "")}</b> (Staff Code: ${esc(r.staff_code || "—")}) was employed with
      <b>${esc(COMPANY.name)}</b> as <b>${esc(r.designation || "Staff")}</b> at our <b>${esc(r.branch || "Chennai")}</b> branch
      from <b>${jd}</b> to <b>${ld}</b>.<br/><br/>
      During the period of employment, we found them sincere, hardworking, honest and dedicated to the duties assigned.
      Their conduct and character were good, and they are being relieved of their duties at their own request, with all dues settled.<br/><br/>
      We thank them for their services and wish them every success in their future endeavours.
    </div>
    <div style="display:flex;justify-content:flex-end;padding-top:40px;">
      <div style="text-align:right;font-size:12px;line-height:1.7;padding-right:24px;">
        <b>For ${esc(COMPANY.name)}</b>
        <div style="height:64px;"></div>
        Authorized Signatory
      </div>
    </div>
  `;
  open("NTC Experience Letter", inner, { frame: true, fullPage: true });
}

export function printLoaRegister(rows) {
  const dmy2 = (d) => (d ? `${String(d).slice(8, 10)}-${String(d).slice(5, 7)}-${String(d).slice(0, 4)}` : "");
  const body = (rows || [])
    .map(
      (r) => `<tr>
        <td style="text-align:center;font-weight:700;">${esc(r.loa_no)}</td>
        <td>${dmy2(r.date)}</td>
        <td>${esc(r.office || "")}</td>
        <td>${r.from_date ? dmy2(r.from_date) : "Beginning"} to ${r.to_date ? dmy2(r.to_date) : "Today"}</td>
        <td class="right">${inr(r.closing)}</td>
        <td>${esc(r.created_by || "")}</td>
      </tr>`
    )
    .join("");
  const inner = `
    <div class="title" style="letter-spacing:2px;">LOA REGISTER</div>
    <div class="muted" style="text-align:center;margin-bottom:10px;">All issued Letters of Account — total ${(rows || []).length}</div>
    <table class="tight">
      <thead><tr>
        <th style="text-align:center;">LOA No.</th><th>Date</th><th>Branch</th><th>Period</th><th class="right">CBS</th><th>Issued By</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="6" class="muted">No LOAs issued yet</td></tr>`}</tbody>
    </table>
  `;
  open("LOA Register", inner, { frame: true });
}
